import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { getTranslator, persistLanguage, readPersistedLanguage } from './translations.js';
import { ShoppingCart, ChevronLeft, Sparkles, Coins, Gem, Crown, Scroll, CheckCircle, Loader, Lock, X, MessageCircle, Send, Image as ImageIcon, MapPin, Phone, Package, Truck, Trash2, AlertCircle, Clock, XCircle, LogOut } from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { Toaster, toast } from 'react-hot-toast';
import PackFeature from './PackFeature';
import TreasureWheel from './TreasureWheel';
import { AppNavbarMobile, AppNavbarDesktop, MobileNavBottomSpacer, MobileNavBottomReserve, SwipeCommanderButton } from './AppNavbar';
import PWAInstallModal from './PWAInstallModal';
import AccessModal from './AccessModal';
import AccessPubModal from './AccessPubModal';
import FloatingChatPanel, { LiquidProgressBar } from './FloatingChatPanel';
import { applyDeviceLayout, refineDeviceModelAsync, detectDevice } from './deviceDetect';
import { safeUserError, GENERIC_USER_ERROR } from './utils/safeUserError';
import { getApiAuthHeaders } from './utils/apiAuthFetch';
import { prepareChatImage } from './utils/prepareChatImage';
import { uploadWithProgress } from './utils/uploadWithProgress';
import {
  getOrCreateKeyPair,
  registerLicense,
  verifyAndAuthenticate,
  hasStoredLicenseKey,
  ensureNfcAuth,
  checkNfcSession,
  getStoredNfcId,
  getStoredNfcShopChatId,
  getOrCreateNfcId,
  isNfcEntryActive,
  markNfcEntry,
  trackNfcHit,
} from './utils/licenseAuth';
import { recoverApiSession, resilientJsonRequest } from './utils/sessionRecovery';


// ── Vibration tactile globale (légère, iOS + Android) ──────────────────────
// Ordre de priorité :
//  1) Telegram WebApp HapticFeedback (iOS ET Android dans Telegram) → le plus fin
//  2) navigator.vibrate (Android Chrome / WebView)
//  3) Astuce iOS Safari 17.4+ : <input type="checkbox" switch> cliqué par programme
const Haptics = (() => {
  let last = 0;
  let iosLabel = null;
  const getTg = () => { try { return window.Telegram?.WebApp || null; } catch (e) { return null; } };
  const ensureIosSwitch = () => {
    if (iosLabel || typeof document === 'undefined' || !document.body) return iosLabel;
    try {
      const label = document.createElement('label');
      label.setAttribute('aria-hidden', 'true');
      label.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      input.tabIndex = -1;
      label.appendChild(input);
      document.body.appendChild(label);
      iosLabel = label;
    } catch (e) { iosLabel = null; }
    return iosLabel;
  };
  const fire = (kind = 'light') => {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (now - last < 40) return; // anti-rafale : jamais deux vibrations collées
    last = now;
    try {
      const hf = getTg()?.HapticFeedback;
      if (hf) {
        if (kind === 'success' || kind === 'error' || kind === 'warning') hf.notificationOccurred(kind);
        else if (kind === 'select') hf.selectionChanged();
        else hf.impactOccurred('light');
        return;
      }
    } catch (e) { /* fallback */ }
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(kind === 'success' ? [10, 40, 14] : kind === 'error' ? [18, 50, 18] : 12);
        return;
      }
    } catch (e) { /* fallback */ }
    try {
      const l = ensureIosSwitch();
      if (l) l.click();
    } catch (e) { /* silencieux */ }
  };
  return { tap: () => fire('light'), select: () => fire('select'), success: () => fire('success'), error: () => fire('error') };
})();

const HAPTIC_SELECTOR = 'button, a[href], summary, select, [role="button"], [role="tab"], [role="switch"], [role="menuitem"], [role="option"], input[type="checkbox"], input[type="radio"], input[type="range"], input[type="button"], input[type="submit"], .cursor-pointer';
const HAPTIC_IGNORE = '[disabled], [aria-disabled="true"], [data-no-haptic], input[type="text"], input[type="tel"], input[type="email"], input[type="password"], input[type="number"], input[type="search"], textarea';

// Un seul écouteur global (phase capture) : couvre client, admin, livreur, dashboard, modales, portails…
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__hapticsInstalled) {
  window.__hapticsInstalled = true;
  document.addEventListener('click', (e) => {
    try {
      if (e.isTrusted === false) return; // ignore les clics programmatiques
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(HAPTIC_IGNORE)) return;
      let interactive = !!t.closest(HAPTIC_SELECTOR);
      if (!interactive) {
        // div/carte cliquable avec onClick React → détectée via cursor:pointer
        const cur = window.getComputedStyle(t).cursor;
        interactive = cur === 'pointer';
      }
      if (interactive) Haptics.tap();
    } catch (err) { /* ne jamais casser un clic */ }
  }, true);
}

// ── Contexte de langue global ──────────────────────────────
const LanguageContext = createContext({ lang: 'fr', setLang: () => {}, t: getTranslator('fr') });
const useLanguage = () => useContext(LanguageContext);

// ── Support Telegram unique (boutons « Contacter le support », « J'en veux plus », paiement Pass) ──
const SUPPORT_TELEGRAM_USERNAME = 'not_enough1m';
const SUPPORT_TELEGRAM_URL = `https://t.me/${SUPPORT_TELEGRAM_USERNAME}`;
const SUPPORT_TELEGRAM_APP_URL = `tg://resolve?domain=${SUPPORT_TELEGRAM_USERNAME}`;
const openSupportTelegram = () => {
  try {
    const wa = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    if (wa && typeof wa.openTelegramLink === 'function') {
      wa.openTelegramLink(SUPPORT_TELEGRAM_URL);
      return;
    }
  } catch (e) { /* fallback ci-dessous */ }
  // Ouvre Telegram dans la conversation, jamais Safari / le profil web
  window.location.href = SUPPORT_TELEGRAM_APP_URL;
};

// ── Composant sélecteur de langue (Liquid Glass) ───────────
const LanguageSwitcher = () => {
  const { lang, setLang } = useLanguage();
  const langs = ['FR', 'EN', 'DE'];
  return (
    <div className="gs-top-bar-1" style={{
      position: 'fixed',
      top: 'var(--gs-top-row)',
      right: 12,
      zIndex: 'var(--gs-z-chrome)',
      display: 'flex',
      gap: 2,
      background: 'linear-gradient(135deg, rgba(74,144,226,0.18) 0%, rgba(30,60,140,0.28) 60%, rgba(10,20,80,0.38) 100%)',
      backdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
      WebkitBackdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
      border: '1px solid rgba(107,163,232,0.32)',
      borderRadius: 10,
      padding: '3px 4px',
      boxShadow: '0 4px 18px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.15)',
    }}>
      {langs.map((l) => {
        const code = l.toLowerCase();
        const active = lang === code;
        return (
          <button
            key={l}
            onClick={() => setLang(code)}
            style={{
              fontSize: 10,
              fontWeight: active ? 700 : 500,
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.06em',
              color: active ? '#fff' : 'rgba(200,220,255,0.7)',
              background: active
                ? 'linear-gradient(135deg, rgba(74,144,226,0.55) 0%, rgba(30,80,180,0.65) 100%)'
                : 'transparent',
              border: active ? '1px solid rgba(107,163,232,0.5)' : '1px solid transparent',
              borderRadius: 6,
              minWidth: 28,
              padding: '3px 5px',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              boxShadow: active ? '0 2px 8px rgba(74,144,226,0.35)' : 'none',
              outline: 'none',
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
};

// Style global premium pour une interface valorisée à des millions d'euros
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    /* Importation de polices premium */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:wght@400;600;700;800;900&display=swap');
    
    /* Variables CSS thème montagne/suisse */
    :root {
      --snow-white: #ffffff;
      --snow-light: #f8f9fa;
      --snow-medium: #e9ecef;
      --mountain-gray: #6c757d;
      --mountain-gray-dark: #495057;
      --mountain-gray-darker: #343a40;
      --swiss-blue: #4a90e2;
      --swiss-blue-light: #6ba3e8;
      --alpine-silver: #c0c0c0;
      --alpine-silver-light: #e0e0e0;
      --dark-bg: #1a1d24;
      --dark-surface: #2a2f3a;
      --dark-elevated: #3a3f4a;
      --text-primary: #ffffff;
      --text-secondary: #e9ecef;
      --text-muted: #adb5bd;
      --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.2);
      --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.3);
      --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.4);
      --shadow-xl: 0 16px 64px rgba(0, 0, 0, 0.5);
      --shadow-snow: 0 0 40px rgba(255, 255, 255, 0.2);
      --shadow-snow-lg: 0 0 80px rgba(255, 255, 255, 0.15);
    }
    
    /* ══ SPLASH SCREEN — Liquid Glass animations ══ */
    @keyframes splashRipple {
      0%   { transform: scale(0.85); opacity: 0.55; }
      50%  { transform: scale(1.12); opacity: 0.20; }
      100% { transform: scale(0.85); opacity: 0.55; }
    }
    @keyframes splashPulseRing {
      0%   { transform: scale(1);    opacity: 0.70; }
      60%  { transform: scale(1.18); opacity: 0.15; }
      100% { transform: scale(1);    opacity: 0.70; }
    }
    @keyframes splashFloat {
      0%, 100% { transform: translateY(0px);   }
      50%       { transform: translateY(-10px); }
    }
    @keyframes splashDotPulse {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
      40%           { transform: scale(1.0); opacity: 1.0; }
    }
    @keyframes splashShimmer {
      0%   { background-position: -400px 0; }
      100% { background-position:  400px 0; }
    }
    @keyframes splashLogoBreath {
      0%, 100% { transform: scale(1); filter: drop-shadow(0 0 28px rgba(74,144,226,0.42)); }
      50%      { transform: scale(1.04); filter: drop-shadow(0 0 48px rgba(107,163,232,0.72)); }
    }
    @keyframes splashBarShimmer {
      0%   { transform: translateX(-120%); }
      100% { transform: translateX(220%); }
    }
    @keyframes splashLiquidIn {
      0%   { opacity: 0; transform: scale(0.82) translateY(22px); filter: blur(14px); }
      60%  { opacity: 1; transform: scale(1.03) translateY(-4px); filter: blur(0px);  }
      100% { opacity: 1; transform: scale(1.00) translateY(0px);  filter: blur(0px);  }
    }
    @keyframes splashRevealApp {
      0%   { opacity: 0; transform: scale(0.96) translateY(16px); filter: blur(8px);  }
      100% { opacity: 1; transform: scale(1.00) translateY(0px);  filter: blur(0px);  }
    }
    @keyframes splashExitBlur {
      0%   { opacity: 1; transform: scale(1.00); filter: blur(0px);  }
      100% { opacity: 0; transform: scale(1.06); filter: blur(20px); }
    }
    @keyframes orb1Float {
      0%, 100% { transform: translate(0px, 0px)   scale(1.00); }
      33%       { transform: translate(18px,-12px) scale(1.08); }
      66%       { transform: translate(-8px, 10px) scale(0.95); }
    }
    @keyframes orb2Float {
      0%, 100% { transform: translate(0px, 0px)    scale(1.00); }
      40%       { transform: translate(-14px, 8px)  scale(1.06); }
      70%       { transform: translate(10px,-14px)  scale(0.96); }
    }
    .splash-liquid-card { animation: splashLiquidIn 0.75s cubic-bezier(0.22,1,0.36,1) both; }
    .splash-exit        { animation: splashExitBlur 0.65s cubic-bezier(0.55,0,1,0.45) forwards; }
    .app-reveal         { animation: splashRevealApp 0.72s cubic-bezier(0.22,1,0.36,1) both; }
    .splash-dot-1 { animation: splashDotPulse 1.4s ease-in-out 0.00s infinite; }
    .splash-dot-2 { animation: splashDotPulse 1.4s ease-in-out 0.20s infinite; }
    .splash-dot-3 { animation: splashDotPulse 1.4s ease-in-out 0.40s infinite; }
    .orb-1 { animation: orb1Float 7s ease-in-out infinite; }
    .orb-2 { animation: orb2Float 9s ease-in-out infinite; }
    html, body {
      background: #0d1117 !important;
      min-height: 100%;
      min-height: 100dvh;
      min-height: -webkit-fill-available;
    }
    #root {
      background: transparent !important;
      min-height: 100%;
      min-height: 100dvh;
      min-height: -webkit-fill-available;
      position: relative;
    }
    :root {
      --gs-safe-top: max(env(safe-area-inset-top, 0px), var(--device-safe-top, 0px));
      --gs-safe-bottom: max(env(safe-area-inset-bottom, 0px), var(--device-safe-bottom, 0px));
      --gs-top-row: calc(var(--gs-safe-top) + 8px);
      --gs-top-row-2: calc(var(--gs-safe-top) + 46px);
      --gs-top-row-3: calc(var(--gs-safe-top) + 78px);
      --gs-top-content: calc(var(--gs-safe-top) + 110px);
      --gs-top-side-pad: 16px;
      /* Échelle z-index cohérente (évite superpositions absurdes) */
      --gs-z-base: 0;
      --gs-z-visitors: 1;      /* « x personnes ont visité » — au-dessus du fond, sous tout le reste */
      --gs-z-content: 10;
      --gs-z-chrome: 40;       /* Pass / coins / langue — accueil */
      --gs-z-nav: 50;          /* navbar mobile */
      --gs-z-sheet: 200;       /* bottom sheets / welcome */
      --gs-z-modal: 1000;      /* abonnement, paiement, pays, nom, arcade… */
      --gs-z-splash: 2000;     /* splash */
      --gs-z-lock: 4000;       /* maintenance / PWA / Access plein écran */
      --gs-z-chat: 4500;       /* FloatingChatPanel — au-dessus Access/PWA */
    }
    html.device-ios {
      --gs-top-row: calc(var(--gs-safe-top) + 6px);
      --gs-top-row-2: calc(var(--gs-safe-top) + 44px);
      --gs-top-row-3: calc(var(--gs-safe-top) + 76px);
      --gs-top-content: calc(var(--gs-safe-top) + 108px);
      --gs-top-side-pad: 16px;
    }
    html.device-android {
      --gs-top-row: calc(var(--gs-safe-top) + 8px);
      --gs-top-row-2: calc(var(--gs-safe-top) + 48px);
      --gs-top-row-3: calc(var(--gs-safe-top) + 80px);
      --gs-top-content: calc(var(--gs-safe-top) + 112px);
      --gs-top-side-pad: 16px;
    }
    .gs-page-pad {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      padding-bottom: 1.25rem;
      padding-top: calc(var(--gs-safe-top) + 14px) !important;
    }
    .gs-page-pad-lg {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
      padding-bottom: 1.5rem;
      padding-top: var(--gs-top-content) !important;
    }
    .gs-fixed-top {
      top: var(--gs-top-row) !important;
    }
    .gs-top-bar-1 {
      top: var(--gs-top-row) !important;
    }
    .gs-top-bar-2 {
      top: var(--gs-top-row-2) !important;
    }
    .gs-visitors-strip {
      top: var(--gs-top-row-3) !important;
      left: 0;
      right: 0;
      padding-left: var(--gs-top-side-pad);
      padding-right: var(--gs-top-side-pad);
      box-sizing: border-box;
    }
    .gs-bg-layer {
      position: fixed !important;
      top: 0;
      left: 0;
      width: 100%;
      /* 100vh stable (pas 100dvh) — évite le tremblement à l'ouverture du signet */
      height: 100vh;
      min-height: 100%;
      min-height: -webkit-fill-available;
      z-index: 0 !important;
      overflow: hidden !important;
      pointer-events: none !important;
      background-color: #0d1117;
      contain: paint;
      /* 100% 100% : l'art vertical (montagne + ville) reste visible — cover croppe le centre noir */
      background-image: url(/emoji/SWISS3.png);
      background-repeat: no-repeat !important;
      background-position: center center !important;
      background-size: 100% 100% !important;
    }
    html, body {
      overflow-x: hidden !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-feature-settings: 'kern' 1, 'liga' 1;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
      margin: 0;
      padding: 0;
      overscroll-behavior-y: none;
    }
    
    h1, h2, h3, h4, h5, h6 {
      font-family: 'Playfair Display', 'Inter', serif;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
    
    /* Classes premium réutilisables - Liquid Glass Apple Style */
    .card-premium {
      background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.25) 0%, 
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.15) 100%);
      backdrop-filter: blur(40px) saturate(300%) brightness(1.1);
      -webkit-backdrop-filter: blur(40px) saturate(300%) brightness(1.1);
      border: 1px solid rgba(255, 255, 255, 0.4);
      box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.12),
        0 2px 8px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(255, 255, 255, 0.2);
      transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      position: relative;
      overflow: hidden;
    }
    
    .card-premium::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.6) 50%, 
        transparent 100%);
      pointer-events: none;
    }
    
    .card-premium:hover {
      border-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(45px) saturate(350%) brightness(1.15);
      -webkit-backdrop-filter: blur(45px) saturate(350%) brightness(1.15);
      box-shadow: 
        0 12px 48px rgba(0, 0, 0, 0.15),
        0 4px 16px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.6),
        inset 0 -1px 0 rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
    
    .glass-card {
      background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.3) 0%, 
        rgba(255, 255, 255, 0.15) 50%,
        rgba(255, 255, 255, 0.2) 100%);
      backdrop-filter: blur(50px) saturate(300%) brightness(1.1);
      -webkit-backdrop-filter: blur(50px) saturate(300%) brightness(1.1);
      border: 1px solid rgba(255, 255, 255, 0.4);
      box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        0 2px 8px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(255, 255, 255, 0.2);
      position: relative;
      overflow: hidden;
    }
    
    .glass-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.7) 50%, 
        transparent 100%);
      pointer-events: none;
    }
    
    .shadow-multilayer {
      box-shadow: 
        0 2px 4px rgba(0, 0, 0, 0.2),
        0 4px 8px rgba(0, 0, 0, 0.25),
        0 8px 16px rgba(0, 0, 0, 0.3),
        0 16px 32px rgba(0, 0, 0, 0.35),
        0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    }
    
    .gradient-snow {
      background: linear-gradient(135deg, 
        #ffffff 0%, 
        #f8f9fa 25%, 
        #e9ecef 50%, 
        #f8f9fa 75%, 
        #ffffff 100%);
      background-size: 200% 200%;
      animation: gradient-shift 3s ease infinite;
    }
    
    @keyframes gradient-shift {
      /* Optimisé : utilise opacity au lieu de background-position */
      0%, 100% { opacity: 1; }
      50% { opacity: 0.85; }
    }
    
    @keyframes marquee {
      from { transform: translateX(100%); }
      to { transform: translateX(-100%); }
    }
    
    /* Masquer la scrollbar pour le scroll horizontal des plans */
    .subscription-scroll-container::-webkit-scrollbar {
      display: none;
    }
    
    .subscription-scroll-container {
      -ms-overflow-style: none;
      scrollbar-width: none;
      touch-action: pan-x;
      -webkit-overflow-scrolling: touch;
    }
    
    .subscription-scroll-container:active {
      cursor: grabbing;
    }
    
    .marquee-container {
      overflow: hidden;
      white-space: nowrap;
      width: 100%;
      position: relative;
      mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
      -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
    }
    
    .marquee-content {
      display: inline-flex;
      animation: marquee 120s linear infinite;
      will-change: transform;
    }
    
    .marquee-content:hover {
      animation-play-state: paused;
    }
    
    .gradient-text-snow {
      background: linear-gradient(135deg, 
        #ffffff 0%, 
        #f8f9fa 25%, 
        #e9ecef 50%, 
        #adb5bd 75%, 
        #6c757d 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      background-size: 200% 200%;
      animation: gradient-shift 3s ease infinite;
    }
    
    /* Texte glacé bleu cristallin pour GLOBAL STRAINS */
    .frosty-blue-text {
      font-family: 'Arial Black', 'Arial Bold', 'Helvetica Neue', sans-serif;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #7dd3fc;
      background: linear-gradient(135deg, 
        #a5d8ff 0%, 
        #7dd3fc 25%, 
        #60c5f0 50%, 
        #4fc3f7 75%, 
        #29b6f6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-shadow: 
        0 0 10px rgba(125, 211, 252, 0.5),
        0 0 20px rgba(125, 211, 252, 0.3),
        0 0 30px rgba(125, 211, 252, 0.2),
        0 2px 4px rgba(0, 0, 0, 0.3),
        0 4px 8px rgba(0, 0, 0, 0.2);
      filter: drop-shadow(0 0 8px rgba(125, 211, 252, 0.4)) 
              drop-shadow(0 0 16px rgba(125, 211, 252, 0.3))
              drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
      position: relative;
    }
    
    .frosty-blue-text::before {
      content: attr(data-text);
      position: absolute;
      left: 0;
      top: 0;
      z-index: -1;
      background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.3) 0%, 
        rgba(255, 255, 255, 0.1) 50%, 
        rgba(255, 255, 255, 0.3) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      filter: blur(1px);
      opacity: 0.6;
    }
    
    .glow-snow {
      box-shadow: 
        0 0 20px rgba(255, 255, 255, 0.3),
        0 0 40px rgba(255, 255, 255, 0.2),
        0 0 60px rgba(255, 255, 255, 0.15);
    }
    
    .glow-snow-lg {
      box-shadow: 
        0 0 40px rgba(255, 255, 255, 0.4),
        0 0 80px rgba(255, 255, 255, 0.3),
        0 0 120px rgba(255, 255, 255, 0.2);
    }
    
    @keyframes spin-smooth {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
    .spin-smooth {
      animation: spin-smooth 1s linear infinite;
      will-change: transform;
      transform-origin: center;
    }
    
    /* Animation de brillance premium */
    @keyframes shimmer {
      0% {
        background-position: -1000px 0;
      }
      100% {
        background-position: 1000px 0;
      }
    }
    
    .shimmer-effect {
      /* Animation désactivée pour performances (120 FPS) */
      background: transparent;
      pointer-events: none;
    }
    
    /* Styles iOS pour les contrôles vidéo */
    video::-webkit-media-controls-panel {
      background-color: rgba(0, 0, 0, 0.7) !important;
      border-radius: 0 0 12px 12px;
    }
    
    video::-webkit-media-controls-play-button {
      background-color: rgba(255, 255, 255, 0.9) !important;
      border-radius: 50% !important;
      width: 44px !important;
      height: 44px !important;
    }
    
    video::-webkit-media-controls-current-time-display,
    video::-webkit-media-controls-time-remaining-display {
      color: #ffffff !important;
      font-size: 14px !important;
      font-weight: 500 !important;
    }
    
    video::-webkit-media-controls-timeline {
      background-color: rgba(255, 255, 255, 0.3) !important;
      border-radius: 2px !important;
      height: 4px !important;
    }
    
    video::-webkit-media-controls-timeline::-webkit-slider-thumb {
      background-color: #ffffff !important;
      border-radius: 50% !important;
      width: 16px !important;
      height: 16px !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
    }
    
    video::-webkit-media-controls-volume-slider {
      background-color: rgba(255, 255, 255, 0.3) !important;
      border-radius: 2px !important;
      height: 4px !important;
    }
    
    video::-webkit-media-controls-volume-slider::-webkit-slider-thumb {
      background-color: #ffffff !important;
      border-radius: 50% !important;
      width: 12px !important;
      height: 12px !important;
    }
    
    video::-webkit-media-controls-mute-button {
      background-color: transparent !important;
      border-radius: 50% !important;
      width: 44px !important;
      height: 44px !important;
    }
    
    video::-webkit-media-controls-fullscreen-button {
      background-color: transparent !important;
      border-radius: 50% !important;
      width: 44px !important;
      height: 44px !important;
    }
    
    /* Pour Firefox */
    video::-moz-media-controls {
      background-color: rgba(0, 0, 0, 0.7) !important;
    }
    
    /* Pour les navigateurs modernes */
    video::media-controls {
      background-color: rgba(0, 0, 0, 0.7) !important;
    }
    
    /* Animation de battement pour le bouton prioritaire */
    @keyframes priority-pulse {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5);
      }
      50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
      }
    }
    
    .priority-button-pulse {
      animation: priority-pulse 1s ease-in-out infinite;
    }
    
    /* Empêcher le zoom sur mobile */
    * {
      touch-action: manipulation;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }
    
    /* Permettre le scroll horizontal dans les containers dédiés */
    .chart-scroll-container {
      touch-action: pan-x pan-y !important;
      overflow-x: scroll !important;
      -webkit-overflow-scrolling: touch !important;
      cursor: grab;
    }
    .chart-scroll-container:active {
      cursor: grabbing;
    }
    
    /* Permettre la sélection de texte dans les inputs et textarea */
    input, textarea {
      -webkit-user-select: text !important;
      user-select: text !important;
      touch-action: auto;
    }
    
    /* Empêcher le zoom automatique sur iOS pour les inputs */
    input[type="text"],
    input[type="email"],
    input[type="number"],
    input[type="tel"],
    input[type="url"],
    input[type="search"],
    textarea {
      font-size: 16px !important;
      -webkit-text-size-adjust: 100%;
    }
    
    html, body {
      width: 100%;
      position: relative;
    }
    
    /* Empêcher le zoom avec la molette */
    * {
      -ms-touch-action: pan-y;
    }
    
    /* ── Ambient Light — désactivé ── */

    /* ── PWA Fullscreen / Safe-area insets (iPhone notch, Dynamic Island, Android cutout) ── */
    /* En mode standalone PWA, la navbar du bas doit respecter l'encoche système */
    @supports (padding-bottom: env(safe-area-inset-bottom)) {
      .app-navbar-bottom {
        padding-bottom: env(safe-area-inset-bottom, 0px) !important;
      }
      .app-navbar-bottom-inner {
        /* Compense l'inset pour que les icônes ne soient pas sous la barre système */
        margin-bottom: env(safe-area-inset-bottom, 0px);
      }
      /* Spacer du bas : inclure l'inset système dans la hauteur réservée */
      .mobile-nav-bottom-spacer {
        height: calc(68px + env(safe-area-inset-bottom, 0px)) !important;
      }
      /* Navbar du haut : décaler sous la status bar / Dynamic Island */
      .app-navbar-top {
        padding-top: env(safe-area-inset-top, 0px) !important;
      }
    }

    /* Mode plein écran PWA — fond dark sur html/body seulement (#root reste transparent pour SWISS) */
    @media all and (display-mode: standalone) {
      html, body { background: #0d1117 !important; }
      #root { background: transparent !important; }
      .mobile-nav-bottom-spacer {
        height: var(--device-nav-height, calc(96px + env(safe-area-inset-bottom, 0px))) !important;
      }
    }
    html.device-standalone,
    html.device-standalone body {
      background: #0d1117 !important;
    }
    html.device-standalone #root {
      background: transparent !important;
    }
    html.device-ios.device-standalone body {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      background: #0d1117 !important;
    }
    .gs-mobile-nav-fixed {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: var(--gs-z-nav) !important;
      transform: translateZ(0);
      -webkit-transform: translateZ(0);
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    }
  `;
  if (!document.head.querySelector('style[data-spin-animation]')) {
    style.setAttribute('data-spin-animation', 'true');
    document.head.appendChild(style);
  }
}

const formatChatTimestamp = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const orderStatusStyles = {
  0: { label: 'Préparation', color: '#e9ecef', bg: 'rgba(233, 236, 239, 0.2)' },
  1: { label: 'En Route', color: '#ffffff', bg: 'rgba(255, 255, 255, 0.2)' },
  2: { label: 'Arrivé Imminente', color: '#f8f9fa', bg: 'rgba(248, 249, 250, 0.2)' },
  3: { label: 'Livré', color: '#4a90e2', bg: 'rgba(74, 144, 226, 0.2)' },
};

const DEVICE_ID_KEY = 'telegramShopDeviceId';
const STAFF_PANEL_KEY = 'gs_staff_panel';
const STAFF_CHAT_KEY = 'gs_staff_chat';
const STAFF_ORDER_KEY = 'gs_staff_order'; // commande ciblée par une notification

const readStaffPanelFromLocation = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const panel = params.get('panel') || sessionStorage.getItem(STAFF_PANEL_KEY) || '';
    const chatId = params.get('chatId') || sessionStorage.getItem(STAFF_CHAT_KEY) || '';
    if (panel === 'driver' || panel === 'dashboard') {
      sessionStorage.setItem(STAFF_PANEL_KEY, panel);
      if (chatId) sessionStorage.setItem(STAFF_CHAT_KEY, chatId);
      return { panel, chatId };
    }
  } catch { /* silencieux */ }
  return { panel: '', chatId: '' };
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return value ? value.split('=')[1] : null;
};

const setCookieValue = (name, value, maxAgeSeconds = 60 * 60 * 24 * 365) => {
  if (typeof document === 'undefined') return;
  const expires = maxAgeSeconds <= 0 ? 'expires=Thu, 01 Jan 1970 00:00:00 GMT' : `max-age=${maxAgeSeconds}`;
  document.cookie = `${name}=${value}; path=/; ${expires}`;
};

const ensureDeviceId = () => {
  if (typeof window === 'undefined') return null;
  let existingId = null;
  try {
    existingId = window.localStorage.getItem(DEVICE_ID_KEY);
  } catch (error) {
    console.warn('localStorage device id inaccessible', error);
  }
  if (!existingId) {
    try {
      existingId = window.sessionStorage.getItem(DEVICE_ID_KEY);
    } catch {
      existingId = null;
    }
  }
  if (!existingId) {
    existingId = getCookieValue(DEVICE_ID_KEY);
  }
  if (!existingId) {
    const fallbackRandom = Math.random().toString(36).slice(2);
    existingId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `dev_${fallbackRandom}`;
  }
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, existingId);
  } catch {}
  try {
    window.sessionStorage.setItem(DEVICE_ID_KEY, existingId);
  } catch {}
  setCookieValue(DEVICE_ID_KEY, existingId);
  return existingId;
};

// Licence par appareil : génère la paire de clés ECDSA P-256 (clé privée en localStorage)
// et enregistre la clé publique côté serveur, liée au chatId.
// Appel en arrière-plan (non bloquant) après chaque auth réussie.
const ensureLicenseRegistered = async (chatId) => {
  if (!chatId) return;
  try {
    const { publicKeyPem } = await getOrCreateKeyPair();
    const deviceId = ensureDeviceId();
    const result = await registerLicense(publicKeyPem, deviceId);
    if (result?.success) {
      console.log(`🔐 Licence enregistrée pour ${String(chatId).slice(-4)} (${result.status || 'active'})`);
    } else if (result?.code === 'LICENSE_ON_OTHER_DEVICE') {
      console.warn('⚠️ Licence déjà active sur un autre appareil');
    } else {
      console.warn('⚠️ Enregistrement licence:', result?.error || result?.code);
    }
  } catch (e) {
    console.warn('⚠️ ensureLicenseRegistered échoué (non bloquant):', e?.message);
  }
};

const persistValueAcrossStorages = (key, value) => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(key, value);
    } catch {}
    try {
      window.sessionStorage.setItem(key, value);
    } catch {}
  }
  setCookieValue(key, value);
};

const readPersistentValue = (key) => {
  if (typeof window !== 'undefined') {
    try {
      const fromLocal = window.localStorage.getItem(key);
      if (fromLocal !== null) return fromLocal;
    } catch {}
    try {
      const fromSession = window.sessionStorage.getItem(key);
      if (fromSession !== null) return fromSession;
    } catch {}
  }
  return getCookieValue(key);
};

const removePersistentValue = (key) => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(key);
    } catch {}
    try {
      window.sessionStorage.removeItem(key);
    } catch {}
  }
  setCookieValue(key, '', 0);
};

// 🪙 Composant CoinIcon — affiche la pièce Global Coins de façon fiable
const CoinIcon = ({ size = 18, style = {} }) => {
  const [imgError, setImgError] = React.useState(false);
  if (imgError) {
    return (
      <span
        aria-label="coin"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          fontSize: size * 0.9,
          lineHeight: 1,
          userSelect: 'none',
          ...style
        }}
      >
        🪙
      </span>
    );
  }
  return (
    <img
      src="/emoji/coin1.png"
      alt="🪙"
      onError={() => setImgError(true)}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        ...style
      }}
    />
  );
};

const detectMobileOrTelegram = () => {
  if (typeof window === 'undefined') return true;
  const ua = navigator.userAgent || '';
  const tg = !!(window.Telegram && window.Telegram.WebApp);
  return tg
    || window.innerWidth < 768
    || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
};

// Composant Background Sophistiqué — 1 image, zoom calé 1× à l'ouverture (pas de transform)
const SophisticatedBackground = () => {
  const [isMobile, setIsMobile] = useState(detectMobileOrTelegram);
  const layerRef = useRef(null);
  const sizedOnce = useRef(false);

  useEffect(() => {
    const el = layerRef.current;
    if (!el || typeof window === 'undefined') return;

    const applySize = () => {
      // Viewport + écran : on prend le max pour couvrir y compris le côté droit
      const iw = window.innerWidth || document.documentElement.clientWidth || 0;
      const ih = window.innerHeight || document.documentElement.clientHeight || 0;
      const sw = window.screen?.width || 0;
      const sh = window.screen?.height || 0;
      const w = Math.ceil(Math.max(iw, Math.min(sw, sh) || 0, 1));
      const h = Math.ceil(Math.max(ih, Math.max(sw, sh) || 0, 1));

      const aspect = w / Math.max(h, 1);
      let zoom = 1.10;
      if (aspect < 0.48) zoom = 1.18;
      else if (aspect < 0.55) zoom = 1.14;
      else if (aspect > 0.7) zoom = 1.08;

      const bw = Math.ceil(w * zoom);
      const bh = Math.ceil(h * zoom);
      el.style.width = `${bw}px`;
      el.style.height = `${bh}px`;
      el.style.left = `${Math.floor((w - bw) / 2)}px`;
      el.style.top = `${Math.floor((h - bh) / 2)}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
      sizedOnce.current = true;
    };

    if (!sizedOnce.current) applySize();

    const onOrient = () => {
      sizedOnce.current = false;
      setIsMobile(detectMobileOrTelegram());
      // Léger délai après rotation — une seule fois, pas de boucle resize
      setTimeout(applySize, 300);
    };
    window.addEventListener('orientationchange', onOrient);
    return () => window.removeEventListener('orientationchange', onOrient);
  }, []);

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  return (
    <div className="gs-bg-layer" ref={layerRef} aria-hidden="true">
      {/* Orbes de lumière statiques pour performance - thème neige */}
      {!isMobile && (
        <>
          <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-15 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
        }}
          />
          <div
        className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-12 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(233, 236, 239, 0.3) 0%, transparent 70%)',
        }}
          />
          <div
        className="absolute top-1/2 right-0 w-80 h-80 rounded-full opacity-10 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(248, 249, 250, 0.25) 0%, transparent 70%)',
        }}
          />
        </>
      )}

      {/* Pattern de grille premium subtil - thème neige */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Particules animées - Désactivées sur mobile pour performance */}
      {!isMobile && (
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={{
            fpsLimit: 30,
            interactivity: {
              events: {
                onHover: {
                  enable: false,
                },
                onClick: {
                  enable: false,
                },
              },
            },
            particles: {
              color: {
                value: ["#ffffff", "#f8f9fa", "#e9ecef"],
              },
              links: {
                enable: false,
              },
              move: {
                direction: "none",
                enable: true,
                outModes: {
                  default: "out",
                },
                random: true,
                speed: 0.2,
                straight: false,
              },
              number: {
                density: {
                  enable: true,
                  area: 2000,
                },
                value: 10,
              },
              opacity: {
                value: { min: 0.2, max: 0.4 },
                animation: {
                  enable: false,
                },
              },
              shape: {
                type: "circle",
              },
              size: {
                value: { min: 1, max: 3 },
                animation: {
                  enable: false,
                },
              },
            },
            detectRetina: false,
            smooth: false,
          }}
          className="absolute inset-0"
        />
      )}

      {/* Vignette légère */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(8, 12, 22, 0.28) 100%)',
        }}
      />
    </div>
  );
};

// Composant AmbientLightEffect — désactivé
const AmbientLightEffect = () => null;


// Helper function pour les appels fetch avec timeout
const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Timeout: La requête a pris trop de temps');
    }
    throw error;
  }
};

// ── Cache local des commandes livreur ───────────────────────────────────────
// Une notification de commande peut arriver alors que l'app est fermée, en
// arrière-plan ou que le réseau est instable. Sans persistance, le livreur
// ouvre l'app sur une liste vide (« Aucune commande ») alors qu'une commande
// vient de lui être notifiée. On conserve donc la dernière liste synchronisée.
const DRIVER_ORDERS_CACHE_KEY = 'gs_driver_orders_v1';

const readDriverOrdersCache = () => {
  try {
    const raw = window.localStorage.getItem(DRIVER_ORDERS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const orders = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.orders) ? parsed.orders : []);
    return orders.filter((o) => o && o.orderId);
  } catch {
    return [];
  }
};

const writeDriverOrdersCache = (orders) => {
  try {
    if (!Array.isArray(orders)) return;
    const clean = orders.filter((o) => o && o.orderId);
    window.localStorage.setItem(
      DRIVER_ORDERS_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), orders: clean })
    );
  } catch {
    /* quota dépassé / mode privé → on continue sans cache */
  }
};
 

// ═══════════════════════════════════════════════════════════════════
// COMPOSANT ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────────
// DONUT CHART SVG — cercle de progression animé
// ─────────────────────────────────────────────────────────────
const DonutRing = ({ value, max, color, size = 88, stroke = 9, label, sub }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const cx = size / 2, cy = size / 2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${pct * circ} ${circ}`} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 5px ${color}88)`, transition: 'stroke-dasharray 1s cubic-bezier(.4,0,.2,1)' }} />
        <g style={{ transform: 'rotate(90deg)', transformOrigin: `${cx}px ${cy}px` }}>
          <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
            style={{ fill: '#fff', fontSize: size * 0.20, fontWeight: '900', fontFamily: 'Inter,sans-serif' }}>
            {value ?? '—'}
          </text>
          {sub && (
            <text x={cx} y={cy + size * 0.155} textAnchor="middle" dominantBaseline="middle"
              style={{ fill: 'rgba(255,255,255,0.4)', fontSize: size * 0.115, fontFamily: 'Inter,sans-serif' }}>
              {sub}
            </text>
          )}
        </g>
      </svg>
      {label && <p style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.75)', textAlign: 'center', margin: 0, maxWidth: size + 16 }}>{label}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ADMIN DASHBOARD
// ─────────────────────────────────────────────────────────────
// Construit les headers d'authentification pour les appels admin.
// Même logique que getApiAuthHeaders() (utils/apiAuthFetch) :
// envoie le pwaToken (signet longue durée) OU le deviceId enregistré.
// Sans cela le backend répond 401 "preuve d'identité requise".
function buildAdminAuthHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  try {
    const pwaToken = localStorage.getItem('pwaToken') || '';
    if (pwaToken) {
      headers['x-pwa-token'] = pwaToken;
      return headers;
    }
    const DEVICE_KEY = 'telegramShopDeviceId';
    const deviceId = localStorage.getItem(DEVICE_KEY) || sessionStorage.getItem(DEVICE_KEY) || '';
    if (deviceId && deviceId.length >= 8) {
      headers['x-device-id'] = deviceId;
    }
  } catch { /* localStorage inaccessible → continuer sans */ }
  return headers;
}

const AdminDashboard = ({ chatId, BACKEND_URL }) => {
  // Wrapper fetch qui injecte automatiquement les headers d'authentification.
  // Sans ces headers, le backend répond 401 "preuve d'identité requise".
  const adminFetch = (url, options = {}) => {
    const authHeaders = buildAdminAuthHeaders();
    // Injecter x-chat-id si on a un chatId admin connu — backend acceptera si isAccessAdmin(chatId)
    if (chatId) {
      authHeaders['x-chat-id'] = String(chatId);
    }
    const merged = {
      ...options,
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
    };
    // Si body est un objet JSON, s'assurer que Content-Type est bien mis
    return fetch(url, merged);
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [silentLoading, setSilentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  // Notifications marketing
  const [notifList, setNotifList] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSending, setNotifSending] = useState(null); // type en cours d'envoi
  // LUFFA canal
  const [luffaLink, setLuffaLink] = useState('');
  // WHATSAPP canal
  const [whatsappLink, setWhatsappLink] = useState('');
  // Sondage marketing
  const [surveyQuestion, setSurveyQuestion] = useState('');
  const [surveyAnswers, setSurveyAnswers] = useState(['', '', '', '']);
  const [surveyVotes, setSurveyVotes] = useState([]);
  const [surveyVotesLoading, setSurveyVotesLoading] = useState(false);
  // Avis clients
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  // Ajustements revenus
  const [showRevenueModal, setShowRevenueModal] = useState(null); // 'ajout'|'retrait'|null
  const [revenueAmount, setRevenueAmount] = useState('');
  const [revenueMotif, setRevenueMotif] = useState('');
  const [revenueAdjustments, setRevenueAdjustments] = useState([]);
  const [revenueAdjTotal, setRevenueAdjTotal] = useState(0);
  const [revenueSubmitting, setRevenueSubmitting] = useState(false);
  // Jeu arcade admin
  const [adminGameLeaderboard, setAdminGameLeaderboard] = useState([]);
  const [adminGameLoading, setAdminGameLoading] = useState(false);
  const [adminGameWeek, setAdminGameWeek] = useState('');
  // Clients bannis
  const [bannedClients, setBannedClients] = useState([]);
  const [bannedLoading, setBannedLoading] = useState(false);
  const [unbanningId, setUnbanningId] = useState(null);
  // Access (PCS / Paysafecard / crypto reçu)
  const [accessDeposits, setAccessDeposits] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessActionId, setAccessActionId] = useState(null);
  const [accessChatDep, setAccessChatDep] = useState(null);
  const [accessChatMsgs, setAccessChatMsgs] = useState([]);
  const [accessChatInput, setAccessChatInput] = useState('');
  const [accessChatMinimized, setAccessChatMinimized] = useState(false);
  const [accessChatSending, setAccessChatSending] = useState(false);
  const [accessChatUploading, setAccessChatUploading] = useState(false);
  const [accessChatUploadProgress, setAccessChatUploadProgress] = useState(0);
  const [accessChatUploadingId, setAccessChatUploadingId] = useState(null);
  const [accessExpandedId, setAccessExpandedId] = useState(null);
  const [accessVerifyUrl, setAccessVerifyUrl] = useState(null);
  // TRAFFIC — bot activation
  const [trafficData, setTrafficData] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficAction, setTrafficAction] = useState(null);
  const [trafficMsg, setTrafficMsg] = useState(null);
  // Historique commandes livrées
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  // Clôture mensuelle
  const [showMonthlyRecap, setShowMonthlyRecap] = useState(false);
  const [monthlyResetLoading, setMonthlyResetLoading] = useState(false);

  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/reviews?chatId=${chatId}&limit=50`);
      const json = await res.json();
      if (json.success) { setReviews(json.reviews || []); setReviewsTotal(json.total || 0); }
    } catch { /* */ }
    setReviewsLoading(false);
  };

  const loadRevenueAdjustments = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/revenue-adjustments?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) { setRevenueAdjustments(json.adjustments || []); setRevenueAdjTotal(json.total || 0); }
    } catch { /* */ }
  };

  const submitRevenueAdjustment = async () => {
    if (!revenueAmount || parseFloat(revenueAmount) <= 0) return;
    setRevenueSubmitting(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/revenue-adjustment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, type: showRevenueModal, amount: parseFloat(revenueAmount), motif: revenueMotif })
      });
      const json = await res.json();
      if (json.success) {
        setShowRevenueModal(null);
        setRevenueAmount('');
        setRevenueMotif('');
        await loadRevenueAdjustments();
        doFetch(true);
      }
    } catch { /* */ }
    setRevenueSubmitting(false);
  };

  const loadAdminGameLeaderboard = async () => {
    setAdminGameLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/game-leaderboard?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) { setAdminGameLeaderboard(json.top7 || []); setAdminGameWeek(json.weekKey || ''); }
    } catch { /* */ }
    setAdminGameLoading(false);
  };

  const loadBannedClients = async () => {
    setBannedLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/banned-clients?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) setBannedClients(json.banned || []);
    } catch { /* */ }
    setBannedLoading(false);
  };

  const loadAccessDeposits = async () => {
    setAccessLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/access-deposits?chatId=${encodeURIComponent(chatId || '')}`);
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setAccessDeposits(json.deposits || []);
      } else if (res.status === 401 || res.status === 403) {
        setAccessDeposits([]);
        console.warn('Access admin: session expirée — rouvrir le dashboard depuis Telegram');
      }
    } catch { /* */ }
    setAccessLoading(false);
  };

  const confirmAccessDeposit = async (id, isCrypto = false) => {
    setAccessActionId(id);
    try {
      const path = isCrypto
        ? `${BACKEND_URL}/admin/access-deposits/${encodeURIComponent(id)}/confirm-crypto`
        : `${BACKEND_URL}/admin/access-deposits/${encodeURIComponent(id)}/confirm`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId }),
      });
      const json = await res.json();
      if (json.success) await loadAccessDeposits();
    } catch { /* */ }
    setAccessActionId(null);
  };

  const cancelAccessDeposit = async (id) => {
    setAccessActionId(id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/access-deposits/${encodeURIComponent(id)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId }),
      });
      const json = await res.json();
      if (json.success) await loadAccessDeposits();
    } catch { /* */ }
    setAccessActionId(null);
  };

  const resolveAccessMediaUrl = (raw) => {
    let imgRaw = String(raw || '');
    if (!imgRaw) return '';
    if (imgRaw.startsWith('blob:')) return imgRaw;
    // nginx SPA sur /uploads → servir via /api/uploads
    let pathname = imgRaw;
    let search = '';
    if (/^https?:\/\//i.test(imgRaw)) {
      try {
        const u = new URL(imgRaw);
        pathname = u.pathname || '/';
        search = u.search || '';
      } catch { return imgRaw; }
    } else if (!pathname.startsWith('/')) {
      pathname = `/${pathname}`;
    }
    pathname = pathname.replace(/^\/api\/uploads\//i, '/uploads/');
    if (pathname.startsWith('/uploads/')) pathname = `/api${pathname}`;
    return `https://global-strains.shop${pathname}${search}`;
  };

  const mapAccessChatMessages = (msgs) => (msgs || []).map((m) => {
    const isImage = m.type === 'image' || m.mediaType === 'image';
    return {
      ...m,
      type: isImage ? 'image' : (m.type || 'text'),
      url: isImage ? resolveAccessMediaUrl(m.url || m.mediaUrl) : (m.url || null),
      content: m.content || m.text || '',
    };
  });

  const openAccessAdminChat = async (dep) => {
    setAccessChatDep(dep);
    setAccessChatMinimized(false);
    setAccessChatInput('');
    try {
      const r = await adminFetch(
        `${BACKEND_URL}/activation/deposit/${encodeURIComponent(dep.id)}/chat?chatId=${encodeURIComponent(chatId)}&markRead=1`,
        { headers: { ...getApiAuthHeaders(), 'x-chat-id': String(chatId || ''), 'x-mark-read': '1' } }
      );
      const j = await r.json();
      setAccessChatMsgs(mapAccessChatMessages(j.messages || []));
    } catch { setAccessChatMsgs([]); }
    loadAccessDeposits();
  };

  // Poll chat Access admin (photos / messages client en live)
  useEffect(() => {
    if (!accessChatDep?.id || accessChatMinimized) return undefined;
    const depId = accessChatDep.id;
    const tick = async () => {
      try {
        const r = await adminFetch(
          `${BACKEND_URL}/activation/deposit/${encodeURIComponent(depId)}/chat?chatId=${encodeURIComponent(chatId)}&markRead=1`,
          { headers: { ...getApiAuthHeaders(), 'x-chat-id': String(chatId || ''), 'x-mark-read': '1' } }
        );
        const j = await r.json();
        if (j.success) setAccessChatMsgs(mapAccessChatMessages(j.messages || []));
      } catch { /* soft */ }
    };
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  // mapAccessChatMessages est stable (fn locale pure)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessChatDep?.id, accessChatMinimized, chatId, BACKEND_URL]);

  const sendAccessAdminChatText = async () => {
    const text = String(accessChatInput || '').trim();
    if (!text || !accessChatDep || accessChatSending) return;
    setAccessChatSending(true);
    try {
      const r = await adminFetch(`${BACKEND_URL}/activation/deposit/${encodeURIComponent(accessChatDep.id)}/chat`, {
        method: 'POST',
        headers: {
          ...getApiAuthHeaders({ 'Content-Type': 'application/json' }),
          'x-chat-id': String(chatId || ''),
        },
        body: JSON.stringify({ chatId, content: text, text, role: 'admin' }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.success) {
        setAccessChatMsgs(mapAccessChatMessages(j.messages || []));
        setAccessChatInput('');
      } else {
        toast.error(j.message || 'Message non envoyé');
      }
    } catch {
      toast.error('Erreur envoi message');
    }
    setAccessChatSending(false);
  };

  // ── Blob store partagé pour les images de chat (même store que FloatingChatPanel) ──
  const rememberChatBlob = (id, url) => {
    try {
      if (!id || !url) return;
      if (!(window.__gsChatBlobById instanceof Map)) window.__gsChatBlobById = new Map();
      window.__gsChatBlobById.set(String(id), url);
    } catch { /* */ }
  };
  const forgetChatBlob = (id) => {
    try {
      if (id && window.__gsChatBlobById instanceof Map) window.__gsChatBlobById.delete(String(id));
    } catch { /* */ }
  };

  const sendAccessAdminChatPhoto = async (event) => {
    const rawFile = event?.target?.files?.[0];
    try { event.target.value = ''; } catch { /* */ }
    if (!rawFile || !accessChatDep || accessChatUploading) return;

    let preparedFile = rawFile;
    try {
      preparedFile = await prepareChatImage(rawFile);
    } catch (prepErr) {
      toast.error(prepErr?.message || 'Photo illisible — JPG/PNG');
      return;
    }

    const localUrl = URL.createObjectURL(preparedFile);
    const tempId = `tmp_accadm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    rememberChatBlob(tempId, localUrl);

    setAccessChatUploading(true);
    setAccessChatUploadProgress(5);
    setAccessChatUploadingId(tempId);

    const tempMessage = {
      id: tempId,
      role: 'admin',
      type: 'image',
      mediaType: 'image',
      url: localUrl,
      mediaUrl: localUrl,
      content: '',
      authorName: 'Admin',
      createdAt: new Date().toISOString(),
    };

    setAccessChatMsgs((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      arr.push(tempMessage);
      return arr;
    });

    let uploadOk = false;
    let realMsgId = null;
    try {
      const form = new FormData();
      form.append('file', preparedFile, preparedFile.name || 'photo.jpg');
      form.append('role', 'admin');
      form.append('mediaType', 'image');
      form.append('chatId', String(chatId || ''));

      const headers = { ...getApiAuthHeaders() };
      delete headers['Content-Type'];
      delete headers['content-type'];
      headers['x-chat-id'] = String(chatId || '');

      const res = await uploadWithProgress(
        `${BACKEND_URL}/activation/deposit/${encodeURIComponent(accessChatDep.id)}/chat/media`,
        form,
        {
          method: 'POST',
          headers,
          timeout: 180000,
          onProgress: (p) => {
            const safe = Math.max(5, Math.min(98, Number(p) || 0));
            setAccessChatUploadProgress(safe);
          },
        }
      );

      const raw = await res.text();
      let j = {};
      try { j = raw ? JSON.parse(raw) : {}; } catch { j = {}; }

      if (res.ok && j.success) {
        uploadOk = true;
        setAccessChatUploadProgress(100);
        const mapped = mapAccessChatMessages(j.messages || []);
        const mine = [...mapped].reverse().find((m) => m && m.role === 'admin' && (m.type === 'image' || m.type === 'video'));
        if (mine?.id) {
          realMsgId = mine.id;
          rememberChatBlob(mine.id, localUrl);
        }
        setAccessChatMsgs(mapped);
      } else {
        throw new Error(j.message || 'Photo non envoyée');
      }
    } catch (err) {
      toast.error(err?.message || 'Erreur envoi photo');
      setAccessChatMsgs((prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        return arr.filter((m) => m.id !== tempId);
      });
    } finally {
      try { forgetChatBlob(tempId); } catch { /* */ }
      if (!uploadOk) { try { forgetChatBlob(realMsgId); } catch { /* */ } }
      setAccessChatUploading(false);
      setAccessChatUploadProgress(0);
      setAccessChatUploadingId(null);
    }
  };

  const handleUnban = async (targetChatId) => {
    setUnbanningId(targetChatId);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/unban-client?chatId=${chatId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetChatId })
      });
      const json = await res.json();
      if (json.success) {
        setBannedClients(prev => prev.filter(c => String(c.chatId) !== String(targetChatId)));
      }
    } catch { /* */ }
    setUnbanningId(null);
  };

  const doFetch = async (silent = false) => {
    if (silent) setSilentLoading(true); else setLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/dashboard?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) { setData(json.data); setLastRefresh(new Date()); setError(null); }
      else setError(json.message || 'Accès refusé');
    } catch { setError('Impossible de charger le dashboard'); }
    finally { setLoading(false); setSilentLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { doFetch(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => doFetch(true), 30000); return () => clearInterval(t); }, []);

  // Charger la liste des notifs marketing (pour l'onglet admin)
  const loadNotifList = async () => {
    setNotifLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/marketing-notif?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) setNotifList(json.notifications || []);
    } catch { /* */ }
    setNotifLoading(false);
  };

  const loadSurveyVotes = async () => {
    setSurveyVotesLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/marketing-survey-votes?chatId=${encodeURIComponent(chatId)}`);
      const json = await res.json();
      if (json.success) setSurveyVotes(json.surveys || []);
    } catch { /* */ }
    setSurveyVotesLoading(false);
  };

  const loadOrderHistory = async () => {
    setOrderHistoryLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/order-history?chatId=${chatId}`);
      const json = await res.json();
      if (json.success) setOrderHistory(json.orders || []);
    } catch { /* */ }
    setOrderHistoryLoading(false);
  };

  // ── Logique clôture mensuelle (jour calendaire Europe/Paris) ──
  const isLastDayOfMonth = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const y = Number(parts.find((p) => p.type === 'year')?.value);
      const m = Number(parts.find((p) => p.type === 'month')?.value);
      const d = Number(parts.find((p) => p.type === 'day')?.value);
      if (!y || !m || !d) return false;
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return d >= lastDay;
    } catch {
      const now = new Date();
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return now.getDate() >= lastDay;
    }
  };

  const monthlyResetAvailable = isLastDayOfMonth();

  const handleMonthlyReset = async () => {
    setMonthlyResetLoading(true);
    try {
      const r = await adminFetch(`${BACKEND_URL}/admin/dashboard/reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId })
      });
      if ((await r.json()).success) {
        setShowMonthlyRecap(false);
        doFetch();
      }
    } catch { /* */ }
    setMonthlyResetLoading(false);
  };

  const loadTraffic = async () => {
    setTrafficLoading(true);
    setTrafficMsg(null);
    try {
      const [res, nfcRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/admin/activation-traffic?chatId=${encodeURIComponent(chatId)}`),
        adminFetch(`${BACKEND_URL}/admin/nfc/traffic?chatId=${encodeURIComponent(chatId)}`).catch(() => null),
      ]);
      const json = await res.json();
      let nfc = null;
      try {
        if (nfcRes && nfcRes.ok) {
          const nfcJson = await nfcRes.json();
          if (nfcJson.success) nfc = nfcJson;
        }
      } catch { /* soft */ }
      if (json.success) setTrafficData({ ...json, nfc });
      else setTrafficMsg({ ok: false, text: json.message || 'Erreur chargement TRAFFIC' });
    } catch {
      setTrafficMsg({ ok: false, text: 'Erreur réseau TRAFFIC' });
    }
    setTrafficLoading(false);
  };

  const reregisterActivationWebhook = async () => {
    setTrafficAction('webhook');
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/activation-traffic/reregister-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId }),
      });
      const json = await res.json();
      setTrafficMsg({ ok: !!json.success, text: json.message || (json.success ? 'Webhook OK' : 'Échec webhook') });
    } catch {
      setTrafficMsg({ ok: false, text: 'Erreur réseau webhook' });
    }
    setTrafficAction(null);
  };

  const checkActivationWebhook = async () => {
    setTrafficAction('webhook-info');
    try {
      const res = await adminFetch(`${BACKEND_URL}/admin/activation-traffic/webhook?chatId=${encodeURIComponent(chatId)}`);
      const json = await res.json();
      if (json.success) {
        const url = json.telegram?.url || json.configuredUrl || '—';
        const pending = json.telegram?.pending_update_count ?? '?';
        setTrafficMsg({ ok: true, text: `Webhook: ${url} · pending updates: ${pending}` });
      } else {
        setTrafficMsg({ ok: false, text: json.message || 'Webhook indisponible' });
      }
    } catch {
      setTrafficMsg({ ok: false, text: 'Erreur réseau info webhook' });
    }
    setTrafficAction(null);
  };

  useEffect(() => {
    if (activeTab === 'notifications') { loadNotifList(); loadSurveyVotes(); }
    if (activeTab === 'avis') loadReviews();
    if (activeTab === 'profit') loadRevenueAdjustments();
    if (activeTab === 'jeu') loadAdminGameLeaderboard();
    if (activeTab === 'bannis') loadBannedClients();
    if (activeTab === 'access') loadAccessDeposits();
    if (activeTab === 'products') loadOrderHistory();
    if (activeTab === 'traffic') loadTraffic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Activer ou désactiver une notification marketing
  const toggleNotif = async (type, activate, config = {}) => {
    setNotifSending(type);
    try {
      await adminFetch(`${BACKEND_URL}/admin/marketing-notif`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, type, active: activate, ...config })
      });
      await loadNotifList();
    } catch { /* */ }
    setNotifSending(null);
  };


  // ── Helpers format ──
  const fmtMin = (m) => m == null ? '—' : m < 60 ? `${m} min` : `${Math.floor(m/60)}h${String(m%60).padStart(2,'0')}`;
  const fmtH   = (h) => h == null ? '—' : `${String(h).padStart(2,'0')}h`;
  const fmtCHF = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)} CHF`;

  // ── Styles liquid glass GS (même langage que l'app) ──
  const glassBg =
    'linear-gradient(145deg, rgba(96,165,250,0.28) 0%, rgba(37,99,235,0.20) 45%, rgba(29,78,216,0.28) 100%), rgba(8,18,52,0.58)';
  const glassBorder = '1.5px solid rgba(147,197,253,0.38)';
  const glassShadow =
    '0 12px 40px rgba(10,30,100,0.40), 0 0 36px rgba(74,144,226,0.16), inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(255,255,255,0.08)';
  const glassBlur = 'blur(40px) saturate(300%) brightness(1.08)';

  const card = {
    background: glassBg,
    backdropFilter: glassBlur,
    WebkitBackdropFilter: glassBlur,
    border: glassBorder,
    borderRadius: '20px',
    padding: '18px',
    boxShadow: glassShadow,
    position: 'relative',
    overflow: 'hidden',
  };
  const accentCard = (c) => ({
    ...card,
    border: `1.5px solid ${c}55`,
    background: `linear-gradient(145deg, ${c}28 0%, rgba(37,99,235,0.18) 50%, rgba(8,18,52,0.55) 100%)`,
    boxShadow: `0 12px 36px rgba(10,30,100,0.35), 0 0 28px ${c}22, inset 0 1.5px 0 rgba(255,255,255,0.30)`,
  });

  // ── KPI Rectangle ──
  const KBox = ({ icon, label, value, sub, color = '#4a90e2' }) => (
    <div className="dash-glass-shine" style={{ ...accentCard(color) }}>
      <span style={{ position: 'absolute', top: 6, right: 10, fontSize: 40, opacity: 0.12, lineHeight: 1, pointerEvents: 'none' }}>{icon}</span>
      <p style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.14em', color: `${color}cc`, marginBottom: 6, position: 'relative' }}>{label}</p>
      <p style={{ fontSize: '28px', fontWeight: '900', color: '#fff', lineHeight: 1, marginBottom: 3, position: 'relative', textShadow: '0 0 24px rgba(96,165,250,0.25)' }}>{value ?? '—'}</p>
      {sub && <p style={{ fontSize: '11px', color: 'rgba(191,219,254,0.55)', marginTop: 4, position: 'relative' }}>{sub}</p>}
    </div>
  );

  // ── Barre horizontale classement ──
  const RankBar = ({ name, value, max, color, rank }) => {
    const rankBg = ['rgba(245,158,11,0.22)', 'rgba(156,163,175,0.18)', 'rgba(205,124,47,0.18)'];
    const rankCol = ['#f59e0b', '#9ca3af', '#cd7c2f'];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr 42px', gap: '10px', alignItems: 'center', padding: '5px 0' }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: rank < 3 ? rankBg[rank] : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', color: rank < 3 ? rankCol[rank] : 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{rank + 1}</span>
        <div>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0', margin: '0 0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${max > 0 ? (value / max) * 100 : 0}%`, background: `linear-gradient(90deg,${color},${color}88)`, borderRadius: 2, transition: 'width .6s ease' }} />
          </div>
        </div>
        <span style={{ fontSize: '13px', fontWeight: '800', color, textAlign: 'right' }}>{value}</span>
      </div>
    );
  };

  // ── Écrans loading / erreur ──
  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 50% 20%, rgba(37,99,235,0.35) 0%, rgba(6,12,36,0.98) 55%, #040814 100%)',
    }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:.6}50%{opacity:1}}`}</style>
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', border: '3px solid rgba(147,197,253,0.25)', borderTopColor: '#60a5fa', animation: 'spin .8s linear infinite', margin: '0 auto 20px', boxShadow: '0 0 24px rgba(74,144,226,0.35)' }} />
        <p style={{ color: 'rgba(191,219,254,0.7)', fontSize: 14, animation: 'pulse 1.5s ease infinite', fontWeight: 600 }}>Chargement Global Strains…</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 50% 20%, rgba(37,99,235,0.28) 0%, rgba(6,12,36,0.98) 55%, #040814 100%)',
      padding: 20,
    }}>
      <div className="dash-glass-shine" style={{ ...accentCard('#ef4444'), textAlign: 'center', color: '#fff', maxWidth: 360, padding: '36px 28px' }}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>⚠️</div>
        <p style={{ marginBottom: 18, fontSize: 15, color: 'rgba(255,255,255,0.85)' }}>{error}</p>
        <button onClick={() => doFetch()} style={{
          padding: '11px 28px', borderRadius: 12,
          background: 'linear-gradient(135deg, rgba(74,144,226,0.85), rgba(37,99,235,0.95))',
          border: '1.5px solid rgba(147,197,253,0.45)', color: '#fff', cursor: 'pointer', fontWeight: 700,
          boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
        }}>Réessayer</button>
      </div>
    </div>
  );

  const accessUnreadTotal = (accessDeposits || []).reduce((a, d) => a + (Number(d.adminUnread) || 0), 0);
  const tabs = [
    { id: 'overview',       label: 'Overview',         icon: '📊' },
    { id: 'products',       label: 'Commandes',        icon: '🛒' },
    { id: 'profit',         label: 'Revenus',          icon: '💰' },
    { id: 'jeu',            label: 'Jeu Arcade',       icon: '🎮' },
    { id: 'engagement',     label: 'Tracking clients', icon: '👥' },
    { id: 'traffic',        label: 'TRAFFIC',          icon: '📡' },
    { id: 'notifications',  label: 'Notifications',    icon: '📣' },
    { id: 'avis',           label: 'Avis clients',     icon: '⭐' },
    { id: 'access',         label: accessUnreadTotal > 0 ? `Access · ${accessUnreadTotal}` : 'Access', icon: accessUnreadTotal > 0 ? '🔴' : '🔓' },
    { id: 'bannis',         label: 'Bannis',           icon: '🚫' },
  ];

  const hourData = data?.hourDistrib || Array(24).fill(0);
  const maxHour = Math.max(...hourData, 1);
  const totalCmdsChart = hourData.reduce((a, b) => a + b, 0);

  return (
    <>
    <div className="dash-shell" style={{
      minHeight: '100vh',
      fontFamily: '"Inter",system-ui,sans-serif',
      color: '#fff',
      background: '#040814',
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeUp { from { opacity:0;transform:translateY(10px) } to { opacity:1;transform:translateY(0) } }
        .da { animation: fadeUp .35s ease both }
        .tab-pill { transition: all .2s cubic-bezier(0.25,0.46,0.45,0.94); }
        .tab-pill:hover { filter: brightness(1.12); }
        .bar-col:hover .bar-fill { filter: brightness(1.25); }
        .prod-row:hover {
          background: linear-gradient(145deg, rgba(96,165,250,0.22) 0%, rgba(37,99,235,0.14) 100%) !important;
          border-color: rgba(147,197,253,0.35) !important;
        }
        .dash-glass-shine { position: relative; overflow: hidden; }
        .dash-glass-shine::before {
          content: '';
          position: absolute;
          top: 0; left: 8%; right: 8%; height: 1.5px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.72) 50%, transparent);
          pointer-events: none; z-index: 2;
        }
        .dash-shell .dash-panel {
          background: linear-gradient(145deg, rgba(96,165,250,0.28) 0%, rgba(37,99,235,0.20) 45%, rgba(29,78,216,0.28) 100%), rgba(8,18,52,0.58);
          backdrop-filter: blur(40px) saturate(300%) brightness(1.08);
          -webkit-backdrop-filter: blur(40px) saturate(300%) brightness(1.08);
          border: 1.5px solid rgba(147,197,253,0.38);
          border-radius: 20px;
          box-shadow: 0 12px 40px rgba(10,30,100,0.40), 0 0 36px rgba(74,144,226,0.16), inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(255,255,255,0.08);
          position: relative;
          overflow: hidden;
        }
        .dash-shell .dash-panel::before {
          content: '';
          position: absolute;
          top: 0; left: 8%; right: 8%; height: 1.5px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.72) 50%, transparent);
          pointer-events: none; z-index: 2;
        }
        .dash-shell .dash-btn {
          padding: 8px 16px; border-radius: 12px; font-weight: 600; font-size: 13; cursor: pointer;
          background: linear-gradient(135deg, rgba(96,165,250,0.28), rgba(37,99,235,0.22));
          border: 1.5px solid rgba(147,197,253,0.40);
          color: #dbeafe;
          box-shadow: 0 4px 16px rgba(37,99,235,0.25), inset 0 1px 0 rgba(255,255,255,0.25);
          backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
          transition: all .18s ease;
        }
        .dash-shell .dash-btn:hover { filter: brightness(1.12); }
        .dash-shell .dash-btn:disabled { opacity: 0.45; cursor: not-allowed; }
      `}</style>

      {/* Univers GS — fond app + voile bleu liquid */}
      <img
        src="/emoji/SWISS3.png"
        alt=""
        style={{
          position: 'fixed', inset: 0, zIndex: 0,
          width: '100%', height: '100%', objectFit: 'fill', objectPosition: 'center',
          opacity: 0.72, pointerEvents: 'none', filter: 'saturate(1.2) brightness(1.05)',
        }}
      />
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 90% 55% at 50% -10%, rgba(74,144,226,0.45) 0%, transparent 55%),
          radial-gradient(ellipse 70% 50% at 100% 80%, rgba(37,99,235,0.22) 0%, transparent 50%),
          radial-gradient(ellipse 60% 40% at 0% 60%, rgba(30,64,175,0.20) 0%, transparent 45%),
          linear-gradient(165deg, rgba(4,10,28,0.72) 0%, rgba(8,18,52,0.82) 45%, rgba(4,8,20,0.90) 100%)
        `,
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1080, margin: '0 auto', padding: '18px 14px 52px' }}>

        {/* ═══ HEADER ═══ */}
        <div className="da dash-glass-shine" style={{
          ...card,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 18, flexWrap: 'wrap', gap: 12, padding: '16px 18px',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: silentLoading ? '#f59e0b' : '#4ade80', boxShadow: `0 0 10px ${silentLoading ? '#f59e0b' : '#4ade80'}` }} />
              <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.25em', color: 'rgba(191,219,254,0.65)' }}>Global Strains · Admin</span>
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: '#f0f9ff', letterSpacing: '-.5px', margin: 0, textShadow: '0 0 28px rgba(96,165,250,0.35)' }}>Dashboard Analytics</h1>
            {lastRefresh && <p style={{ fontSize: 11, color: 'rgba(191,219,254,0.45)', marginTop: 4 }}>Mis à jour {lastRefresh.toLocaleTimeString('fr-FR')} · auto-refresh /30s</p>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="dash-btn" onClick={() => doFetch()} disabled={loading || silentLoading}>
              Actualiser
            </button>
            <button
              className="dash-btn"
              onClick={() => monthlyResetAvailable && setShowMonthlyRecap(true)}
              disabled={!monthlyResetAvailable}
              title={!isLastDayOfMonth() ? 'Disponible le dernier jour du mois' : 'Clôturer le mois'}
              style={monthlyResetAvailable ? {
                background: 'linear-gradient(135deg, rgba(251,191,36,0.35), rgba(245,158,11,0.28))',
                border: '1.5px solid rgba(251,191,36,0.55)',
                color: '#fde68a',
              } : {
                opacity: 0.4,
                color: 'rgba(255,255,255,0.35)',
              }}
            >
              {'📅 Clôture mensuelle'}
            </button>
          </div>
        </div>

        {/* ═══ TABS ═══ */}
        <div className="dash-glass-shine" style={{
          ...card,
          marginBottom: 20, padding: 6, borderRadius: 18,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className="tab-pill"
              style={{
                padding: '8px 2px', borderRadius: 12, border: activeTab === t.id ? '1px solid rgba(191,219,254,0.45)' : '1px solid transparent',
                cursor: 'pointer', width: '100%',
                fontWeight: 600, textAlign: 'center', touchAction: 'manipulation',
                background: activeTab === t.id
                  ? 'linear-gradient(135deg, rgba(96,165,250,0.55) 0%, rgba(37,99,235,0.65) 100%)'
                  : 'transparent',
                color: activeTab === t.id ? '#fff' : 'rgba(191,219,254,0.62)',
                boxShadow: activeTab === t.id
                  ? '0 4px 18px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.35)'
                  : 'none',
              }}>
              <div style={{ fontSize: 16, lineHeight: 1.2 }}>{t.icon}</div>
              <div style={{ fontSize: 9, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</div>
            </button>
          ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════
            TAB — VUE D'ENSEMBLE
        ═══════════════════════════════════════ */}
        {activeTab === 'overview' && data && (
          <div className="da">

            {/* Ligne 1 : Donuts clients + KPIs temporels */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: 12, marginBottom: 12 }}>

              {/* Card donuts */}
              <div style={{ ...card }}>
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.38)', marginBottom: 16 }}>Clients &amp; Fidélisation</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 14 }}>
                  <DonutRing value={data.totalClients} max={Math.max(data.totalClients, 1)} color="#3b82f6" size={84} stroke={8} label="Clients" sub="total" />
                  <DonutRing value={data.wheelWinners} max={Math.max(data.totalClients, 1)} color="#f59e0b" size={84} stroke={8} label="Gagnants 🎡" sub="historiques" />
                  <DonutRing value={data.totalReferrals} max={Math.max(data.totalClients, 1)} color="#10b981" size={84} stroke={8} label="Parrainages 🤝" sub="filleuls" />
                  <DonutRing value={data.passClicks} max={Math.max(data.passClicks, 1)} color="#8b5cf6" size={84} stroke={8} label="Pass" sub="clics uniques*" />
                </div>
              </div>

              {/* KPIs 2×2 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <KBox  label="Livraison moy." value={fmtMin(data.avgDeliveryMin)} sub="" color="#ef4444" />
                <KBox label="Visites / jour" value={data.todayVisits ?? '—'} sub="clients uniques · Paris" color="#06b6d4" />
                <KBox  label="Heure de Pointe" value={fmtH(data.peakHour)} sub={`Moy ${fmtH(data.avgHour)} · Paris`} color="#f97316" />
                <KBox  label="Bénéfice net" value={`${((data.totalProfit||0)+(data.manualAdjustmentTotal||0)).toFixed(0)}`} sub="CHF" color="#22c55e" />
                <KBox  label="CA total" value={`${(data.totalRevenue||0).toFixed(0)}`} sub="CHF" color="#38bdf8" />
              </div>
            </div>

            {/* Ligne 2 : petits KPIs résumés */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
              <KBox icon="📦" label="Livrées" value={data.deliveredOrdersTotal ?? data.deliveredOrders} sub={`actives ${data.deliveredOrders}${data.archivedOrders ? ` · arch. ${data.archivedOrders}` : ''}`} color="#22c55e" />
              <KBox icon="🛒" label="Ajouts panier" value={data.totalCartAdds} sub={`${data.uniqueCartClients ?? '—'} clients uniques`} color="#3b82f6" />
              <KBox icon="👥" label="Clients" value={data.totalClients} sub="enregistrés" color="#a78bfa" />
              <KBox icon="🎡" label="Gagnants roue" value={data.wheelWinners} sub={`${data.wheelWinsTotal ?? data.wheelWinners} gains · ${data.wheelBonusesActive ?? 0} en stock`} color="#f59e0b" />
              <KBox icon="📲" label="Clics install" value={data.pwaInstallClicks ?? 0} sub="Safari/Chrome · 1×/client" color="#38bdf8" />
              <KBox icon="🏠" label="App installée" value={data.pwaInstalled ?? 0} sub="écran d'accueil · preuve" color="#22d3ee" />
            </div>

            {/* ═══ GRAPHIQUE HORAIRE PRO ═══ */}
            <div style={{ ...card, marginBottom: 14 }}>
              {/* En-tête graphique */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Heure de commandes régulières</p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 3 }}>{totalCmdsChart} commandes · PIC à {fmtH(data.peakHour)}</p>
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.4)', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#3b82f6', display: 'inline-block' }} />Normal</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#f59e0b', display: 'inline-block' }} />PIC</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#6366f1', display: 'inline-block' }} />Nuit (0-5h)</span>
                </div>
              </div>

              {/* Zone chart avec axe Y */}
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Axe Y */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, paddingBottom: 20, flexShrink: 0 }}>
                  {[maxHour, Math.round(maxHour * .75), Math.round(maxHour * .5), Math.round(maxHour * .25), 0].map((v, i) => (
                    <span key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,0.22)', textAlign: 'right', lineHeight: 1, minWidth: 18 }}>{v}</span>
                  ))}
                </div>

                {/* Barres + axe X */}
                <div style={{ flex: 1 }}>
                  {/* Lignes de grille */}
                  <div style={{ position: 'relative', height: 130, marginBottom: 6 }}>
                    {[75, 50, 25].map(pct => (
                      <div key={pct} style={{ position: 'absolute', left: 0, right: 0, bottom: `${pct}%`, borderTop: '1px dashed rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
                    ))}
                    {/* Barres */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 2 }}>
                      {hourData.map((count, h) => {
                        const pct  = maxHour > 0 ? (count / maxHour) * 100 : 0;
                        const isPeak   = h === data.peakHour;
                        const isNight  = h >= 0 && h < 6;
                        const isHov    = hoveredBar === h;
                        const baseColor = isPeak ? '#f59e0b' : isNight ? '#6366f1' : '#3b82f6';
                        return (
                          <div key={h} className="bar-col" style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', position: 'relative', cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredBar(h)}
                            onMouseLeave={() => setHoveredBar(null)}>
                            {/* Tooltip */}
                            {isHov && (
                              <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 5, background: 'rgba(15,20,30,.95)', border: `1px solid ${baseColor}55`, color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 7, whiteSpace: 'nowrap', zIndex: 20, pointerEvents: 'none', boxShadow: `0 2px 12px rgba(0,0,0,.5)` }}>
                                {String(h).padStart(2,'0')}h · {count} cmd
                              </div>
                            )}
                            {/* Barre */}
                            <div className="bar-fill" style={{
                              width: '100%', height: `${Math.max(pct, count > 0 ? 2.5 : 0.8)}%`,
                              background: isPeak
                                ? 'linear-gradient(180deg,#fbbf24 0%,#f59e0b 100%)'
                                : isNight
                                  ? `linear-gradient(180deg,#818cf8 0%,#6366f1 100%)`
                                  : isHov
                                    ? 'linear-gradient(180deg,#60a5fa 0%,#3b82f6 100%)'
                                    : 'linear-gradient(180deg,rgba(59,130,246,.75) 0%,rgba(59,130,246,.45) 100%)',
                              borderRadius: '3px 3px 0 0', transition: 'height .2s ease',
                              boxShadow: isPeak ? '0 0 8px rgba(245,158,11,.45)' : isHov ? '0 0 6px rgba(59,130,246,.45)' : 'none',
                            }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Axe X */}
                  <div style={{ display: 'flex', gap: 2 }}>
                    {hourData.map((_, h) => (
                      <div key={h} style={{ flex: 1, textAlign: 'center' }}>
                        {h % 4 === 0 && (
                          <span style={{ fontSize: 9, color: h === data.peakHour ? '#f59e0b' : 'rgba(255,255,255,0.28)', fontWeight: h === data.peakHour ? 700 : 400, display: 'block', lineHeight: 1 }}>
                            {String(h).padStart(2,'0')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Classements côte à côte ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
              <div style={{ ...card }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 }}> Produits en Tendance 🔥</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginBottom: 14 }}>les plus cliqués</p>
                {data.mostClicked?.length > 0
                  ? data.mostClicked.slice(0, 7).map(([name, count], i) => <RankBar key={i} name={name} value={count} max={data.mostClicked[0][1]} color="#3b82f6" rank={i} />)
                  : <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12, textAlign: 'center', padding: '18px 0' }}>Aucun produits en tendance</p>}
              </div>
              <div style={{ ...card }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 }}> Ajouts au panier</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginBottom: 14 }}>Total : {data.totalCartAdds} </p>
                {data.cartByProduct?.length > 0
                  ? data.cartByProduct.slice(0, 7).map(([name, count], i) => <RankBar key={i} name={name} value={count} max={data.cartByProduct[0][1]} color="#10b981" rank={i} />)
                  : <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12, textAlign: 'center', padding: '18px 0' }}>Aucun ajout au panier</p>}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — PRODUITS
        ═══════════════════════════════════════ */}
        {activeTab === 'products' && data && (
          <div className="da">
            {/* Résumé global produits */}
            {data.productStats && Object.keys(data.productStats).length > 0 && (() => {
              const entries = Object.entries(data.productStats);
              const totRev  = entries.reduce((s, [,v]) => s + v.revenue, 0);
              const totProf = entries.reduce((s, [,v]) => s + v.profit, 0);
              const totG    = entries.reduce((s, [,v]) => s + v.grams, 0);
              const best    = entries.sort((a,b) => b[1].revenue - a[1].revenue)[0];
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
                  <KBox icon="💶" label="CA Total" value={`${totRev.toFixed(0)}`} sub="CHF · ventes livrées" color="#3b82f6" />
                  <KBox icon="💰" label="Bénéfice" value={`${totProf.toFixed(0)}`} sub="CHF net" color="#22c55e" />
                  <KBox icon="⚖️" label="Grammes" value={`${totG.toFixed(0)}g`} sub="total vendus" color="#f97316" />
                  <KBox icon={<img src="/emoji/or1.webp" alt="top" style={{width:20,height:20,objectFit:'contain'}} />} label="Top produit" value={best ? best[0].split(' ')[0] : '—'} sub={best ? `${best[1].revenue.toFixed(0)} CHF` : ''} color="#f59e0b" />
                </div>
              );
            })()}

            {/* ═══ HISTORIQUE DES COMMANDES LIVRÉES ═══ */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(74,144,226,0.10) 0%, rgba(30,60,140,0.14) 100%)',
              border: '1px solid rgba(74,144,226,0.25)',
              borderRadius: 18,
              padding: '18px 16px',
              marginBottom: 14,
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    📋 Historique des commandes livrées
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', margin: '3px 0 0' }}>
                    {orderHistory.length} commande{orderHistory.length !== 1 ? 's' : ''} · plus récentes en premier
                  </p>
                </div>
                {/* Case total grammes vendus */}
                {data.productStats && (() => {
                  const totG = Object.values(data.productStats).reduce((s, v) => s + v.grams, 0);
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(249,115,22,0.18) 0%, rgba(234,88,12,0.12) 100%)',
                      border: '1px solid rgba(249,115,22,0.35)',
                      borderRadius: 12,
                      padding: '10px 16px',
                      textAlign: 'center',
                    }}>
                      <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(249,115,22,0.75)', margin: '0 0 3px' }}>⚖️ Grammes vendus</p>
                      <p style={{ fontSize: 26, fontWeight: 900, color: '#fb923c', margin: 0, lineHeight: 1 }}>{totG.toFixed(0)}<span style={{ fontSize: 13, opacity: 0.6 }}>g</span></p>
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', margin: '3px 0 0' }}>total cumulé</p>
                    </div>
                  );
                })()}
              </div>

              {/* Liste des commandes */}
              {orderHistoryLoading ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid rgba(74,144,226,0.25)', borderTopColor: '#4a90e2', animation: 'spin .8s linear infinite', margin: '0 auto 10px' }} />
                  <p style={{ fontSize: 12, margin: 0 }}>Chargement de l'historique…</p>
                </div>
              ) : orderHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 0', color: 'rgba(255,255,255,0.22)' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
                  <p style={{ fontSize: 12, margin: 0 }}>Aucune commande livrée pour l'instant</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 480, overflowY: 'auto', paddingRight: 2 }}>
                  {orderHistory.map((order, i) => {
                    const margeColor = order.margePct >= 40 ? '#4ade80' : order.margePct >= 20 ? '#fbbf24' : '#f87171';
                    return (
                      <div key={order.id || i} style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.042) 0%, rgba(255,255,255,0.022) 100%)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 12,
                        padding: '12px 14px',
                        transition: 'background .15s',
                      }}>
                        {/* Ligne principale */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{
                              width: 30, height: 30, borderRadius: '50%',
                              background: 'linear-gradient(135deg, rgba(74,144,226,0.3), rgba(30,80,180,0.4))',
                              border: '1px solid rgba(74,144,226,0.4)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 13, fontWeight: 800, color: '#7dd3fc', flexShrink: 0,
                            }}>
                              {(order.clientName || 'C')[0].toUpperCase()}
                            </div>
                            <div>
                              <p style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9', margin: 0 }}>{order.clientName}</p>
                              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.30)', margin: '1px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                                📅 {order.date}
                                {order.isPriority && <span style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 4, padding: '1px 5px', color: '#fbbf24', fontSize: 9, fontWeight: 700 }}>⚡ PRIORITAIRE</span>}
                                <span style={{ background: 'rgba(74,144,226,0.12)', border: '1px solid rgba(74,144,226,0.25)', borderRadius: 4, padding: '1px 5px', color: '#7dd3fc', fontSize: 9, fontWeight: 700 }}>{order.country === 'suisse' ? '🇨🇭 CH' : '🇫🇷 FR'}</span>
                              </p>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.22)', padding: '3px 9px', borderRadius: 7 }}>
                              {order.revenueCHF.toFixed(2)} CHF
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: order.profit >= 0 ? '#4ade80' : '#f87171', background: order.profit >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', border: `1px solid ${order.profit >= 0 ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, padding: '3px 9px', borderRadius: 7 }}>
                              {order.profit >= 0 ? '+' : ''}{order.profit.toFixed(2)} bén.
                            </span>
                          </div>
                        </div>

                        {/* Produits commandés */}
                        {order.products && order.products.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                            {order.products.map((p, pi) => (
                              <span key={pi} style={{
                                fontSize: 10, fontWeight: 600,
                                color: 'rgba(255,255,255,0.65)',
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.10)',
                                borderRadius: 6, padding: '2px 8px',
                              }}>
                                {p.name} · {typeof p.quantity === 'number' ? `${p.quantity}g` : p.quantity}
                                {p.price > 0 ? ` · ${p.price.toFixed(0)} CHF` : ''}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Barre de marge */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.25)', marginBottom: 3 }}>
                            <span>Marge client</span>
                            <span style={{ color: margeColor, fontWeight: 700 }}>{order.margePct}%</span>
                          </div>
                          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(Math.max(order.margePct, 0), 100)}%`, background: `linear-gradient(90deg, ${margeColor}, ${margeColor}88)`, borderRadius: 2, transition: 'width .6s ease' }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cards produits */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.productStats && Object.keys(data.productStats).length > 0
                ? Object.entries(data.productStats)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .map(([name, s], i) => {
                      const maxRev   = Object.values(data.productStats).reduce((m, v) => Math.max(m, v.revenue), 1);
                      const revPct   = (s.revenue / maxRev) * 100;
                      const margePct = s.revenue > 0 ? (s.profit / s.revenue) * 100 : 0;
                      const margeColor = margePct >= 40 ? '#22c55e' : margePct >= 20 ? '#f59e0b' : '#ef4444';
                      const rankBadgeEmoji = ['🥇','🥈','🥉'][i];
                      const rankBadgeImg = i === 0 ? '/emoji/or1.webp' : i === 1 ? '/emoji/silver1.webp' : i === 2 ? '/emoji/bronze1.png' : null;
                      const rankBadge = rankBadgeImg
                        ? <img src={rankBadgeImg} alt={rankBadgeEmoji} style={{ width: 22, height: 22, objectFit: 'contain', verticalAlign: 'middle', filter: i===0?'drop-shadow(0 1px 5px rgba(251,191,36,0.7))':i===1?'drop-shadow(0 1px 5px rgba(180,200,255,0.6))':'drop-shadow(0 1px 5px rgba(205,127,50,0.6))' }} />
                        : <span style={{fontSize:14,fontWeight:700,color:'rgba(255,255,255,0.5)'}}>#{i+1}</span>;
                      return (
                        <div key={i} className="prod-row dash-glass-shine" style={{ ...card, transition: 'background .18s, border-color .18s' }}>
                          {/* Ligne titre */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontSize: 18, lineHeight: 1 }}>{rankBadge}</span>
                              <p style={{ fontWeight: 800, fontSize: 15, color: '#f1f5f9', margin: 0 }}>{name}</p>
                            </div>
                            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa', background: 'rgba(59,130,246,0.13)', padding: '3px 10px', borderRadius: 8 }}>{s.revenue.toFixed(2)} CHF</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: s.profit >= 0 ? '#4ade80' : '#f87171', background: s.profit >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', padding: '3px 10px', borderRadius: 8 }}>
                                {s.profit >= 0 ? '+' : ''}{s.profit.toFixed(2)} CHF bén.
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: margeColor, background: `${margeColor}18`, padding: '3px 10px', borderRadius: 8 }}>
                                {margePct.toFixed(0)}% marge
                              </span>
                            </div>
                          </div>
                          {/* Stats secondaires */}
                          <div style={{ display: 'flex', gap: 18, fontSize: 11, color: 'rgba(255,255,255,0.40)', marginBottom: 10, flexWrap: 'wrap' }}>
                            <span>⚖️ {s.grams.toFixed(1)}g vendus</span>
                            <span>📦 {s.orders} commande{s.orders > 1 ? 's' : ''}</span>
                            <span>🏷️ Coût : {(s.revenue - s.profit).toFixed(2)} CHF</span>
                          </div>
                          {/* Barre CA */}
                          <div style={{ marginBottom: 5 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(255,255,255,0.28)', marginBottom: 4 }}>
                              <span>Chiffre d'affaires</span><span>{revPct.toFixed(0)}% du top</span>
                            </div>
                            <div style={{ height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${revPct}%`, background: 'linear-gradient(90deg,#3b82f6,#60a5fa)', borderRadius: 3, transition: 'width .6s ease' }} />
                            </div>
                          </div>
                          {/* Barre marge */}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(255,255,255,0.28)', marginBottom: 4 }}>
                              <span>Marge bénéficiaire</span><span style={{ color: margeColor }}>{margePct.toFixed(0)}%</span>
                            </div>
                            <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.max(margePct, 0)}%`, background: `linear-gradient(90deg,${margeColor},${margeColor}88)`, borderRadius: 2, transition: 'width .6s ease' }} />
                            </div>
                          </div>
                        </div>
                      );
                    })
                : (
                  <div style={{ ...card, textAlign: 'center', padding: '48px 20px', color: 'rgba(255,255,255,0.28)' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                    <p>Aucune commande livrée pour l'instant</p>
                  </div>
                )
              }
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — BÉNÉFICES
        ═══════════════════════════════════════ */}
        {activeTab === 'profit' && data && (
          <div className="da">

            {/* ── Bouton clôture mensuelle (onglet Revenus) ── */}
            <div className="dash-glass-shine" style={{
              ...(monthlyResetAvailable ? accentCard('#fbbf24') : card),
              padding: '14px 16px', marginBottom: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
            }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: monthlyResetAvailable ? '#fbbf24' : 'rgba(255,255,255,0.35)', margin: '0 0 3px' }}>
                  📅 Clôture mensuelle
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>
                  {!isLastDayOfMonth()
                    ? `Disponible le dernier jour du mois (heure Paris). Clôture la période : bénéfice, CA, livrées, ajustements et analytics.`
                    : `Dernier jour du mois (Paris) — récapitulatif puis clôture de période.`}
                </p>
              </div>
              <button
                onClick={() => monthlyResetAvailable && setShowMonthlyRecap(true)}
                disabled={!monthlyResetAvailable}
                style={{
                  padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 12,
                  cursor: monthlyResetAvailable ? 'pointer' : 'not-allowed',
                  background: monthlyResetAvailable
                    ? 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(245,158,11,0.20))'
                    : 'rgba(255,255,255,0.04)',
                  border: monthlyResetAvailable ? '1px solid rgba(251,191,36,0.45)' : '1px solid rgba(255,255,255,0.08)',
                  color: monthlyResetAvailable ? '#fbbf24' : 'rgba(255,255,255,0.22)',
                  opacity: monthlyResetAvailable ? 1 : 0.6,
                  whiteSpace: 'nowrap',
                }}
              >
                {monthlyResetAvailable ? '📋 Voir récap & clôturer' : '🔒 Non disponible'}
              </button>
            </div>

            {/* Hero bénéfice + CA total */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
              <div style={{ ...accentCard('#22c55e'), display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <p style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Bénéfice net total</p>
                  <p style={{ fontSize: 38, fontWeight: 900, color: '#4ade80', margin: 0, lineHeight: 1 }}>
                    {((data.totalProfit||0) + (data.manualAdjustmentTotal||0)).toFixed(2)} <span style={{ fontSize: 15, opacity: .6 }}>CHF</span>
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', marginTop: 5 }}>CA − coût{data.manualAdjustmentTotal ? ` + ajust. (${data.manualAdjustmentTotal >= 0 ? '+' : ''}${data.manualAdjustmentTotal?.toFixed(2)})` : ''}</p>
                </div>
                <DonutRing value={Math.max(Math.round((data.totalProfit||0)+(data.manualAdjustmentTotal||0)), 0)} max={Math.max(Math.round(data.totalRevenue||0), 1)} color="#22c55e" size={80} stroke={8} label="net" />
              </div>
              <div className="dash-glass-shine" style={{ ...accentCard('#38bdf8'), display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <p style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(56,189,248,0.7)', marginBottom: 6 }}>CA total</p>
                  <p style={{ fontSize: 38, fontWeight: 900, color: '#38bdf8', margin: 0, lineHeight: 1 }}>
                    {(data.totalRevenue||0).toFixed(2)} <span style={{ fontSize: 15, opacity: .6 }}>CHF</span>
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', marginTop: 5 }}>Coût : {((data.totalRevenue||0)-(data.totalProfit||0)).toFixed(2)} CHF</p>
                </div>
                <DonutRing value={Math.max(Math.round(data.totalProfit||0), 0)} max={Math.max(Math.round(data.totalRevenue||0), 1)} color="#38bdf8" size={80} stroke={8} label="marge" />
              </div>
            </div>

            {/* Bénéfices quotidiens */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
              <div className="dash-glass-shine" style={{ ...accentCard('#22c55e'), padding: '18px 20px' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(74,222,128,0.6)', marginBottom: 8 }}>Bénéfice net exact — aujourd'hui</p>
                <p style={{ fontSize: 36, fontWeight: 900, color: (data.todayProfit||0) >= 0 ? '#4ade80' : '#f87171', margin: 0, lineHeight: 1 }}>
                  {(data.todayProfit||0).toFixed(2)} <span style={{ fontSize: 16, opacity: .65 }}>CHF</span>
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', marginTop: 6 }}>⏰ Réinitialisé à 2h (heure Paris)</p>
              </div>
              <div className="dash-glass-shine" style={{ ...card, padding: '18px 20px' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(96,165,250,0.65)', marginBottom: 8 }}>Bénéfice net moyen / jour</p>
                <p style={{ fontSize: 36, fontWeight: 900, color: '#60a5fa', margin: 0, lineHeight: 1 }}>
                  {(data.avgDailyProfit||0).toFixed(2)} <span style={{ fontSize: 16, opacity: .65 }}>CHF</span>
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', marginTop: 6 }}>📊 Basé sur l'historique quotidien</p>
              </div>
            </div>

            {/* ═══ GRAPHIQUES LINÉAIRES PROFESSIONNELS ═══ */}
            {(() => {
              const profitHistory  = (data.profitHistory  || []).slice().sort((a,b) => a.date.localeCompare(b.date));
              const revenueHistory = (data.revenueHistory || []).slice().sort((a,b) => a.date.localeCompare(b.date));

              // Courbe bezier lissée
              const smoothPath = (pts) => {
                if (pts.length < 2) return pts.map((p,i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
                let d = `M${pts[0].x},${pts[0].y}`;
                for (let i = 1; i < pts.length; i++) {
                  const prev = pts[i-1], cur = pts[i];
                  const cpx = (prev.x + cur.x) / 2;
                  d += ` C${cpx},${prev.y} ${cpx},${cur.y} ${cur.x},${cur.y}`;
                }
                return d;
              };

              // Sélecteur de période : filtre les N derniers jours
              const RANGES = [
                { label: '7j',  days: 7 },
                { label: '14j', days: 14 },
                { label: '30j', days: 30 },
                { label: 'Tout', days: 9999 },
              ];

              const ProfessionalLineChart = ({ title, series, field, color, icon }) => {
                const [range, setRange] = React.useState(30);
                const [tooltip, setTooltip] = React.useState(null);
                const svgRef = React.useRef(null);
                const containerRef = React.useRef(null);

                // Filtrer selon la période sélectionnée
                const filtered = range >= 9999 ? series : series.slice(-range);

                const PAD_L = 52, PAD_R = 20, PAD_T = 18, PAD_B = 36;
                const H     = 220;

                // Largeur = 100% du conteneur (pas de scroll)
                const [svgW, setSvgW] = React.useState(340);
                React.useEffect(() => {
                  const obs = new ResizeObserver(entries => {
                    const w = entries[0]?.contentRect?.width;
                    if (w > 80) setSvgW(w);
                  });
                  if (containerRef.current) obs.observe(containerRef.current);
                  return () => obs.disconnect();
                }, []);

                const TOTAL_W = svgW;
                const innerW  = TOTAL_W - PAD_L - PAD_R;
                const innerH  = H - PAD_T - PAD_B;

                const vals   = filtered.map(s => s[field] ?? 0);
                const maxV   = vals.length ? Math.max(...vals, 0) : 1;
                const minV   = vals.length ? Math.min(...vals, 0) : 0;
                const spread = maxV - minV || 1;
                const yPad   = spread * 0.18;
                const yMin   = Math.max(0, minV - yPad);
                const yMax   = maxV + yPad;
                const yRange = yMax - yMin || 1;

                const toX = i => PAD_L + (filtered.length > 1 ? (i / (filtered.length - 1)) * innerW : innerW / 2);
                const toY = v  => PAD_T + innerH - ((v - yMin) / yRange) * innerH;

                const points = filtered.map((s, i) => ({
                  x: +toX(i).toFixed(2),
                  y: +toY(s[field] ?? 0).toFixed(2),
                  val: s[field] ?? 0,
                  date: s.date,
                }));

                const linePath = smoothPath(points);
                const baseY    = toY(yMin);
                const areaPath = points.length > 1
                  ? `${linePath} L${points[points.length-1].x},${baseY} L${points[0].x},${baseY} Z`
                  : '';

                const last   = vals[vals.length - 1] ?? 0;
                const prev   = vals[vals.length - 2] ?? last;
                const diff   = last - prev;
                const tUp    = diff >= 0;
                const tColor = tUp ? '#4ade80' : '#f87171';
                const avg    = vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : 0;
                const total  = vals.reduce((a,b)=>a+b,0);

                // Labels X : afficher max 7 labels quelle que soit la période
                const labelEvery = Math.max(1, Math.ceil(filtered.length / 7));

                const nTicks = 5;
                const yTicks = Array.from({length: nTicks}, (_, i) => yMin + (yRange * i / (nTicks - 1)));

                const gradId = `grad_${field}_${range}`;
                const glowId = `glow_${field}_${range}`;

                // Tooltip sur hover/touch — trouve le point le plus proche en X
                const handlePointerMove = React.useCallback((e) => {
                  if (!svgRef.current) return;
                  const rect = svgRef.current.getBoundingClientRect();
                  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                  const mx = clientX - rect.left;
                  if (points.length === 0) return;
                  let closest = points[0], minDx = Infinity;
                  points.forEach(p => { const dx = Math.abs(p.x - mx); if (dx < minDx) { minDx = dx; closest = p; } });
                  setTooltip(closest);
                }, [points]);

                const handlePointerLeave = React.useCallback(() => setTooltip(null), []);

                return (
                  <div className="dash-glass-shine" style={{
                    ...card,
                    marginBottom: 18,
                    border: `1.5px solid ${color}55`,
                    borderRadius: 22,
                    padding: 0,
                    boxShadow: `0 12px 40px rgba(10,30,100,0.40), 0 0 28px ${color}22, inset 0 1.5px 0 rgba(255,255,255,0.28)`,
                  }}>

                    {/* ── HEADER ── */}
                    <div style={{ padding: '14px 16px 12px', background: 'rgba(96,165,250,0.08)', borderBottom: '1px solid rgba(147,197,253,0.18)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: `linear-gradient(135deg, ${color}35, ${color}18)`,
                            border: `1px solid ${color}45`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 18, flexShrink: 0,
                          }}>{icon}</div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>{title}</p>
                            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', margin: '2px 0 0' }}>
                              {filtered.length} jour{filtered.length!==1?'s':''}
                            </p>
                          </div>
                        </div>
                        {/* Badges stats */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          {[
                            { label: 'Dernier',   val: last.toFixed(0)+' CHF',  bg: `${color}20`, bd: `${color}45`, c: color },
                            { label: 'Moy/j',     val: avg.toFixed(0)+' CHF',   bg: 'rgba(255,255,255,0.05)', bd: 'rgba(255,255,255,0.10)', c: 'rgba(255,255,255,0.55)' },
                            { label: 'Total',     val: total.toFixed(0)+' CHF', bg: 'rgba(255,255,255,0.05)', bd: 'rgba(255,255,255,0.10)', c: 'rgba(255,255,255,0.55)' },
                            { label: 'vs veille', val: `${diff>=0?'+':''}${diff.toFixed(0)}`, bg: tUp?'rgba(74,222,128,0.10)':'rgba(248,113,113,0.10)', bd: tUp?'rgba(74,222,128,0.28)':'rgba(248,113,113,0.28)', c: tColor },
                          ].map((k,i) => (
                            <div key={i} style={{ background: k.bg, border: `1px solid ${k.bd}`, borderRadius: 8, padding: '4px 9px' }}>
                              <div style={{ fontSize: 7, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.30)', marginBottom: 2 }}>{k.label}</div>
                              <div style={{ fontSize: 12, fontWeight: 800, color: k.c, lineHeight: 1 }}>{k.val}</div>
                            </div>
                          ))}
                          {/* Sélecteur de période */}
                          <div style={{ display: 'flex', gap: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: 3 }}>
                            {RANGES.map(r => (
                              <button key={r.days} onClick={() => setRange(r.days)} style={{
                                fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                background: range === r.days ? color : 'transparent',
                                color: range === r.days ? '#000' : 'rgba(255,255,255,0.45)',
                                transition: 'all 0.15s',
                              }}>{r.label}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── SVG CHART — largeur 100%, pas de scroll ── */}
                    {filtered.length < 2 ? (
                      <div style={{ padding: '44px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 38, opacity: 0.2, marginBottom: 10 }}>📈</div>
                        <p style={{ color: 'rgba(255,255,255,0.20)', fontSize: 12, margin: 0 }}>
                          Minimum 2 jours de données pour afficher le graphique
                        </p>
                      </div>
                    ) : (
                      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
                        <svg
                          ref={svgRef}
                          xmlns="http://www.w3.org/2000/svg"
                          width={TOTAL_W} height={H}
                          style={{ display: 'block', width: '100%', height: H, overflow: 'visible', touchAction: 'none' }}
                          onMouseMove={handlePointerMove}
                          onMouseLeave={handlePointerLeave}
                          onTouchMove={handlePointerMove}
                          onTouchEnd={handlePointerLeave}
                        >
                          <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%"   stopColor={color} stopOpacity="0.38" />
                              <stop offset="60%"  stopColor={color} stopOpacity="0.08" />
                              <stop offset="100%" stopColor={color} stopOpacity="0.00" />
                            </linearGradient>
                            <filter id={glowId} x="-20%" y="-60%" width="140%" height="220%">
                              <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur"/>
                              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                            </filter>
                          </defs>

                          {/* Background zone */}
                          <rect x={PAD_L} y={PAD_T} width={innerW} height={innerH} fill="rgba(255,255,255,0.008)" rx={3}/>

                          {/* Grilles horizontales */}
                          {yTicks.map((v, i) => {
                            const y = toY(v);
                            return (
                              <g key={i}>
                                <line x1={PAD_L} y1={y} x2={PAD_L+innerW} y2={y}
                                  stroke={i===0?'rgba(255,255,255,0.12)':'rgba(255,255,255,0.045)'}
                                  strokeWidth={i===0?1:0.7} strokeDasharray={i===0?'none':'4 6'}/>
                                <text x={PAD_L-7} y={y+4} textAnchor="end" fontSize={8.5} fontWeight="500"
                                  fill="rgba(255,255,255,0.28)" fontFamily="Inter,-apple-system,sans-serif">
                                  {v>=1000?`${(v/1000).toFixed(1)}k`:v.toFixed(0)}
                                </text>
                              </g>
                            );
                          })}

                          {/* Grilles verticales légères */}
                          {points.filter((_,i)=>i%labelEvery===0).map((p,i)=>(
                            <line key={i} x1={p.x} y1={PAD_T} x2={p.x} y2={PAD_T+innerH}
                              stroke="rgba(255,255,255,0.03)" strokeWidth={0.7}/>
                          ))}

                          {/* Aire dégradée */}
                          {areaPath && <path d={areaPath} fill={`url(#${gradId})`}/>}

                          {/* Ligne glow */}
                          <path d={linePath} fill="none" stroke={color} strokeWidth={4.5}
                            strokeLinecap="round" strokeLinejoin="round" opacity={0.15} filter={`url(#${glowId})`}/>

                          {/* Ligne principale */}
                          <path d={linePath} fill="none" stroke={color} strokeWidth={2.5}
                            strokeLinecap="round" strokeLinejoin="round"/>

                          {/* Axes */}
                          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T+innerH} stroke="rgba(255,255,255,0.10)" strokeWidth={1}/>
                          <line x1={PAD_L} y1={PAD_T+innerH} x2={PAD_L+innerW} y2={PAD_T+innerH} stroke="rgba(255,255,255,0.10)" strokeWidth={1}/>

                          {/* Points */}
                          {points.map((p, i) => {
                            const isLast = i === points.length - 1;
                            const isMax  = p.val === maxV && maxV > 0;
                            const isHovered = tooltip && tooltip.date === p.date;
                            return (
                              <g key={i}>
                                {isHovered && (
                                  <line x1={p.x} y1={PAD_T} x2={p.x} y2={PAD_T+innerH}
                                    stroke={color} strokeWidth={1} opacity={0.35} strokeDasharray="3 4"/>
                                )}
                                {isLast ? (
                                  <>
                                    <circle cx={p.x} cy={p.y} r={16} fill={color} opacity={0.06}>
                                      <animate attributeName="r" values="8;18;8" dur="2.8s" repeatCount="indefinite"/>
                                      <animate attributeName="opacity" values="0.08;0;0.08" dur="2.8s" repeatCount="indefinite"/>
                                    </circle>
                                    <circle cx={p.x} cy={p.y} r={5} fill="#fff" stroke={color} strokeWidth={2.5}/>
                                    <rect x={p.x+8} y={p.y-13} width={26} height={12} rx={6} fill={color} opacity={0.95}/>
                                    <text x={p.x+21} y={p.y-5} textAnchor="middle" fontSize={7} fontWeight="800"
                                      fill="#000" fontFamily="Inter,sans-serif">LIVE</text>
                                  </>
                                ) : isMax ? (
                                  <>
                                    <circle cx={p.x} cy={p.y} r={4.5} fill="#f59e0b" stroke="rgba(0,0,0,0.4)" strokeWidth={1.5}/>
                                    <text x={p.x} y={p.y-9} textAnchor="middle" fontSize={8} fontWeight="700"
                                      fill="#fbbf24" fontFamily="Inter,sans-serif">▲{p.val.toFixed(0)}</text>
                                  </>
                                ) : (
                                  <circle cx={p.x} cy={p.y} r={isHovered?4:2.5} fill={color}
                                    stroke="rgba(0,0,0,0.5)" strokeWidth={1} opacity={isHovered?1:0.75}/>
                                )}
                              </g>
                            );
                          })}

                          {/* Labels axe X */}
                          {points.map((p, i) => {
                            if (i % labelEvery !== 0 && i !== points.length-1) return null;
                            const isLast = i === points.length-1;
                            return (
                              <g key={i}>
                                <line x1={p.x} y1={PAD_T+innerH} x2={p.x} y2={PAD_T+innerH+4}
                                  stroke="rgba(255,255,255,0.15)" strokeWidth={1}/>
                                <text x={p.x} y={H-3} textAnchor="middle"
                                  fontSize={isLast?9:8} fontWeight={isLast?'700':'400'}
                                  fill={isLast?color:'rgba(255,255,255,0.30)'}
                                  fontFamily="Inter,-apple-system,sans-serif">
                                  {p.date ? p.date.slice(5).replace('-','/') : ''}
                                </text>
                              </g>
                            );
                          })}

                        </svg>

                        {/* Tooltip flottant */}
                        {tooltip && (
                          <div style={{
                            position: 'absolute',
                            top: PAD_T,
                            left: Math.min(tooltip.x + 10, svgW - 120),
                            background: 'rgba(10,14,24,0.95)',
                            border: `1px solid ${color}60`,
                            borderRadius: 10,
                            padding: '7px 12px',
                            pointerEvents: 'none',
                            zIndex: 10,
                            boxShadow: `0 4px 20px rgba(0,0,0,0.5)`,
                          }}>
                            <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter,sans-serif' }}>
                              {tooltip.date ? tooltip.date.slice(5).replace('-','/') : ''}
                            </p>
                            <p style={{ margin: '3px 0 0', fontSize: 15, fontWeight: 800, color, fontFamily: 'Inter,sans-serif' }}>
                              {tooltip.val.toFixed(2)} CHF
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <>
                  <ProfessionalLineChart
                    title="Bénéfice Net — historique quotidien"
                    series={profitHistory} field="profit"
                    color="#22c55e" icon="📈"
                  />
                  <ProfessionalLineChart
                    title="Chiffre d'Affaires — historique quotidien"
                    series={revenueHistory} field="revenue"
                    color="#38bdf8" icon="💹"
                  />
                </>
              );
            })()}

            {/* Top clients */}
            <div style={{ ...card }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 16 }}>💰 Bénéfice par client — Top 20</p>
              {data.profitByClient?.length > 0
                ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {data.profitByClient.map((c, i) => {
                      const maxP = Math.max(data.profitByClient[0].profit, 1);
                      const rankIcons = ['/emoji/or1.webp', '/emoji/silver1.webp', '/emoji/bronze1.png'];
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(147,197,253,0.20)' }}>
                          <span style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>{rankIcons[i] ? <img src={rankIcons[i]} alt={`rank${i+1}`} style={{ width: 22, height: 22, objectFit: 'contain', filter: i===0?'drop-shadow(0 1px 5px rgba(251,191,36,0.7))':i===1?'drop-shadow(0 1px 5px rgba(180,200,255,0.6))':'drop-shadow(0 1px 5px rgba(205,127,50,0.6))' }} /> : <span style={{fontSize:13,fontWeight:700,color:'rgba(255,255,255,0.5)'}}>#{i+1}</span>}</span>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0', margin: '0 0 5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</p>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2 }}>
                                <div style={{ height: '100%', width: `${c.profit > 0 ? (c.profit/maxP)*100 : 0}%`, background: c.profit >= 0 ? 'linear-gradient(90deg,#22c55e,#4ade80)' : 'linear-gradient(90deg,#ef4444,#f87171)', borderRadius: 2, transition: 'width .6s' }} />
                              </div>
                              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.30)', flexShrink: 0 }}>{c.orders} cmd · {c.revenue.toFixed(0)} CHF CA</span>
                            </div>
                          </div>
                          <span style={{ fontWeight: 800, fontSize: 14, color: c.profit >= 0 ? '#4ade80' : '#f87171', flexShrink: 0 }}>{fmtCHF(c.profit)}</span>
                        </div>
                      );
                    })}
                  </div>
                )
                : <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: 32 }}>Aucune donnée</p>
              }
            </div>

            {/* ═══ AJUSTEMENTS MANUELS ═══ */}
            <div className="dash-glass-shine" style={{ ...card, marginTop: 8 }}>
              <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.38)', marginBottom: 14 }}>Ajustements manuels</p>
              {/* Solde ajusté */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Total ajustements :</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: revenueAdjTotal >= 0 ? '#4ade80' : '#f87171' }}>
                  {revenueAdjTotal >= 0 ? '+' : ''}{revenueAdjTotal.toFixed(2)} CHF
                </span>
              </div>
              {/* Boutons */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                <button onClick={() => setShowRevenueModal('ajout')} style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(135deg,rgba(34,197,94,0.25),rgba(16,185,129,0.15))', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 10, color: '#4ade80', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>➕ Ajouter bénéfice</button>
                <button onClick={() => setShowRevenueModal('retrait')} style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(135deg,rgba(239,68,68,0.22),rgba(220,38,38,0.12))', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 10, color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>➖ Retrait bénéfice</button>
              </div>
              {/* Historique */}
              {revenueAdjustments.length > 0 ? (
                <div>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>Historique ({revenueAdjustments.length})</p>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {[...revenueAdjustments].reverse().map((a) => (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 12px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: a.type === 'ajout' ? '#4ade80' : '#f87171', margin: 0 }}>{a.type === 'ajout' ? '➕' : '➖'} {a.type === 'ajout' ? 'Ajout' : 'Retrait'}</p>
                          {a.motif && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: '3px 0 0' }}>{a.motif}</p>}
                          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', margin: '3px 0 0' }}>{new Date(a.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</p>
                        </div>
                        <span style={{ fontWeight: 800, fontSize: 15, color: a.type === 'ajout' ? '#4ade80' : '#f87171', flexShrink: 0, marginLeft: 12 }}>
                          {a.type === 'ajout' ? '+' : '-'}{a.amount.toFixed(2)} CHF
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>Aucun ajustement</p>
              )}
            </div>

            {/* Modal ajustement */}
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — JEU ARCADE (ADMIN)
        ═══════════════════════════════════════ */}
        {activeTab === 'jeu' && (
          <div className="da">
            <div className="dash-glass-shine" style={{ ...accentCard('#a855f7') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(100,180,255,0.85)', margin: 0 }}>🌍 Global Game — Classement Flappy Bird 🐦</p>
                <button onClick={loadAdminGameLeaderboard} style={{ padding: '6px 14px', background: 'rgba(30,100,255,0.2)', border: '1px solid rgba(60,140,255,0.4)', borderRadius: 8, color: '#7dd3fc', fontSize: 12, cursor: 'pointer' }}>🔄 Actualiser</button>
              </div>
              {adminGameWeek && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '4px 0 8px' }}>Période : {adminGameWeek}</p>}
              <div style={{ marginBottom: 16 }}>
                <button
                  onClick={async () => {
                    if (!window.confirm('⚠️ Remettre le classement à zéro ? Cette action est irréversible.')) return;
                    try {
                      const r = await adminFetch(`${BACKEND_URL}/admin/game-reset?chatId=${chatId}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-chat-id': chatId },
                      });
                      const d = await r.json();
                      alert(d.message || (d.success ? '✅ Classement remis à zéro !' : '❌ Erreur'));
                      if (d.success) loadAdminGameLeaderboard();
                    } catch(e) { alert('Erreur réseau'); }
                  }}
                  style={{ padding: '8px 18px', background: 'linear-gradient(135deg,rgba(239,68,68,0.25),rgba(185,28,28,0.3))', border: '1px solid rgba(239,68,68,0.45)', borderRadius: 10, color: '#fca5a5', fontSize: 12, fontWeight: 700, cursor: 'pointer', width: '100%' }}
                >
                  🗑️ Reset le classement à zéro
                </button>
              </div>
              {adminGameLoading ? (
                <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 32 }}>⏳ Chargement...</p>
              ) : adminGameLeaderboard.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: 32 }}>Aucune partie jouée</p>
              ) : (
                <div>
                  {adminGameLeaderboard.map((entry) => {
                    const rankEmoji = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`;
                    const isFirst = entry.rank === 1;
                    return (
            <div key={entry.chatId} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: isFirst ? 'linear-gradient(135deg,rgba(30,100,255,0.2),rgba(10,60,200,0.12))' : 'rgba(255,255,255,0.04)', border: `1px solid ${isFirst ? 'rgba(60,140,255,0.4)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: isFirst ? 24 : 18, minWidth: 32, textAlign: 'center' }}>{rankEmoji}</span>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: 700, color: isFirst ? '#7dd3fc' : '#e2e8f0', fontSize: 14 }}>{entry.realName}</p>
                          <p style={{ margin: '2px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                            {entry.submittedAt ? new Date(entry.submittedAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : ''}
                            {entry.accessPreview ? ' · aperçu' : ' · client'}
                            {entry.freeAccessAwarded ? ' · accès envoyé' : ''}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontWeight: 900, fontSize: 22, color: isFirst ? '#7dd3fc' : '#60a5fa' }}>{entry.clicks} 🍎</p>
                          <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>pommes</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {isFirst && entry.isShopClient && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Attribuer 2G à ${entry.realName} ?`)) return;
                                try {
                                  const r = await adminFetch(`${BACKEND_URL}/admin/game-award-winner?chatId=${chatId}`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'x-chat-id': chatId },
                                    body: JSON.stringify({ winnerChatId: entry.chatId })
                                  });
                                  const d = await r.json();
                                  alert(d.message || (d.success ? '✅ 2G attribués !' : '❌ Erreur'));
                                } catch(e) { alert('Erreur réseau'); }
                              }}
                              style={{ padding: '7px 12px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              🎁 2G
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              const who = entry.accessPreview ? 'joueur aperçu' : 'client';
                              if (!window.confirm(`Envoyer le message « Réclamer accès » à ${entry.realName} (${who}) ?\n\nCela n'active pas le compte automatiquement.`)) return;
                              try {
                                const r = await adminFetch(`${BACKEND_URL}/admin/game-award-access?chatId=${chatId}`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'x-chat-id': chatId },
                                  body: JSON.stringify({ winnerChatId: entry.chatId, winnerName: entry.realName })
                                });
                                const d = await r.json();
                                alert(d.message || (d.success ? '✅ Message envoyé !' : '❌ Erreur'));
                                if (d.success) loadAdminGameLeaderboard();
                              } catch(e) { alert('Erreur réseau'); }
                            }}
                            style={{ padding: '7px 12px', background: entry.accessPreview ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            🔓 Accès
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'engagement' && data && (
          <div className="da">
            {/* Donuts engagement */}
            <div style={{ ...card, marginBottom: 14 }}>
              <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.38)', marginBottom: 20 }}>Engagement global</p>
              <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 20 }}>
                <DonutRing value={data.wheelWinners} max={Math.max(data.totalClients,1)} color="#f59e0b" size={92} stroke={8} label="Gagnants 🎡" sub={`${data.wheelWinsTotal ?? data.wheelWinners} gains · ${data.totalClients>0?Math.round(data.wheelWinners/data.totalClients*100):0}%`} />
                <DonutRing value={data.totalReferrals} max={Math.max(data.totalClients,1)} color="#10b981" size={92} stroke={8} label="Parrainages 🤝" sub="filleuls livrés" />
                <DonutRing value={data.pwaInstalled ?? 0} max={Math.max(data.totalClients,1)} color="#22d3ee" size={92} stroke={8} label="App 🏠" sub={`${data.totalClients>0?Math.round((data.pwaInstalled||0)/data.totalClients*100):0}%`} />
                <DonutRing value={data.pwaInstallClicks ?? 0} max={Math.max(data.totalClients,1)} color="#38bdf8" size={92} stroke={8} label="Clics 📲" sub="install 1×" />
                <DonutRing value={data.passClicks} max={Math.max(data.passClicks,1)} color="#8b5cf6" size={92} stroke={8} label="Clics Pass" sub="intérêt" />
                <DonutRing value={data.deliveredOrders} max={Math.max(data.totalOrders,1)} color="#22c55e" size={92} stroke={8} label="Livrées 📦" sub={`/${data.totalOrders}`} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
              <KBox label="Visites / jour" value={data.todayVisits ?? '—'} sub="clients uniques" color="#06b6d4" />
              <KBox  label="Livraison moy." value={fmtMin(data.avgDeliveryMin)} sub="cmd → livré" color="#ef4444" />
              <KBox  label="Heure pointe" value={fmtH(data.peakHour)} sub="plus de cmd" color="#f97316" />
              <KBox  label="Clients" value={data.totalClients} sub="enregistrés" color="#a78bfa" />
              <KBox label="Clics install" value={data.pwaInstallClicks ?? 0} sub="Safari/Chrome" color="#38bdf8" />
              <KBox label="App installée" value={data.pwaInstalled ?? 0} sub="écran d'accueil" color="#22d3ee" />
            </div>

            {data.resetAt && (
              <div style={{ ...accentCard('#f59e0b'), display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                  Dernier reset : <strong style={{ color: '#fbbf24' }}>{new Date(data.resetAt).toLocaleString('fr-FR')}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — TRAFFIC (bot activation)
        ═══════════════════════════════════════ */}
        {activeTab === 'traffic' && (
          <div className="da">
            <div className="dash-glass-shine" style={{ ...card, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(191,219,254,0.55)', margin: '0 0 6px' }}>TRAFFIC LIVE</p>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#f0f9ff' }}>Coup d&apos;œil activation</h2>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: 'rgba(191,219,254,0.5)', lineHeight: 1.45 }}>
                    Combien arrivent · combien payent · ce qui bloque.
                  </p>
                </div>
                <button className="dash-btn" onClick={loadTraffic} disabled={trafficLoading}>{trafficLoading ? '…' : 'Actualiser'}</button>
              </div>
              {trafficMsg && (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                  background: trafficMsg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  border: `1px solid ${trafficMsg.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  color: trafficMsg.ok ? '#4ade80' : '#fca5a5',
                }}>{trafficMsg.text}</div>
              )}
            </div>

            {trafficLoading && !trafficData ? (
              <div style={{ ...card, textAlign: 'center', color: 'rgba(255,255,255,0.45)', padding: 40 }}>Chargement…</div>
            ) : trafficData ? (
              <>
                {/* 3 chiffres clés */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
                  <div className="dash-glass-shine" style={{ ...accentCard('#22c55e'), textAlign: 'center', padding: '16px 10px' }}>
                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(167,243,208,0.8)', fontWeight: 700 }}>NOUVEAUX CLIENTS</p>
                    <p style={{ margin: '8px 0 4px', fontSize: 36, fontWeight: 900, color: '#4ade80', lineHeight: 1 }}>{trafficData.funnel?.confirmed ?? 0}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>dépôts validés aujourd&apos;hui</p>
                  </div>
                  <div className="dash-glass-shine" style={{ ...accentCard('#f59e0b'), textAlign: 'center', padding: '16px 10px' }}>
                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(253,230,138,0.85)', fontWeight: 700 }}>EN ATTENTE</p>
                    <p style={{ margin: '8px 0 4px', fontSize: 36, fontWeight: 900, color: '#fbbf24', lineHeight: 1 }}>{trafficData.live?.pendingDeposits ?? 0}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>à valider dans Access</p>
                  </div>
                </div>

                {/* Funnel simple en 4 cases */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'Arrivées', tip: '1er /start par client / jour', v: trafficData.funnel?.starts ?? 0, c: '#60a5fa' },
                    { label: 'Captcha OK', tip: '1er captcha OK par client / jour', v: trafficData.funnel?.captchaOk ?? 0, c: '#4ade80' },
                    { label: 'Ont payé', tip: 'Dépôt démarré', v: trafficData.funnel?.depositStarts ?? 0, c: '#fbbf24' },
                    { label: 'Validés', tip: 'Compte créé', v: trafficData.funnel?.confirmed ?? 0, c: '#c4b5fd' },
                  ].map((x) => (
                    <div key={x.label} className="dash-glass-shine" style={{ ...card, padding: '12px 10px', textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: x.c }}>{x.v}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11, fontWeight: 700, color: '#e2e8f0' }}>{x.label}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{x.tip}</p>
                    </div>
                  ))}
                </div>

                {/* Méthodes de dépôt — courbe visuelle */}
                <div className="dash-glass-shine" style={{ ...card, marginBottom: 14 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(191,219,254,0.5)', margin: '0 0 6px' }}>Comment ils paient</p>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '0 0 14px' }}>Aujourd&apos;hui — crypto · PCS · Paysafecard</p>
                  {(() => {
                    const raw = trafficData.funnel?.methods || {};
                    const bucket = { crypto: 0, pcs: 0, paysafecard: 0 };
                    Object.entries(raw).forEach(([k, v]) => {
                      const key = String(k).toLowerCase();
                      const n = Number(v) || 0;
                      if (key === 'pcs') bucket.pcs += n;
                      else if (key === 'paysafecard' || key === 'paysafe') bucket.paysafecard += n;
                      else bucket.crypto += n; // btc eth ltc usdt_trx etc.
                    });
                    const rows = [
                      { id: 'crypto', label: 'Crypto', color: '#f59e0b', v: bucket.crypto },
                      { id: 'pcs', label: 'PCS', color: '#60a5fa', v: bucket.pcs },
                      { id: 'paysafecard', label: 'Paysafecard', color: '#a78bfa', v: bucket.paysafecard },
                    ];
                    const maxV = Math.max(...rows.map((r) => r.v), 1);
                    const total = rows.reduce((a, r) => a + r.v, 0);
                    if (total === 0) {
                      return <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Aucun dépôt démarré aujourd&apos;hui</p>;
                    }
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {rows.map((r) => (
                          <div key={r.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                              <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{r.label}</span>
                              <span style={{ color: r.color, fontWeight: 800 }}>{r.v} <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>({Math.round((r.v / total) * 100)}%)</span></span>
                            </div>
                            <div style={{ height: 12, borderRadius: 8, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', width: `${(r.v / maxV) * 100}%`, minWidth: r.v ? 8 : 0,
                                background: `linear-gradient(90deg, ${r.color}, ${r.color}99)`,
                                borderRadius: 8, transition: 'width .5s ease',
                              }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Alertes utiles live */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
                  <div className="dash-glass-shine" style={{ ...card, padding: 14 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 800, color: '#fca5a5' }}>À surveiller</p>
                    <p style={{ margin: '0 0 4px', fontSize: 13, color: '#e2e8f0' }}>
                      Captcha raté : <strong style={{ color: '#f87171' }}>{trafficData.funnel?.captchaFail ?? 0}</strong>
                    </p>
                    <p style={{ margin: '0 0 4px', fontSize: 13, color: '#e2e8f0' }}>
                      Bloqués : <strong style={{ color: '#fb923c' }}>{trafficData.funnel?.eligibilityBlocked ?? 0}</strong>
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: '#e2e8f0' }}>
                      Annulés : <strong style={{ color: '#f87171' }}>{trafficData.funnel?.cancelled ?? 0}</strong>
                    </p>
                  </div>
                </div>

                {/* TRAFFIC NFC (DB ordernfc — sans bot Telegram) */}
                <div className="dash-glass-shine" style={{ ...card, marginBottom: 14, border: '1px solid rgba(34,211,238,0.28)' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 800, color: '#67e8f9' }}>📱 NFC physique</p>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                    Lien tag : <code style={{ color: '#a5f3fc' }}>https://global-strains.shop/?src=nfc</code> · 1 scan / appareil / jour
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 10, background: 'rgba(34,211,238,0.08)' }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#22d3ee' }}>{trafficData.nfc?.funnel?.hits ?? 0}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Scans</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 10, background: 'rgba(74,222,128,0.08)' }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#4ade80' }}>{trafficData.nfc?.funnel?.authOk ?? 0}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Auth OK</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#e2e8f0' }}>{trafficData.nfc?.clientsTotal ?? 0}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Clients NFC</p>
                    </div>
                  </div>
                </div>

                {/* Outils webhook activation */}
                <div className="dash-glass-shine" style={{ ...card, marginBottom: 14 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(191,219,254,0.5)', margin: '0 0 12px' }}>Outils</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" onClick={checkActivationWebhook} disabled={!!trafficAction}
                        style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(96,165,250,0.4)', color: '#93c5fd', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        Vérifier le bot
                      </button>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Le bot activation reçoit-il encore les messages ?</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" onClick={reregisterActivationWebhook} disabled={!!trafficAction}
                        style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(96,165,250,0.4)', color: '#93c5fd', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        Réparer le webhook
                      </button>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Si plus personne n&apos;arrive sur le bot</span>
                    </div>
                  </div>
                </div>

                {/* Live feed */}
                <div className="dash-glass-shine" style={{ ...card }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(191,219,254,0.5)', margin: '0 0 12px' }}>En direct</p>
                  {(trafficData.recent || []).length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12, margin: 0 }}>Rien pour l&apos;instant</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                      {(trafficData.recent || []).slice(0, 40).map((ev, idx) => {
                        const label = String(ev?.type || ev?.event || 'event');
                        const when = ev?.at || ev?.createdAt || '';
                        let whenTxt = '';
                        try { whenTxt = when ? new Date(when).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''; } catch { /* */ }
                        return (
                          <div key={`${label}-${idx}-${when}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: '#e2e8f0' }}>
                            <span style={{ fontWeight: 700 }}>{label}</span>
                            <span style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>{whenTxt}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ ...card, textAlign: 'center', color: 'rgba(255,255,255,0.45)', padding: 40 }}>
                Aucune donnée — clique Actualiser
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — NOTIFICATIONS MARKETING
        ═══════════════════════════════════════ */}
        {activeTab === 'notifications' && (
          <div className="da">
            <div style={{ ...card, marginBottom: 14 }}>
              <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.38)', marginBottom: 6 }}>Notifications marketing</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.40)', marginBottom: 24, lineHeight: 1.5 }}>
                Active une notification popup qui apparaîtra à la prochaine connexion de <strong style={{ color: 'rgba(255,255,255,0.7)' }}>tous les clients</strong>. Une seule notif active à la fois par type.
              </p>

              {/* ── Bouton PASS ── */}
              {(() => {
                const passNotif = notifList.find(n => n.type === 'pass');
                const isActive = passNotif?.active === true;
                return (
                  <div className="dash-glass-shine" style={{ ...(isActive ? accentCard('#fbbf24') : card), marginBottom: 14, transition: 'all .25s' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 36, lineHeight: 1 }}>👑</span>
                        <div>
                          <p style={{ fontSize: 15, fontWeight: 800, color: isActive ? '#fbbf24' : '#f1f5f9', margin: '0 0 3px' }}>Promotion Pass Premium</p>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>Incite les clients à s'abonner à un pass Argent ou Or</p>
                        </div>
                      </div>
                      {/* Badge statut */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: isActive ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${isActive ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.1)'}` }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#22c55e' : 'rgba(255,255,255,0.3)', boxShadow: isActive ? '0 0 6px #22c55e' : 'none' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                      </div>
                    </div>

                    {/* Aperçu du message */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 18, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>Aperçu de la popup client</p>
                      <p style={{ fontSize: 14, fontWeight: 800, color: '#fbbf24', marginBottom: 6 }}>👑 Passez au niveau supérieur !</p>
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>
                        Profitez des avantages exclusifs des <strong>Pass Argent & Or</strong> : livraison prioritaire, bonus de fidélité et bien plus. Rejoignez nos membres premium dès maintenant.
                      </p>
                      <div style={{ marginTop: 12, padding: '8px 16px', borderRadius: 10, background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.3)', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#fbbf24' }}>
                        Voir les Pass Premium →
                      </div>
                    </div>

                    {/* Boutons action */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isActive ? (
                        <button
                          disabled={notifSending === 'pass'}
                          onClick={() => toggleNotif('pass', true, {
                            title: '👑 Passez au niveau supérieur !',
                            message: 'Profitez des avantages exclusifs des Pass Argent & Or : livraison prioritaire, bonus de fidélité et bien plus. Rejoignez nos membres premium dès maintenant.',
                            cta: 'Voir les Pass Premium →'
                          })}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none', color: '#000', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'pass' ? 0.6 : 1, transition: 'opacity .2s' }}>
                          {notifSending === 'pass' ? '...' : '🚀 Activer la notification Pass'}
                        </button>
                      ) : (
                        <button
                          disabled={notifSending === 'pass'}
                          onClick={() => toggleNotif('pass', false)}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'pass' ? 0.6 : 1 }}>
                          {notifSending === 'pass' ? '...' : '⏹ Désactiver'}
                        </button>
                      )}
                    </div>

                    {isActive && passNotif && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 10, textAlign: 'right' }}>
                        Activée le {new Date(passNotif.createdAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })()}


              {/* ── Bouton LUFFA ── */}
              {(() => {
                const luffaNotif = notifList.find(n => n.type === 'luffa');
                const isActive = luffaNotif?.active === true;
                return (
                  <div className="dash-glass-shine" style={{ ...(isActive ? accentCard('#22c55e') : card), marginBottom: 14, transition: 'all .25s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src="/emoji/luffa.png" alt="Luffa" style={{ width: 36, height: 36, objectFit: 'contain' }} onError={(e) => { e.target.style.display='none'; }} />
                        <div>
                          <p style={{ fontSize: 15, fontWeight: 800, color: isActive ? '#4ade80' : '#f1f5f9', margin: '0 0 3px' }}>LUFFA — Rejoindre le canal</p>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>Popup pour rediriger les clients vers le canal Luffa</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: isActive ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${isActive ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.1)'}` }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#22c55e' : 'rgba(255,255,255,0.3)', boxShadow: isActive ? '0 0 6px #22c55e' : 'none' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                      </div>
                    </div>
                    {/* Lien du canal */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.50)', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                        Lien du canal Luffa
                      </label>
                      <input
                        type="text"
                        value={luffaLink}
                        onChange={(e) => setLuffaLink(e.target.value)}
                        placeholder="https://t.me/luffa_canal"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(34,197,94,0.30)', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 18, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>Aperçu de la popup client</p>
                      <p style={{ fontSize: 14, fontWeight: 800, color: '#4ade80', marginBottom: 6 }}>🌿 Rejoins le canal Luffa !</p>
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>Rejoins notre canal officiel et reçois <strong>+10 Global Coins</strong> offerts.</p>
                      <div style={{ marginTop: 12, padding: '8px 16px', borderRadius: 10, background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4ade80' }}>
                        Rejoindre le canal →
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isActive ? (
                        <button
                          disabled={notifSending === 'luffa' || !luffaLink.trim()}
                          onClick={() => toggleNotif('luffa', true, {
                            title: '🌿 Rejoins le canal Luffa !',
                            message: 'Rejoins notre canal LUFFA pour ne jamais perdre contact avec nous si le canal saute et reçois +10 Global Coins offerts pour ton geste.',
                            cta: 'Rejoindre le canal',
                            link: luffaLink.trim()
                          })}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: luffaLink.trim() ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'rgba(255,255,255,0.1)', border: 'none', color: luffaLink.trim() ? '#000' : 'rgba(255,255,255,0.3)', fontWeight: 800, fontSize: 13, cursor: luffaLink.trim() ? 'pointer' : 'not-allowed', opacity: notifSending === 'luffa' ? 0.6 : 1, transition: 'opacity .2s' }}>
                          {notifSending === 'luffa' ? '...' : '🌿 Activer la notification Luffa'}
                        </button>
                      ) : (
                        <button
                          disabled={notifSending === 'luffa'}
                          onClick={() => toggleNotif('luffa', false)}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'luffa' ? 0.6 : 1 }}>
                          {notifSending === 'luffa' ? '...' : '⏹ Désactiver'}
                        </button>
                      )}
                    </div>
                    {isActive && luffaNotif && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 10, textAlign: 'right' }}>
                        Activée le {new Date(luffaNotif.createdAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })()}


              {/* ── Bouton WHATSAPP ── */}
              {(() => {
                const waNotif = notifList.find(n => n.type === 'whatsapp');
                const isActive = waNotif?.active === true;
                return (
                  <div className="dash-glass-shine" style={{ ...(isActive ? accentCard('#25d366') : card), marginBottom: 14, transition: 'all .25s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src="/emoji/wat1.png" alt="WhatsApp" style={{ width: 36, height: 36, objectFit: 'contain' }} onError={(e) => { e.target.style.display='none'; e.target.parentNode.innerHTML = '<span style="font-size:32px">💬</span>'; }} />
                        <div>
                          <p style={{ fontSize: 15, fontWeight: 800, color: isActive ? '#4ade80' : '#f1f5f9', margin: '0 0 3px' }}>WhatsApp — Rejoindre le groupe</p>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>Popup pour rediriger les clients vers le groupe WhatsApp</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: isActive ? 'rgba(37,211,102,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${isActive ? 'rgba(37,211,102,0.35)' : 'rgba(255,255,255,0.1)'}` }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#25d366' : 'rgba(255,255,255,0.3)', boxShadow: isActive ? '0 0 6px #25d366' : 'none' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                      </div>
                    </div>
                    {/* Lien WhatsApp */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.50)', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                        Lien du groupe WhatsApp
                      </label>
                      <input
                        type="text"
                        value={whatsappLink}
                        onChange={(e) => setWhatsappLink(e.target.value)}
                        placeholder="https://chat.whatsapp.com/..."
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(37,211,102,0.30)', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 18, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>Aperçu de la popup client</p>
                      <p style={{ fontSize: 14, fontWeight: 800, color: '#25d366', marginBottom: 6 }}> Enregistrer le contact</p>
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>Désormais , pour la WHITE,EXTA,KETA, sa se passe sur whatsapp.</p>
                      <div style={{ marginTop: 12, padding: '8px 16px', borderRadius: 10, background: 'rgba(37,211,102,0.2)', border: '1px solid rgba(37,211,102,0.3)', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#25d366' }}>
                        Enregistrer le contact →
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isActive ? (
                        <button
                          disabled={notifSending === 'whatsapp' || !whatsappLink.trim()}
                          onClick={() => toggleNotif('whatsapp', true, {
                            title: ' Rejoins-nous sur WhatsApp !',
                            message: 'Désormais , pour la WHITE,EXTA,KETA, sa se passe sur whatsapp. Envoyer un message pour passez commande.',
                            cta: 'Enregistrer le contact',
                            link: whatsappLink.trim()
                          })}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: whatsappLink.trim() ? 'linear-gradient(135deg,#25d366,#128c7e)' : 'rgba(255,255,255,0.1)', border: 'none', color: whatsappLink.trim() ? '#000' : 'rgba(255,255,255,0.3)', fontWeight: 800, fontSize: 13, cursor: whatsappLink.trim() ? 'pointer' : 'not-allowed', opacity: notifSending === 'whatsapp' ? 0.6 : 1, transition: 'opacity .2s' }}>
                          {notifSending === 'whatsapp' ? '...' : '💬 Activer la notification WhatsApp'}
                        </button>
                      ) : (
                        <button
                          disabled={notifSending === 'whatsapp'}
                          onClick={() => toggleNotif('whatsapp', false)}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'whatsapp' ? 0.6 : 1 }}>
                          {notifSending === 'whatsapp' ? '...' : '⏹ Désactiver'}
                        </button>
                      )}
                    </div>
                    {isActive && waNotif && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 10, textAlign: 'right' }}>
                        Activée le {new Date(waNotif.createdAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })()}


              {/* ── Bouton GLOBAL GAME ── */}
              {(() => {
                const gameNotif = notifList.find(n => n.type === 'global_game');
                const isActive = gameNotif?.active === true;
                return (
                  <div className="dash-glass-shine" style={{ ...(isActive ? accentCard('#f59e0b') : card), marginBottom: 14, transition: 'all .25s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,rgba(251,191,36,0.25),rgba(245,158,11,0.15))', border: '1.5px solid rgba(251,191,36,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 18, lineHeight: 1 }}>🎮</span>
                        </div>
                        <div>
                          <p style={{ fontSize: 15, fontWeight: 800, color: isActive ? '#fbbf24' : '#f1f5f9', margin: '0 0 3px' }}>Global Game — Nouveau Jeu</p>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>Popup avant Luffa — annonce le jeu global + 2G offert si 1er du classement</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: isActive ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${isActive ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.1)'}` }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#22c55e' : 'rgba(255,255,255,0.3)', boxShadow: isActive ? '0 0 6px #22c55e' : 'none' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                      </div>
                    </div>

                    {/* Aperçu */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 18, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.28)', marginBottom: 8 }}>Aperçu de la popup client</p>
                      <p style={{ fontSize: 14, fontWeight: 800, color: '#22d3ee', marginBottom: 6 }}>Nouveau jeu disponible !</p>
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>
                        Le <strong>Global Game</strong> est lancé. Si tu es <strong>premier du classement</strong>, tu remportes <strong>2G offerts</strong> sur ta prochaine commande. Rejoins la compétition maintenant !
                      </p>
                      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.35)', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#fbbf24' }}>Jouer</div>
                        <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', textAlign: 'center', fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.45)' }}>Passer</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isActive ? (
                        <button
                          disabled={notifSending === 'global_game'}
                          onClick={() => toggleNotif('global_game', true, {
                            title: 'Nouveau jeu disponible !',
                            message: 'Le Global Game est lancé. Si tu es premier du classement, tu remportes 2G offerts sur ta prochaine commande. Rejoins la compétition maintenant !',
                            cta: 'Jouer'
                          })}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none', color: '#000', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'global_game' ? 0.6 : 1, transition: 'opacity .2s' }}>
                          {notifSending === 'global_game' ? '...' : 'Activer Global Game'}
                        </button>
                      ) : (
                        <button
                          disabled={notifSending === 'global_game'}
                          onClick={() => toggleNotif('global_game', false)}
                          style={{ flex: 1, padding: '12px 20px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: notifSending === 'global_game' ? 0.6 : 1 }}>
                          {notifSending === 'global_game' ? '...' : 'Désactiver'}
                        </button>
                      )}
                    </div>

                    {isActive && gameNotif && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 10, textAlign: 'right' }}>
                        Activée le {new Date(gameNotif.createdAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* ── Sondage marketing (multi-réponses) ── */}
              {(() => {
                const surveyNotif = notifList.find((n) => n.type === 'survey');
                const isActive = surveyNotif?.active === true;
                const filledAnswers = surveyAnswers.filter((a) => String(a || '').trim()).length;
                const canActivate = String(surveyQuestion || '').trim().length >= 3 && filledAnswers >= 2;
                const accent = '#f472b6';
                return (
                  <div className="dash-glass-shine" style={{ ...(isActive ? accentCard(accent) : card), marginBottom: 14, transition: 'all .25s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: 'linear-gradient(135deg,rgba(244,114,182,0.28),rgba(236,72,153,0.16))',
                          border: '1.5px solid rgba(244,114,182,0.40)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: 18, lineHeight: 1 }}>🗳️</span>
                        </div>
                        <div>
                          <p style={{ fontSize: 15, fontWeight: 800, color: isActive ? '#f9a8d4' : '#f1f5f9', margin: '0 0 3px' }}>Sondage client</p>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: 0 }}>
                            Question + 4 réponses · le client peut cocher plusieurs choix
                          </p>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20,
                        background: isActive ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)',
                        border: `1px solid ${isActive ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.1)'}`,
                      }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#22c55e' : 'rgba(255,255,255,0.3)', boxShadow: isActive ? '0 0 6px #22c55e' : 'none' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#4ade80' : 'rgba(255,255,255,0.45)' }}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                      </div>
                    </div>

                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(249,168,212,0.75)', marginBottom: 6 }}>Question</label>
                    <textarea
                      value={surveyQuestion}
                      onChange={(e) => setSurveyQuestion(e.target.value)}
                      placeholder="Ex: Qu’est-ce qui t’intéresse le plus en ce moment ?"
                      rows={2}
                      style={{
                        width: '100%', boxSizing: 'border-box', marginBottom: 12,
                        padding: '12px 14px', borderRadius: 12, resize: 'vertical',
                        background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(244,114,182,0.28)',
                        color: '#fff', fontSize: 13, fontWeight: 600, outline: 'none',
                      }}
                    />
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(249,168,212,0.75)', margin: '0 0 8px' }}>4 réponses</p>
                    <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                      {surveyAnswers.map((ans, idx) => (
                        <input
                          key={`survey-ans-${idx}`}
                          type="text"
                          value={ans}
                          onChange={(e) => {
                            const next = [...surveyAnswers];
                            next[idx] = e.target.value;
                            setSurveyAnswers(next);
                          }}
                          placeholder={`Réponse ${idx + 1}`}
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            padding: '11px 14px', borderRadius: 12,
                            background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(244,114,182,0.22)',
                            color: '#fff', fontSize: 13, fontWeight: 600, outline: 'none',
                          }}
                        />
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!isActive ? (
                        <button
                          type="button"
                          disabled={notifSending === 'survey' || !canActivate}
                          onClick={async () => {
                            await toggleNotif('survey', true, {
                              title: surveyQuestion.trim(),
                              message: surveyQuestion.trim(),
                              question: surveyQuestion.trim(),
                              answers: surveyAnswers.map((a) => a.trim()),
                              cta: 'Voter',
                            });
                            loadSurveyVotes();
                          }}
                          style={{
                            flex: 1, padding: '12px 20px', borderRadius: 12,
                            background: canActivate ? 'linear-gradient(135deg,#f472b6,#db2777)' : 'rgba(255,255,255,0.1)',
                            border: 'none', color: canActivate ? '#fff' : 'rgba(255,255,255,0.3)',
                            fontWeight: 800, fontSize: 13,
                            cursor: canActivate ? 'pointer' : 'not-allowed',
                            opacity: notifSending === 'survey' ? 0.6 : 1,
                          }}
                        >
                          {notifSending === 'survey' ? '...' : '🗳️ Activer le sondage'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={notifSending === 'survey'}
                          onClick={() => toggleNotif('survey', false)}
                          style={{
                            flex: 1, padding: '12px 20px', borderRadius: 12,
                            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                            color: '#fca5a5', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                            opacity: notifSending === 'survey' ? 0.6 : 1,
                          }}
                        >
                          {notifSending === 'survey' ? '...' : '⏹ Désactiver'}
                        </button>
                      )}
                    </div>
                    {isActive && surveyNotif && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 10, textAlign: 'right' }}>
                        Activé le {new Date(surveyNotif.createdAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Historique des votes — style commandes */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(244,114,182,0.10) 0%, rgba(157,23,77,0.14) 100%)',
                border: '1px solid rgba(244,114,182,0.28)',
                borderRadius: 18,
                padding: '18px 16px',
                marginBottom: 14,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      📊 Votes sondages
                    </p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', margin: '3px 0 0' }}>
                      {surveyVotes.length} question{surveyVotes.length !== 1 ? 's' : ''} · plus récentes en premier
                    </p>
                  </div>
                  <button
                    type="button"
                    className="dash-btn"
                    onClick={loadSurveyVotes}
                    disabled={surveyVotesLoading}
                    style={{ fontSize: 12 }}
                  >
                    {surveyVotesLoading ? '…' : 'Actualiser'}
                  </button>
                </div>

                {surveyVotesLoading && surveyVotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.3)' }}>
                    <p style={{ fontSize: 12, margin: 0 }}>Chargement des votes…</p>
                  </div>
                ) : surveyVotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '28px 0', color: 'rgba(255,255,255,0.22)' }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
                    <p style={{ fontSize: 12, margin: 0 }}>Aucun vote pour l’instant</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 520, overflowY: 'auto', paddingRight: 2 }}>
                    {surveyVotes.map((s) => {
                      const totalPicks = (s.counts || []).reduce((a, b) => a + (Number(b) || 0), 0) || 1;
                      return (
                        <div
                          key={s.id}
                          style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.042) 0%, rgba(255,255,255,0.022) 100%)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            borderRadius: 12,
                            padding: '12px 14px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#fce7f3', lineHeight: 1.35, flex: 1 }}>
                              {s.question || 'Sans titre'}
                            </p>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <p style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#f9a8d4', lineHeight: 1 }}>{s.voters || 0}</p>
                              <p style={{ margin: '2px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>votant{(s.voters || 0) !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {(s.answers || []).map((ans, idx) => {
                              const c = Number((s.counts || [])[idx]) || 0;
                              const pct = Math.round((c / totalPicks) * 100);
                              return (
                                <div key={`${s.id}-a-${idx}`}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                                    <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{ans}</span>
                                    <span style={{ color: '#f9a8d4', fontWeight: 800 }}>{c} <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>({pct}%)</span></span>
                                  </div>
                                  <div style={{ height: 8, borderRadius: 8, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                                    <div style={{
                                      height: '100%', width: `${Math.max(c ? 6 : 0, (c / totalPicks) * 100)}%`,
                                      background: 'linear-gradient(90deg, #f472b6, #db2777)',
                                      borderRadius: 8,
                                    }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {s.createdAt && (
                            <p style={{ margin: '10px 0 0', fontSize: 10, color: 'rgba(255,255,255,0.28)', textAlign: 'right' }}>
                              {new Date(s.createdAt).toLocaleString('fr-FR')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Info globale */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55, margin: 0 }}>
                  Anti-spam client : sondage actif → sondage + max 1 pub ensuite · sinon 1 pub max (hors bonus parrain). Cooldown 3j (fermer) / 14j (CTA). Coins / survey satisfaction passent avant. Pour forcer un nouveau message, désactive puis réactive.
                </p>
              </div>
            </div>

            {notifLoading && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>Chargement…</div>
            )}
          </div>
        )}


        {/* ═══ TAB: AVIS CLIENTS ═══ */}
        {activeTab === 'avis' && (
          <div className="da" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', margin: '0 0 4px' }}>⭐ Avis clients</h2>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>{reviewsTotal} avis au total</p>
              </div>
              <button
                onClick={loadReviews}
                style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(74,144,226,0.18)', border: '1px solid rgba(74,144,226,0.35)', color: '#4a90e2', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                🔄 Actualiser
              </button>
            </div>

            {reviewsLoading && (
              <div style={{ textAlign: 'center', padding: 28, color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>Chargement des avis…</div>
            )}

            {!reviewsLoading && reviews.length === 0 && (
              <div className="dash-glass-shine" style={{ ...accentCard('#4a90e2'), textAlign: 'center', padding: '36px 24px' }}>
                <div style={{ fontSize: 42, marginBottom: 12 }}>💬</div>
                <p style={{ color: 'rgba(191,219,254,0.55)', fontSize: 14, margin: 0 }}>Aucun avis pour le moment.<br/>Les avis apparaîtront ici après les livraisons.</p>
              </div>
            )}

            {!reviewsLoading && reviews.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reviews.map((review) => (
                  <div key={review.id} className="dash-glass-shine" style={{
                    ...card,
                    padding: '16px 18px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #4a90e2 0%, #2d5a7e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                          {(review.clientName || 'C').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{review.clientName || 'Client'}</p>
                          {review.orderId && (
                            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', margin: '2px 0 0', fontFamily: 'monospace' }}>Cmd #{review.orderId.slice(-6).toUpperCase()}</p>
                          )}
                        </div>
                      </div>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', margin: 0 }}>
                        {new Date(review.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div style={{ background: 'rgba(96,165,250,0.12)', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid rgba(147,197,253,0.65)' }}>
                      <p style={{ fontSize: 13.5, color: 'rgba(240,249,255,0.85)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        "{review.comment}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — ACCESS (PCS / Paysafecard)
        ═══════════════════════════════════════ */}
        {activeTab === 'access' && (
          <div className="da" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="dash-glass-shine" style={{ ...accentCard('#4a90e2'), padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h2 style={{ fontSize: 17, fontWeight: 900, color: '#f1f5f9', margin: '0 0 4px' }}>🔓 Access — dossiers clients</h2>
                  <p style={{ fontSize: 11, color: 'rgba(191,219,254,0.55)', margin: 0, lineHeight: 1.45 }}>
                    Clique une fiche pour afficher Chat / Vérif / Confirmer / Annuler. Reclique pour refermer.
                  </p>
                </div>
                <button
                  onClick={loadAccessDeposits}
                  style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(74,144,226,0.2)', border: '1px solid rgba(74,144,226,0.4)', color: '#93c5fd', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  🔄 Actualiser
                </button>
              </div>
            </div>

            {accessLoading && (
              <div style={{ textAlign: 'center', padding: 28, color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>Chargement…</div>
            )}

            {!accessLoading && accessDeposits.length === 0 && (
              <div className="dash-glass-shine" style={{ ...card, padding: '40px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
                <p style={{ color: 'rgba(191,219,254,0.55)', fontSize: 14, margin: 0 }}>Aucun dossier à traiter.</p>
              </div>
            )}

            {!accessLoading && accessDeposits.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {accessDeposits.map((dep) => {
                  const created = dep.createdAt
                    ? new Date(dep.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                    : '—';
                  const actionable = dep.status === 'pending' || dep.status === 'pending_admin_confirmation';
                  const isCryptoPending = dep.status === 'pending_admin_confirmation';
                  const confirmed = dep.status === 'confirmed';
                  const cancelled = dep.status === 'cancelled';
                  const waitingVerify = !!(dep.verifyRequested && !dep.hasSelfie && dep.verifyStatus !== 'selfie_sent');
                  let statusLabel = 'En attente';
                  let statusColor = '#fbbf24';
                  if (confirmed) { statusLabel = 'Confirmé'; statusColor = '#22c55e'; }
                  else if (cancelled) { statusLabel = 'Annulé'; statusColor = '#f87171'; }
                  else if (isCryptoPending) { statusLabel = 'Crypto reçue'; statusColor = '#a78bfa'; }
                  else if (waitingVerify) { statusLabel = 'En attente de vérif'; statusColor = '#fbbf24'; }
                  else if (dep.hasSelfie || dep.verifyStatus === 'selfie_sent') { statusLabel = 'Selfie reçu'; statusColor = '#86efac'; }
                  const busy = accessActionId === dep.id;
                  const unread = dep.adminUnread || 0;
                  const expanded = accessExpandedId === dep.id;
                  return (
                    <div key={dep.id} className="dash-glass-shine" style={{
                      ...accentCard('#4a90e2'),
                      padding: 0,
                      overflow: 'hidden',
                      border: expanded ? '1.5px solid rgba(147,197,253,0.55)' : '1.5px solid #4a90e255',
                    }}>
                      <button
                        type="button"
                        onClick={() => setAccessExpandedId((prev) => (prev === dep.id ? null : dep.id))}
                        style={{
                          width: '100%', textAlign: 'left', cursor: 'pointer',
                          background: 'transparent', border: 'none', padding: '14px 16px', color: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 15, fontWeight: 800, color: '#f1f5f9' }}>{dep.name || 'Sans nom'}</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, background: `${statusColor}22`, padding: '2px 8px', borderRadius: 6 }}>
                                {statusLabel}
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#93c5fd', background: 'rgba(74,144,226,0.18)', padding: '2px 8px', borderRadius: 6 }}>
                                {(dep.method || '').toUpperCase() || '—'}
                              </span>
                              {unread > 0 && (
                                <span style={{
                                  minWidth: 18, height: 18, borderRadius: 999, background: '#ef4444', color: '#fff',
                                  fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                                }}>{unread > 9 ? '9+' : unread}</span>
                              )}
                            </div>
                            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '0 0 4px', fontFamily: 'monospace' }}>
                              …{String(dep.chatId || '').slice(-6)} · {dep.city || '—'} · {dep.messageCount || 0} msg
                            </p>
                            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', margin: 0 }}>
                              {created} · 50 CHF → 60 GC
                            </p>
                          </div>
                          <span style={{ color: 'rgba(191,219,254,0.65)', fontSize: 18, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>
                            {expanded ? '▾' : '▸'}
                          </span>
                        </div>
                      </button>

                      {expanded && (
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 16px 14px',
                          borderTop: '1px solid rgba(147,197,253,0.15)', paddingTop: 12,
                        }}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openAccessAdminChat(dep); }}
                            style={{
                              position: 'relative', padding: '10px 14px', borderRadius: 10,
                              border: '1px solid rgba(96,165,250,0.45)', background: 'rgba(59,130,246,0.2)',
                              color: '#93c5fd', fontWeight: 800, fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            Chat
                            {unread > 0 && (
                              <span style={{
                                position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
                                borderRadius: 999, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                              }}>{unread > 9 ? '9+' : unread}</span>
                            )}
                          </button>
                          {(actionable || dep.hasSelfie || dep.verifyRequested) && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (dep.hasSelfie || dep.verifyStatus === 'selfie_sent') {
                                  const url = dep.selfieUrl
                                    || (dep.selfiePath ? `/uploads/activation/${String(dep.selfiePath).split(/[/\\]/).pop()}` : null);
                                  if (url) setAccessVerifyUrl(resolveAccessMediaUrl(url));
                                  return;
                                }
                                setAccessActionId(dep.id);
                                try {
                                  const r = await adminFetch(`${BACKEND_URL}/admin/access-deposits/${encodeURIComponent(dep.id)}/request-verify`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
                                    body: JSON.stringify({ chatId }),
                                  });
                                  const j = await r.json();
                                  if (j.already && (j.deposit?.selfieUrl || dep.selfieUrl)) {
                                    setAccessVerifyUrl(resolveAccessMediaUrl(j.deposit?.selfieUrl || dep.selfieUrl));
                                  }
                                  await loadAccessDeposits();
                                } catch { /* */ }
                                setAccessActionId(null);
                              }}
                              style={{
                                padding: '10px 14px', borderRadius: 10,
                                border: waitingVerify
                                  ? '1px solid rgba(251,191,36,0.55)'
                                  : '1px solid rgba(167,139,250,0.45)',
                                background: waitingVerify
                                  ? 'rgba(251,191,36,0.18)'
                                  : 'rgba(139,92,246,0.2)',
                                color: waitingVerify ? '#fbbf24' : '#c4b5fd',
                                fontWeight: 800, fontSize: 12,
                                cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                              }}
                            >
                              {dep.hasSelfie || dep.verifyStatus === 'selfie_sent'
                                ? 'Voir selfie'
                                : waitingVerify
                                  ? 'Vérif…'
                                  : 'Vérif'}
                            </button>
                          )}
                          {actionable && (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={(e) => { e.stopPropagation(); confirmAccessDeposit(dep.id, isCryptoPending); }}
                                style={{
                                  padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(34,197,94,0.45)',
                                  background: 'rgba(34,197,94,0.18)', color: '#86efac', fontWeight: 800, fontSize: 12,
                                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                                }}
                              >
                                Confirmer
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={(e) => { e.stopPropagation(); cancelAccessDeposit(dep.id); }}
                                style={{
                                  padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.45)',
                                  background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontWeight: 800, fontSize: 12,
                                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                                }}
                              >
                                Annuler
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Chat admin Access — même panneau plein écran que le chat livreur */}
            <FloatingChatPanel
              title={accessChatDep?.name || 'Client'}
              subtitle={`Access · ${(accessChatDep?.method || '').toUpperCase() || '—'}`}
              isOpen={!!accessChatDep}
              isMinimized={accessChatMinimized}
              onClose={() => { setAccessChatDep(null); setAccessChatMsgs([]); setAccessChatMinimized(false); }}
              onMinimize={() => setAccessChatMinimized((prev) => !prev)}
              messages={accessChatMsgs}
              inputValue={accessChatInput}
              onInputChange={setAccessChatInput}
              onSend={sendAccessAdminChatText}
              onPhotoUpload={sendAccessAdminChatPhoto}
              photoInputId="access-admin-chat-photo"
              photoAccept="image/*"
              unreadCount={accessChatDep?.adminUnread || 0}
              role="admin"
              sending={accessChatSending}
              uploading={accessChatUploading}
              uploadProgress={accessChatUploadProgress}
              uploadingMessageId={accessChatUploadingId}
              placeholder="Répondre au client…"
              backendUrl={BACKEND_URL}
            />

            {/* Vérif selfie — plein écran admin */}
            {accessVerifyUrl && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)',
                background: 'radial-gradient(ellipse 120% 80% at 50% -10%, rgba(56,120,220,0.28) 0%, transparent 55%), rgba(5,10,30,0.96)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: 'max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))',
              }}>
                <p style={{
                  margin: '0 0 6px', fontSize: 11, fontWeight: 800, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'rgba(147,197,253,0.85)',
                }}>
                  Face ID
                </p>
                <p style={{ color: '#f1f5f9', fontWeight: 900, fontSize: 20, margin: '0 0 18px' }}>
                  Vérification identité
                </p>
                <img
                  src={accessVerifyUrl.startsWith('http') || accessVerifyUrl.startsWith('blob:')
                    ? accessVerifyUrl
                    : `${String(BACKEND_URL || '').replace(/\/api\/?$/i, '') || 'https://global-strains.shop'}${accessVerifyUrl.startsWith('/') ? '' : '/'}${accessVerifyUrl}`}
                  alt="Selfie"
                  style={{
                    maxWidth: 'min(100%, 440px)', maxHeight: '68vh', borderRadius: 24,
                    border: '2px solid rgba(147,197,253,0.45)',
                    boxShadow: '0 24px 56px rgba(0,0,0,0.5)', objectFit: 'contain',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setAccessVerifyUrl(null)}
                  style={{
                    marginTop: 20, padding: '14px 28px', borderRadius: 14,
                    border: '1.5px solid rgba(191,219,254,0.4)',
                    background: 'linear-gradient(135deg, rgba(96,165,250,0.35), rgba(37,99,235,0.4))',
                    color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
            TAB — CLIENTS BANNIS
        ═══════════════════════════════════════ */}
        {activeTab === 'bannis' && (
          <div className="da" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', margin: '0 0 4px' }}>🚫 Clients bannis</h2>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                  {bannedClients.length} client{bannedClients.length !== 1 ? 's' : ''} banni{bannedClients.length !== 1 ? 's' : ''} · Ban automatique le 14 de chaque mois si &lt; 1 commande
                </p>
              </div>
              <button
                onClick={loadBannedClients}
                style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.13)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                🔄 Actualiser
              </button>
            </div>

            {bannedLoading && (
              <div style={{ textAlign: 'center', padding: 28, color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>Chargement…</div>
            )}

            {!bannedLoading && bannedClients.length === 0 && (
              <div className="dash-glass-shine" style={{ ...card, padding: '40px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0 }}>Aucun client banni pour le moment.</p>
              </div>
            )}

            {!bannedLoading && bannedClients.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {bannedClients.map((client) => {
                  const passEmoji = client.pass === 'or' ? '🥇' : client.pass === 'argent' ? '🥈' : '🥉';
                  const bannedDate = client.bannedAt
                    ? new Date(client.bannedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                    : 'Date inconnue';
                  const isBanning = unbanningId === String(client.chatId);
                  return (
                    <div key={client.chatId} className="dash-glass-shine" style={{
                      ...accentCard('#ef4444'),
                      padding: '14px 16px',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      alignItems: 'center',
                      gap: 12,
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {passEmoji} {client.name || 'Sans nom'}
                          </span>
                        </div>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '0 0 3px', fontFamily: 'monospace' }}>
                          ID: {client.chatId}
                        </p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 6 }}>
                            📦 {client.ordersCount || 0} commande{(client.ordersCount || 0) !== 1 ? 's' : ''}
                          </span>
                          <span style={{ fontSize: 10, color: '#f87171', background: 'rgba(239,68,68,0.12)', padding: '2px 8px', borderRadius: 6 }}>
                            🚫 Banni le {bannedDate}
                          </span>
                          {client.bannedReason && (
                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 6 }}>
                              {client.bannedReason}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnban(client.chatId)}
                        disabled={isBanning}
                        style={{
                          padding: '9px 16px',
                          borderRadius: 10,
                          background: isBanning ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, rgba(34,197,94,0.25), rgba(16,185,129,0.15))',
                          border: '1px solid rgba(34,197,94,0.4)',
                          color: '#4ade80',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: isBanning ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          opacity: isBanning ? 0.6 : 1,
                        }}
                      >
                        {isBanning ? '⏳ …' : '✅ Débannir'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Modal Ajustement Bénéfice Admin */}
        {showRevenueModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(circle at 50% 40%, rgba(30,80,200,0.35) 0%, rgba(4,8,24,0.82) 70%)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div className="dash-glass-shine" style={{
              ...card,
              padding: 28, width: '100%', maxWidth: 400,
              boxShadow: '0 24px 70px rgba(10,30,100,0.55), 0 0 40px rgba(74,144,226,0.22), inset 0 1.5px 0 rgba(255,255,255,0.35)',
            }}>
              <h3 style={{ color: showRevenueModal === 'ajout' ? '#4ade80' : '#f87171', margin: '0 0 22px', fontSize: 19, fontWeight: 800 }}>
                {showRevenueModal === 'ajout' ? '➕ Ajouter bénéfice' : '➖ Retrait bénéfice'}
              </h3>
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Montant (CHF)</p>
                <input
                  type="number" min="0" step="0.01"
                  value={revenueAmount}
                  onChange={e => setRevenueAmount(e.target.value)}
                  placeholder="Ex: 50.00"
                  autoFocus
                  style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, color: '#fff', fontSize: 17, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Motif</p>
                <textarea
                  value={revenueMotif}
                  onChange={e => setRevenueMotif(e.target.value)}
                  placeholder="Raison de l'ajustement..."
                  rows={3}
                  style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, color: '#fff', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowRevenueModal(null); setRevenueAmount(''); setRevenueMotif(''); }} style={{ flex: 1, padding: '12px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
                <button onClick={submitRevenueAdjustment} disabled={revenueSubmitting || !revenueAmount} style={{ flex: 2, padding: '12px 0', background: showRevenueModal === 'ajout' ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#ef4444,#dc2626)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                  {revenueSubmitting ? '⏳ Enregistrement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <p style={{ fontSize: 10, color: 'rgba(147,197,253,0.35)', letterSpacing: '0.14em', fontWeight: 600 }}>GLOBAL STRAINS · DASHBOARD ADMIN</p>
        </div>
      </div>
    </div>

      {/* ═══════════════════════════════════════
          POPUP RÉCAPITULATIF CLÔTURE MENSUELLE
      ═══════════════════════════════════════ */}
      {showMonthlyRecap && data && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)',
          background: 'radial-gradient(circle at 50% 30%, rgba(30,80,200,0.40) 0%, rgba(4,8,24,0.88) 65%)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div className="dash-glass-shine" style={{
            ...accentCard('#fbbf24'),
            padding: '26px 22px',
            maxWidth: 520,
            width: '100%',
            maxHeight: '88vh',
            overflowY: 'auto',
            boxShadow: '0 24px 80px rgba(10,30,100,0.55), 0 0 40px rgba(251,191,36,0.18), inset 0 1.5px 0 rgba(255,255,255,0.35)',
          }}>
            {/* Header popup */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'rgba(251,191,36,0.6)', margin: '0 0 4px' }}>Clôture mensuelle</p>
                <h2 style={{ fontSize: 20, fontWeight: 900, color: '#f1f5f9', margin: 0, letterSpacing: '-0.3px' }}>
                  📅 Récapitulatif du mois
                </h2>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', margin: '4px 0 0' }}>
                  {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button onClick={() => setShowMonthlyRecap(false)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', width: 36, height: 36, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            {/* Totaux globaux */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div className="dash-glass-shine" style={{ ...accentCard('#22c55e'), padding: '14px 16px' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(74,222,128,0.65)', margin: '0 0 6px' }}>Bénéfice net total</p>
                <p style={{ fontSize: 28, fontWeight: 900, color: '#4ade80', margin: 0, lineHeight: 1 }}>
                  {((data.totalProfit||0) + (data.manualAdjustmentTotal||0)).toFixed(2)}
                  <span style={{ fontSize: 13, opacity: 0.6 }}> CHF</span>
                </p>
              </div>
              <div className="dash-glass-shine" style={{ ...accentCard('#38bdf8'), padding: '14px 16px' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(56,189,248,0.65)', margin: '0 0 6px' }}>Chiffre d'affaires</p>
                <p style={{ fontSize: 28, fontWeight: 900, color: '#38bdf8', margin: 0, lineHeight: 1 }}>
                  {(data.totalRevenue||0).toFixed(2)}
                  <span style={{ fontSize: 13, opacity: 0.6 }}> CHF</span>
                </p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.14), rgba(79,70,229,0.08))', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 14, padding: '14px 16px' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(129,140,248,0.65)', margin: '0 0 6px' }}>Commandes livrées</p>
                <p style={{ fontSize: 28, fontWeight: 900, color: '#818cf8', margin: 0, lineHeight: 1 }}>
                  {data.deliveredOrders ?? 0}
                  <span style={{ fontSize: 13, opacity: 0.6 }}> cmd</span>
                </p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(234,179,8,0.08))', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 14, padding: '14px 16px' }}>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(251,191,36,0.65)', margin: '0 0 6px' }}>Marge globale</p>
                <p style={{ fontSize: 28, fontWeight: 900, color: '#fbbf24', margin: 0, lineHeight: 1 }}>
                  {(data.totalRevenue||0) > 0 ? (((data.totalProfit||0) / (data.totalRevenue||1)) * 100).toFixed(1) : '0.0'}
                  <span style={{ fontSize: 13, opacity: 0.6 }}>%</span>
                </p>
              </div>
            </div>

            {/* Stats livreurs */}
            {data.driverStats && Object.keys(data.driverStats).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.38)', margin: '0 0 10px' }}>🚚 Performance livreurs</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(data.driverStats)
                    .sort((a, b) => b[1].orders - a[1].orders)
                    .map(([name, s], i) => {
                      const margePct = s.revenue > 0 ? (s.profit / s.revenue) * 100 : 0;
                      const margeColor = margePct >= 40 ? '#4ade80' : margePct >= 20 ? '#fbbf24' : '#f87171';
                      return (
                        <div key={i} style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.09)',
                          borderRadius: 12, padding: '12px 14px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <p style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', margin: 0 }}>
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`} {name}
                            </p>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', background: 'rgba(59,130,246,0.13)', padding: '2px 9px', borderRadius: 7 }}>
                              {s.orders} cmd
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#38bdf8', background: 'rgba(56,189,248,0.10)', padding: '2px 9px', borderRadius: 7 }}>
                              {(s.revenue||0).toFixed(2)} CHF CA
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: (s.profit||0) >= 0 ? '#4ade80' : '#f87171', background: (s.profit||0) >= 0 ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)', padding: '2px 9px', borderRadius: 7 }}>
                              {(s.profit||0) >= 0 ? '+' : ''}{(s.profit||0).toFixed(2)} CHF bén.
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: margeColor, background: `${margeColor}18`, padding: '2px 9px', borderRadius: 7 }}>
                              {margePct.toFixed(0)}% marge
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Avertissement */}
            <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.28)', borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#fca5a5', margin: '0 0 4px' }}>⚠️ Action irréversible</p>
              <p style={{ fontSize: 11, color: 'rgba(252,165,165,0.65)', margin: 0, lineHeight: 1.5 }}>
                Clôture la période en cours : bénéfice, CA et livrées repartent à zéro (historique lifetime conservé). Analytics (Pass, panier, clics) et ajustements manuels sont remis à zéro. Un snapshot est archivé.
              </p>
            </div>

            {/* Boutons action */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowMonthlyRecap(false)}
                style={{ flex: 1, padding: '13px 0', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                Annuler
              </button>
              <button
                onClick={handleMonthlyReset}
                disabled={monthlyResetLoading}
                style={{
                  flex: 1.5, padding: '13px 0', borderRadius: 12,
                  background: monthlyResetLoading ? 'rgba(239,68,68,0.15)' : 'linear-gradient(135deg, rgba(239,68,68,0.35), rgba(220,38,38,0.28))',
                  border: '1px solid rgba(239,68,68,0.45)',
                  color: '#fca5a5', cursor: monthlyResetLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 14,
                  boxShadow: monthlyResetLoading ? 'none' : '0 4px 16px rgba(239,68,68,0.2)',
                }}
              >
                {monthlyResetLoading ? '⏳ Clôture en cours…' : '✅ Confirmer la clôture'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const TelegramShopApp = () => {
  const initialStaffPanel = readStaffPanelFromLocation();
  const [currentView, setCurrentView] = useState(initialStaffPanel.panel === 'driver' ? 'driver-panel' : 'home');

  // Initialisation Telegram WebApp (obligatoire pour mini-app mobile)
  useEffect(() => {
    try {
      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready();
        tg.expand();
        if (typeof tg.enableClosingConfirmation === 'function') {
          tg.enableClosingConfirmation(false);
        }
      }
    } catch (e) {
      console.warn('Telegram WebApp init:', e);
    }
    applyDeviceLayout();
    // Ne pas relancer le layout au settle du visualViewport (cause du tremblement à l'ouverture)
    refineDeviceModelAsync().then(() => {
      // Met à jour seulement le nom du modèle, sans réécrire les insets si déjà posés
      try { applyDeviceLayout(); } catch (e) { /* silencieux */ }
    }).catch(() => {});
    let orientTimer = null;
    const onOrient = () => {
      clearTimeout(orientTimer);
      orientTimer = setTimeout(() => applyDeviceLayout(), 280);
    };
    window.addEventListener('orientationchange', onOrient);
    return () => {
      clearTimeout(orientTimer);
      window.removeEventListener('orientationchange', onOrient);
    };
  }, []);

  // ── Système de langue ──────────────────────────────────
  const [lang, setLangState] = useState(() => readPersistedLanguage());
  const setLang = useCallback((l) => {
    persistLanguage(l);
    setLangState(l);
  }, []);
  const t = useMemo(() => getTranslator(lang), [lang]);

  const initialPwaInstallFlag = (() => {
    try {
      const flag = new URLSearchParams(window.location.search).get('showInstall');
      if (flag === '1' || flag === 'denied') return flag;
    } catch { /* silencieux */ }
    return null;
  })();

  const initialAccessMode = (() => {
    try {
      return new URLSearchParams(window.location.search).get('mode') === 'access';
    } catch { return false; }
  })();

  // Entrée NFC physique (?src=nfc) — pas de bot Telegram
  const initialNfcEntry = (() => {
    try {
      if (new URLSearchParams(window.location.search).get('src') === 'nfc') {
        markNfcEntry();
        return true;
      }
      return isNfcEntryActive();
    } catch {
      return isNfcEntryActive();
    }
  })();

  // Système de Passs et authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNfcMode] = useState(initialNfcEntry);
  const [nfcReady, setNfcReady] = useState(false);
  const [nfcId, setNfcId] = useState(() => getStoredNfcId());
  const [nfcError, setNfcError] = useState(null);
  const [isAccessMode] = useState(initialAccessMode);
  /** Aperçu boutique restreint (mode Access → « Tester l'app ») */
  const [shopPreviewMode, setShopPreviewMode] = useState(false);
  const [accessEntryStep, setAccessEntryStep] = useState(null);
  const [showAccessPubModal, setShowAccessPubModal] = useState(false);
  const [showPreviewGamePromo, setShowPreviewGamePromo] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showAccountDisabledModal, setShowAccountDisabledModal] = useState(false);
  const [activationBotUrl, setActivationBotUrl] = useState('https://t.me/');
  const [showOrderNameModal, setShowOrderNameModal] = useState(false);
  const [orderNameDraft, setOrderNameDraft] = useState('');
  const [pendingOrderPaidWithCoins, setPendingOrderPaidWithCoins] = useState(false);
  const [savingOrderName, setSavingOrderName] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(!initialPwaInstallFlag && !initialStaffPanel.panel && !initialAccessMode);
  const [splashProgress, setSplashProgress] = useState(6);
  // Splash aussi en mode access (loading habituel puis modal)
  const [splashHold, setSplashHold] = useState(!initialPwaInstallFlag && !initialStaffPanel.panel);
  const [isBlocked, setIsBlocked] = useState(false);
  const [userPass, setUserPass] = useState('bronze'); // 'bronze', 'argent', 'or'
  const [passExpiryDate, setPassExpiryDate] = useState(null); // Date d'expiration du pass (null pour bronze)
  const [wheelBonuses, setWheelBonuses] = useState({ DRY: 0, MOUSS: 0, CALI: 0, SAHAS: 0 }); // Bonus roue / pack
  const [wheelBonusDetail, setWheelBonusDetail] = useState(null); // Détail de la variante du bonus (CALI, MOUSS, DRY)
  const [gameBonus2g, setGameBonus2g] = useState(0); // 🎮 Bonus Global Game (2G offerts sur weed/hash)
  // 🪙 Global Coins
  const [globalCoins, setGlobalCoins] = useState(0);
  const [freePacks, setFreePacks] = useState(0);
  const [packPriorityBonus, setPackPriorityBonus] = useState(false);
  const [pendingCoinsNotification, setPendingCoinsNotification] = useState(null);
  const [doubleGcOffer, setDoubleGcOffer] = useState(null);
  const [showCoinsPopup, setShowCoinsPopup] = useState(false);
  // Sondage satisfaction
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);
  const [surveyComment, setSurveyComment] = useState('');
  const [surveySending, setSurveySending] = useState(false);
  const [pendingSurveyData, setPendingSurveyData] = useState(null);
  const [coinAnimationActive, setCoinAnimationActive] = useState(false);
  const [showInsufficientCoinsPopup, setShowInsufficientCoinsPopup] = useState(false);
  const [userOrdersCount, setUserOrdersCount] = useState(0);
  const [userReferralsCount, setUserReferralsCount] = useState(0);
  const [userName, setUserName] = useState('');
  const [telegramChatId, setTelegramChatId] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(!!initialPwaInstallFlag);
  const [pwaInstallLocked, setPwaInstallLocked] = useState(!!initialPwaInstallFlag);
  const [pwaInstallDenied, setPwaInstallDenied] = useState(initialPwaInstallFlag === 'denied');


  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupStep, setSignupStep] = useState(1); // 1: infos, 2: choix pass, 3: paiement
  const [signupData, setSignupData] = useState({ name: '', selectedPass: 'bronze' }); // Système basé uniquement sur chatId
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false); // Modal de paiement pour utilisateurs connectés
  const [selectedPassForPayment, setSelectedPassForPayment] = useState(null); // Pass sélectionné pour le paiement
  const [showCountryModal, setShowCountryModal] = useState(false); // Modal pour sélectionner le pays AVANT le modal de nom
  const [showCityModal, setShowCityModal] = useState(false); // Modal pour sélectionner la ville de livraison
  const [selectedCity, setSelectedCity] = useState(() => localStorage.getItem('clientSelectedCity') || ''); // Ville de livraison choisie
  const [showNameModal, setShowNameModal] = useState(false); // Modal pour définir le nom si non défini
  const [tempName, setTempName] = useState(''); // Nom temporaire saisi par l'utilisateur
  const [savingName, setSavingName] = useState(false); // État de chargement pour la sauvegarde du nom
  const [isRegistering, setIsRegistering] = useState(false); // État de chargement pour l'enregistrement du compte
  // Configuration des pays activés (récupérée depuis le backend, contrôlée via /on|/off france|suisse)
  const [countryConfig, setCountryConfig] = useState({ france: false, suisse: true });
  const [, setClientSelectedCountry] = useState(() => {
    // Récupérer le pays choisi depuis localStorage
    return localStorage.getItem('clientSelectedCountry') || null;
  }); // Pays choisi par le client (france ou suisse) - seule la fonction setter est utilisée
  
  // Notifications FOMO
  const [fomoNotifications, setFomoNotifications] = useState([]);
  // Notifications marketing admin → clients (file anti-spam : 1 pub max / ouverture)
  const [marketingPopup, setMarketingPopup] = useState(null); // La notif marketing à afficher en popup
  const [marketingPopupSeen, setMarketingPopupSeen] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('marketingPopupSeen') || '[]'); } catch { return []; }
  });
  const marketingQueueRef = useRef([]); // [{ kind, data }]
  const marketingDirectorReadyRef = useRef(false);
  const [marketingQueueVersion, setMarketingQueueVersion] = useState(0); // bump pour réveiller le directeur
  const [parrainBonusPopup, setParrainBonusPopup] = useState(null); // Popup bonus 2g parrain
  const [luffaPopup, setLuffaPopup] = useState(null); // Popup LUFFA canal
  const [globalGamePopup, setGlobalGamePopup] = useState(null); // Popup Global Game
  const [whatsappPopup, setWhatsappPopup] = useState(null); // Popup WHATSAPP canal
  const [surveyPopup, setSurveyPopup] = useState(null); // Popup sondage marketing
  const [surveySelected, setSurveySelected] = useState([]); // index multi-choix
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [showGoldUpsellPopup, setShowGoldUpsellPopup] = useState(false); // Popup upsell Pass Or avant ajout panier
  const [goldUpsellDismissed, setGoldUpsellDismissed] = useState(false); // Si l'user a dit 'une autre fois'
  const [, setViewersCount] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [, setDailyOrdersCount] = useState(() => Math.floor(Math.random() * 17) + 4);
  // activeMembersCount supprimé car non utilisé (seulement setActiveMembersCount est utilisé)
  const [, setActiveMembersCount] = useState(() => Math.floor(Math.random() * 8) + 2);
  
 
  const BACKEND_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "https://global-strains.shop/api";

  // Dashboard admin
  const [, setIsAdminUser] = useState(false);
  const [isDashboardMode, setIsDashboardMode] = useState(initialStaffPanel.panel === 'dashboard');
  // Déclaré tôt : les effets marketing (file popups) lisent isDriverMode avant le bloc livreur
  const [isDriverMode, setIsDriverMode] = useState(initialStaffPanel.panel === 'driver');

  // Session tracking pour le dashboard
  const sessionStartRef = useRef(Date.now());

  // Tracker les events analytics (silencieux)
  const trackEvent = useCallback(async (event, data = {}) => {
    try {
      // Inclure le chatId dans chaque event pour le rate-limiting et l'attribution
      const payload = { event, data: { ...data, chatId: telegramChatId || null } };
      await fetch(`${BACKEND_URL}/admin/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) { /* silencieux */ }
  }, [BACKEND_URL, telegramChatId]);

  // Tracker la durée de session à la fermeture
  // sendBeacon envoie en text/plain par défaut → on utilise un Blob avec Content-Type JSON
  useEffect(() => {
    sessionStartRef.current = Date.now();
    const handleUnload = () => {
      const dur = Math.round((Date.now() - sessionStartRef.current) / 1000);
      if (dur >= 15) {
        const payload = JSON.stringify({ event: 'session_end', data: { durationSeconds: dur, chatId: telegramChatId || null } });
        // Blob avec Content-Type JSON pour que le backend puisse parser le body
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(`${BACKEND_URL}/admin/track`, blob);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [BACKEND_URL, telegramChatId]);

  // Tracker la visite quotidienne unique (max 1 par chatId par jour)
  // Se déclenche dès que telegramChatId est connu
  useEffect(() => {
    if (!telegramChatId) return;
    const todayKey = 'visitTracked_' + new Date().toISOString().slice(0, 10);
    if (sessionStorage.getItem(todayKey) === telegramChatId) return; // Déjà envoyé cette session
    sessionStorage.setItem(todayKey, telegramChatId);
    // Envoyer l'event au backend
    fetch(`${BACKEND_URL}/admin/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'page_visit', data: { chatId: telegramChatId } })
    }).catch(() => {});
  }, [BACKEND_URL, telegramChatId]);

  // Compteur visites du jour (synchronisé avec todayVisits du backend)
  const [liveUsersCount, setLiveUsersCount] = useState(() => Math.floor(Math.random() * 50) + 1);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Gestion du scroll pour la navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Sync liveUsersCount with todayVisits from backend (every 60s)
  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const r = await fetch(BACKEND_URL + '/admin/today-visits');
        if (r.ok) {
          const d = await r.json();
          if (d.todayVisits && d.todayVisits > 0) setLiveUsersCount(d.todayVisits);
        }
      } catch { /* silent */ }
    };
    fetchVisits();
    const interval = setInterval(fetchVisits, 60 * 1000);
    return () => clearInterval(interval);
  }, [BACKEND_URL]);

  // Effacer le pays et la ville précédents à chaque ouverture pour forcer la sélection
  useEffect(() => {
    localStorage.removeItem('clientSelectedCountry');
    localStorage.removeItem('clientSelectedCity');
    setSelectedCity('');
    console.log('🌍 Pays et ville précédents effacés - Le client devra choisir son pays et sa ville');
  }, []);

  // Récupérer la configuration des pays activés depuis le backend (poll — /on|/off fr|ch)
  useEffect(() => {
    const fetchCountryConfig = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "https://global-strains.shop/api";
        const res = await fetch(`${backendUrl}/country-config`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const france = data.france === true;
            const suisse = data.suisse === true;
            setCountryConfig({ france, suisse });
            console.log('🌍 Config pays récupérée:', { france, suisse });
          }
        }
      } catch (err) {
        console.warn('⚠️ Impossible de récupérer la config pays, valeurs par défaut utilisées:', err?.message);
        setCountryConfig({ france: false, suisse: true });
      }
    };
    fetchCountryConfig();
    const iv = setInterval(fetchCountryConfig, 20000);
    return () => clearInterval(iv);
  }, []);

  // Mettre à jour countryConfig quand le modal de sélection s'ouvre (re-fetch pour avoir l'état le plus récent)
  useEffect(() => {
    if (!showCountryModal) return;
    const fetchCountryStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/country-config`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const france = data.france === true;
            const suisse = data.suisse === true;
            setCountryConfig({ france, suisse });
            // Si seulement un pays est disponible, le pré-sélectionner automatiquement
            if (!france && suisse) {
              setSelectedCountry('suisse');
            } else if (france && !suisse) {
              setSelectedCountry('france');
            }
          }
        }
      } catch (err) {
        console.warn('⚠️ Impossible de récupérer le statut des pays, valeurs par défaut utilisées');
      }
    };
    fetchCountryStatus();
  }, [showCountryModal, BACKEND_URL]);

  // Empêcher le zoom tactile et le défilement horizontal sur mobile
  useEffect(() => {
    // Empêcher le zoom avec plusieurs doigts
    const preventZoom = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Empêcher le double-tap zoom
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e) => {
      const now = new Date().getTime();
      const timeSince = now - lastTouchEnd;
      if (timeSince < 300 && timeSince > 0) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      lastTouchEnd = now;
    };

    // Empêcher le défilement horizontal
    let touchStartX = 0;
    let touchStartY = 0;
    const preventHorizontalScroll = (e) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }
    };

    const preventHorizontalScrollMove = (e) => {
      if (e.touches.length === 1) {
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const deltaX = Math.abs(touchX - touchStartX);
        const deltaY = Math.abs(touchY - touchStartY);
        
        // Si le mouvement horizontal est plus important que le vertical, empêcher
        if (deltaX > deltaY && deltaX > 10) {
          e.preventDefault();
        }
      }
    };

    // Empêcher les gestes de zoom
    const preventGestures = (e) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchstart', preventHorizontalScroll, { passive: false });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('touchmove', preventHorizontalScrollMove, { passive: false });
    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestures, { passive: false });
    document.addEventListener('gesturechange', preventGestures, { passive: false });
    document.addEventListener('gestureend', preventGestures, { passive: false });

    // Empêcher le zoom avec la molette de la souris (si utilisée sur mobile)
    const preventWheelZoom = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', preventWheelZoom, { passive: false });

    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchstart', preventHorizontalScroll);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('touchmove', preventHorizontalScrollMove);
      document.removeEventListener('touchend', preventDoubleTapZoom);
      document.removeEventListener('gesturestart', preventGestures);
      document.removeEventListener('gesturechange', preventGestures);
      document.removeEventListener('gestureend', preventGestures);
      document.removeEventListener('wheel', preventWheelZoom);
    };
  }, []);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedUICategory, setSelectedUICategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [bulkQuantity, setBulkQuantity] = useState(100);
  const [isBulkMode, setIsBulkMode] = useState(false);
  // showBulkSection supprimé - mode bulk désactivé
  const [orderName, setOrderName] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [isPriorityDelivery, setIsPriorityDelivery] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(() => localStorage.getItem('clientSelectedCountry') || null); // 'france', 'suisse', ou null si non choisi
  // Système de panier
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showReferralCode, setShowReferralCode] = useState(false);
  const [parrainageCodeInput, setParrainageCodeInput] = useState('');
  const [hasParrainageBonus, setHasParrainageBonus] = useState(() => {
    return localStorage.getItem('parrainageBonus1g') === 'true';
  });
  const [referralBonusGrams, setReferralBonusGrams] = useState(() => {
    return parseInt(localStorage.getItem('referralBonusGrams') || '2', 10);
  });
  // Modal pour sélectionner le produit qui recevra le bonus de parrainage
  const [showParrainageProductModal, setShowParrainageProductModal] = useState(false);
  const [pendingParrainageCode, setPendingParrainageCode] = useState(null);
  const [pendingParrainageItem, setPendingParrainageItem] = useState(null);
  const [orderStatus, setOrderStatus] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMinimumAlert, setShowMinimumAlert] = useState(false);
  const [showTelegramConfirm, setShowTelegramConfirm] = useState(false);
  const [telegramNotificationsEnabled, setTelegramNotificationsEnabled] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [showConnectedWelcome, setShowConnectedWelcome] = useState(false);
  const [showSoldOutNotif, setShowSoldOutNotif] = useState(false);
  // Ne jamais bloquer l'interface avec checkingShopStatus après le premier chargement
  const [checkingShopStatus, setCheckingShopStatus] = useState(() => {
    const hasCheckedBefore = sessionStorage.getItem('shopStatusChecked') === 'true';
    const panelParam = new URLSearchParams(window.location.search).get('panel');
    const isAdminOrDriverUrl = panelParam === 'driver' || panelParam === 'dashboard';
    const storedCountry = localStorage.getItem('clientSelectedCountry');
    const hasValidCountry = storedCountry === 'france' || storedCountry === 'suisse';
    // Ne bloquer que si pays déjà choisi (sinon modal pays invisible derrière le loader)
    return !hasCheckedBefore && !isAdminOrDriverUrl && hasValidCountry;
  });
  const [telegramLink, setTelegramLink] = useState(null);
  const [queueInfo, setQueueInfo] = useState({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0, hasEnRouteOrdersAhead: false });
  const [countdownMinutes, setCountdownMinutes] = useState(0);
  const [, setWaitingMinutes] = useState(0); // Temps d'attente estimé avant que le livreur soit en route
  const [backendTotalMinutes, setBackendTotalMinutes] = useState(null); // Durée totale réelle du trajet (backend)

  // Mémorisation du temps total pour éviter les recalculs (optimisation 120 FPS)
  const totalDeliveryTime = useMemo(() => {
    if (!activeOrder) return 25; // Défaut France standard
    return activeOrder.country === 'suisse'
      ? (activeOrder.isPriority ? 25 : 40)
      : (activeOrder.isPriority ? 15 : 25);
  }, [activeOrder]);
  // Durée totale utilisée pour la barre de progression : valeur réelle du backend si connue
  // (inclut le « temps exact » défini par le livreur), sinon valeur par défaut, jamais < temps restant
  const effectiveTotalDeliveryTime = Math.max(backendTotalMinutes || totalDeliveryTime, countdownMinutes, 1);

  // États pour le jeu "Qui Paye aujourd'hui ?"
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [showGame, setShowGame] = useState(false);
  const [fingerPositions, setFingerPositions] = useState([]);
  const [gameWinner, setGameWinner] = useState(null);
  const [gamePhase, setGamePhase] = useState('waiting'); // 'waiting', 'touching', 'result', 'exit'
  const [numberOfPlayers, setNumberOfPlayers] = useState(2); // Nombre de joueurs sélectionnés (2-6)
  const [showPlayerSelection, setShowPlayerSelection] = useState(false); // Popup de sélection du nombre de joueurs
  // Jeu Snake — Global Game
  const [showArcadeModal, setShowArcadeModal] = useState(false);
  const [arcadePhase, setArcadePhase] = useState('intro'); // 'intro'|'playing'|'submitting'|'results'
  const [arcadeLeaderboard, setArcadeLeaderboard] = useState([]);
  const [arcadeMyRank, setArcadeMyRank] = useState(null);
  const [arcadeMyClicks, setArcadeMyClicks] = useState(null); // score snake (pommes)

  const [priorityUpgradeLoading, setPriorityUpgradeLoading] = useState(false);
  const [showPriorityPopup, setShowPriorityPopup] = useState(false); // Pop-up « PASSEZ PRIORITAIRE » (1re visite du suivi)
  const previousOrderStatusRef = useRef(0); // Pour détecter le passage de 0 à 1
  const lastBackendTimeRef = useRef(null); // Pour stocker le dernier temps du backend et détecter les ajustements
  const lastBackendSyncRef = useRef(null); // Horodatage du dernier temps reçu du backend pour le décompte
  const firstShopStatusCheckRef = useRef(true); // Pour tracker si c'est la première vérification du statut
  const countdownMinutesRef = useRef(0); // Pour stocker la valeur actuelle de countdownMinutes sans déclencher de re-render
  const orderStatusRef = useRef(0); // Pour stocker la valeur actuelle de orderStatus dans le minuteur
  // Système de parrainage et tours bonus
  const [referralCode, setReferralCode] = useState(() => {
    let code = localStorage.getItem('referralCode');
    if (!code) {
      // Générer un code temporaire pour cet utilisateur
      code = 'REF' + Math.random().toString(36).substring(2, 9).toUpperCase();
      localStorage.setItem('referralCode', code);
    }
    return code;
  });

  const [deviceId] = useState(() => ensureDeviceId());
  const treasureTurnsStorageKey = useMemo(
    () => (deviceId ? `treasureTurns:${deviceId}` : 'treasureTurns'),
    [deviceId]
  );
  const activeOrderStorageKey = useMemo(
    () => (deviceId ? `activeOrder:${deviceId}` : 'activeOrder'),
    [deviceId]
  );

  const getStoredActiveOrder = useCallback(() => {
    if (activeOrderStorageKey) {
      return readPersistentValue(activeOrderStorageKey);
    }
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('activeOrder');
  }, [activeOrderStorageKey]);

  const saveActiveOrderData = useCallback(
    (orderData) => {
      if (!orderData) {
        if (activeOrderStorageKey) {
          removePersistentValue(activeOrderStorageKey);
        } else if (typeof window !== 'undefined') {
          localStorage.removeItem('activeOrder');
        }
        return;
      }
      const payload = typeof orderData === 'string' ? orderData : JSON.stringify(orderData);
      if (activeOrderStorageKey) {
        persistValueAcrossStorages(activeOrderStorageKey, payload);
      } else if (typeof window !== 'undefined') {
        localStorage.setItem('activeOrder', payload);
      }
    },
    [activeOrderStorageKey]
  );

  // Système de tours hebdomadaires : réinitialisation chaque lundi
  const getWeekKey = useCallback(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Ajuster pour lundi = 1
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return `week_${monday.getFullYear()}_${monday.getMonth()}_${monday.getDate()}`;
  }, []);

  // Système robuste de tours hebdomadaires avec marqueur d'utilisation
  const getWheelUsedKey = useCallback(() => {
    const weekKey = getWeekKey();
    return `wheelUsed_${weekKey}`;
  }, [getWeekKey]);

  // Initialiser treasureTurns à 0 - sera synchronisé depuis le backend (source de vérité unique)
  const [treasureTurns, setTreasureTurns] = useState(0);

  // DÉSACTIVÉ : Le backend est maintenant la source de vérité unique pour les tours de roue
  // Ce useEffect ne doit plus modifier treasureTurns car il est synchronisé depuis le backend
  // La logique de réinitialisation hebdomadaire est gérée par le backend


  // DÉSACTIVÉ : Le backend est maintenant la source de vérité unique pour les tours de roue
  // Ce useEffect ne doit plus modifier treasureTurns car il est synchronisé depuis le backend
  
  // Synchroniser l'état du bonus de parrainage uniquement
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const hasBonus = localStorage.getItem('parrainageBonus1g') === 'true';
    setHasParrainageBonus(hasBonus);
    
    // Vérifier aussi côté backend si bonus disponible (source de vérité)
    if (isAuthenticated && telegramChatId) {
      fetch(`${BACKEND_URL}/client/referral-bonus?chatId=${telegramChatId}`)
        .then(r => r.json())
        .then(j => {
          if (j.hasBonus === true && !hasBonus) {
            localStorage.setItem('parrainageBonus1g', 'true');
            setHasParrainageBonus(true);
          } else if (j.hasBonus === false && hasBonus) {
            // Backend dit pas de bonus = pas de bonus
            localStorage.removeItem('parrainageBonus1g');
            setHasParrainageBonus(false);
          }
          if (j.grams != null) {
            localStorage.setItem('referralBonusGrams', String(j.grams));
            setReferralBonusGrams(j.grams);
          }
        })
        .catch(() => {});
    }
    
    // Vérifier aussi les parrainages en attente à chaque changement de vue (si l'utilisateur est parrain)
    const parrainagesEnAttente = JSON.parse(localStorage.getItem('parrainagesEnAttente') || '[]');
    const parrainagesTraites = JSON.parse(localStorage.getItem('parrainagesTraites') || '[]');
      
      // Filtrer les parrainages où cet utilisateur est le parrain
    const mesParrainages = parrainagesEnAttente.filter(p => p.parrainCode === referralCode);
      
      // Filtrer seulement ceux qui n'ont pas encore été traités
    const nouveauxParrainages = mesParrainages.filter(p => 
      !parrainagesTraites.some(pt => pt.filleulCode === p.filleulCode && pt.parrainCode === p.parrainCode)
    );
    
    if (nouveauxParrainages.length > 0) {
        console.log(`🎁 Parrain : ${nouveauxParrainages.length} nouveau(x) filleul(s) détecté(s)`);
        
        // Le parrain reçoit +1 tour de roue par nouveau filleul
        // IMPORTANT : Les tours bonus s'ajoutent aux tours existants (gratuit hebdomadaire ou autres bonus)
        const turnsBonus = nouveauxParrainages.length;
        // Utiliser la valeur actuelle de treasureTurns plutôt que de lire depuis localStorage
        // pour éviter les conflits avec les autres useEffect
        const currentTurns = Math.max(treasureTurns, 0);
        const newTurns = currentTurns + turnsBonus;
        persistValueAcrossStorages(treasureTurnsStorageKey, newTurns.toString());
      setTreasureTurns(newTurns);
        console.log(`✅ Parrain : +${turnsBonus} tour(s) de roue accordé(s) (total: ${newTurns} tours)`);
      
        // Le parrain reçoit 1g offert à sa prochaine commande (à chaque nouveau filleul)
        // On s'assure qu'il a toujours au moins 1g offert disponible
        localStorage.setItem('parrainageBonus1g', 'true');
        setHasParrainageBonus(true);
        console.log(`✅ Parrain : 1g offert sur la prochaine commande (${nouveauxParrainages.length} nouveau(x) filleul(s))`);
      
        // Marquer ces parrainages comme traités
      nouveauxParrainages.forEach(p => {
        if (!parrainagesTraites.some(pt => pt.filleulCode === p.filleulCode && pt.parrainCode === p.parrainCode)) {
          parrainagesTraites.push({
            parrainCode: p.parrainCode,
            filleulCode: p.filleulCode,
            date: p.date
          });
        }
      });
      localStorage.setItem('parrainagesTraites', JSON.stringify(parrainagesTraites));
      
        // Retirer les parrainages traités de la liste d'attente
      const parrainagesEnAttenteRestants = parrainagesEnAttente.filter(p => 
        !nouveauxParrainages.some(np => np.filleulCode === p.filleulCode && np.parrainCode === p.parrainCode)
      );
      localStorage.setItem('parrainagesEnAttente', JSON.stringify(parrainagesEnAttenteRestants));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, referralCode, treasureTurns, treasureTurnsStorageKey, deviceId, getWeekKey, getWheelUsedKey]); // Re-synchroniser quand on change de vue (notamment quand on revient à 'home')

  // Fonction pour ajouter une notification FOMO
  const addFomoNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now();
    // Erreurs client : jamais de détail technique (auth, réseau, HTTP…)
    const safeMessage = type === 'error' ? safeUserError(message) : message;
    const notification = { id, message: safeMessage, type, duration };
    setFomoNotifications(prev => [...prev, notification]);
    
    setTimeout(() => {
      setFomoNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  }, []);

  // Anti-spam popups : cooldown local (dismiss 3j, CTA 14j) + 1 seule pub / ouverture
  const MKT_COOLDOWN_DISMISS_MS = 3 * 24 * 60 * 60 * 1000;
  const MKT_COOLDOWN_CTA_MS = 14 * 24 * 60 * 60 * 1000;
  const isMarketingOnCooldown = useCallback((notifId) => {
    if (notifId == null) return true;
    try {
      const raw = JSON.parse(localStorage.getItem('gs_mkt_cooldown') || '{}');
      const e = raw[String(notifId)];
      if (!e || !e.at) return false;
      const cool = e.action === 'cta' ? MKT_COOLDOWN_CTA_MS : MKT_COOLDOWN_DISMISS_MS;
      return Date.now() - Number(e.at) < cool;
    } catch {
      return false;
    }
  }, []);

  const markMarketingSeen = useCallback((notifId, action = 'dismiss') => {
    if (notifId == null) return;
    const sid = String(notifId);
    setMarketingPopupSeen((prev) => {
      const n = prev.some((x) => String(x) === sid) ? prev : [...prev, notifId];
      try { sessionStorage.setItem('marketingPopupSeen', JSON.stringify(n)); } catch { /* */ }
      return n;
    });
    try {
      const raw = JSON.parse(localStorage.getItem('gs_mkt_cooldown') || '{}');
      raw[sid] = { at: Date.now(), action: action === 'cta' ? 'cta' : 'dismiss' };
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      Object.keys(raw).forEach((k) => {
        if (!raw[k]?.at || Number(raw[k].at) < cutoff) delete raw[k];
      });
      localStorage.setItem('gs_mkt_cooldown', JSON.stringify(raw));
    } catch { /* */ }
  }, []);

  // Charger les notifs → file d'attente (pas de cascade setTimeout)
  useEffect(() => {
    if (!isAuthenticated || isDriverMode || isDashboardMode) return;
    let cancelled = false;
    marketingQueueRef.current = [];
    marketingDirectorReadyRef.current = false;

    const loadMarketingNotifs = async () => {
      try {
        const effCountry = typeof getEffectiveCountry === 'function' ? getEffectiveCountry() : '';
        const params = new URLSearchParams();
        if (telegramChatId) params.set('chatId', telegramChatId);
        if (effCountry) params.set('country', effCountry);
        const qs = params.toString();
        const res = await fetch(`${BACKEND_URL}/client/marketing-notif${qs ? '?' + qs : ''}`);
        const json = await res.json();
        if (cancelled || !json.success || !Array.isArray(json.notifications)) {
          marketingDirectorReadyRef.current = true;
          if (!cancelled) setMarketingQueueVersion((v) => v + 1);
          return;
        }
        const seen = new Set((marketingPopupSeen || []).map(String));
        const isFresh = (n) =>
          n && n.active && n.id != null
          && !seen.has(String(n.id))
          && !isMarketingOnCooldown(n.id);

        const queue = [];
        // 1) Récompense parrain = utile, toujours prioritaire (pas une « pub »)
        const parrainBonus = json.notifications.find((n) => n.type === 'parrain_bonus' && isFresh(n));
        if (parrainBonus) queue.push({ kind: 'parrain_bonus', data: parrainBonus });

        // 2) Sondage (si actif) + max 1 pub ensuite / ouverture
        //    Sans sondage → 1 seule pub max (anti-spam)
        const pushPromo = (n) => {
          if (!n) return;
          const t = String(n.type || '');
          if (t === 'global_game') queue.push({ kind: 'global_game', data: n });
          else if (t === 'survey') queue.push({ kind: 'survey', data: n });
          else if (t === 'luffa') queue.push({ kind: 'luffa', data: n });
          else if (t === 'whatsapp') queue.push({ kind: 'whatsapp', data: n });
          else queue.push({ kind: 'pass', data: n });
        };
        const surveyPick = json.notifications.find(
          (n) => n.type === 'survey' && isFresh(n)
            && Array.isArray(n.answers) && n.answers.filter(Boolean).length >= 2
        );
        if (surveyPick) pushPromo(surveyPick);

        const promoAfterSurvey =
          json.notifications.find((n) => n.type === 'global_game' && isFresh(n))
          || json.notifications.find((n) => n.type === 'pass' && isFresh(n))
          || json.notifications.find((n) => n.type === 'luffa' && isFresh(n))
          || json.notifications.find((n) => n.type === 'whatsapp' && isFresh(n))
          || null;
        // Avec sondage : +1 pub max. Sans sondage : 1 pub max.
        if (promoAfterSurvey) pushPromo(promoAfterSurvey);

        marketingQueueRef.current = queue;
      } catch { /* silencieux */ }
      if (!cancelled) {
        marketingDirectorReadyRef.current = true;
        setMarketingQueueVersion((v) => v + 1);
      }
    };
    loadMarketingNotifs();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isDriverMode, isDashboardMode, BACKEND_URL, telegramChatId]);

  // Directeur : 1 popup à la fois, après coins/sondage, délai calme
  useEffect(() => {
    if (!isAuthenticated || isDriverMode || isDashboardMode || maintenanceMode) return;
    if (!marketingDirectorReadyRef.current) return;
    // Transactionnel / autre popup déjà ouvert → attendre
    if (showCoinsPopup || showSurveyPopup) return;
    if (parrainBonusPopup || marketingPopup || luffaPopup || whatsappPopup || globalGamePopup || surveyPopup) return;
    if (showAccountDisabledModal || showOrderNameModal || showConnectedWelcome) return;
    if (splashHold || isCheckingAuth) return;

    const next = marketingQueueRef.current[0];
    if (!next) return;

    const delayMs = next.kind === 'parrain_bonus' ? 1600 : 2200;
    const t = setTimeout(() => {
      // Re-check au moment du fire
      if (showCoinsPopup || showSurveyPopup) return;
      if (parrainBonusPopup || marketingPopup || luffaPopup || whatsappPopup || globalGamePopup || surveyPopup) return;
      const item = marketingQueueRef.current.shift();
      if (!item) return;
      if (item.kind === 'parrain_bonus') setParrainBonusPopup(item.data);
      else if (item.kind === 'global_game') setGlobalGamePopup(item.data);
      else if (item.kind === 'survey') { setSurveySelected([]); setSurveyPopup(item.data); }
      else if (item.kind === 'luffa') setLuffaPopup(item.data);
      else if (item.kind === 'whatsapp') setWhatsappPopup(item.data);
      else setMarketingPopup(item.data);
    }, delayMs);
    return () => clearTimeout(t);
  }, [
    isAuthenticated, isDriverMode, isDashboardMode, maintenanceMode,
    showCoinsPopup, showSurveyPopup,
    parrainBonusPopup, marketingPopup, luffaPopup, whatsappPopup, globalGamePopup, surveyPopup,
    showAccountDisabledModal, showOrderNameModal, showConnectedWelcome,
    splashHold, isCheckingAuth, marketingQueueVersion,
  ]);

  // Vérifier l'authentification avec Telegram chat ID
  // Fonction helper pour extraire le chatId depuis Telegram WebApp initData
  const getChatIdFromTelegram = () => {
    try {
      if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData) {
        const initData = window.Telegram.WebApp.initData;
        if (initData) {
          // Parser initData (format: key=value&key=value)
          const params = new URLSearchParams(initData);
          const userStr = params.get('user');
          if (userStr) {
            try {
              const user = JSON.parse(decodeURIComponent(userStr));
              if (user.id) {
                console.log('✅ ChatId récupéré depuis Telegram WebApp:', user.id);
                return String(user.id);
              }
            } catch (e) {
              console.warn('⚠️ Erreur parsing user depuis initData:', e);
            }
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Erreur récupération chatId depuis Telegram WebApp:', error);
    }
    return null;
  };

  const authBootstrapRef = useRef(false);
  // Ref toujours à jour vers checkAuth — permet de l'appeler depuis des useEffect avec deps []
  const checkAuthRef = useRef(null);
  // Bloque checkAuth pendant la résolution asynchrone du token PWA (évite modal nom intempestif)
  // Pré-bloquer checkAuth si un token PWA est présent AVANT le premier render
const pwaTokenResolvingRef = useRef(
  (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('c')) return true;           // rétrocompat /go/ → ?c=
      if (params.get('t')) return true;           // token long (rétrocompat)
      if (localStorage.getItem('pwaToken')) return true;
      if (localStorage.getItem('telegramChatId')) return false;
      // Signet = URL racine seule : attendre /api/device/resolve avant checkAuth
      const did = localStorage.getItem(DEVICE_ID_KEY) || getCookieValue(DEVICE_ID_KEY);
      if (did && did.length >= 8) return true;
    } catch { /* silencieux */ }
    return false;
  })()
);
// true dès qu'un token a été résolu avec succès → on sait que c'est un vrai client du signet
const pwaTokenResolvedRef = useRef(false);

  const checkAuth = useCallback(async () => {
      // Attendre la fin de la résolution du token PWA avant de vérifier l'auth
      const earlyParams = new URLSearchParams(window.location.search);
      const earlyStaff = earlyParams.get('panel') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STAFF_PANEL_KEY) : '');
      if (pwaTokenResolvingRef.current && earlyStaff !== 'driver' && earlyStaff !== 'dashboard') {
        console.log('⏳ checkAuth différé — résolution token PWA en cours');
        setIsCheckingAuth(false);
        return;
      }
      // Mode NFC : l'auth est gérée par l'effet ensureNfcAuth (pas de Telegram)
      if (isNfcMode || isNfcEntryActive()) {
        if (nfcReady) {
          setIsCheckingAuth(false);
          setSplashHold(false);
        }
        return;
      }
      if (!authBootstrapRef.current) {
        setIsCheckingAuth(true);
      }
      try {
        // Récupérer le chat ID depuis Telegram WebApp (priorité 1), puis URL (priorité 2), puis localStorage (priorité 3)
        const chatIdFromTelegram = getChatIdFromTelegram();
        const urlParams = new URLSearchParams(window.location.search);
        const staffPanel = urlParams.get('panel') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STAFF_PANEL_KEY) : '');
        const isDriverPanel = staffPanel === 'driver';
        const isDashboardPanel = staffPanel === 'dashboard';
        const chatIdFromUrl = (isDriverPanel || isDashboardPanel)
          ? (urlParams.get('chatId') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STAFF_CHAT_KEY) : null))
          : null;
        const chatIdFromStorage = localStorage.getItem('telegramChatId');
        const viewFromUrl = urlParams.get('view'); // Récupérer le paramètre view (ex: tracking)
        const orderIdFromUrl = urlParams.get('orderId'); // Récupérer l'orderId si présent
        const chatId = chatIdFromTelegram || chatIdFromUrl || chatIdFromStorage;
        
        // Si view=tracking est présent, rediriger vers la vue de suivi de commande
        if (viewFromUrl === 'tracking' && orderIdFromUrl) {
          // Charger la commande et rediriger vers la vue de suivi
          // Cette logique sera exécutée après l'authentification
          setTimeout(() => {
            // Charger la commande depuis le backend
            fetch(`${BACKEND_URL}/status/${orderIdFromUrl}`)
              .then(res => res.json())
              .then(data => {
                if (data.success && data.order) {
                  // Créer un objet activeOrder à partir des données
                  const orderData = {
                    orderId: orderIdFromUrl,
                    product: data.order.product,
                    quantity: data.order.quantity,
                    price: data.order.price,
                    address: data.order.address,
                    isPriority: data.order.isPriority,
                    createdAt: data.order.createdAt,
                    status: data.status,
                    country: data.order.country || 'france',
                    products: data.order.products || null, // Produits du panier si commande panier
                    isCartOrder: data.order.isCartOrder || false
                  };
                  setActiveOrder(orderData);
                  setOrderStatus(data.status);
                  setCurrentView('order-tracking');
                }
              })
              .catch(error => {
                console.error('Erreur chargement commande:', error);
              });
          }, 1000); // Attendre que l'authentification soit terminée
        }
        
        // Si on est en mode driver, récupérer le chatId depuis l'URL et le définir
        if (isDriverPanel) {
          const driverCid = chatIdFromTelegram || chatIdFromUrl || localStorage.getItem('telegramChatId');
          if (driverCid) {
            setTelegramChatId(driverCid);
            localStorage.setItem('telegramChatId', driverCid);
            try {
              sessionStorage.setItem(STAFF_PANEL_KEY, 'driver');
              sessionStorage.setItem(STAFF_CHAT_KEY, driverCid);
            } catch { /* silencieux */ }
          }
          setIsDriverMode(true);
          setCurrentView('driver-panel');
          setIsCheckingAuth(false);
          setSplashHold(false);
          return;
        }
        
        // Récupérer le chat ID depuis Telegram WebApp, URL ou localStorage (pour les clients)
        if (chatIdFromTelegram) {
          setTelegramChatId(chatIdFromTelegram);
          // Sauvegarder le chat ID de Telegram dans localStorage
          localStorage.setItem('telegramChatId', chatIdFromTelegram);
          console.log(`✅ Chat ID récupéré depuis Telegram WebApp: ${chatIdFromTelegram}`);
        } else if (chatIdFromUrl) {
          setTelegramChatId(chatIdFromUrl);
          // Sauvegarder le chat ID de l'URL dans localStorage
          localStorage.setItem('telegramChatId', chatIdFromUrl);
          console.log(`✅ Chat ID récupéré depuis URL: ${chatIdFromUrl}`);
        } else if (chatIdFromStorage) {
          // Si pas de chat ID dans Telegram ou URL, utiliser celui du localStorage
          setTelegramChatId(chatIdFromStorage);
          console.log(`✅ Chat ID récupéré depuis localStorage: ${chatIdFromStorage}`);
        }
        
        // Si pas de chatId, l'utilisateur doit utiliser /start sur le bot Telegram
        let finalChatId = chatId;
        if (!finalChatId) {
          // Tentative de récupération du chatId via le cookie signet (gs_cid) lu côté serveur.
          // Le cookie est HttpOnly donc illisible en JS, mais /api/device/whoami le lit.
          try {
            const did = ensureDeviceId() || '';
            const whoami = await fetchWithTimeout(
              `${BACKEND_URL}/device/whoami?d=${encodeURIComponent(did)}`,
              { credentials: 'include' },
              5000
            );
            if (whoami.ok) {
              const wdata = await whoami.json();
              if (wdata?.success && wdata.chatId) {
                finalChatId = String(wdata.chatId);
                localStorage.setItem('telegramChatId', finalChatId);
                setTelegramChatId(finalChatId);
                console.log('✅ ChatId récupéré via cookie signet:', finalChatId);
              }
            }
          } catch { /* soft — on continue sans chatId */ }
        }
        if (!finalChatId) {
          setIsAuthenticated(false);
          setIsCheckingAuth(false);
          setCheckingShopStatus(false);
          // Token en résolution → attendre, ne rien afficher
          if (pwaTokenResolvingRef.current) {
            console.log('⏳ Pas de chatId — token en résolution, attente...');
            return;
          }
          // Si un pwaToken ou activationPwaToken existe dans le storage, il est peut-être
          // en train d'être résolu ou son useEffect ne s'est pas encore déclenché → attendre
          const maybeToken = localStorage.getItem('pwaToken') || localStorage.getItem('activationPwaToken');
          if (maybeToken) {
            console.log('⏳ Pas de chatId mais token présent — attente résolution...');
            return;
          }
          // Aucun identifiant → pas de modal « Enregistrer » (obsolète).
          // Le client doit passer par le bot d'activation.
          setShowNameModal(false);
          setShowSignupModal(false);
          setShowAccountDisabledModal(false);
          console.log('⚠️ ChatId introuvable — ouvrir bot activation / Access');
          return;
        }

        // ── BYPASS COMPLET POUR ADMIN DASHBOARD ──
        // panel=dashboard → skip pays / ville / nom / blocage, accès direct
        if (isDashboardPanel) {
          setTelegramChatId(finalChatId);
          setIsDashboardMode(true);
          setIsCheckingAuth(false);
          setSplashHold(false);
          try {
            sessionStorage.setItem(STAFF_PANEL_KEY, 'dashboard');
            if (finalChatId) sessionStorage.setItem(STAFF_CHAT_KEY, finalChatId);
          } catch { /* silencieux */ }
          return;
        }

        // ÉTAPE 0: Vérifier si l'utilisateur est bloqué
        try {
          const blockedResponse = await fetchWithTimeout(`${BACKEND_URL}/check-blocked/${finalChatId}`, {}, 5000);
          if (blockedResponse.ok) {
            const blockedData = await blockedResponse.json();
            if (blockedData.success && blockedData.isBlocked) {
              console.log('🚫 Utilisateur bloqué - Accès refusé');
              setIsBlocked(true);
              setIsAuthenticated(false);
              setIsCheckingAuth(false);
              return;
            }
          }
        } catch (blockedError) {
          console.error('Erreur vérification blocage:', blockedError);
          // En cas d'erreur, continuer (ne pas bloquer par défaut)
        }

        // ÉTAPE 1: /client/check AVANT pays/ville — les anciens clients ne doivent
        // jamais retomber sur onboarding/modal nom si le compte existe déjà.
        let storedCountry = localStorage.getItem('clientSelectedCountry');
        let storedCity = localStorage.getItem('clientSelectedCity');
        let prefetchedClient = null;
        const devIdForLink = ensureDeviceId() || '';
        try {
          const doEarlyCheck = () => fetchWithTimeout(
            `${BACKEND_URL}/client/check?chatId=${finalChatId}&deviceId=${encodeURIComponent(devIdForLink)}`,
            { headers: getApiAuthHeaders() },
            5000
          );
          let earlyCheck = await doEarlyCheck();
          // 401 = session serveur perdue (cookie/deviceId) pour un client pourtant connu :
          // on la répare puis on relit la fiche, sinon il retomberait sur le modal pays/onboarding.
          if (earlyCheck.status === 401) {
            const healed = await recoverApiSession();
            if (healed) earlyCheck = await doEarlyCheck();
          }
          if (earlyCheck.ok) {
            const earlyData = await earlyCheck.json();
            if (earlyData?.success && earlyData.client) prefetchedClient = earlyData.client;
          }
        } catch { /* soft — onboarding classique en fallback */ }

        // RÈGLE ABSOLUE : si le backend retourne une fiche client (data.client existe),
        // c'est un client connu — on skip TOUJOURS l'onboarding pays/ville/nom.
        // Peu importe si name est vide, globalCoins = 0, ordersCount = 0, etc.
        // Les anciens clients créés avant le bot activation n'ont parfois aucun de ces signaux
        // mais leur fiche EXISTE dans la DB → accès direct, pas de modal bloquant.
        const knownShopClient = !!(prefetchedClient);
        if (knownShopClient) {
          // Hydrater pays/ville manquants (localStorage vidé / nouvel appareil)
          if (!storedCountry || (storedCountry !== 'france' && storedCountry !== 'suisse')) {
            storedCountry = 'suisse';
            localStorage.setItem('clientSelectedCountry', storedCountry);
          }
          const validCitiesKnown = storedCountry === 'suisse' ? ['Genève'] : ['Nantes'];
          if (!storedCity || !validCitiesKnown.includes(storedCity)) {
            storedCity = storedCountry === 'suisse' ? 'Genève' : 'Nantes';
            localStorage.setItem('clientSelectedCity', storedCity);
          }
          console.log('✅ Ancien client reconnu — skip onboarding pays/ville/nom');
        } else {
          if (!storedCountry || (storedCountry !== 'france' && storedCountry !== 'suisse')) {
            console.log('🌍 Aucun pays choisi - Affichage modal de sélection de pays');
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
            setShowCountryModal(true);
            return;
          }
          const validCitiesForCountry = storedCountry === 'suisse' ? ['Genève'] : ['Nantes'];
          if (!storedCity || !validCitiesForCountry.includes(storedCity)) {
            console.log('🏙️ Aucune ville choisie - Affichage modal de sélection de ville');
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
            setSelectedCountry(storedCountry);
            setClientSelectedCountry(storedCountry);
            setShowCityModal(true);
            return;
          }
        }
        
        // Mettre à jour selectedCountry et selectedCity avec les choix stockés (ville = source de vérité)
        let countryFromStorage = storedCountry;
        if (storedCity === 'Genève' && storedCountry !== 'suisse') {
          countryFromStorage = 'suisse';
          localStorage.setItem('clientSelectedCountry', 'suisse');
        } else if (storedCity === 'Nantes' && storedCountry !== 'france') {
          countryFromStorage = 'france';
          localStorage.setItem('clientSelectedCountry', 'france');
        }
        setClientSelectedCountry(countryFromStorage);
        setSelectedCountry(countryFromStorage);
        setSelectedCity(storedCity);
        
        // Vérifier avec le backend si ce chat ID existe (réutilise le prefetch si dispo)
        try {
          const response = prefetchedClient
            ? { ok: true, json: async () => ({ success: true, client: prefetchedClient }) }
            : await fetchWithTimeout(`${BACKEND_URL}/client/check?chatId=${finalChatId}&deviceId=${encodeURIComponent(devIdForLink)}`, { headers: getApiAuthHeaders() }, 5000);
          
          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }
          
        const data = await response.json();
        
          if (data.success && data.client) {
            // ─── RÈGLE ABSOLUE ───────────────────────────────────────────────────────
            // Si le backend retourne data.client, la fiche EXISTE → connexion immédiate,
            // JAMAIS de modal nom bloquant, quels que soient name / ordersCount / globalCoins.
            // Le nom sera demandé uniquement au moment de passer commande (non-bloquant).
            // Cela couvre :
            //   • anciens clients boutique créés avant le bot activation (name peut être vide)
            //   • clients activation sans historique shop
            //   • clients avec token/deviceId résolu ou non
            // ─────────────────────────────────────────────────────────────────────────
            const hasName = !!(data.client.name && data.client.name.trim() !== '');

            // Hydration commune : toujours connecter le client quelle que soit la situation
            const hydrateExistingClient = () => {
              setIsAuthenticated(true);
              setUserPass(data.client.pass || 'bronze');
              setPassExpiryDate(data.client.passExpiryDate || null);
              setWheelBonuses(normalizeWheelBonusesFromApi(data.client.wheelBonuses));
              setGameBonus2g(data.client.gameBonus2g || 0);
              setGlobalCoins(data.client.globalCoins || 0);
              setFreePacks(data.client.freePacks || 0);
              setPackPriorityBonus(data.client.packPriorityBonus === true);
              setDoubleGcOffer(data.client.doubleGcNextOrder || null);
              if (data.client.pendingCoinsNotification) { setPendingCoinsNotification(data.client.pendingCoinsNotification); setShowCoinsPopup(true); }
              if (data.client.pendingSurvey) { setPendingSurveyData(data.client.pendingSurvey); setShowSurveyPopup(true); }
              setUserOrdersCount(data.client.ordersCount || 0);
              setUserReferralsCount(data.client.referralsCount || 0);
              if (data.client.referralCode) { setReferralCode(data.client.referralCode); localStorage.setItem('referralCode', data.client.referralCode); }
              setShowNameModal(false); // JAMAIS de modal bloquant si la fiche existe
              const bt = parseInt(data.client.treasureTurns, 10) || 0;
              setTreasureTurns(bt);
              if (treasureTurnsStorageKey) persistValueAcrossStorages(treasureTurnsStorageKey, bt.toString());
              // Enregistrer/renouveler la licence de l'appareil en arrière-plan
              ensureLicenseRegistered(finalChatId);
            };

            if (!hasName) {
              // Fiche existe mais pas de nom → accès direct au shop, nom demandé à la commande
              console.log('📝 Client sans nom (fiche existante) — accès shop immédiat, nom à la commande');
              hydrateExistingClient();
              setUserName('');
              setOrderName('');
            } else {
              // Client existant avec nom → connexion automatique complète
              console.log('✅ Client avec nom reconnu — connexion automatique');
              hydrateExistingClient();
              setUserName(data.client.name || '');
              setOrderName(data.client.name || '');
            }
        } else if (data.accountRevoked || data.accountDisabled) {
            console.log('♻️ Compte révoqué — modal réactivation');
            // FIX boucle infinie /resetall : effacer les identifiants locaux liés à l'ancien compte
            // ET forcer pwaTokenResolvingRef à false pour couper la boucle de retry.
            try {
              localStorage.removeItem('pwaToken');
              localStorage.removeItem('activationPwaToken');
              // Licence appareil / NFC liées à l'ancien compte → supprimer pour
              // empêcher une reconnexion sur un compte révoqué.
              localStorage.removeItem('gs_license_private_jwk');
              localStorage.removeItem('gs_license_public_pem');
              localStorage.removeItem('gs_nfc_id');
              localStorage.removeItem('gs_nfc_shop_chat');
              sessionStorage.removeItem('gs_license_private_jwk');
              sessionStorage.removeItem('gs_license_public_pem');
              // Supprimer telegramChatId local + cookie gs_cid pour éviter
              // de recontacter l'ancien compte révoqué.
              localStorage.removeItem('telegramChatId');
              setTelegramChatId(null);
              setCookieValue('gs_cid', '', 0);
            } catch { /* */ }
            // CRITIQUE : forcer les refs de résolution à false pour stopper toute boucle
            pwaTokenResolvingRef.current = false;
            pwaTokenResolvedRef.current = false;
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
            setSplashHold(false);
            setShowNameModal(false);
            setShowSignupModal(false);
            setShowAccountDisabledModal(true);
          } else {
            // Client non trouvé : shop accessible sans modal « Enregistrer ».
            // Nom demandé uniquement au moment de commander.
            console.log('📝 Client non trouvé — shop sans modal nom (nom à la commande)');
            setIsAuthenticated(!!finalChatId || pwaTokenResolvedRef.current);
            setShowNameModal(false);
            setShowSignupModal(false);
            setUserName('');
            setOrderName('');
          }
        } catch (error) {
          // Erreur réseau sur /client/check : ne JAMAIS bloquer avec un modal nom.
          // Si on a un chatId, on laisse l'app s'ouvrir normalement (shop visible),
          // le client pourra commander et le nom sera demandé à ce moment-là.
          console.error('Erreur vérification client:', error);
          if (finalChatId) {
            setIsAuthenticated(true); // accès shop quand même — mieux qu'un modal bloquant
            setShowNameModal(false);
            try { toast.error('Connexion instable — certaines données peuvent être absentes.'); } catch { /* */ }
          } else {
            setIsAuthenticated(false);
          }
          setIsCheckingAuth(false);
          console.log('⚠️ Erreur backend — accès shop maintenu (modal nom évité)');
        }
      } catch (error) {
        console.error('Erreur vérification auth:', error);
        const urlParams = new URLSearchParams(window.location.search);
        const isDriverPanel = urlParams.get('panel') === 'driver';
        if (isDriverPanel) {
          setIsDriverMode(true);
          setIsCheckingAuth(false);
          return;
        }
        // Erreur totale : ne pas bloquer avec modal nom — si on a un chatId on laisse passer
        const chatIdFromTelegram = getChatIdFromTelegram();
        const chatIdFromUrl = urlParams.get('chatId');
        const chatIdToUse = chatIdFromTelegram || chatIdFromUrl || localStorage.getItem('telegramChatId');
        if (chatIdToUse) {
          setTelegramChatId(chatIdToUse);
          if (chatIdToUse !== localStorage.getItem('telegramChatId')) {
            localStorage.setItem('telegramChatId', chatIdToUse);
          }
          // Accès shop maintenu même en cas d'erreur totale
          setIsAuthenticated(true);
          setShowNameModal(false);
          try { toast.error('Session instable — rouvre depuis le bot si les données semblent incorrectes.'); } catch { /* */ }
        } else {
          setIsAuthenticated(false);
        }
      } finally {
        authBootstrapRef.current = true;
        setIsCheckingAuth(false);
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treasureTurnsStorageKey, BACKEND_URL]);

  // Toujours maintenir checkAuthRef à jour
  checkAuthRef.current = checkAuth;

  // ── Restauration automatique de session ──────────────────────────────────
  // Si l'écran « Session à rétablir » s'affiche (pas d'auth, pas de nom,
  // aucun modal onboarding), on retente checkAuth automatiquement au bout de
  // 1,5s plutôt que de laisser le client devant un écran bloquant.
  // Si après plusieurs tentatives la résolution est toujours bloquée
  // (pwaTokenResolvingRef resté à true), on force le déblocage pour que
  // checkAuth puisse tenter les autres méthodes (cookie/device/localStorage).
  const restoreRetryCountRef = useRef(0);
  useEffect(() => {
    if (isAuthenticated) { restoreRetryCountRef.current = 0; return undefined; }
    if (isCheckingAuth) return undefined;
    if (userName || showNameModal || showCountryModal || showCityModal) return undefined;
    if (isBlocked) return undefined;
    // Ne jamais retenter si le modal "compte révoqué" est affiché — ce serait une boucle infinie
    if (showAccountDisabledModal) return undefined;
    restoreRetryCountRef.current += 1;
    if (restoreRetryCountRef.current >= 3 && pwaTokenResolvingRef.current) {
      console.warn('⏰ Restauration bloquée détectée — force déblocage pwaTokenResolvingRef');
      pwaTokenResolvingRef.current = false;
    }
    // Filet de sécurité : si après plusieurs cycles il n'existe AUCUNE identité
    // locale (ni chatId, ni token), un rechargement complet resynchronise les
    // cookies HttpOnly + le service worker. Une seule fois par session, sinon on
    // remplace un écran bloqué par une boucle de rechargement.
    if (restoreRetryCountRef.current >= 8) {
      let hasIdentity = false;
      try {
        hasIdentity = !!(
          localStorage.getItem('telegramChatId')
          || localStorage.getItem('pwaToken')
          || localStorage.getItem('activationPwaToken')
        );
      } catch { hasIdentity = true; }
      let alreadyReloaded = false;
      try { alreadyReloaded = sessionStorage.getItem('gs_session_reloaded') === '1'; } catch { alreadyReloaded = true; }
      if (!hasIdentity && !alreadyReloaded) {
        try { sessionStorage.setItem('gs_session_reloaded', '1'); } catch { /* */ }
        console.warn('🔄 Aucune identité locale — rechargement complet unique');
        window.location.reload();
        return undefined;
      }
    }
    const t = setTimeout(() => {
      console.log('🔄 Restauration automatique de session — retry checkAuth');
      if (checkAuthRef.current) checkAuthRef.current();
    }, 1500);
    return () => clearTimeout(t);
  }, [isCheckingAuth, isAuthenticated, userName, showNameModal, showCountryModal, showCityModal, isBlocked, showAccountDisabledModal]);

  // ── NFC physique (?src=nfc) : auth sans Telegram ──────────────────────────
  useEffect(() => {
    if (!isNfcMode && !isNfcEntryActive()) return undefined;
    markNfcEntry();
    let cancelled = false;

    (async () => {
      setIsCheckingAuth(true);
      try {
        const idHint = getOrCreateNfcId();
        trackNfcHit(idHint);

        const session = await checkNfcSession();
        if (cancelled) return;
        if (session?.success && session.nfcId) {
          setNfcId(session.nfcId);
          setNfcReady(true);
          setIsAuthenticated(true);
          // chatId boutique = 10 chiffres aléatoires (format Telegram) — pas le nfcId ; /reset → order.json seulement
          const shopCid = session.chatId || session.shopChatId || session.client?.chatId;
          if (shopCid) {
            setTelegramChatId(shopCid);
            try { localStorage.setItem('telegramChatId', shopCid); } catch { /* */ }
          }
          const nm = session.client?.name;
          if (nm) setUserName(nm);
          if (!localStorage.getItem('clientSelectedCountry')) {
            localStorage.setItem('clientSelectedCountry', 'suisse');
            localStorage.setItem('clientSelectedCity', 'Genève');
          }
          setIsShopOpen(true);
          setShowNameModal(false);
          setShowSignupModal(false);
          setSplashHold(false);
          setIsCheckingAuth(false);
          return;
        }

        const auth = await ensureNfcAuth();
        if (cancelled) return;
        if (auth?.success && auth.nfcId) {
          setNfcId(auth.nfcId);
          setNfcReady(true);
          setNfcError(null);
          setIsAuthenticated(true);
          const shopCid = auth.chatId || auth.shopChatId || auth.client?.chatId;
          if (shopCid) {
            setTelegramChatId(shopCid);
            try { localStorage.setItem('telegramChatId', shopCid); } catch { /* */ }
          }
          const nm = auth.client?.name;
          if (nm) {
            setUserName(nm);
            setShowNameModal(false);
          } else if (auth.isNew) {
            // Nom demandé à la commande (comme le flux boutique actuel)
            setShowNameModal(false);
          }
          if (!localStorage.getItem('clientSelectedCountry')) {
            localStorage.setItem('clientSelectedCountry', 'suisse');
            localStorage.setItem('clientSelectedCity', 'Genève');
          }
          setIsShopOpen(true);
          setShowSignupModal(false);
        } else {
          setNfcError(auth?.error || 'Authentification NFC échouée');
          // Accès lecture dégradé si réseau HS
          setIsAuthenticated(true);
          setIsShopOpen(true);
        }
      } catch (e) {
        if (!cancelled) {
          setNfcError(e?.message || 'Erreur NFC');
          setIsAuthenticated(true);
          setIsShopOpen(true);
        }
      } finally {
        if (!cancelled) {
          setNfcReady(true);
          setIsCheckingAuth(false);
          setSplashHold(false);
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNfcMode, BACKEND_URL]);
    
  useEffect(() => {
    if (!isAccessMode) return;
    // Mode activation : pas d'auth boutique, on récupère le chatId Telegram
    // Token PWA passé par le bot (?t=) → auth API même sans initData
    try {
      const t = new URLSearchParams(window.location.search).get('t');
      // Token activation isolé — ne pas écraser le pwaToken boutique
      if (t) localStorage.setItem('activationPwaToken', t);
    } catch { /* */ }
    try {
      const tg = window.Telegram?.WebApp;
      if (tg) { tg.ready(); tg.expand(); }
    } catch { /* */ }
    const pickId = () => {
      try {
        const fromTg = (() => {
          try {
            const initData = window.Telegram?.WebApp?.initData;
            if (!initData) return null;
            const userStr = new URLSearchParams(initData).get('user');
            if (!userStr) return null;
            const user = JSON.parse(decodeURIComponent(userStr));
            return user?.id ? String(user.id) : null;
          } catch { return null; }
        })();
        const fromUrl = new URLSearchParams(window.location.search).get('chatId');
        const fromLs = localStorage.getItem('telegramChatId');
        return fromTg || fromUrl || fromLs || null;
      } catch { return null; }
    };
    const apply = () => {
      const cid = pickId();
      if (cid) {
        setTelegramChatId(cid);
        try { localStorage.setItem('telegramChatId', cid); } catch { /* */ }
        return true;
      }
      return false;
    };
    // Le token d'activation (?t=) porte le chatId côté serveur. Indispensable
    // quand l'app est ouverte hors Telegram (lien classique) ou quand initData
    // n'est pas encore disponible : sans ça le modal Access restait bloqué sur
    // « Connexion Telegram en cours… » et le compte ne pouvait pas se reconnecter.
    const resolveChatIdFromActivationToken = async () => {
      let token = '';
      try { token = new URLSearchParams(window.location.search).get('t') || ''; } catch { /* */ }
      if (!token) {
        try { token = localStorage.getItem('activationPwaToken') || ''; } catch { /* */ }
      }
      if (!token) return;
      try {
        const r = await fetchWithTimeout(
          `/api/pwa/resolve-token?t=${encodeURIComponent(token)}`,
          { credentials: 'include' },
          6000
        );
        const data = await r.json();
        if (data?.success && data.chatId) {
          const cid = String(data.chatId);
          try { localStorage.setItem('telegramChatId', cid); } catch { /* */ }
          setTelegramChatId(cid);
          console.log('✅ [ACCESS] chatId résolu via token d\'activation:', cid);
        }
      } catch { /* soft — le modal garde son message d'attente */ }
    };

    if (!apply()) resolveChatIdFromActivationToken();

    // Telegram initData parfois dispo un tick plus tard
    const t1 = setTimeout(apply, 400);
    const t2 = setTimeout(apply, 1200);
    const t3 = setTimeout(() => { if (!apply()) resolveChatIdFromActivationToken(); }, 1600);
    setIsCheckingAuth(false);

    // Reprise aperçu temporaire (nom + shop) après fermeture sans payer
    try {
      if (sessionStorage.getItem('gs_access_preview') === '1') {
        const nm = String(localStorage.getItem('gs_access_name') || sessionStorage.getItem('gs_access_name') || '').trim();
        if (nm.length >= 2) {
          setUserName(nm);
          setShopPreviewMode(true);
          setShowAccessModal(false);
          setCurrentView('home');
          setIsShopOpen(true);
        }
      }
    } catch { /* */ }

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isAccessMode]);

  // Après splash access → ouvrir AccessModal (sauf aperçu boutique déjà actif)
  useEffect(() => {
    if (!isAccessMode) return;
    if (splashHold) {
      setShowAccessModal(false);
      return;
    }
    if (shopPreviewMode) return;
    setShowAccessModal(true);
  }, [isAccessMode, splashHold, shopPreviewMode]);

  const openAccessPub = useCallback(() => {
    setShowPreviewGamePromo(false);
    setShowAccessPubModal(true);
    try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light'); } catch { /* */ }
  }, []);

  // Lien bot activation (username dynamique depuis le backend / .env)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/activation/bot-link`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data?.success && data.url) {
          setActivationBotUrl(String(data.deepLink || data.url));
        }
      } catch { /* soft */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ancien modal « Enregistrer » / nom bloquant : jamais affiché
  useEffect(() => {
    if (showNameModal) setShowNameModal(false);
  }, [showNameModal]);
  useEffect(() => {
    if (showSignupModal) setShowSignupModal(false);
  }, [showSignupModal]);

  const openActivationBot = useCallback(() => {
    const url = activationBotUrl && activationBotUrl !== 'https://t.me/'
      ? activationBotUrl
      : null;
    if (!url) {
      try { toast.error('Bot d\'activation indisponible — réessaie dans un instant.'); } catch { /* */ }
      return;
    }
    try {
      const tg = window.Telegram?.WebApp;
      if (tg?.openTelegramLink) tg.openTelegramLink(url);
      else if (tg?.openLink) tg.openLink(url);
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = url;
    }
  }, [activationBotUrl]);

  const startAccessFromPub = useCallback(() => {
    setShowAccessPubModal(false);
    let saved = '';
    try { saved = String(localStorage.getItem('gs_access_name') || '').trim(); } catch { /* */ }
    if (saved.length >= 2) {
      setUserName(saved);
      setAccessEntryStep('fee');
    } else {
      setAccessEntryStep('name');
    }
    setShowAccessModal(true);
  }, []);

  const onAccessActivationConfirmed = useCallback(() => {
    setShopPreviewMode(false);
    setAccessEntryStep(null);
    try {
      sessionStorage.removeItem('gs_access_preview');
      sessionStorage.setItem('gs_access_won', '1');
    } catch { /* */ }
    // Fermer le modal "compte révoqué" si affiché — le client vient d'obtenir un nouveau compte
    setShowAccountDisabledModal(false);
    // Réinitialiser le compteur de retry pour que checkAuth reparte proprement
    restoreRetryCountRef.current = 0;
    pwaTokenResolvingRef.current = false;
    pwaTokenResolvedRef.current = true;
    setIsShopOpen(true);
    // Garder AccessModal ouvert (claim) — ne pas relancer splash / écran vide
    setShowAccessModal(true);
    // Relancer checkAuth après un tick pour que le nouveau pwaToken soit résolu
    setTimeout(() => {
      if (checkAuthRef.current) checkAuthRef.current();
    }, 800);
  }, []);

  // Preuve réelle « app sur écran d'accueil » = ouverture en mode standalone (1× / client)
  useEffect(() => {
    if (!telegramChatId) return;
    let standalone = false;
    try { standalone = !!detectDevice()?.isStandalone; } catch { standalone = false; }
    if (!standalone) return;
    const key = `gs_pwa_standalone_${telegramChatId}`;
    try {
      if (localStorage.getItem(key) === '1') return;
    } catch { /* */ }
    fetch(`${BACKEND_URL}/client/pwa-event`, {
      method: 'POST',
      headers: { ...getApiAuthHeaders(), 'Content-Type': 'application/json', 'x-chat-id': String(telegramChatId) },
      body: JSON.stringify({ chatId: String(telegramChatId), type: 'standalone_open' }),
      credentials: 'include',
    }).then((r) => {
      if (r.ok) {
        try { localStorage.setItem(key, '1'); } catch { /* */ }
      }
    }).catch(() => {});
  }, [telegramChatId, BACKEND_URL]);

  // ── Service Worker PWA : MAJ auto sans réinstaller le signet ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    let reg = null;
    let reloading = false;

    const onSwMessage = (event) => {
      if (event?.data?.type !== 'SW_UPDATED' || reloading) return;
      // Nouvelle version dispo — reload doux (session deviceId / pwaToken conservée)
      reloading = true;
      try { window.location.reload(); } catch { /* */ }
    };

    const checkUpdate = () => {
      try { if (reg) reg.update(); } catch { /* */ }
    };

    const onLoad = () => {
      navigator.serviceWorker.register('/sw.js')
        .then((r) => { reg = r; checkUpdate(); })
        .catch(() => { /* sandbox Telegram */ });
    };

    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad);

    navigator.serviceWorker.addEventListener('message', onSwMessage);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkUpdate();
    });
    window.addEventListener('focus', checkUpdate);
    const iv = setInterval(checkUpdate, 15 * 60 * 1000);

    return () => {
      clearInterval(iv);
      window.removeEventListener('load', onLoad);
      window.removeEventListener('focus', checkUpdate);
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // ── Identification au démarrage (nouvelle logique sans token dans l'URL) ──
  //
  // PRIORITÉ :
  //  1. ?showInstall=1        → modal tuto installation
  //  2. ?c=CHATID             → rétrocompat anciens redirects
  //  3. deviceId → /api/device/resolve  ← signet = nom de domaine seul
  //  4. localStorage pwaToken → rétrocompat anciens signets ?t=
  //  5. localStorage chatId  → secours même appareil
  //  6. onboarding (+ /start bot Telegram)
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAccessMode) return; // flux activation géré à part
    if (isNfcMode || isNfcEntryActive()) {
      markNfcEntry();
      return; // auth NFC gérée par l'effet dédié
    }
    try {
      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready();
        tg.expand();
      }
    } catch { /* silencieux */ }
    ensureDeviceId();
    // Safety timeout : si la résolution de session (licence/device/pwaToken)
    // ne termine pas sous 8s, on force la main à checkAuth pour éviter un
    // écran « Session à rétablir » bloquant sans retour.
    const safetyTimer = setTimeout(() => {
      if (pwaTokenResolvingRef.current) {
        console.warn('⏰ Safety timeout : résolution session dépassée — reprise checkAuth');
        pwaTokenResolvingRef.current = false;
        if (checkAuthRef.current) checkAuthRef.current();
      }
    }, 8000);
    const params = new URLSearchParams(window.location.search);
    const chatIdFromMiniApp = getChatIdFromTelegram();
    const panelParam = params.get('panel') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STAFF_PANEL_KEY) : '');

    // Le lien de notification d'une commande (orderId) doit survivre au
    // nettoyage de l'URL (history.replaceState) et aux rechargements du Service
    // Worker : on le mémorise avant toute manipulation d'URL.
    const orderIdFromUrl = params.get('orderId');
    if (orderIdFromUrl) {
      try { sessionStorage.setItem(STAFF_ORDER_KEY, orderIdFromUrl); } catch { /* silencieux */ }
    }

    const enterStaffPanel = (panel, cid) => {
      if (cid) {
        localStorage.setItem('telegramChatId', cid);
        setTelegramChatId(cid);
        try {
          sessionStorage.setItem(STAFF_PANEL_KEY, panel);
          sessionStorage.setItem(STAFF_CHAT_KEY, cid);
        } catch { /* silencieux */ }
      }
      pwaTokenResolvingRef.current = false;
      setIsCheckingAuth(false);
      setSplashHold(false);
      if (panel === 'driver') {
        setIsDriverMode(true);
        setCurrentView('driver-panel');
        // Focus sur la commande notifiée dès l'entrée dans le panel livreur.
        try {
          const oid = sessionStorage.getItem(STAFF_ORDER_KEY);
          if (oid) setDriverDeepLinkOrderId(oid);
        } catch { /* silencieux */ }
      } else if (panel === 'dashboard') {
        setIsDashboardMode(true);
      }
    };

    if (panelParam === 'driver') {
      enterStaffPanel('driver', params.get('chatId') || chatIdFromMiniApp || sessionStorage.getItem(STAFF_CHAT_KEY));
      return;
    }
    if (panelParam === 'dashboard') {
      enterStaffPanel('dashboard', params.get('chatId') || chatIdFromMiniApp || sessionStorage.getItem(STAFF_CHAT_KEY));
      return;
    }

    const installFlag = params.get('showInstall');
    if (installFlag === '1' || installFlag === 'denied') {
      const cid = getChatIdFromTelegram() || params.get('chatId') || localStorage.getItem('telegramChatId');
      if (cid) {
        setTelegramChatId(cid);
        localStorage.setItem('telegramChatId', cid);
        pwaTokenResolvedRef.current = true;
      }
      setPwaInstallDenied(installFlag === 'denied');
      setPwaInstallLocked(true);
      setIsCheckingAuth(false);
      setShowInstallModal(true);
      return;
    }

    if (window.location.search) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
    }

    if (chatIdFromMiniApp) {
      fetchWithTimeout(`${BACKEND_URL}/driver/check?chatId=${encodeURIComponent(chatIdFromMiniApp)}`, {}, 5000)
        .then((r) => r.json())
        .then((data) => {
          if (data?.isDriver) {
            enterStaffPanel('driver', chatIdFromMiniApp);
            return;
          }
          if (data?.isAdmin) {
            enterStaffPanel('dashboard', chatIdFromMiniApp);
            return;
          }
          localStorage.setItem('telegramChatId', chatIdFromMiniApp);
          pwaTokenResolvingRef.current = false;
          pwaTokenResolvedRef.current = false;
          setTelegramChatId(chatIdFromMiniApp);
          if (checkAuthRef.current) checkAuthRef.current();
        })
        .catch(() => {
          localStorage.setItem('telegramChatId', chatIdFromMiniApp);
          pwaTokenResolvingRef.current = false;
          pwaTokenResolvedRef.current = false;
          setTelegramChatId(chatIdFromMiniApp);
          if (checkAuthRef.current) checkAuthRef.current();
        });
      return;
    }

    // 2. Redirection /go/:alias → /?c=CHATID
    const cid = params.get('c');
    if (cid) {
      console.log('✅ /go/ redirect → chatId:', cid);
      localStorage.setItem('telegramChatId', cid);
      pwaTokenResolvedRef.current = true;
      pwaTokenResolvingRef.current = false;
      setTelegramChatId(cid);
      setShowNameModal(false);
      setShowCountryModal(false);
      if (checkAuthRef.current) checkAuthRef.current();
      return;
    }

    // 3. pwaToken (rétrocompat) + token d'activation (après /resetall + réactivation),
    //    puis deviceId, puis localStorage
    const storedToken = params.get('t')
      || localStorage.getItem('pwaToken')
      || localStorage.getItem('activationPwaToken');
    if (storedToken) {
      pwaTokenResolvingRef.current = true;
      // Garder le token à vie (auth API signet) — ne jamais le supprimer au succès
      try { localStorage.setItem('pwaToken', storedToken); } catch { /* */ }
      fetchWithTimeout(`/api/pwa/resolve-token?t=${encodeURIComponent(storedToken)}`, { credentials: 'include' }, 6000)
        .then(r => r.json())
        .then(data => {
          pwaTokenResolvingRef.current = false;
          if (data.success && data.chatId) {
            localStorage.setItem('telegramChatId', data.chatId);
            try { localStorage.setItem('pwaToken', storedToken); } catch { /* */ }
            pwaTokenResolvedRef.current = true;
            setTelegramChatId(data.chatId);
            setShowNameModal(false);
            setShowCountryModal(false);
            const did = ensureDeviceId();
            if (did) {
              fetch('/api/device/link', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ deviceId: did }),
              }).catch(() => {});
            }
            if (checkAuthRef.current) checkAuthRef.current();
          } else if (data.accountDisabled || data.accountRevoked) {
            // Compte révoqué (/resetall) → modal liquid glass + CTA bot activation
            // FIX boucle infinie : effacer les tokens liés à l'ancien compte
            try {
              localStorage.removeItem('pwaToken');
              localStorage.removeItem('activationPwaToken');
              // Licence appareil liée à l'ancien compte → supprimer pour empêcher
              // une reconnexion via challenge-response sur un compte révoqué.
              localStorage.removeItem('gs_license_private_jwk');
              localStorage.removeItem('gs_license_public_pem');
              localStorage.removeItem('gs_nfc_id');
              localStorage.removeItem('gs_nfc_shop_chat');
              sessionStorage.removeItem('gs_license_private_jwk');
              sessionStorage.removeItem('gs_license_public_pem');
              // FIX : supprimer telegramChatId local + cookie gs_cid pour éviter
              // de recontacter l'ancien compte révoqué.
              localStorage.removeItem('telegramChatId');
              setTelegramChatId(null);
              setCookieValue('gs_cid', '', 0);
            } catch { /* */ }
            pwaTokenResolvingRef.current = false;
            setShowAccountDisabledModal(true);
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
            setSplashHold(false);
            setShowNameModal(false);
            setShowSignupModal(false);
          } else {
            // Token vraiment invalide côté serveur — fallback device / storage
            resolveByDeviceThenStorage();
          }
        })
        .catch(() => { pwaTokenResolvingRef.current = false; resolveByDeviceThenStorage(); });
      return;
    }

    resolveByDeviceThenStorage();

    function applyResolvedChatId(chatId) {
      localStorage.setItem('telegramChatId', chatId);
      pwaTokenResolvedRef.current = true;
      setTelegramChatId(chatId);
      setShowNameModal(false);
      setShowCountryModal(false);
    }

    function resolveByDeviceThenStorage() {
      // 1. Licence par appareil (challenge-response) — priorité pour les visites NFC/signet.
      // Si l'appareil possède une clé privée + un chatId stocké, on prouve la possession
      // de la clé avant de tomber sur les méthodes moins sûres (cookie/deviceId).
      const storedForLicense = localStorage.getItem('telegramChatId');
      if (storedForLicense && hasStoredLicenseKey()) {
        pwaTokenResolvingRef.current = true;
        verifyAndAuthenticate(storedForLicense)
          .then((result) => {
            pwaTokenResolvingRef.current = false;
            if (result?.success && result.chatId) {
              console.log('🔐 Licence vérifiée (challenge-response) → chatId:', result.chatId);
              applyResolvedChatId(result.chatId);
              ensureLicenseRegistered(result.chatId);
              if (checkAuthRef.current) checkAuthRef.current();
              return;
            }
            // Échec licence → fallback sur cookie/device/storage
            console.warn('⚠️ Vérification licence échouée:', result?.error || 'inconnue');
            fallbackResolve();
          })
          .catch(() => {
            pwaTokenResolvingRef.current = false;
            fallbackResolve();
          });
        return;
      }

      fallbackResolve();

      function fallbackResolve() {
      const did = ensureDeviceId() || '';
      let cookieCid = '';
      try { cookieCid = decodeURIComponent(getCookieValue('gs_cid') || ''); } catch { cookieCid = ''; }
      if (cookieCid) {
        console.log('✅ cookie signet → chatId');
        applyResolvedChatId(cookieCid);
        if (did) {
          fetch('/api/device/link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ deviceId: did, chatId: cookieCid }),
          }).catch(() => {});
        }
        pwaTokenResolvingRef.current = false;
        if (checkAuthRef.current) checkAuthRef.current();
        return;
      }
      const storedEarly = localStorage.getItem('telegramChatId');
      if (storedEarly) {
        pwaTokenResolvedRef.current = true;
        setTelegramChatId(storedEarly);
        pwaTokenResolvingRef.current = false;
        if (checkAuthRef.current) checkAuthRef.current();
        return;
      }
      if (!did) {
        const storedChatId = localStorage.getItem('telegramChatId');
        if (storedChatId) {
          pwaTokenResolvedRef.current = true;
          setTelegramChatId(storedChatId);
        }
        if (checkAuthRef.current) checkAuthRef.current();
        return;
      }
      pwaTokenResolvingRef.current = true;
      fetchWithTimeout(`/api/device/whoami?d=${encodeURIComponent(did)}`, { credentials: 'include' }, 5000)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.success && data.chatId) {
            console.log('✅ whoami signet → chatId:', data.chatId);
            applyResolvedChatId(data.chatId);
            pwaTokenResolvingRef.current = false;
            if (checkAuthRef.current) checkAuthRef.current();
            return null;
          }
          return fetchWithTimeout(`/api/device/resolve?d=${encodeURIComponent(did)}`, {}, 5000).then((r) => r.json());
        })
        .then((data) => {
          if (!data) return;
          pwaTokenResolvingRef.current = false;
          if (data.success && data.chatId) {
            console.log('✅ deviceId résolu → chatId:', data.chatId);
            applyResolvedChatId(data.chatId);
          } else {
            const storedChatId = localStorage.getItem('telegramChatId');
            if (storedChatId) {
              pwaTokenResolvedRef.current = true;
              setTelegramChatId(storedChatId);
            }
          }
          if (checkAuthRef.current) checkAuthRef.current();
        })
        .catch(() => {
          pwaTokenResolvingRef.current = false;
          const storedChatId = localStorage.getItem('telegramChatId');
          if (storedChatId) {
            pwaTokenResolvedRef.current = true;
            setTelegramChatId(storedChatId);
          }
          if (checkAuthRef.current) checkAuthRef.current();
        });
      } // fin fallbackResolve
    }
    return () => clearTimeout(safetyTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Ne plus afficher automatiquement le modal de nom
  // Un client existant doit accéder directement à l'interface avec ses données
  // Le client peut définir son nom plus tard s'il le souhaite via le profil
  
  // Mettre à jour les compteurs FOMO toutes les minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setViewersCount(Math.floor(Math.random() * 10) + 1);
      setDailyOrdersCount(Math.floor(Math.random() * 17) + 4);
      setActiveMembersCount(Math.floor(Math.random() * 8) + 2);
    }, 60000); // Toutes les minutes
    
    return () => clearInterval(interval);
  }, []);

  // Vérifier si l'utilisateur arrive via un lien de parrainage
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    
    // Si quelqu'un arrive avec un lien de parrainage (c'est un filleul)
    if (refCode && refCode !== referralCode) {
      // Validation : le code de parrainage doit être valide (format REF + alphanumérique)
      // Format: REF suivi de 7 caractères alphanumériques en majuscules
      const refCodeRegex = /^REF[A-Z0-9]{7}$/;
      if (!refCodeRegex.test(refCode)) {
        console.warn('Code de parrainage invalide:', refCode);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Vérifier si ce parrainage n'a pas déjà été traité
      const parrainagesRecus = JSON.parse(localStorage.getItem('parrainagesRecus') || '[]');
      // Protection : limiter le nombre de parrainages reçus (max 10 pour éviter les abus)
      if (parrainagesRecus.length >= 10) {
        console.warn('Limite de parrainages atteinte');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      if (!parrainagesRecus.includes(refCode)) {
        console.log('🎁 Nouveau parrainage détecté ! Code parrain:', refCode);
        
        // Le filleul reçoit 1g offert à sa prochaine commande
        localStorage.setItem('parrainageBonus1g', 'true');
        setHasParrainageBonus(true);
        console.log('✅ Filleul : 1g offert sur la prochaine commande');
        
        // Marquer ce parrainage comme reçu (pour éviter les doublons)
        parrainagesRecus.push(refCode);
        localStorage.setItem('parrainagesRecus', JSON.stringify(parrainagesRecus));
        
        // Enregistrer le parrainage pour que le parrain puisse réclamer son bonus
        const parrainagesEnAttente = JSON.parse(localStorage.getItem('parrainagesEnAttente') || '[]');
        // Vérifier que ce parrainage n'existe pas déjà (protection contre les doublons)
        const parrainageExiste = parrainagesEnAttente.some(p => 
          p.parrainCode === refCode && p.filleulCode === referralCode
        );
        if (!parrainageExiste) {
        parrainagesEnAttente.push({
          parrainCode: refCode,
          date: new Date().toISOString(),
          filleulCode: referralCode
        });
          // Limiter à 100 parrainages en attente pour éviter la saturation
          const limitedParrainages = parrainagesEnAttente.slice(-100);
          localStorage.setItem('parrainagesEnAttente', JSON.stringify(limitedParrainages));
          console.log('✅ Parrainage enregistré pour le parrain:', refCode);
        }
      }
      
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [referralCode, isAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const staff = readStaffPanelFromLocation();
    if (urlParams.get('panel') === 'driver' || staff.panel === 'driver') {
      setIsDriverMode(true);
      setCurrentView('driver-panel');
      const deepLinkOrderId = urlParams.get('orderId')
        || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STAFF_ORDER_KEY) : '');
      if (deepLinkOrderId) {
        setDriverDeepLinkOrderId(deepLinkOrderId);
      }
      const cid = urlParams.get('chatId') || staff.chatId;
      if (cid) setTelegramChatId(cid);
    }
    if (urlParams.get('panel') === 'dashboard' || staff.panel === 'dashboard') {
      const chatIdParam = urlParams.get('chatId') || staff.chatId;
      if (chatIdParam) {
        setIsDashboardMode(true);
        setIsAdminUser(true);
        setTelegramChatId(chatIdParam);
      }
    }
  }, []);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatSending, setChatSending] = useState(false);
  const [chatUploading, setChatUploading] = useState(false);
  const [chatUploadProgress, setChatUploadProgress] = useState(0);
  const [chatUploadingMessageId, setChatUploadingMessageId] = useState(null);
  const chatPollingRef = useRef(null);
  const chatLastCountRef = useRef(0);

  // /off fr|ch → ferme la boutique (y compris aperçu Access) — APRÈS déclarations selectedCountry / isDriverMode
  useEffect(() => {
    if (isDriverMode || isDashboardMode) return;
    const country = selectedCountry || localStorage.getItem('clientSelectedCountry') || (shopPreviewMode || isAccessMode ? 'suisse' : null);
    if (!country || !['france', 'suisse'].includes(country)) return;
    if (countryConfig[country] === false) {
      setIsShopOpen(false);
    } else if (shopPreviewMode || isAccessMode) {
      setIsShopOpen(true);
    }
  }, [countryConfig, selectedCountry, shopPreviewMode, isAccessMode, isDriverMode, isDashboardMode]);

  const [driverOrders, setDriverOrders] = useState([]);
  const [driverLoadingOrders, setDriverLoadingOrders] = useState(false);
  const [driverOrdersError, setDriverOrdersError] = useState(false); // dernier fetch /driver/orders en échec
  const [driverOrdersErrorKind, setDriverOrdersErrorKind] = useState(null); // 'auth' | 'network' | 'server' | 'unknown'
  const driverOrdersRef = useRef([]); // accès synchrone à la liste (non bloquant en arrière-plan)
  const [driverCardTick, setDriverCardTick] = useState(0); // ticker pour les badges countdown en temps réel
  const [selectedDriverOrder, setSelectedDriverOrder] = useState(null);
  const [driverDeliveredCount, setDriverDeliveredCount] = useState(0);
  const [driverEarnings, setDriverEarnings] = useState(0);
  const [driverCountry, setDriverCountry] = useState('france'); // Pays du livreur (déterminé par ses commandes)
  const [driverOrderDetailView, setDriverOrderDetailView] = useState(null); // Commande en vue détaillée
  const [exactTimeInput, setExactTimeInput] = useState(''); // Input pour le temps exact
  const [showExactTimeModal, setShowExactTimeModal] = useState(false); // Modal pour saisir le temps exact
  const [isDriverChatOpen, setIsDriverChatOpen] = useState(false);
  const [isDriverChatMinimized, setIsDriverChatMinimized] = useState(false);
  const [driverChatMessages, setDriverChatMessages] = useState([]);
  const [driverChatInput, setDriverChatInput] = useState('');
  const [driverChatUnread, setDriverChatUnread] = useState(0);
  const [driverChatSending, setDriverChatSending] = useState(false);
  const [driverChatUploading, setDriverChatUploading] = useState(false);
  const [driverChatUploadProgress, setDriverChatUploadProgress] = useState(0);
  const [driverChatUploadingId, setDriverChatUploadingId] = useState(null);
  const [driverChatPreview, setDriverChatPreview] = useState(null);
  const [driverChatPreviewMeta, setDriverChatPreviewMeta] = useState(null);
  const driverChatPollingRef = useRef(null);
  const driverChatLastCountRef = useRef(0);

  const chatBlobByMsgIdRef = useRef(new Map());
  const chatRetryByMsgIdRef = useRef(new Map());
  if (typeof window !== 'undefined') {
    try {
      if (!(window.__gsChatBlobById instanceof Map)) {
        window.__gsChatBlobById = chatBlobByMsgIdRef.current;
      } else if (window.__gsChatBlobById !== chatBlobByMsgIdRef.current) {
        chatBlobByMsgIdRef.current = window.__gsChatBlobById;
      }
    } catch { /* */ }
  }
  const rememberChatBlob = (msgId, blobUrl) => {
    if (!msgId || !blobUrl) return;
    chatBlobByMsgIdRef.current.set(String(msgId), blobUrl);
  };
  const forgetChatBlob = (msgId) => {
    if (!msgId) return;
    const key = String(msgId);
    const map = chatBlobByMsgIdRef.current;
    if (map.has(key)) {
      try { URL.revokeObjectURL(map.get(key)); } catch { /* */ }
      map.delete(key);
    }
    chatRetryByMsgIdRef.current.delete(key);
  };
  const handleChatImgError = (e, msg) => {
    if (!e || !e.currentTarget || !msg) return;
    const key = String(msg.id || msg._id || Math.random());
    const img = e.currentTarget;
    const attempts = (chatRetryByMsgIdRef.current.get(key) || 0) + 1;
    chatRetryByMsgIdRef.current.set(key, attempts);
    if (attempts === 1) {
      const sep = (img.src || '').includes('?') ? '&' : '?';
      img.src = `${img.src}${sep}r=${Date.now()}`;
      return;
    }
    if (attempts === 2) {
      const alt = resolveMediaUrlFallback(msg.url || msg.mediaUrl || '');
      if (alt && alt !== img.src) {
        img.src = alt;
        return;
      }
    }
    const savedBlob = chatBlobByMsgIdRef.current.get(key);
    if (savedBlob) {
      img.src = savedBlob;
    }
  };
  const resolveMediaUrlFallback = (raw) => {
    let img = String(raw || '');
    if (!img) return '';
    if (img.startsWith('blob:')) return img;
    // nginx SPA sur /uploads → servir via /api/uploads
    let pathname = img;
    let search = '';
    if (/^https?:\/\//i.test(img)) {
      try {
        const u = new URL(img);
        pathname = u.pathname || '/';
        search = u.search || '';
      } catch { return img; }
    } else if (!pathname.startsWith('/')) {
      pathname = `/${pathname}`;
    }
    pathname = pathname.replace(/^\/api\/uploads\//i, '/uploads/');
    if (pathname.startsWith('/uploads/')) pathname = `/api${pathname}`;
    return `https://global-strains.shop${pathname}${search}`;
  };
  const [driverDeepLinkOrderId, setDriverDeepLinkOrderId] = useState(null);
  const driverOrderStats = useMemo(() => {
    return driverOrders.reduce(
      (acc, order) => {
        acc.total += 1;
        if (order?.isPriority) {
          acc.priority += 1;
        } else {
          acc.standard += 1;
        }
        if (order?.cancelled) {
          acc.cancelled += 1;
          return acc;
        }
        switch (order?.status) {
          case 0:
            acc.preparing += 1;
            break;
          case 1:
            acc.enRoute += 1;
            break;
          case 2:
            acc.arriving += 1;
            break;
          case 3:
            acc.delivered += 1;
            break;
          default:
            break;
        }
        return acc;
      },
      {
        total: 0,
        priority: 0,
        standard: 0,
        preparing: 0,
        enRoute: 0,
        arriving: 0,
        delivered: 0,
        cancelled: 0,
      }
    );
  }, [driverOrders]);
  const hasDriverNotifications = driverChatUnread > 0;

  // useEffect pour afficher la popup du jeu 4 secondes après la commande
  useEffect(() => {
    if (currentView === 'order-confirmation' && activeOrder) {
      const timer = setTimeout(() => {
        setShowGamePopup(true);
      }, 4000); // 4 secondes

      return () => clearTimeout(timer);
    } else {
      // Réinitialiser la popup si on quitte la vue
      setShowGamePopup(false);
    }
  }, [currentView, activeOrder]);

  // Timer automatique pour désigner le perdant après 3 secondes
  useEffect(() => {
    if (gamePhase === 'touching' && fingerPositions.length === numberOfPlayers) {
      const timer = setTimeout(() => {
        const winner = Math.floor(Math.random() * fingerPositions.length);
        setGameWinner(winner);
        setGamePhase('result');
      }, 3000); // 3 secondes

      return () => clearTimeout(timer);
    }
  }, [gamePhase, fingerPositions.length, numberOfPlayers]);

  const MINIMUM_ORDER = 50;
  
  // Prix des Passs
  const PASS_PRICES = {
    bronze: 0,
    argent: 50,
    or: 80
  };
  
  // Calcul des prix selon la Pass
  // Taux de change EUR vers CHF
  const EUR_TO_CHF_RATE = 1; // 1 EUR = 1 CHF

  /** Pays effectif : la ville prime sur selectedCountry pour éviter CHF/€ inversés */
  const getEffectiveCountry = () => {
    const city = (selectedCity || '').trim();
    if (city === 'Genève') return 'suisse';
    if (city === 'Nantes') return 'france';
    if (selectedCountry === 'suisse' || selectedCountry === 'france') return selectedCountry;
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('clientSelectedCountry');
      if (stored === 'suisse' || stored === 'france') return stored;
    }
    return countryConfig.suisse && !countryConfig.france ? 'suisse' : 'france';
  };

  const getCurrencySymbol = (country) => {
    const c = country || (isDriverMode ? driverCountry : getEffectiveCountry());
    return c === 'suisse' ? 'CHF' : '€';
  };

  const persistCountrySelection = (country) => {
    if (country !== 'france' && country !== 'suisse') return;
    localStorage.setItem('clientSelectedCountry', country);
    setClientSelectedCountry(country);
    setSelectedCountry(country);
    const city = country === 'suisse' ? 'Genève' : 'Nantes';
    localStorage.setItem('clientSelectedCity', city);
    setSelectedCity(city);
  };

  /** Produits exclus des bonus 2g (parrainage, roue, jeu) */
  const isProductExcludedFromBonus = (productName, productVariant) => {
    const n = (productName || '').toLowerCase();
    const v = (productVariant || '').toLowerCase();
    if (v === 'caliplates') return true;
    return (
      n.includes('snow') || n.includes('baida') ||
      n.includes('sahas frozen') || n.includes('sahas static') ||
      (n.includes('sahas') && (n.includes('static') || n.includes('frozen'))) ||
      n.includes('bubble hash') ||
      n.includes('cali plate') || n.includes('cali plates') ||
      n.includes('stilpane') || n.includes('makatussin') || n.includes('quagen') ||
      n.includes('oxycod') || n.includes('oxcycod') ||
      n.includes('polkadot') || n.includes('magic chocolate') || n.includes('magicchocolate')
    );
  };

  const isSahasStaticProductName = (productName) => {
    const n = (productName || '').toLowerCase();
    return n.includes('sahas static') || (n.includes('sahas') && n.includes('static') && !n.includes('frozen'));
  };

  const normalizeWheelBonusesFromApi = (wb) => ({
    DRY: Number(wb?.DRY) || 0,
    MOUSS: Number(wb?.MOUSS) || 0,
    CALI: Number(wb?.CALI) || 0,
    SAHAS: Number(wb?.SAHAS) || 0,
  });

  /** Bonus roue / pack : SAHAS uniquement sur Sahas Static ; MOUSS > DRY > CALI sinon */
  const pickWheelBonusForProduct = (bonuses, productName, productVariant) => {
    const b = bonuses || {};
    if (isSahasStaticProductName(productName) && (b.SAHAS || 0) > 0) {
      return { wheelBonusGrams: b.SAHAS, bonusProduct: 'SAHAS' };
    }
    if (isProductExcludedFromBonus(productName, productVariant)) {
      return { wheelBonusGrams: 0, bonusProduct: null };
    }
    if (b.MOUSS > 0) return { wheelBonusGrams: b.MOUSS, bonusProduct: 'MOUSS' };
    if (b.DRY > 0) return { wheelBonusGrams: b.DRY, bonusProduct: 'DRY' };
    if (b.CALI > 0) return { wheelBonusGrams: b.CALI, bonusProduct: 'CALI' };
    return { wheelBonusGrams: 0, bonusProduct: null };
  };

  const convertPrice = (price) => price;

  const formatPrice = (price) => {
    return `${convertPrice(price).toFixed(2)}${getCurrencySymbol()}`;
  };

  const formatPriceDirect = (price, currency = null) => {
    const finalCurrency = currency || getCurrencySymbol(isDriverMode ? driverCountry : undefined);
    return `${Math.round(price)}${finalCurrency}`;
  };

  const getPassPrice = (basePrice, productName) => {
    // Arrondir au multiple de 10 supérieur (ex: 99 -> 100, 104 -> 110)
    const roundUpToTen = (price) => Math.ceil(price / 10) * 10;

    if (userPass === 'bronze') {
      return basePrice;
    } else if (userPass === 'argent') {
      if (productName === 'Baida') {
        return basePrice; // Pas de réduction sur Frozen
      }
      return roundUpToTen(basePrice * 0.9); // -10% arrondi au 10 supérieur
    } else if (userPass === 'or') {
      if (productName === 'Baida') {
        return roundUpToTen(basePrice * 0.95); // -5% sur Frozen arrondi au 10 supérieur
      }
      return roundUpToTen(basePrice * 0.85); // -15% arrondi au 10 supérieur
    }
    return basePrice;
  };
  
  // Fonction de connexion avec nom et mot de passe
  // Fonction supprimée : Plus besoin de login avec mot de passe, tout est basé sur chatId
  
  // Fonction pour enregistrer un client (nom + pass uniquement, basé sur chatId)
  const handleSignup = async () => {
    if (signupStep === 1) {
      // Validation étape 1 - seulement le nom
      if (!signupData.name || signupData.name.trim() === '') {
        addFomoNotification('Le nom est requis', 'error');
      return;
    }
    
      setSignupStep(2);
    } else if (signupStep === 2) {
      // Si Pass Bronze, enregistrer directement le client
      if (signupData.selectedPass === 'bronze') {
    if (!telegramChatId) {
          addFomoNotification('Chat ID introuvable. Utilisez /start sur le bot Telegram.', 'error');
      return;
    }
    
        // Afficher l'écran de chargement
        setIsRegistering(true);
        
    try {
          // Simuler un délai pour l'écran de chargement (2-3 secondes)
          await new Promise(resolve => setTimeout(resolve, 2500));
          
          const response = await fetchWithTimeout(`${BACKEND_URL}/client/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          chatId: telegramChatId,
              name: signupData.name.trim(),
              pass: 'bronze'
        })
      }, 10000);
      
          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }
      
          const data = await response.json();
          if (data.success) {
            // Enregistrement réussi - mettre à jour toutes les données dynamiques
        setIsAuthenticated(true);
            setUserPass(data.client.pass || 'bronze');
            setPassExpiryDate(data.client.passExpiryDate || null);
            setWheelBonuses(normalizeWheelBonusesFromApi(data.client.wheelBonuses));
              setGameBonus2g(data.client.gameBonus2g || 0);
              setGlobalCoins(data.client.globalCoins || 0);
              setFreePacks(data.client.freePacks || 0);
              setPackPriorityBonus(data.client.packPriorityBonus === true);
              setDoubleGcOffer(data.client.doubleGcNextOrder || null);
              if (data.client.pendingCoinsNotification) {
                setPendingCoinsNotification(data.client.pendingCoinsNotification);
                setShowCoinsPopup(true);
              }
              if (data.client.pendingSurvey) {
                setPendingSurveyData(data.client.pendingSurvey);
                setShowSurveyPopup(true);
              }
            setUserOrdersCount(data.client.ordersCount || 0);
            setUserReferralsCount(data.client.referralsCount || 0);
            setUserName(data.client.name || signupData.name.trim());
            setOrderName(data.client.name || signupData.name.trim());
            // Synchroniser les tours de roue depuis le backend
            // Synchroniser les tours de roue depuis le backend (source de vérité)
            if (data.client.treasureTurns !== undefined && data.client.treasureTurns !== null) {
              const backendTurns = parseInt(data.client.treasureTurns, 10) || 0;
              setTreasureTurns(backendTurns);
              if (treasureTurnsStorageKey) {
                persistValueAcrossStorages(treasureTurnsStorageKey, backendTurns.toString());
              }
              console.log('✅ Tours de roue synchronisés depuis le backend (source de vérité):', backendTurns);
      } else {
              setTreasureTurns(0);
              if (treasureTurnsStorageKey) {
                persistValueAcrossStorages(treasureTurnsStorageKey, '0');
              }
            }
            setShowSignupModal(false);
            setShowNameModal(false);
            
            // La vérification de disponibilité du livreur est gérée par le useEffect principal
            addFomoNotification(t('profileCreatedSuccess'), 'success');
          } else {
            addFomoNotification(data.message || t('profileError'), 'error');
          }
        } catch (error) {
          console.error('Erreur enregistrement client:', error);
          addFomoNotification(t('connectionError2'), 'error');
        } finally {
          // Masquer l'écran de chargement
          setIsRegistering(false);
        }
      } else {
        // Pour Argent et Or, passer à l'étape 3 (paiement)
        setSignupStep(3);
      }
    }
  };
  
  // Fonction pour sauvegarder le nom de l'utilisateur et ouvrir le modal d'abonnement
  const handleSaveName = async () => {
    if (!tempName || tempName.trim() === '') {
      addFomoNotification('Veuillez entrer un nom', 'error');
      return;
    }
    
    if (savingName) {
      return; // Éviter les clics multiples
    }
    
    // Lire le chatId depuis le state React ou en fallback depuis localStorage
    // (nécessaire quand le token PWA vient d'être résolu et le state n'est pas encore propagé)
    const effectiveChatId = telegramChatId || localStorage.getItem('telegramChatId');
    if (!effectiveChatId) {
      addFomoNotification('Chat ID introuvable. Utilisez /start sur le bot Telegram.', 'error');
      return;
    }
    // Synchroniser le state si on avait un fallback localStorage
    if (!telegramChatId && effectiveChatId) {
      setTelegramChatId(effectiveChatId);
    }
    
    setSavingName(true);
    const nameToSave = tempName.trim();

    // ── Vérifier que le nom n'est pas déjà pris ──
    try {
      const checkRes = await fetchWithTimeout(
        `${BACKEND_URL}/client/check-name?name=${encodeURIComponent(nameToSave)}&chatId=${effectiveChatId}`,
        { headers: getApiAuthHeaders() },
        5000
      );
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (!checkData.available) {
          addFomoNotification(checkData.message || t('nameAlreadyUsed'), 'error');
          setSavingName(false);
          return;
        }
      } else if (checkRes.status === 401 || checkRes.status === 403) {
        addFomoNotification(t('connectionError2'), 'error');
        setSavingName(false);
        return;
      }
    } catch (checkErr) {
      // Si le check échoue, on laisse passer (le backend re-vérifie de son côté)
      console.warn('⚠️ Vérification nom impossible:', checkErr);
    }
    
    // Enregistrer le client dans la base de données
    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/client/register`, {
          method: 'POST',
          headers: getApiAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            chatId: effectiveChatId,
          name: nameToSave,
          pass: 'bronze'
          })
        }, 10000);
        
        if (!response.ok) {
        // Cas spécial : nom déjà pris (409 Conflict)
        if (response.status === 409) {
          const errData = await response.json().catch(() => ({}));
          addFomoNotification(errData.message || t('nameAlreadyUsed'), 'error');
          setSavingName(false);
          return;
        }
        if (response.status === 403) {
          const errData = await response.json().catch(() => ({}));
          if (errData.code === 'ACTIVATION_PENDING') {
            addFomoNotification(errData.message || 'Finalise d\'abord ton activation via le bot d\'accès.', 'error');
            setSavingName(false);
            return;
          }
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.client) {
        // Client enregistré avec succès → charger ses données
        setIsAuthenticated(true);
        setUserPass(data.client.pass || 'bronze');
        setPassExpiryDate(data.client.passExpiryDate || null);
        setWheelBonuses(normalizeWheelBonusesFromApi(data.client.wheelBonuses));
              setGameBonus2g(data.client.gameBonus2g || 0);
              setGlobalCoins(data.client.globalCoins || 0);
              setFreePacks(data.client.freePacks || 0);
              setPackPriorityBonus(data.client.packPriorityBonus === true);
              setDoubleGcOffer(data.client.doubleGcNextOrder || null);
              if (data.client.pendingCoinsNotification) {
                setPendingCoinsNotification(data.client.pendingCoinsNotification);
                setShowCoinsPopup(true);
              }
              if (data.client.pendingSurvey) {
                setPendingSurveyData(data.client.pendingSurvey);
                setShowSurveyPopup(true);
              }
        setUserOrdersCount(data.client.ordersCount || 0);
        setUserReferralsCount(data.client.referralsCount || 0);
        setUserName(data.client.name || nameToSave);
        setOrderName(data.client.name || nameToSave);
        
        // Synchroniser les tours de roue depuis le backend
        // Synchroniser les tours de roue depuis le backend (source de vérité)
        if (data.client.treasureTurns !== undefined && data.client.treasureTurns !== null) {
          const backendTurns = parseInt(data.client.treasureTurns, 10) || 0;
          setTreasureTurns(backendTurns);
          if (treasureTurnsStorageKey) {
            persistValueAcrossStorages(treasureTurnsStorageKey, backendTurns.toString());
          }
          console.log('✅ Tours de roue synchronisés depuis le backend (source de vérité):', backendTurns);
        } else {
          setTreasureTurns(0);
          if (treasureTurnsStorageKey) {
            persistValueAcrossStorages(treasureTurnsStorageKey, '0');
          }
        }
        
        setShowNameModal(false);
        setTempName('');
        setSavingName(false);
        addFomoNotification(t('accountCreatedSuccess', nameToSave), 'success');
        console.log('✅ Client enregistré dans la base de données:', data.client);
        
        // La vérification de disponibilité du livreur est maintenant gérée par le useEffect principal
        // On ouvre simplement la boutique ici, le useEffect vérifiera la disponibilité
        setIsShopOpen(true);
      } else {
        throw new Error(data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur enregistrement client:', error);
      addFomoNotification(t('registrationErrorShort'), 'error');
    } finally {
      setSavingName(false);
    }
  };
  
  // Fonction pour gérer le paiement
  const handlePayment = (paymentMethod) => {
    // Utiliser selectedPassForPayment si disponible (modal de paiement), sinon signupData.selectedPass (modal de signup)
    const selectedPass = selectedPassForPayment || signupData.selectedPass;
    const passName = selectedPass === 'argent' ? 'Argent' : 'Or';
    const price = PASS_PRICES[selectedPass];
    
    if (paymentMethod === 'pcs' || paymentMethod === 'paysafecard') {
      // Ouvrir Telegram avec le support @not_enough1m
      openSupportTelegram();
      addFomoNotification(`Contactez @${SUPPORT_TELEGRAM_USERNAME} sur Telegram pour finaliser votre paiement de ${price} CHF pour la Pass ${passName}`, 'info', 8000);
      // Fermer le modal de paiement
      setShowPaymentModal(false);
      setSelectedPassForPayment(null);
      setSelectedPaymentMethod(null);
    } else if (paymentMethod === 'crypto') {
      // Afficher l'adresse BTC (à récupérer du backend)
      addFomoNotification('Adresse BTC: [Bientot Disponible]', 'info', 10000);
      // Fermer le modal de paiement
      setShowPaymentModal(false);
      setSelectedPassForPayment(null);
      setSelectedPaymentMethod(null);
    }
  };
  
  // Fonction pour vérifier l'expiration du pass et rétrograder en bronze si nécessaire
  const checkPassExpiry = useCallback(async () => {
    // Si le pass est bronze, pas d'expiration
    if (userPass === 'bronze' || !passExpiryDate) {
      return;
    }
    
    const now = new Date();
    const expiryDate = new Date(passExpiryDate);
    
    // Si le pass a expiré, rétrograder en bronze
    if (now >= expiryDate) {
      console.log('Pass expiré, rétrogradation en bronze');
      
      if (!telegramChatId) {
        // Si pas de chatId, juste mettre à jour localement
        setUserPass('bronze');
        setPassExpiryDate(null);
        addFomoNotification(t('passExpiredDowngraded'), 'info');
        return;
      }
      
      try {
        // Mettre à jour le client avec le pass bronze
        const response = await fetchWithTimeout(`${BACKEND_URL}/client/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            chatId: telegramChatId,
            name: userName || tempName || 'Client',
            pass: 'bronze'
          })
        }, 10000);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setUserPass('bronze');
            setPassExpiryDate(null);
            addFomoNotification(t('passExpiredDowngraded'), 'info');
          }
        }
      } catch (error) {
        console.error('Erreur lors de la rétrogradation du pass:', error);
        // Mettre à jour localement quand même
        setUserPass('bronze');
        setPassExpiryDate(null);
        addFomoNotification(t('passExpiredDowngraded'), 'info');
      }
    }
  }, [userPass, passExpiryDate, telegramChatId, userName, tempName, addFomoNotification, BACKEND_URL, t]);
  
  // Fonction pour mettre à jour la Pass après paiement
  const upgradePass = async (newPass) => {
    if (!telegramChatId) {
      addFomoNotification('Chat ID introuvable. Utilisez /start sur le bot Telegram.', 'error');
      return;
    }
    
    try {
      // Calculer la date d'expiration : 1 mois à partir de maintenant
      let expiryDate = null;
      if (newPass === 'argent' || newPass === 'or') {
        expiryDate = new Date();
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      }
      
      // Mettre à jour le client avec la nouvelle pass
      const response = await fetchWithTimeout(`${BACKEND_URL}/client/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          chatId: telegramChatId,
          name: userName || tempName || 'Client', // Utiliser le nom existant ou un nom par défaut
          pass: newPass,
          passExpiryDate: expiryDate ? expiryDate.toISOString() : null
        })
      }, 10000);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setUserPass(newPass);
        setPassExpiryDate(expiryDate ? expiryDate.toISOString() : null);
        setShowSubscriptionModal(false);
        
        // La vérification de disponibilité du livreur est gérée par le useEffect principal
        addFomoNotification(t('passUpgradedSuccess', newPass === 'argent' ? t('passArgent') : t('passOr')), 'success');
      }
    } catch (error) {
      addFomoNotification(t('updateErrorShort'), 'error');
    }
  };
  
  // Vérifier l'accès à la Pass selon les commandes
  useEffect(() => {
    if (isAuthenticated && userPass === 'bronze') {
      // Vérifier si l'utilisateur peut accéder à Argent (5 commandes)
      if (userOrdersCount >= 5 && userPass === 'bronze') {
        // L'utilisateur peut être promu automatiquement ou manuellement
        // Ici on peut ajouter une notification
      }
      // Vérifier si l'utilisateur peut accéder à Or (10 commandes + 2 parrainages)
      if (userOrdersCount >= 10 && userReferralsCount >= 2 && userPass !== 'or') {
        // L'utilisateur peut être promu
      }
    }
  }, [isAuthenticated, userOrdersCount, userReferralsCount, userPass]);
  
  // Vérifier l'expiration du pass au chargement et périodiquement
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Vérifier immédiatement
    checkPassExpiry();
    
    // Vérifier toutes les minutes
    const interval = setInterval(() => {
      checkPassExpiry();
    }, 60000); // 60000 ms = 1 minute
    
    return () => clearInterval(interval);
  }, [isAuthenticated, checkPassExpiry]);

  const fetchChatHistory = useCallback(async (orderId, role, chatIdParam = null) => {
    if (!orderId) return [];
    // Résout une URL de média en URL absolue pointant sur le backend.
    // Le serveur génère déjà des URLs absolues, mais cette sécurité gère
    // les cas où l'URL serait relative ou sur le mauvais host.
    const resolveMediaUrl = (raw) => {
      let img = String(raw || '').replace(/\/api\/uploads\//gi, '/uploads/');
      if (!img) return '';
      if (img.startsWith('blob:')) return img;
      if (/^https?:\/\//i.test(img)) {
        try {
          const u = new URL(img);
          u.pathname = u.pathname.replace(/^\/api\/uploads\//i, '/uploads/');
          if (u.pathname.includes('/uploads/')) {
            return `https://global-strains.shop${u.pathname}${u.search || ''}`;
          }
          return u.toString();
        } catch { return img; }
      }
      let origin = String(BACKEND_URL || '').replace(/\/api\/?$/i, '') || '';
      if (!origin || /localhost|127\.0\.0\.1/i.test(origin)) {
        origin = 'https://global-strains.shop';
      }
      try {
        const u = new URL(origin.startsWith('http') ? origin : `https://${origin}`);
        origin = u.origin;
      } catch { origin = 'https://global-strains.shop'; }
      const path = img.startsWith('/') ? img : `/${img}`;
      return `${origin}${path}`;
    };
    const normalizeChatMessages = (msgs) => (msgs || []).map((m) => {
      const isImage = m.type === 'image' || m.mediaType === 'image';
      const isVideo = m.type === 'video' || m.mediaType === 'video';
      const rawUrl = m.url || m.mediaUrl || null;
      return {
        ...m,
        type: isImage ? 'image' : isVideo ? 'video' : (m.type || 'text'),
        url: (isImage || isVideo) && rawUrl ? resolveMediaUrl(rawUrl) : (rawUrl || null),
        content: m.content || m.text || '',
      };
    });
    try {
      const chatId = chatIdParam || (role === 'driver' ? telegramChatId : null);
      const headers = { 'Content-Type': 'application/json' };
      if (role === 'driver' && chatId) {
        headers['X-Chat-Id'] = chatId;
      }
      const url = `${BACKEND_URL}/orders/${orderId}/chat?role=${role}${chatId ? `&chatId=${chatId}` : ''}`;
      const response = await fetchWithTimeout(url, { headers }, 10000);
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Réponse invalide du serveur');
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.messages)) {
        return normalizeChatMessages(data.messages);
      }
    } catch (error) {
      console.error(`[${role}${chatIdParam || (role === 'driver' && telegramChatId) ? ` ChatId:${chatIdParam || telegramChatId}` : ''}] Erreur récupération chat:`, error);
    }
    return [];
  }, [BACKEND_URL, telegramChatId]);

  const postChatMessage = useCallback(async ({ orderId, role, payload, chatIdParam = null }) => {
    if (!orderId) return false;
    try {
      const chatId = chatIdParam || (role === 'driver' ? telegramChatId : null);
      const headers = { 'Content-Type': 'application/json' };
      // Ajouter le chatId pour identifier qui envoie le message
      if (role === 'driver' && chatId) {
        headers['X-Chat-Id'] = chatId;
      }
      // S'assurer que le contenu est bien dans le body
      const requestBody = {
        content: payload.content || payload.text || '',
        type: payload.type || 'text',
        role: role,
        ...(role === 'driver' && chatId ? { chatId: chatId } : {})
      };
      
      console.log(`📤 Envoi message chat:`, {
        orderId: orderId?.slice(0, 8),
        role,
        content: requestBody.content?.substring(0, 50),
        hasChatId: !!requestBody.chatId
      });
      
      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${orderId}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      
      // Vérifier le statut HTTP
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ postChatMessage: Erreur HTTP ${response.status}`, errorText);
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }
      
      // Valider que la réponse est bien du JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('❌ postChatMessage: Réponse non-JSON reçue', textResponse);
        throw new Error('Réponse invalide du serveur (non-JSON)');
      }
      
      const data = await response.json();
      console.log('📥 postChatMessage: Réponse du serveur', { success: data.success, hasMessage: !!data.message });
      
      if (!data.success) {
        console.error('❌ postChatMessage: Le serveur a retourné success: false', data);
        return false;
      }
      
      return true;
    } catch (error) {
      const chatId = chatIdParam || (role === 'driver' ? telegramChatId : null);
      console.error(`❌ [${role}${chatId ? ` ChatId:${chatId}` : ''}] Erreur envoi chat:`, error?.message || String(error));
      return false;
    }
  }, [BACKEND_URL, telegramChatId]);

  const uploadChatPhoto = useCallback(async ({ orderId, role, file, chatIdParam = null, onProgress = null }) => {
    if (!orderId || !file) return { success: false };
    try {
      const chatId = chatIdParam || (role === 'driver' ? telegramChatId : null);

      const isVideo = file.type.startsWith('video/');
      const mediaType = isVideo ? 'video' : 'image';

      const formData = new FormData();
      formData.append('file', file);
      formData.append('role', role);
      formData.append('mediaType', mediaType);
      if (role === 'driver' && chatId) {
        formData.append('chatId', chatId);
      }

      const headers = { ...getApiAuthHeaders() };
      delete headers['Content-Type'];
      delete headers['content-type'];
      if (role === 'driver' && chatId) {
        headers['X-Chat-Id'] = chatId;
      }

      // Utiliser XHR avec progression réelle
      const res = await uploadWithProgress(
        `${BACKEND_URL}/orders/${orderId}/chat/media`,
        formData,
        {
          method: 'POST',
          headers,
          timeout: 180000,
          onProgress: (p) => {
            try { onProgress?.(p); } catch { /* */ }
          },
        }
      );

      const contentType = res.headers?.get?.('content-type') || '';
      if (!res.ok) {
        let msg = 'Erreur envoi photo';
        try {
          const j = await res.json();
          msg = j.message || msg;
        } catch { /* */ }
        return { success: false, message: msg, status: res.status };
      }

      let data = { success: false };
      try {
        data = contentType.includes('application/json')
          ? await res.json()
          : JSON.parse(await res.text() || '{}');
      } catch {
        data = { success: res.ok };
      }
      return { success: Boolean(data.success), messages: data.messages, data };
    } catch (error) {
      const cid = chatIdParam || (role === 'driver' ? telegramChatId : null);
      console.error(`[${role}${cid ? ` ChatId:${cid}` : ''}] Erreur upload média chat:`, error);
      return { success: false, message: error?.message || 'Erreur réseau' };
    }
  }, [BACKEND_URL, telegramChatId]);

  const cancelOrder = useCallback(async (orderId) => {
    if (!orderId) return false;
    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
      });
      // Valider que la réponse est bien du JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Réponse invalide du serveur');
      }
      const data = await response.json();
      if (data.success) {
        toast.success(t('orderCancelledSuccessfully'));
        // Nettoyer complètement la commande et rediriger vers l'accueil
        setTimeout(() => {
          setActiveOrder(null);
          setOrderStatus(0);
          setQueueInfo({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0 });
          setCountdownMinutes(0);
          setWaitingMinutes(0);
          saveActiveOrderData(null);
          setCurrentView('home');
        }, 1500);
        return true;
      } else {
        toast.error(data.message || t('errorCancelling'));
        return false;
      }
    } catch (error) {
      console.error('Erreur annulation commande:', error);
      toast.error(t('cantCancelOrder'));
      return false;
    }
  }, [BACKEND_URL, saveActiveOrderData, t]);

  // Charger la commande active au démarrage depuis le localStorage
  useEffect(() => {
    const loadActiveOrder = async () => {
      try {
        const savedOrder = getStoredActiveOrder();
        if (!savedOrder) {
          console.log('📦 Aucune commande active sauvegardée');
          return;
        }

        const orderData = JSON.parse(savedOrder);
        console.log('📦 Commande active trouvée:', orderData.orderId);

        // Charger immédiatement depuis le localStorage pour affichage rapide
        setActiveOrder(orderData);
        setOrderStatus(orderData.status || 0);
        setTelegramLink(orderData.telegramLink || null);

        // Vérifier le statut avec le backend en arrière-plan
        if (orderData.orderId && BACKEND_URL) {
          try {
            const res = await fetchWithTimeout(`${BACKEND_URL}/status/${orderData.orderId}`, {}, 10000);
            const data = await res.json();
            
            if (data.success && data.order) {
              // Vérifier si la commande est terminée ou annulée
              if (data.status === 3 || data.cancelled) {
                console.log('📦 Commande terminée ou annulée, nettoyage');
                setActiveOrder(null);
                setOrderStatus(0);
                saveActiveOrderData(null);
              } else {
                // Mettre à jour avec les données fraîches du backend
                const updatedOrder = {
                  ...orderData,
                  status: data.status || 0,
                  cancelled: data.cancelled || false,
                };
                console.log('📦 Commande mise à jour depuis le backend:', updatedOrder);
                setActiveOrder(updatedOrder);
                setOrderStatus(data.status || 0);
                
                // Mettre à jour le localStorage avec les données fraîches
                saveActiveOrderData(updatedOrder);
              }
            } else {
              console.log('⚠️ Commande non trouvée sur le serveur, conservation des données locales');
            }
          } catch (error) {
            console.error('❌ Erreur lors de la vérification du statut:', error);
            // En cas d'erreur réseau, garder les données du localStorage
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement de la commande active:', error);
        // En cas d'erreur de parsing, nettoyer le localStorage
        saveActiveOrderData(null);
      }
    };

    loadActiveOrder();
  }, [BACKEND_URL, getStoredActiveOrder, saveActiveOrderData]);

  // Vérifier si on revient de Telegram avec un orderId
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('orderId');
    const panel = urlParams.get('panel');
    if (orderId && panel === 'driver') {
      return;
    }
    if (orderId) {
      // L'utilisateur revient de Telegram après autorisation
      // Charger la commande depuis le backend
      const loadOrderFromBackend = async () => {
        // D'abord, charger depuis le localStorage si disponible (pour affichage immédiat)
      const savedOrder = getStoredActiveOrder();
      if (savedOrder) {
        try {
            const savedOrderData = JSON.parse(savedOrder);
            if (savedOrderData.orderId === orderId) {
              // Charger immédiatement depuis le localStorage
              setActiveOrder(savedOrderData);
              setOrderStatus(savedOrderData.status || 0);
              setTelegramLink(savedOrderData.telegramLink || null);
              setCurrentView('order-confirmation');
            }
          } catch (err) {
            console.error('Erreur parsing localStorage:', err);
          }
        }
        
        // Ensuite, vérifier avec le backend pour mettre à jour
        try {
          const res = await fetchWithTimeout(`${BACKEND_URL}/status/${orderId}`, {}, 10000);
          const data = await res.json();
          if (data.success && data.order) {
            // Vérifier si la commande est terminée ou annulée
            if (data.status === 3 || data.cancelled) {
              // La commande est terminée ou annulée, nettoyer
              setActiveOrder(null);
              setOrderStatus(0);
              saveActiveOrderData(null);
            } else {
              // La commande existe et est active
              const order = {
                orderId: orderId,
                status: data.status || 0,
                telegramLink: data.telegramLink || null,
                ...data.order
              };
            setActiveOrder(order);
              setOrderStatus(data.status || 0);
            setTelegramLink(order.telegramLink || null);
              // Sauvegarder dans le localStorage
              saveActiveOrderData(order);
            setCurrentView('order-confirmation');
          }
          } else {
            // La commande n'existe plus sur le serveur
            // Si on a une commande dans le localStorage, on la garde au cas où
            if (!savedOrder) {
              setActiveOrder(null);
              saveActiveOrderData(null);
            }
          }
        } catch (error) {
          console.error('Erreur chargement commande depuis backend:', error);
          // En cas d'erreur, garder la commande du localStorage si elle existe
          // L'utilisateur pourra toujours voir le bouton "Suivre Ma Commande"
        }
      };
      loadOrderFromBackend();
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [BACKEND_URL, getStoredActiveOrder, saveActiveOrderData]);

  // Filet de sécurité : si on est en mode driver ou dashboard admin, désactiver immédiatement le loading boutique
  // (ni le livreur ni l'admin n'ont à attendre la vérification de disponibilité boutique)
  useEffect(() => {
    if (isDriverMode || isDashboardMode) {
      setCheckingShopStatus(false);
    }
  }, [isDriverMode, isDashboardMode]);

  // Ne jamais garder l'écran « Traitement en cours » pendant les modales d'onboarding
  useEffect(() => {
    if (showCountryModal || showCityModal || showNameModal || isDriverMode || isDashboardMode) {
      setCheckingShopStatus(false);
    }
  }, [showCountryModal, showCityModal, showNameModal, isDriverMode, isDashboardMode]);

  // Vérifier le statut de la boutique (disponibilité des livreurs par pays)
  useEffect(() => {
    if (!selectedCountry || !['france','suisse'].includes(selectedCountry) || isDriverMode) {
      setCheckingShopStatus(false);
      return;
    }
    
    let timeoutId = null;
    
    const checkShopStatus = async (isInitialCheck = false) => {
      try {
        // Pays désactivé via /off → boutique fermée (prioritaire sur les livreurs)
        if (countryConfig[selectedCountry] === false) {
          console.log(`🔴 Pays ${selectedCountry} désactivé (/off) — boutique fermée`);
          setIsShopOpen(false);
          return;
        }
        console.log(`🔍 Vérification disponibilité livreur pour ${selectedCountry}...`);
        const res = await fetchWithTimeout(`${BACKEND_URL}/shop/check-driver/${selectedCountry}`, {}, 10000);
        const data = await res.json();
        if (data.success && data.hasDriver === true) {
          console.log(`✅ Livreur disponible pour ${selectedCountry} - Boutique ouverte`);
          setIsShopOpen(true);
        } else if (data.success && data.hasDriver === false) {
          console.log(`❌ Aucun livreur disponible pour ${selectedCountry} - Boutique fermée`);
          setIsShopOpen(false);
        }
        // Si data.success est false ou réponse inattendue → on garde l'état actuel (pas de fermeture)
      } catch (err) {
        console.error("Erreur vérification disponibilité livreur:", err);
        // En cas d'erreur, on assume que la boutique est ouverte pour ne pas bloquer l'interface
        if (countryConfig[selectedCountry] !== false) setIsShopOpen(true);
      } finally {
        // Ne mettre checkingShopStatus à false QUE lors de la première vérification
        if (isInitialCheck) {
        setCheckingShopStatus(false);
          firstShopStatusCheckRef.current = false;
          // Marquer dans sessionStorage pour éviter de bloquer à nouveau
          sessionStorage.setItem('shopStatusChecked', 'true');
        }
      }
    };

    // Première vérification avec timeout de sécurité
    checkShopStatus(true);
    
    // Timeout de sécurité : si après 8 secondes checkingShopStatus est toujours true, le forcer à false
    timeoutId = setTimeout(() => {
      if (firstShopStatusCheckRef.current) {
        setCheckingShopStatus(false);
        firstShopStatusCheckRef.current = false;
        setIsShopOpen(true); // Par défaut, on assume que la boutique est ouverte
        sessionStorage.setItem('shopStatusChecked', 'true'); // Marquer comme vérifié
      }
    }, 8000);
    
    // Vérifications périodiques (sans affecter checkingShopStatus)
    const interval = setInterval(() => {
      checkShopStatus(false); // Les vérifications périodiques ne touchent pas à checkingShopStatus
    }, 30000); // Vérifier toutes les 30 secondes (au lieu de 5)
    
    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [BACKEND_URL, selectedCountry, isDriverMode, countryConfig]);

  // Fonction pour nettoyer le cache client en préservant les données de parrainage
  // Cette fonction est appelée lors de /start ou /fresh pour nettoyer les commandes
  // tout en préservant les données de parrainage (bonus, codes, tours, etc.)
  const clearClientCachePreservingParrainage = useCallback(() => {
    // Sauvegarder TOUTES les données de parrainage AVANT de nettoyer
    // Ces données doivent être préservées même en cas de /start, /fresh ou down du backend
    const referralCode = localStorage.getItem('referralCode');
    const parrainagesRecus = localStorage.getItem('parrainagesRecus');
    const parrainagesEnAttente = localStorage.getItem('parrainagesEnAttente');
    const parrainagesTraites = localStorage.getItem('parrainagesTraites');
    const parrainageBonus1g = localStorage.getItem('parrainageBonus1g');
    const scopedTreasureTurns = treasureTurnsStorageKey
      ? readPersistentValue(treasureTurnsStorageKey)
      : localStorage.getItem('treasureTurns');
    
    // Sauvegarder TOUS les marqueurs d'utilisation de la roue (anti-triche)
    const currentWeekKey = getWeekKey();
    const treasureTurnsWeekKey = localStorage.getItem('treasureTurnsWeekKey');
    const wheelUsedKey = `wheelUsed_${currentWeekKey}`;
    const wheelUsedLocalStorage = localStorage.getItem(wheelUsedKey);
    const wheelUsedSessionStorage = sessionStorage.getItem(wheelUsedKey);
    const wheelUsedTime = localStorage.getItem(`wheelUsedTime_${currentWeekKey}`);
    const wheelUsedDeviceId = deviceId ? localStorage.getItem(`wheelUsed_${deviceId}_${currentWeekKey}`) : null;
    
    // Sauvegarder aussi tous les marqueurs des semaines précédentes (au cas où)
    const allWheelUsedKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('wheelUsed_') || key.startsWith('wheelUsedTime_') || key.startsWith('treasureTurnsWeekKey'))) {
        allWheelUsedKeys.push({ key, value: localStorage.getItem(key) });
      }
    }
    
    // Sauvegarder les codes de parrainage utilisés
    const parrainageCodesUtilises = localStorage.getItem('parrainageCodesUtilises');
    
    // Nettoyer tout le localStorage (commandes, état de l'app, etc.)
    localStorage.clear();
    
    // Restaurer IMMÉDIATEMENT toutes les données de parrainage
    // Le referralCode est CRITIQUE : il identifie l'utilisateur pour le système de parrainage
    if (referralCode) {
      localStorage.setItem('referralCode', referralCode);
    } else {
      // Si pas de code, en générer un nouveau (ne devrait pas arriver normalement)
      const newCode = 'REF' + Math.random().toString(36).substring(2, 9).toUpperCase();
      localStorage.setItem('referralCode', newCode);
    }
    
    // Restaurer les autres données de parrainage si elles existent
    if (parrainagesRecus) localStorage.setItem('parrainagesRecus', parrainagesRecus);
    if (parrainagesEnAttente) localStorage.setItem('parrainagesEnAttente', parrainagesEnAttente);
    if (parrainagesTraites) localStorage.setItem('parrainagesTraites', parrainagesTraites);
    if (parrainageBonus1g) localStorage.setItem('parrainageBonus1g', parrainageBonus1g);
    
    // Restaurer TOUS les marqueurs d'utilisation de la roue (anti-triche)
    // Ces marqueurs sont CRITIQUES pour empêcher les tours illimités même après /start
    if (treasureTurnsWeekKey) {
      localStorage.setItem('treasureTurnsWeekKey', treasureTurnsWeekKey);
    }
    if (wheelUsedLocalStorage) {
      localStorage.setItem(wheelUsedKey, wheelUsedLocalStorage);
    }
    if (wheelUsedSessionStorage) {
      sessionStorage.setItem(wheelUsedKey, wheelUsedSessionStorage);
    }
    if (wheelUsedTime) {
      localStorage.setItem(`wheelUsedTime_${currentWeekKey}`, wheelUsedTime);
    }
    if (wheelUsedDeviceId && deviceId) {
      localStorage.setItem(`wheelUsed_${deviceId}_${currentWeekKey}`, wheelUsedDeviceId);
    }
    
    // Restaurer tous les autres marqueurs de roue sauvegardés
    allWheelUsedKeys.forEach(({ key, value }) => {
      if (value) {
        localStorage.setItem(key, value);
      }
    });
    
    if (scopedTreasureTurns && treasureTurnsStorageKey) {
      persistValueAcrossStorages(treasureTurnsStorageKey, scopedTreasureTurns);
    } else if (scopedTreasureTurns) {
      localStorage.setItem('treasureTurns', scopedTreasureTurns);
    }
    
    // Restaurer les codes de parrainage utilisés
    if (parrainageCodesUtilises) {
      localStorage.setItem('parrainageCodesUtilises', parrainageCodesUtilises);
    }
    
    // Nettoyer l'état de l'application (commandes, statuts, etc.)
    setActiveOrder(null);
    setOrderStatus(0);
    setCountdownMinutes(0);
    setQueueInfo({ 
      ordersAhead: 0, 
      estimatedWaitMinutes: 0, 
      totalActive: 0, 
      extraDelayMinutes: 0, 
      priorityReductionMinutes: 0, 
      effectiveMinutes: 0, 
      remainingMinutes: 0, 
      hasEnRouteOrdersAhead: false 
    });
    
    // Réinitialiser les états de chat
    setChatMessages([]);
    setChatInput('');
    setChatUnreadCount(0);
    
    console.log('✅ Cache client nettoyé - Données de parrainage préservées:', {
      referralCode: referralCode || 'nouveau code généré',
      hasBonus: !!parrainageBonus1g,
      treasureTurns: scopedTreasureTurns || '0',
      parrainagesEnAttente: parrainagesEnAttente ? JSON.parse(parrainagesEnAttente).length : 0
    });
  }, [treasureTurnsStorageKey, getWeekKey, deviceId]);

  useEffect(() => {
    // Vérifier si le cache doit être nettoyé (lors de /start ou /fresh)
    // Cette fonction est appelée au démarrage de l'application
    // Le backend envoie un signal via /clear-client-cache quand /start est appelé dans Telegram
    const checkClearCache = async () => {
      try {
        const res = await fetchWithTimeout(`${BACKEND_URL}/clear-client-cache`, {}, 5000);
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          if (data.clearCache === true) {
            // Le backend demande de nettoyer le cache (commande /start)
            // Les données de parrainage seront préservées par clearClientCachePreservingParrainage
            clearClientCachePreservingParrainage();
            return;
          }
        }
      } catch (err) {
        // Si le backend est down ou l'endpoint n'existe pas, on ne nettoie pas
        // Les données de parrainage restent dans localStorage (c'est ce qu'on veut)
        // Ne pas logger pour éviter le spam dans la console
        // Les données de parrainage sont TOUJOURS préservées dans localStorage
        // même si le backend est down - c'est la DB JS locale qui les garde
      }
    };
  
    // Appeler la fonction au démarrage
    checkClearCache();
  }, [clearClientCachePreservingParrainage, BACKEND_URL]);

  useEffect(() => {
    // Vérifier si toutes les commandes ont été supprimées (route /fresh)
    // Cette fonction vérifie périodiquement si le backend a fait un /fresh
    // Si oui, on nettoie le cache tout en préservant les données de parrainage
    const checkFreshStatus = async () => {
      try {
        // Vérifier d'abord si on a une commande dans le localStorage
        const savedOrder = getStoredActiveOrder();
        if (savedOrder) {
          try {
            const order = JSON.parse(savedOrder);
            // Vérifier que la commande existe toujours sur le serveur
            const statusRes = await fetchWithTimeout(`${BACKEND_URL}/status/${order.orderId}`, {}, 10000);
            const statusData = await statusRes.json();
            if (statusData.success && statusData.order && !statusData.cancelled && statusData.status !== 3) {
              // La commande existe toujours et est active, ne pas la supprimer
              return false;
            }
          } catch (err) {
            // En cas d'erreur (backend down), ne pas supprimer la commande
            // Les données de parrainage restent préservées dans localStorage
            console.error("Erreur vérification commande dans checkFreshStatus:", err);
            return false;
          }
        }
        
        // Si pas de commande dans localStorage ou commande terminée, vérifier fresh-status
        const res = await fetchWithTimeout(`${BACKEND_URL}/fresh-status`, {}, 10000);
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          if (data.success && data.isFresh) {
            // Le backend a fait un /fresh : toutes les commandes ont été supprimées
            // Nettoyer le cache client MAIS préserver les données de parrainage
            // Les données de parrainage sont la DB JS locale et doivent TOUJOURS être préservées
            clearClientCachePreservingParrainage();
            return true; // Indique que tout a été nettoyé
          }
        }
      } catch (err) {
        // Si le backend est down, on ne peut pas vérifier fresh-status
        // Les données de parrainage restent préservées dans localStorage (c'est correct)
        // Ne pas logger pour éviter le spam
        console.error("Erreur vérification fresh-status:", err);
      }
      return false;
    };

    const loadSavedOrder = async () => {
      // Charger d'abord depuis le localStorage immédiatement (sans attendre le backend)
      const savedOrder = getStoredActiveOrder();
      if (savedOrder) {
        try {
          const order = JSON.parse(savedOrder);
          
          // Vérifier que la commande n'est pas terminée localement
          if (order.status !== 3 && !order.cancelled) {
            // Charger la commande depuis le localStorage immédiatement pour afficher le bouton
            setActiveOrder(order);
            setOrderStatus(order.status || 0);
            setTelegramLink(order.telegramLink || null);
          }
        } catch (error) {
          console.error('Erreur parsing commande localStorage:', error);
          // En cas d'erreur de parsing, nettoyer
          saveActiveOrderData(null);
            setActiveOrder(null);
        }
      }

      // Vérifier fresh-status en arrière-plan (ne pas bloquer l'affichage)
      checkFreshStatus().then(isFresh => {
        if (isFresh) {
          // Si tout a été nettoyé, le cache a déjà été nettoyé par clearClientCachePreservingParrainage
          // Pas besoin de faire autre chose
        }
      }).catch(err => {
        // En cas d'erreur, garder la commande
        console.error('Erreur checkFreshStatus:', err);
      });

      // Vérifier avec le backend pour récupérer les commandes actives du client
      if (telegramChatId && isAuthenticated) {
        try {
          const ordersResponse = await fetchWithTimeout(`${BACKEND_URL}/client/orders?chatId=${telegramChatId}`, {}, 5000);
          const ordersData = await ordersResponse.json();
          
          if (ordersData.success && ordersData.orders && ordersData.orders.length > 0) {
            // Prendre la commande la plus récente (première dans la liste triée)
            const latestOrder = ordersData.orders[0];
            
            // Vérifier que la commande n'est pas terminée
            if (latestOrder.status !== 3 && !latestOrder.cancelled) {
              // Mettre à jour la commande active avec les données du backend
              setActiveOrder(latestOrder);
              setOrderStatus(latestOrder.status || 0);
              
              // Sauvegarder dans le localStorage pour la prochaine fois
              saveActiveOrderData(latestOrder);
              
              console.log('✅ Commande active récupérée depuis le backend:', latestOrder.orderId);
            }
          }
        } catch (error) {
          console.error('Erreur récupération commandes depuis le backend:', error);
          // En cas d'erreur, garder la commande du localStorage si elle existe
        }
      }

      // Vérifier avec le backend pour mettre à jour (en arrière-plan, non bloquant)
      if (savedOrder) {
        try {
          const order = JSON.parse(savedOrder);
          fetchWithTimeout(`${BACKEND_URL}/status/${order.orderId}`, {}, 10000)
            .then(res => res.json())
            .then(data => {
              if (data.success && data.order) {
                // Vérifier si la commande est vraiment terminée ou annulée
                if (data.status === 3 || data.cancelled) {
                  // La commande est terminée ou annulée, nettoyer
                  setActiveOrder(null);
            setOrderStatus(0);
            saveActiveOrderData(null);
                } else {
                  // La commande existe et est active, mettre à jour avec les données du backend
                  const updatedOrder = {
                    ...order,
                    orderId: order.orderId,
                    status: data.status || order.status || 0,
                    telegramLink: order.telegramLink || null,
                    cancelled: data.cancelled || false,
                    ...data.order
                  };
                  setActiveOrder(updatedOrder);
                  setOrderStatus(data.status || 0);
                  // Mettre à jour le localStorage avec les données à jour
                  saveActiveOrderData(updatedOrder);
                }
              } else if (data.success === false) {
                // Si le backend dit que la commande n'existe pas, vérifier si elle est vraiment terminée
                // Ne pas supprimer immédiatement, peut-être que c'est une erreur temporaire
                console.log('Commande non trouvée sur le serveur, conservation du localStorage');
              }
            })
            .catch(backendError => {
              console.error('Erreur vérification commande avec backend:', backendError);
              // En cas d'erreur backend, garder la commande du localStorage
              // L'utilisateur pourra toujours voir le bouton "Suivre Ma Commande"
            });
        } catch (error) {
          // Erreur déjà gérée plus haut
        }
      }
    };

    // Charger la commande seulement si l'utilisateur est authentifié
    if (isAuthenticated && telegramChatId) {
    loadSavedOrder();
    }
    
    // Vérifier périodiquement si toutes les commandes ont été supprimées
    const freshInterval = setInterval(checkFreshStatus, 5000);
    return () => clearInterval(freshInterval);
  }, [BACKEND_URL, clearClientCachePreservingParrainage, getStoredActiveOrder, saveActiveOrderData, isAuthenticated, telegramChatId]);

  useEffect(() => {
    if (activeOrder) {
      saveActiveOrderData(activeOrder);
    } else {
      saveActiveOrderData(null);
    }
  }, [activeOrder, saveActiveOrderData]);

  useEffect(() => {
    const fetchActiveOrder = async () => {
      if (!activeOrder) return;
      try {
        const res = await fetchWithTimeout(`${BACKEND_URL}/status/${activeOrder.orderId}`, {}, 10000);
        
        // Vérifier que la réponse est valide avant de traiter
        if (!res.ok) {
          console.error(`❌ Erreur HTTP ${res.status} lors de la récupération du statut de la commande`);
          // Ne pas mettre à jour le statut si le backend retourne une erreur
          return;
        }
        
        const data = await res.json();
        
        // Si commande introuvable (supprimée après annulation) → nettoyer
        if (!data || !data.success || !data.order) {
          console.log('ℹ️ Commande introuvable sur le serveur — nettoyage activeOrder');
          setActiveOrder(null);
          setOrderStatus(0);
          saveActiveOrderData(null);
          setQueueInfo({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0 });
          if (currentView === 'order-tracking' || currentView === 'order-confirmation') {
            setCurrentView('home');
          }
          return;
        }
        
        // Vérifier que le statut est valide (0-3)
        const newStatus = parseInt(data.status, 10);
        if (isNaN(newStatus) || newStatus < 0 || newStatus > 3) {
          console.error(`❌ Statut invalide reçu du backend: ${data.status}`);
          // Ne pas mettre à jour le statut si le statut est invalide
          return;
        }
        
        // Mettre à jour le statut seulement si toutes les validations passent
        setOrderStatus(newStatus);
          
          if (data.queue) {
            const newQueueInfo = {
              ordersAhead: data.queue.ordersAhead ?? 0,
              estimatedWaitMinutes: data.queue.estimatedWaitMinutes ?? 0,
              totalActive: data.queue.totalActive ?? 0,
              extraDelayMinutes: data.queue.extraDelayMinutes ?? 0,
              priorityReductionMinutes: data.queue.priorityReductionMinutes ?? 0,
              effectiveMinutes: data.queue.effectiveMinutes ?? data.queue.estimatedWaitMinutes ?? 0,
              remainingMinutes: data.queue.remainingMinutes ?? data.queue.effectiveMinutes ?? data.queue.estimatedWaitMinutes ?? 0,
              hasEnRouteOrdersAhead: data.queue.hasEnRouteOrdersAhead ?? false
            };
            setQueueInfo(newQueueInfo);
            
            // Si la commande est en route (status >= 1), utiliser remainingMinutes du backend comme source de vérité
            if (newStatus >= 1) {
              // Le backend calcule remainingMinutes en fonction de enRouteEstimatedMinutes et du temps écoulé
              // Utiliser directement cette valeur comme source de vérité
              if (newQueueInfo.remainingMinutes !== undefined && newQueueInfo.remainingMinutes >= 0) {
                  const backendRemainingTime = Math.max(Math.round(newQueueInfo.remainingMinutes), 0);
                      setCountdownMinutes(backendRemainingTime);
                      lastBackendTimeRef.current = backendRemainingTime;
                      lastBackendSyncRef.current = Date.now();
                
                // Si c'est la première fois qu'on passe en route, logger
                if (orderStatus === 0 && newStatus === 1) {
                  console.log(`🚚 Commande en route ! Temps restant: ${backendRemainingTime} min`);
                    }
                  } else {
                // Fallback si remainingMinutes n'est pas disponible
                const fallbackTime = activeOrder?.country === 'suisse'
                  ? (activeOrder?.isPriority ? 25 : 40)
                  : (activeOrder?.isPriority ? 15 : 25);
                setCountdownMinutes(fallbackTime);
                lastBackendTimeRef.current = fallbackTime;
                lastBackendSyncRef.current = Date.now();
                console.warn(`⚠️ remainingMinutes non disponible, utilisation du temps par défaut: ${fallbackTime} min`);
              }
              // Réinitialiser waitingMinutes car la commande est maintenant en route
              setWaitingMinutes(0);
            } else {
              // Status === 0 : ne pas afficher de countdown, mais calculer l'attente estimée
              setCountdownMinutes(0);
              
              // Calculer l'attente estimée avant que le livreur soit en route
              const calculateWaitingTime = () => {
                let waitingTime = 0;
                
                // 1. Si une commande est actuellement en route, ajouter le temps qu'il reste
                if (newQueueInfo.hasEnRouteOrdersAhead) {
                  // Le temps restant de la commande en route est dans remainingMinutes du backend
                  const enRouteOrderRemainingTime = newQueueInfo.remainingMinutes > 0 ? newQueueInfo.remainingMinutes : 0;
                  waitingTime += enRouteOrderRemainingTime;
                }
                
                // 2. Ajouter le temps basé sur la file d'attente (commandes avant moi)
                const ordersAhead = newQueueInfo.ordersAhead || 0;
                if (ordersAhead > 0) {
                  // Estimation : 5-8 minutes par commande standard, 3-5 minutes par commande prioritaire
                  // Si la commande actuelle est prioritaire, elle passe avant les standard
                  const timePerOrder = activeOrder?.isPriority ? 4 : 6;
                  waitingTime += ordersAhead * timePerOrder;
                }
                
                // 3. Ajouter le temps total de notre commande : Suisse (40min standard / 25min prioritaire) ou France (25min standard / 15min prioritaire)
                const ourOrderTime = activeOrder?.country === 'suisse'
                  ? (activeOrder?.isPriority ? 25 : 40)
                  : (activeOrder?.isPriority ? 15 : 25);
                waitingTime += ourOrderTime;
                
                // Utiliser effectiveMinutes du backend si disponible et plus précis
                if (newQueueInfo.effectiveMinutes > 0 && newQueueInfo.effectiveMinutes < waitingTime) {
                  waitingTime = newQueueInfo.effectiveMinutes;
                }
                
                const enRouteTime = newQueueInfo.hasEnRouteOrdersAhead ? (newQueueInfo.remainingMinutes > 0 ? newQueueInfo.remainingMinutes : 0) : 0;
                const fileTime = ordersAhead * (activeOrder?.isPriority ? 4 : 6);
                console.log(`⏳ Calcul attente : ${enRouteTime} min (reste commande en route) + ${fileTime} min (file d'attente: ${ordersAhead} commande(s)) + ${ourOrderTime} min (notre commande) = ${waitingTime} min`);
                
                return Math.max(0, Math.round(waitingTime));
              };
              
              // Si 2 personnes ou plus avant, ne pas calculer l'attente estimée
              if ((newQueueInfo.ordersAhead ?? 0) >= 2) {
                setWaitingMinutes(0);
                console.log(`⏳ Attente estimée non calculée : ${newQueueInfo.ordersAhead} commande(s) avant (>= 2)`);
              } else {
                const calculatedWaiting = calculateWaitingTime();
                setWaitingMinutes(calculatedWaiting);
                console.log(`⏳ Attente estimée calculée : ${calculatedWaiting} min (${newQueueInfo.ordersAhead} commande(s) avant, ${newQueueInfo.hasEnRouteOrdersAhead ? 'commande en route' : 'pas de commande en route'})`);
              }
            }
          } else {
            setQueueInfo({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0, hasEnRouteOrdersAhead: false });
            setCountdownMinutes(0);
            setWaitingMinutes(0);
          }

          // Mettre à jour activeOrder avec le statut validé (TOUJOURS utiliser le statut du backend)
          // Le backend est la source de vérité, donc utiliser data.status au lieu de newStatus
          const backendStatus = data.status !== undefined ? parseInt(data.status, 10) : newStatus;
          setActiveOrder(prev => ({ ...prev, status: backendStatus, cancelled: data.cancelled || false }));
          
          // Mettre à jour orderStatus avec le statut du backend (source de vérité)
          if (backendStatus !== orderStatus) {
            console.log(`🔄 Statut mis à jour depuis backend: ${orderStatus} -> ${backendStatus}`);
            setOrderStatus(backendStatus);
          }
          
          // Si la commande passe en route, la pop-up prioritaire n'a plus de sens
          if (backendStatus >= 1) {
            setShowPriorityPopup(false);
          }
          
          // Mettre à jour telegramNotificationsEnabled si disponible dans la réponse
          if (data.order && data.order.telegramNotificationsEnabled !== undefined) {
            setTelegramNotificationsEnabled(data.order.telegramNotificationsEnabled);
          }

          // Vérifier si la commande est annulée
          if (data.cancelled) {
            // Afficher le message d'annulation
            const cancellationMessage = data.cancellationReason || t('cancelledBy');
            toast.error(cancellationMessage, { duration: 5000 });
            
            // Nettoyer et rediriger vers l'accueil
            setTimeout(() => {
              setActiveOrder(null);
              setOrderStatus(0);
              setQueueInfo({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0 });
              setCountdownMinutes(0);
              setWaitingMinutes(0);
              saveActiveOrderData(null);
              setCurrentView('home');
            }, 2000);
            return;
          }

          // Vérifier si la commande est livrée (statut 3) - utiliser backendStatus
          if (backendStatus === 3) {
            // Supprimer la conversation Telegram automatiquement
            if (activeOrder?.orderId) {
              fetchWithTimeout(`${BACKEND_URL}/clear-conversation/${activeOrder.orderId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              }, 5000).catch(err => {
                console.error("Erreur suppression conversation Telegram:", err);
              });
            }

            // 🪙 Vérifier les Global Coins gagnés après livraison
            if (telegramChatId) {
              try {
                const clientRes = await fetchWithTimeout(`${BACKEND_URL}/client/verify`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ chatId: telegramChatId })
                }, 8000);
                const clientData = await clientRes.json();
                if (clientData.success && clientData.client) {
                  // Mettre à jour le solde de coins
                  if (clientData.client.globalCoins !== undefined) {
                    setGlobalCoins(clientData.client.globalCoins);
                  }
                  if (clientData.client.freePacks !== undefined) {
                    setFreePacks(clientData.client.freePacks || 0);
                  }
                  if (clientData.client.packPriorityBonus !== undefined) {
                    setPackPriorityBonus(clientData.client.packPriorityBonus === true);
                  }
                  // Afficher la popup de coins si une notification est en attente
                  if (clientData.client.pendingCoinsNotification) {
                    setPendingCoinsNotification(clientData.client.pendingCoinsNotification);
                    setShowCoinsPopup(true);
                  }
                  setDoubleGcOffer(clientData.client.doubleGcNextOrder || null);
                }
              } catch (coinErr) {
                console.error("Erreur récupération coins après livraison:", coinErr);
              }
            }
            
            setShowConfetti(true);
            setTimeout(() => {
              setShowConfetti(false);
              setActiveOrder(null);
              saveActiveOrderData(null);
              setCurrentView('home');
            }, 5000);
        }
      } catch (err) {
        console.error("Erreur récupération statut commande:", err);
        // En cas d'erreur, vérifier si c'est parce que la commande n'existe plus
        const freshRes = await fetchWithTimeout(`${BACKEND_URL}/fresh-status`, {}, 10000);
        const freshData = await freshRes.json();
        if (freshData.success && freshData.isFresh) {
          setActiveOrder(null);
          setOrderStatus(0);
          setQueueInfo({ ordersAhead: 0, estimatedWaitMinutes: 0, totalActive: 0, extraDelayMinutes: 0, priorityReductionMinutes: 0, effectiveMinutes: 0, remainingMinutes: 0 });
          saveActiveOrderData(null);
          if (currentView === 'order-tracking' || currentView === 'order-confirmation') {
            setCurrentView('home');
          }
        }
      }
    };
    const interval = setInterval(fetchActiveOrder, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder, BACKEND_URL, currentView, saveActiveOrderData, orderStatus]);

  // Charger immédiatement les données de queue quand on passe à la vue "order-tracking"
  useEffect(() => {
    if (currentView === 'order-tracking' && activeOrder?.orderId) {
      // Charger immédiatement les données sans attendre l'intervalle
      const loadQueueData = async () => {
        try {
          const res = await fetchWithTimeout(`${BACKEND_URL}/status/${activeOrder.orderId}`, {}, 10000);
          const data = await res.json();
          if (data.success && data.order && data.queue) {
            const newQueueInfo = {
              ordersAhead: data.queue.ordersAhead ?? 0,
              estimatedWaitMinutes: data.queue.estimatedWaitMinutes ?? 0,
              totalActive: data.queue.totalActive ?? 0,
              extraDelayMinutes: data.queue.extraDelayMinutes ?? 0,
              priorityReductionMinutes: data.queue.priorityReductionMinutes ?? 0,
              effectiveMinutes: data.queue.effectiveMinutes ?? data.queue.estimatedWaitMinutes ?? 0,
              remainingMinutes: data.queue.remainingMinutes ?? data.queue.effectiveMinutes ?? data.queue.estimatedWaitMinutes ?? 0,
              hasEnRouteOrdersAhead: data.queue.hasEnRouteOrdersAhead ?? false
            };
            setQueueInfo(newQueueInfo);
            const newStatus = data.status || 0;
            setOrderStatus(newStatus);
            
            // Ne mettre à jour countdownMinutes que si la commande est en route (status >= 1)
            if (newStatus >= 1) {
              // Utiliser remainingMinutes du backend si disponible (inclut les ajustements du livreur)
              // Sinon utiliser le temps par défaut
              const currentActiveOrder = activeOrder || getStoredActiveOrder();
              if (currentActiveOrder) {
                const backendRemainingTime = newQueueInfo.remainingMinutes;
                // Le backend fait foi, y compris quand il renvoie 0 (arrivée imminente) :
                // on ne retombe plus sur 25/40 min par défaut.
                const estimatedTime = Math.max(Math.round(Number(backendRemainingTime) || 0), 0);
                setCountdownMinutes(estimatedTime);
                // Initialiser la référence du backend pour détecter les ajustements futurs
                lastBackendTimeRef.current = estimatedTime;
                lastBackendSyncRef.current = Date.now();
                console.log(`🚚 Commande en route ! Temps estimé fixé à ${estimatedTime} min (${currentActiveOrder.isPriority ? 'prioritaire' : 'standard'})`);
              }
              // Réinitialiser waitingMinutes car la commande est maintenant en route
              setWaitingMinutes(0);
            } else {
              // Status === 0 : ne pas afficher de countdown, mais calculer l'attente estimée
              setCountdownMinutes(0);
              
              // Calculer l'attente estimée avant que le livreur soit en route
              const calculateWaitingTime = () => {
                let waitingTime = 0;
                const currentActiveOrder = activeOrder || getStoredActiveOrder();
                
                // 1. Si une commande est actuellement en route, ajouter le temps qu'il reste
                if (newQueueInfo.hasEnRouteOrdersAhead) {
                  // Le temps restant de la commande en route est dans remainingMinutes du backend
                  const enRouteOrderRemainingTime = newQueueInfo.remainingMinutes > 0 ? newQueueInfo.remainingMinutes : 0;
                  waitingTime += enRouteOrderRemainingTime;
                }
                
                // 2. Ajouter le temps basé sur la file d'attente (commandes avant moi)
                const ordersAhead = newQueueInfo.ordersAhead || 0;
                if (ordersAhead > 0) {
                  // Estimation : 5-8 minutes par commande standard, 3-5 minutes par commande prioritaire
                  // Si la commande actuelle est prioritaire, elle passe avant les standard
                  const timePerOrder = currentActiveOrder?.isPriority ? 4 : 6;
                  waitingTime += ordersAhead * timePerOrder;
                }
                
                // 3. Ajouter le temps total de notre commande : Suisse (40min standard / 25min prioritaire) ou France (25min standard / 15min prioritaire)
                const ourOrderTime = currentActiveOrder?.country === 'suisse'
                  ? (currentActiveOrder?.isPriority ? 25 : 40)
                  : (currentActiveOrder?.isPriority ? 15 : 25);
                waitingTime += ourOrderTime;
                
                // Utiliser effectiveMinutes du backend si disponible et plus précis
                if (newQueueInfo.effectiveMinutes > 0 && newQueueInfo.effectiveMinutes < waitingTime) {
                  waitingTime = newQueueInfo.effectiveMinutes;
                }
                
                const enRouteTime = newQueueInfo.hasEnRouteOrdersAhead ? (newQueueInfo.remainingMinutes > 0 ? newQueueInfo.remainingMinutes : 0) : 0;
                const fileTime = ordersAhead * (currentActiveOrder?.isPriority ? 4 : 6);
                console.log(`⏳ Calcul attente : ${enRouteTime} min (reste commande en route) + ${fileTime} min (file d'attente: ${ordersAhead} commande(s)) + ${ourOrderTime} min (notre commande) = ${waitingTime} min`);
                
                return Math.max(0, Math.round(waitingTime));
              };
              
              // Si 2 personnes ou plus avant, ne pas calculer l'attente estimée
              if ((newQueueInfo.ordersAhead ?? 0) >= 2) {
                setWaitingMinutes(0);
                console.log(`⏳ Attente estimée non calculée : ${newQueueInfo.ordersAhead} commande(s) avant (>= 2)`);
              } else {
                const calculatedWaiting = calculateWaitingTime();
                setWaitingMinutes(calculatedWaiting);
                console.log(`⏳ Attente estimée calculée : ${calculatedWaiting} min (${newQueueInfo.ordersAhead} commande(s) avant, ${newQueueInfo.hasEnRouteOrdersAhead ? 'commande en route' : 'pas de commande en route'})`);
              }
            }
          }
        } catch (error) {
          console.error('Erreur chargement données queue:', error);
        }
      };
      loadQueueData();
    }
  }, [currentView, activeOrder?.orderId, BACKEND_URL, activeOrder, getStoredActiveOrder]);

  useEffect(() => {
    if (!activeOrder?.orderId) {
      setChatMessages([]);
      setChatUnreadCount(0);
      chatLastCountRef.current = 0;
      setCountdownMinutes(0);
      if (chatPollingRef.current) {
        clearInterval(chatPollingRef.current);
        chatPollingRef.current = null;
      }
      return;
    }

    const syncChat = async () => {
      const history = await fetchChatHistory(activeOrder.orderId, 'client');
      setChatMessages(history);
      const diff = Math.max(history.length - chatLastCountRef.current, 0);
      
      // Note: la notification Telegram "+1 message de la part du livreur" est désormais envoyée
      // directement par le serveur au moment où le livreur poste son message (voir POST /orders/:orderId/chat
      // côté backend), ce qui est plus fiable qu'un déclenchement basé sur le polling du frontend.

      if (diff > 0 && (!isChatOpen || isChatMinimized)) {
        setChatUnreadCount((prev) => prev + diff);
      }
      if (isChatOpen && !isChatMinimized) {
        setChatUnreadCount(0);
      }
      chatLastCountRef.current = history.length;
    };

    syncChat();
    if (chatPollingRef.current) {
      clearInterval(chatPollingRef.current);
    }
    chatPollingRef.current = setInterval(syncChat, 4000);

    return () => {
      if (chatPollingRef.current) {
        clearInterval(chatPollingRef.current);
        chatPollingRef.current = null;
      }
    };
  }, [activeOrder?.orderId, fetchChatHistory, isChatOpen, isChatMinimized, telegramLink, BACKEND_URL]);

  useEffect(() => {
    if (isChatOpen && !isChatMinimized) {
      setChatUnreadCount(0);
    }
  }, [isChatOpen, isChatMinimized]);

  // Réinitialiser countdownMinutes, waitingMinutes et previousOrderStatusRef quand il n'y a plus de commande active
  useEffect(() => {
    setBackendTotalMinutes(null);
    lastBackendSyncRef.current = null;
    if (!activeOrder?.orderId) {
      setCountdownMinutes(0);
      setWaitingMinutes(0);
      previousOrderStatusRef.current = 0;
      lastBackendTimeRef.current = null;
    }
  }, [activeOrder?.orderId]);

  // Réinitialiser previousOrderStatusRef quand une nouvelle commande est créée
  useEffect(() => {
    if (activeOrder?.orderId) {
      previousOrderStatusRef.current = orderStatus;
    }
  }, [activeOrder?.orderId, orderStatus]);

  // Mettre à jour previousOrderStatusRef quand le statut change
  useEffect(() => {
    previousOrderStatusRef.current = orderStatus;
  }, [orderStatus]);

  // Mettre à jour les refs à chaque changement
  useEffect(() => {
    countdownMinutesRef.current = countdownMinutes;
  }, [countdownMinutes]);

  useEffect(() => {
    orderStatusRef.current = orderStatus;
  }, [orderStatus]);

  // ── Décompte client (source de vérité = backend) ─────────────────────────────
  // Le backend renvoie remainingMinutes en minutes ENTIÈRES (calculé depuis enRouteAt).
  // On l'affiche donc tel quel à chaque synchro (toutes les 5 s) : plus de décalage ni de
  // « ré-ancrage » du minuteur. Si le réseau coupe, un minuteur local de secours prend le relais.
  useEffect(() => {
    if (!activeOrder?.orderId) return;
    if (orderStatus < 1 || orderStatus > 2) return;

    let isRunning = true;
    const orderId = activeOrder.orderId;

    const syncWithBackend = async () => {
      if (!isRunning) return;
      try {
        const res = await fetchWithTimeout(`${BACKEND_URL}/status/${orderId}`, {}, 8000);
        if (!res.ok || !isRunning) return;
        const data = await res.json();
        if (!isRunning || !data.success || !data.queue || data.queue.remainingMinutes === undefined) return;

        const backendRemaining = Math.max(Math.round(Number(data.queue.remainingMinutes) || 0), 0);
        lastBackendTimeRef.current = backendRemaining;
        lastBackendSyncRef.current = Date.now();
        setCountdownMinutes((prev) => (prev === backendRemaining ? prev : backendRemaining));

        // Durée totale réelle du trajet (sert uniquement à la barre de progression)
        const total = Number(data.queue.enRouteTotalMinutes);
        if (Number.isFinite(total) && total > 0) {
          setBackendTotalMinutes((prev) => (prev === total ? prev : total));
        }
      } catch (error) {
        // Réseau indisponible : le minuteur de secours ci-dessous continue le décompte
      }
    };

    syncWithBackend();
    const intervalId = setInterval(syncWithBackend, 5000);
    return () => {
      isRunning = false;
      clearInterval(intervalId);
    };
  }, [activeOrder?.orderId, orderStatus, BACKEND_URL]);

  // Minuteur local de SECOURS : n'agit que si aucune synchro backend n'a réussi depuis > 65 s
  useEffect(() => {
    if (!activeOrder?.orderId) return;
    if (orderStatus < 1 || orderStatus > 2) return;

    const tick = () => {
      const lastSync = lastBackendSyncRef.current;
      const lastValue = lastBackendTimeRef.current;
      if (lastSync == null || lastValue == null) return;
      const sinceSync = Date.now() - lastSync;
      if (sinceSync < 65000) return; // le backend répond : il fait foi
      const projected = Math.max(lastValue - Math.floor(sinceSync / 60000), 0);
      setCountdownMinutes((prev) => (prev === projected ? prev : projected));
    };

    const intervalId = setInterval(tick, 10000);
    return () => clearInterval(intervalId);
  }, [activeOrder?.orderId, orderStatus]);

  // Minuteur pour waitingMinutes SUPPRIMÉ : on utilise directement queueInfo.remainingMinutes du backend
  // Le backend recalcule dynamiquement le temps restant à chaque poll (toutes les 3s)
  // Pas besoin de décrémentation locale pour status=0

  // ── Pop-up « PASSEZ PRIORITAIRE » : affichée UNE SEULE FOIS, à la toute première visite du suivi
  // de CETTE commande (clé mémorisée par orderId), uniquement si la commande n'est pas encore
  // prioritaire, pas annulée et pas encore partie en livraison.
  useEffect(() => {
    if (currentView !== 'order-tracking' || !activeOrder?.orderId) return;
    const orderId = activeOrder.orderId;
    const storageKey = `priorityPopupSeen_${orderId}`;
    if (readPersistentValue(storageKey)) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (readPersistentValue(storageKey)) return;
      try {
        // On vérifie l'état RÉEL côté backend avant d'afficher quoi que ce soit
        const res = await fetchWithTimeout(`${BACKEND_URL}/status/${orderId}`, {}, 8000);
        const data = await res.json();
        if (cancelled) return;
        if (!data?.success) return; // réessaiera à la prochaine visite
        const backendStatus = parseInt(data.status, 10);
        const alreadyPriority = !!(data.order && data.order.isPriority);
        if (data.cancelled || alreadyPriority || (Number.isFinite(backendStatus) && backendStatus >= 1)) {
          // Rien à proposer pour cette commande : on ne la montrera jamais plus
          persistValueAcrossStorages(storageKey, '1');
          return;
        }
        persistValueAcrossStorages(storageKey, '1');
        setShowPriorityPopup(true);
      } catch (e) {
        // Réseau indisponible : on ne mémorise rien, la pop-up pourra s'afficher à la prochaine entrée
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [currentView, activeOrder?.orderId, BACKEND_URL]);

  // Si la commande devient prioritaire / part en route / est annulée, la pop-up se ferme toute seule
  useEffect(() => {
    if (showPriorityPopup && (activeOrder?.isPriority || activeOrder?.cancelled || orderStatus >= 1 || !activeOrder?.orderId)) {
      setShowPriorityPopup(false);
    }
  }, [showPriorityPopup, activeOrder?.isPriority, activeOrder?.cancelled, activeOrder?.orderId, orderStatus]);

  const handleChatSend = async () => {
    if (!chatInput.trim() || !activeOrder?.orderId) return;
    setChatSending(true);
    const success = await postChatMessage({
      orderId: activeOrder.orderId,
      role: 'client',
      payload: { type: 'text', content: chatInput.trim() }
    });
    if (success) {
      setChatInput('');
      const updated = await fetchChatHistory(activeOrder.orderId, 'client');
      setChatMessages(updated);
      chatLastCountRef.current = updated.length;
    } else {
      toast.error(t('cantSendMessage'));
    }
    setChatSending(false);
  };

  const handleChatPhotoUpload = async (event) => {
    const rawFile = event.target.files?.[0];
    if (event?.target) { try { event.target.value = ''; } catch { /* */ } }
    if (!rawFile || !activeOrder?.orderId || chatUploading) return;

    let preparedFile = rawFile;
    try {
      preparedFile = await prepareChatImage(rawFile);
    } catch (prepErr) {
      toast.error(prepErr?.message || t('photoUploadFailed'));
      return;
    }

    const localUrl = URL.createObjectURL(preparedFile);
    const tempId = `tmp_chat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    rememberChatBlob(tempId, localUrl);

    setChatUploading(true);
    setChatUploadProgress(5);
    setChatUploadingMessageId(tempId);

    const tempMessage = {
      id: tempId,
      role: 'client',
      type: 'image',
      mediaType: 'image',
      url: localUrl,
      mediaUrl: localUrl,
      content: '',
      authorName: 'Moi',
      createdAt: new Date().toISOString(),
    };

    setChatMessages((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      arr.push(tempMessage);
      chatLastCountRef.current = arr.length;
      return arr;
    });

    let uploadOk = false;
    let realMsgId = null;
    try {
      const result = await uploadChatPhoto({
        orderId: activeOrder.orderId,
        role: 'client',
        file: preparedFile,
        onProgress: (p) => {
          const safe = Math.max(5, Math.min(98, Number(p) || 0));
          setChatUploadProgress(safe);
        },
      });

      if (result?.success) {
        uploadOk = true;
        setChatUploadProgress(100);
        let updatedMsgs = null;
        if (Array.isArray(result.messages) && result.messages.length > 0) {
          updatedMsgs = result.messages.map((m) => ({
            ...m,
            type: m.type || (m.mediaType === 'image' ? 'image' : (m.mediaType === 'video' ? 'video' : 'text')),
            url: resolveMediaUrlFallback(m.url || m.mediaUrl || null),
            mediaUrl: resolveMediaUrlFallback(m.url || m.mediaUrl || null),
            content: m.content || m.text || '',
          }));
          const mine = [...updatedMsgs].reverse().find((m) => m && m.role === 'client' && (m.type === 'image' || m.type === 'video'));
          if (mine?.id) {
            realMsgId = mine.id;
            rememberChatBlob(mine.id, localUrl);
          }
        } else {
          updatedMsgs = await fetchChatHistory(activeOrder.orderId, 'client');
        }
        if (updatedMsgs) {
          setChatMessages(updatedMsgs);
          chatLastCountRef.current = updatedMsgs.length;
        }
      } else {
        throw new Error(result?.message || t('photoUploadFailed'));
      }
    } catch (err) {
      toast.error(err?.message || t('photoUploadFailed'));
      setChatMessages((prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        const out = arr.filter((m) => m.id !== tempId);
        chatLastCountRef.current = out.length;
        return out;
      });
    } finally {
      try { forgetChatBlob(tempId); } catch { /* */ }
      if (!uploadOk) {
        try { forgetChatBlob(realMsgId); } catch { /* */ }
      }
      setChatUploading(false);
      setChatUploadProgress(0);
      setChatUploadingMessageId(null);
    }
  };

  const fetchDriverOrders = useCallback(async (options = {}) => {
    const { showSpinner = false } = options;
    if (!isDriverMode) return;
    if (showSpinner) setDriverLoadingOrders(true);
    try {
      // Tous les livreurs voient les mêmes commandes - pas de filtrage par chatId
      // Le chatId est envoyé uniquement pour identifier qui fait les actions
      const headers = { ...getApiAuthHeaders(), 'Content-Type': 'application/json' };
      if (telegramChatId) {
        headers['X-Chat-Id'] = telegramChatId; // Pour identifier le livreur qui fait les actions
      }
      const url = `${BACKEND_URL}/driver/orders${telegramChatId ? `?chatId=${encodeURIComponent(telegramChatId)}` : ''}`;
      // resilientJsonRequest : récupère la session sur 401 (token expiré) et
      // retente une fois sur erreur réseau/5xx — indispensable après un long
      // séjour en arrière-plan ou une connexion instable.
      const { response, data, error } = await resilientJsonRequest(() => fetchWithTimeout(url, { headers }, 10000));
      if (response && response.ok && data?.success && Array.isArray(data.orders)) {
        setDriverOrders(data.orders);
        writeDriverOrdersCache(data.orders);
        setDriverOrdersError(false);
        setDriverOrdersErrorKind(null);
        if (typeof data.deliveredCount === 'number') {
          setDriverDeliveredCount(data.deliveredCount);
        }
      } else {
        // Ne jamais vider la liste affichée sur une réponse inattendue :
        // le livreur doit conserver les commandes déjà connues.
        const status = response?.status || 0;
        const kind = (status === 401 || status === 403)
          ? 'auth'
          : (status === 0 || error)
            ? 'network'
            : (status >= 500 ? 'server' : 'unknown');
        console.warn(`[driver/orders] échec — status=${status} kind=${kind} message=${data?.message || error?.message || 'aucun'}`);
        setDriverOrdersError(true);
        setDriverOrdersErrorKind(kind);
      }
    } catch (error) {
      console.error('Erreur récupération commandes livreur:', error);
      setDriverOrdersError(true);
      setDriverOrdersErrorKind('network');
    } finally {
      setDriverLoadingOrders(false);
    }
  }, [BACKEND_URL, isDriverMode, telegramChatId]);

  // Hydratation depuis le cache local dès l'entrée en mode livreur : la liste
  // s'affiche immédiatement (hors ligne / réseau lent) avant la synchro serveur.
  useEffect(() => {
    if (!isDriverMode) return;
    setDriverOrders((prev) => (prev.length > 0 ? prev : readDriverOrdersCache()));
  }, [isDriverMode]);

  // Garde une référence synchrone de la liste pour les effets non re-déclenchés.
  useEffect(() => {
    driverOrdersRef.current = driverOrders;
  }, [driverOrders]);

  useEffect(() => {
    if (!isDriverMode) return undefined;
    fetchDriverOrders({ showSpinner: true });
    const interval = setInterval(() => fetchDriverOrders(), 5000);
    const loadingGuard = setTimeout(() => setDriverLoadingOrders(false), 12000);
    // Reprise après arrière-plan (app en veille, téléphone verrouillé, onglet
    // gelé) : les timers JS sont suspendus, on resynchronise immédiatement dès
    // que l'app redevient visible / connectée.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchDriverOrders();
    };
    const onResume = () => fetchDriverOrders();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      clearInterval(interval);
      clearTimeout(loadingGuard);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, [isDriverMode, fetchDriverOrders]);

  // Filet : splash max 5 s puis afficher l'app (modales pays/nom si besoin)
  useEffect(() => {
    if (!isCheckingAuth || isDriverMode || isDashboardMode) return;
    const authGuard = setTimeout(() => {
      setIsCheckingAuth(false);
      setCheckingShopStatus(false);
    }, 5000);
    return () => clearTimeout(authGuard);
  }, [isCheckingAuth, isDriverMode, isDashboardMode]);

  useEffect(() => {
    if (pwaInstallLocked || isDriverMode || isDashboardMode) {
      setSplashHold(false);
      return;
    }
    const hold = setTimeout(() => setSplashHold(false), 3400);
    return () => clearTimeout(hold);
  }, [pwaInstallLocked, isDriverMode, isDashboardMode]);

  // Mode maintenance client (OFF par défaut — uniquement si API confirme success + true)
  useEffect(() => {
    if (isDriverMode || isDashboardMode || isAccessMode) {
      setMaintenanceMode(false);
      return undefined;
    }
    let cancelled = false;
    const checkMaint = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/shop-status`, { credentials: 'include' });
        const json = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && json?.success === true && json?.maintenance === true) {
          setMaintenanceMode(true);
        } else if (!cancelled) {
          setMaintenanceMode(false);
        }
      } catch {
        /* ne pas forcer maintenance sur erreur réseau */
      }
    };
    checkMaint();
    const iv = setInterval(checkMaint, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [BACKEND_URL, isDriverMode, isDashboardMode, isAccessMode]);

  // Écran « déjà connecté » — UNIQUEMENT vrai retour de session (auth + nom, hors onboarding)
  useEffect(() => {
    if (isCheckingAuth || splashHold || isAccessMode || isDriverMode || isDashboardMode || pwaInstallLocked) {
      setShowConnectedWelcome(false);
      return undefined;
    }
    if (maintenanceMode) {
      setShowConnectedWelcome(false);
      return undefined;
    }
    // Nouveaux users / onboarding : jamais cet écran (bloquait pays / nom / Enregistrer)
    if (!isAuthenticated || !userName) {
      setShowConnectedWelcome(false);
      return undefined;
    }
    if (showNameModal || showCountryModal || showCityModal || showSignupModal) {
      setShowConnectedWelcome(false);
      return undefined;
    }
    if (!telegramChatId) return undefined;
    try {
      if (sessionStorage.getItem('gs_connected_welcome') === '1') return undefined;
      sessionStorage.setItem('gs_connected_welcome', '1');
    } catch { /* */ }
    setShowConnectedWelcome(true);
    const t = setTimeout(() => setShowConnectedWelcome(false), 2200);
    return () => {
      clearTimeout(t);
      setShowConnectedWelcome(false);
    };
  }, [
    isCheckingAuth, splashHold, isAccessMode, isDriverMode, isDashboardMode,
    pwaInstallLocked, telegramChatId, maintenanceMode,
    isAuthenticated, userName, showNameModal, showCountryModal, showCityModal, showSignupModal,
  ]);

  useEffect(() => {
    if (pwaInstallLocked || isDriverMode || isDashboardMode) {
      setSplashProgress(100);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / 3400, 1);
      setSplashProgress(6 + t * 94);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pwaInstallLocked, isDriverMode, isDashboardMode]);

  // Masquer le loader HTML natif dès que React a monté le splash
  useEffect(() => {
    if (typeof window.__hideBootLoader === 'function') {
      window.__hideBootLoader();
    }
  }, []);

  // Ticker 1 seconde pour mettre à jour les badges de temps restant sur les cartes livreur
  useEffect(() => {
    if (!isDriverMode || currentView !== 'driver-panel') return;
    const ticker = setInterval(() => setDriverCardTick(t => t + 1), 1000);
    return () => clearInterval(ticker);
  }, [isDriverMode, currentView]);

  // Détecter les déconnexions automatiques (fermeture du navigateur, perte de connexion)
  useEffect(() => {
    if (!isDriverMode || !telegramChatId) return;

    // Fonction pour notifier le backend de la déconnexion
    const notifyDisconnect = async (isManual = false) => {
      try {
        // Utiliser sendBeacon pour garantir l'envoi même si la page se ferme
        const disconnectData = JSON.stringify({ 
          chatId: telegramChatId,
          isManual: isManual 
        });
        
        // Essayer d'abord avec sendBeacon (plus fiable pour les déconnexions)
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            `${BACKEND_URL}/driver/disconnect`,
            new Blob([disconnectData], { type: 'application/json' })
          );
        } else {
          // Fallback : fetch avec keepalive
          fetch(`${BACKEND_URL}/driver/disconnect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: disconnectData,
            keepalive: true
          }).catch(() => {
            // Ignorer les erreurs car la page peut être en train de se fermer
          });
        }
      } catch (error) {
        // Ignorer les erreurs car la page peut être en train de se fermer
        console.error('Erreur notification déconnexion:', error);
      }
    };

    // Détecter la fermeture du navigateur/onglet
    const handleBeforeUnload = () => {
      // Notifier le backend de la déconnexion
      notifyDisconnect(false);
    };

    // Détecter la perte de connexion réseau
    const handleOffline = () => {
      // Connexion perdue - notifier le backend
      notifyDisconnect(false);
    };

    // Ajouter les event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('offline', handleOffline);

    // Nettoyer les event listeners au démontage
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isDriverMode, telegramChatId, BACKEND_URL]);

  // Récupérer les revenus du livreur et déterminer son pays
  useEffect(() => {
    if (!isDriverMode || !telegramChatId) return;
    
    const fetchDriverEarnings = async () => {
      try {
        const response = await fetchWithTimeout(
          `${BACKEND_URL}/driver/earnings?chatId=${telegramChatId}`, 
          { headers: { 'X-Chat-Id': telegramChatId } },
          10000
        );
        const data = await response.json();
        if (data.success && data.earnings) {
          setDriverEarnings(data.earnings.totalEarnings || 0);
        }
      } catch (error) {
        console.error('Erreur récupération revenus:', error);
      }
    };
    
    // Déterminer le pays du livreur en fonction de ses commandes
    const determineDriverCountry = () => {
      if (driverOrders.length > 0) {
        // Compter les commandes par pays
        const franceCount = driverOrders.filter(o => !o.country || o.country === 'france').length;
        const suisseCount = driverOrders.filter(o => o.country === 'suisse').length;
        // Si plus de commandes en Suisse, le livreur est assigné à la Suisse
        if (suisseCount > franceCount) {
          setDriverCountry('suisse');
        } else {
          setDriverCountry('france');
        }
      }
    };
    
    fetchDriverEarnings();
    determineDriverCountry();
    const interval = setInterval(() => {
      fetchDriverEarnings();
      determineDriverCountry();
    }, 5000);
    return () => clearInterval(interval);
  }, [isDriverMode, telegramChatId, BACKEND_URL, driverOrders]);

  const syncDriverChat = useCallback(async (orderId) => {
    if (!orderId) return;
    // Tous les livreurs voient tous les messages - pas d'isolation
    const history = await fetchChatHistory(orderId, 'driver', telegramChatId);
    setDriverChatMessages(history);
    const diff = Math.max(history.length - driverChatLastCountRef.current, 0);
    if (diff > 0 && (!isDriverChatOpen || isDriverChatMinimized)) {
      setDriverChatUnread((prev) => prev + diff);
    }
    if (isDriverChatOpen && !isDriverChatMinimized) {
      setDriverChatUnread(0);
    }
    driverChatLastCountRef.current = history.length;
  }, [fetchChatHistory, isDriverChatOpen, isDriverChatMinimized, telegramChatId]);

  useEffect(() => {
    if (!selectedDriverOrder?.orderId || !isDriverChatOpen || !telegramChatId) {
      setDriverChatMessages([]);
      driverChatLastCountRef.current = 0;
      if (driverChatPollingRef.current) {
        clearInterval(driverChatPollingRef.current);
        driverChatPollingRef.current = null;
      }
      return;
    }

    syncDriverChat(selectedDriverOrder.orderId);
    if (driverChatPollingRef.current) {
      clearInterval(driverChatPollingRef.current);
    }
    // Polling isolé par chatId - chaque livreur a son propre interval
    driverChatPollingRef.current = setInterval(() => syncDriverChat(selectedDriverOrder.orderId), 4000);

    return () => {
      if (driverChatPollingRef.current) {
        clearInterval(driverChatPollingRef.current);
        driverChatPollingRef.current = null;
      }
    };
  }, [selectedDriverOrder?.orderId, isDriverChatOpen, syncDriverChat, telegramChatId]);

  const handleDriverContact = useCallback((order, options = {}) => {
    setSelectedDriverOrder(order);
    setIsDriverChatOpen(true);
    setIsDriverChatMinimized(false);
    setDriverChatUnread(0);
    driverChatLastCountRef.current = 0;
    syncDriverChat(order.orderId);
    if (options.navigateToChat) {
      setCurrentView('driver-chat');
    }
  }, [syncDriverChat]);

  const openDriverChatFromPanel = useCallback(() => {
    if (selectedDriverOrder) {
      setIsDriverChatOpen(true);
      setIsDriverChatMinimized(false);
      setCurrentView('driver-chat');
      return;
    }
    if (driverOrders.length > 0) {
      handleDriverContact(driverOrders[0], { navigateToChat: true });
      return;
    }
    toast.info(t('noOrderForChat'));
  }, [selectedDriverOrder, driverOrders, handleDriverContact, t]);

  useEffect(() => {
    if (!driverDeepLinkOrderId || driverOrders.length === 0) return;
    const matchingOrder = driverOrders.find((order) => order.orderId === driverDeepLinkOrderId);
    if (matchingOrder) {
      handleDriverContact(matchingOrder, { navigateToChat: true });
      setDriverDeepLinkOrderId(null);
      try { sessionStorage.removeItem(STAFF_ORDER_KEY); } catch { /* silencieux */ }
    }
  }, [driverDeepLinkOrderId, driverOrders, handleDriverContact]);

  // Filet de sécurité « notification » : si la commande notifiée n'est pas (encore)
  // présente dans la liste synchronisée, on la récupère directement par son id
  // pour garantir qu'elle reste visible, quel que soit l'état de la synchro.
  useEffect(() => {
    if (!driverDeepLinkOrderId) return undefined;
    const timer = setTimeout(async () => {
      if (!driverDeepLinkOrderId) return;
      if (driverOrdersRef.current.some((o) => o.orderId === driverDeepLinkOrderId)) return;
      try {
        const res = await fetchWithTimeout(
          `${BACKEND_URL}/status/${encodeURIComponent(driverDeepLinkOrderId)}`,
          { headers: getApiAuthHeaders() },
          10000
        );
        const data = await res.json().catch(() => null);
        const order = data?.order || (data?.success ? data : null);
        if (order && order.orderId) {
          setDriverOrders((prev) => (prev.some((o) => o.orderId === order.orderId) ? prev : [order, ...prev]));
        }
      } catch {
        /* la resynchronisation périodique prendra le relais */
      }
    }, 6000);
    return () => clearTimeout(timer);
  }, [driverDeepLinkOrderId, BACKEND_URL]);

  const handleDriverChatSend = async () => {
    // Vérifications avec logs de débogage
    if (!driverChatInput.trim()) {
      console.warn('⚠️ handleDriverChatSend: driverChatInput est vide');
      return;
    }
    if (!selectedDriverOrder?.orderId) {
      console.warn('⚠️ handleDriverChatSend: selectedDriverOrder ou orderId manquant', { selectedDriverOrder });
      toast.error(t('noOrderSelected'));
      return;
    }
    if (!telegramChatId) {
      console.warn('⚠️ handleDriverChatSend: telegramChatId manquant');
      toast.error(t('chatIdMissing'));
      return;
    }
    
    console.log('📤 handleDriverChatSend: Envoi du message', {
      orderId: selectedDriverOrder.orderId?.slice(0, 8),
      chatId: telegramChatId,
      contentLength: driverChatInput.trim().length
    });
    
    setDriverChatSending(true);
    try {
    const success = await postChatMessage({
      orderId: selectedDriverOrder.orderId,
      role: 'driver',
        payload: { type: 'text', content: driverChatInput.trim() },
        chatIdParam: telegramChatId // Identifier qui envoie le message
    });
      
    if (success) {
        console.log('✅ handleDriverChatSend: Message envoyé avec succès');
      setDriverChatInput('');
      await syncDriverChat(selectedDriverOrder.orderId);
    } else {
        console.error('❌ handleDriverChatSend: postChatMessage a retourné false');
      toast.error(t('messageNotSent'));
    }
    } catch (error) {
      console.error('❌ handleDriverChatSend: Erreur lors de l\'envoi', error);
      toast.error(t('sendMessageError') + ": " + (error?.message || 'Erreur inconnue'));
    } finally {
    setDriverChatSending(false);
    }
  };

  const handleDriverPhotoUpload = async (event) => {
    const rawFile = event.target.files?.[0];
    if (event?.target) { try { event.target.value = ''; } catch { /* */ } }
    if (!rawFile || !selectedDriverOrder?.orderId || !telegramChatId || driverChatUploading) return;

    let preparedFile = rawFile;
    try {
      preparedFile = await prepareChatImage(rawFile);
    } catch (prepErr) {
      toast.error(prepErr?.message || t('cantSendPhotoClient'));
      return;
    }

    const localUrl = URL.createObjectURL(preparedFile);
    const tempId = `tmp_dchat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    rememberChatBlob(tempId, localUrl);

    setDriverChatUploading(true);
    setDriverChatUploadProgress(5);
    setDriverChatUploadingId(tempId);

    const tempMessage = {
      id: tempId,
      role: 'driver',
      type: 'image',
      mediaType: 'image',
      url: localUrl,
      mediaUrl: localUrl,
      content: '',
      authorName: 'Livreur',
      createdAt: new Date().toISOString(),
    };

    setDriverChatMessages((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      arr.push(tempMessage);
      driverChatLastCountRef.current = arr.length;
      return arr;
    });

    let uploadOk = false;
    let realMsgId = null;
    try {
      const result = await uploadChatPhoto({
        orderId: selectedDriverOrder.orderId,
        role: 'driver',
        file: preparedFile,
        chatIdParam: telegramChatId,
        onProgress: (p) => {
          const safe = Math.max(5, Math.min(98, Number(p) || 0));
          setDriverChatUploadProgress(safe);
        },
      });

      if (result?.success) {
        uploadOk = true;
        setDriverChatUploadProgress(100);
        if (Array.isArray(result.messages) && result.messages.length > 0) {
          const mapped = result.messages.map((m) => ({
            ...m,
            type: m.type || (m.mediaType === 'image' ? 'image' : (m.mediaType === 'video' ? 'video' : 'text')),
            url: resolveMediaUrlFallback(m.url || m.mediaUrl || null),
            mediaUrl: resolveMediaUrlFallback(m.url || m.mediaUrl || null),
            content: m.content || m.text || '',
          }));
          const mine = [...mapped].reverse().find((m) => m && m.role === 'driver' && (m.type === 'image' || m.type === 'video'));
          if (mine?.id) {
            realMsgId = mine.id;
            rememberChatBlob(mine.id, localUrl);
          }
          setDriverChatMessages(mapped);
          driverChatLastCountRef.current = mapped.length;
        } else {
          await syncDriverChat(selectedDriverOrder.orderId);
        }
      } else {
        throw new Error(result?.message || t('cantSendPhotoClient'));
      }
    } catch (err) {
      toast.error(err?.message || t('cantSendPhotoClient'));
      setDriverChatMessages((prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        const out = arr.filter((m) => m.id !== tempId);
        driverChatLastCountRef.current = out.length;
        return out;
      });
    } finally {
      try { forgetChatBlob(tempId); } catch { /* */ }
      if (!uploadOk) {
        try { forgetChatBlob(realMsgId); } catch { /* */ }
      }
      setDriverChatUploading(false);
      setDriverChatUploadProgress(0);
      setDriverChatUploadingId(null);
    }
  };

  const handleClearDriverChat = async () => {
    if (!selectedDriverOrder?.orderId || !telegramChatId) return;
    
    // Confirmer avant de nettoyer
    if (!window.confirm('Êtes-vous sûr de vouloir nettoyer cette conversation ?\n\nLes messages suivants seront supprimés :\n• ✅ Temps exacte défini\n• ⏱️ Temps exacte\n• Tous les autres messages de la conversation')) {
      return;
    }
    
    try {
      // Appeler l'API pour nettoyer la conversation (inclut les messages de temps exact)
      const response = await fetchWithTimeout(`${BACKEND_URL}/clear-conversation/${selectedDriverOrder.orderId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Chat-Id': telegramChatId // Identifier qui nettoie la conversation
        },
        body: JSON.stringify({
          includeTimeMessages: true, // Inclure les messages de temps exact dans le nettoyage
          includeTimeExactMessages: true, // Inclure les messages "✅ Temps exacte défini"
          includeTimeEstimateMessages: true, // Inclure les messages "⏱️ Temps exacte"
          chatId: telegramChatId // Pour identifier le livreur
        })
      }, 10000);
      
      const data = await response.json();
      
      if (data.success) {
        // Recharger les messages après nettoyage
        await syncDriverChat(selectedDriverOrder.orderId);
        toast.success(t('conversationCleaned'));
      } else {
        toast.error(t('errorCleaningConvo'));
      }
    } catch (error) {
      console.error('Erreur nettoyage conversation:', error);
      toast.error(t('errorCleaningConvo'));
    }
  };

  useEffect(() => {
    if (isDriverChatOpen && !isDriverChatMinimized) {
      setDriverChatUnread(0);
    }
  }, [isDriverChatOpen, isDriverChatMinimized]);

  const handlePriorityUpgrade = async () => {
    if (!activeOrder?.orderId || activeOrder?.isPriority || priorityUpgradeLoading) return;
    setPriorityUpgradeLoading(true);
    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${activeOrder.orderId}/upgrade-priority`, {
        method: 'POST'
      }, 10000);
      // Valider que la réponse est bien du JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Réponse invalide du serveur');
      }
      const data = await response.json();
      if (data.success && data.order) {
        setActiveOrder((prev) => prev ? { ...prev, isPriority: true, price: data.order.price } : prev);
        setShowPriorityPopup(false);
        toast.success(t('orderPriority'));
        
        // Rafraîchir immédiatement les données du backend pour obtenir le temps recalculé
        try {
          const statusRes = await fetchWithTimeout(`${BACKEND_URL}/status/${activeOrder.orderId}`, {}, 10000);
          const statusData = await statusRes.json();
          if (statusData.success && statusData.queue) {
            const newQueueInfo = {
              ordersAhead: statusData.queue.ordersAhead ?? 0,
              estimatedWaitMinutes: statusData.queue.estimatedWaitMinutes ?? 0,
              totalActive: statusData.queue.totalActive ?? 0,
              extraDelayMinutes: statusData.queue.extraDelayMinutes ?? 0,
              priorityReductionMinutes: statusData.queue.priorityReductionMinutes ?? 0,
              effectiveMinutes: statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
              remainingMinutes: statusData.queue.remainingMinutes ?? statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
              hasEnRouteOrdersAhead: statusData.queue.hasEnRouteOrdersAhead ?? false
            };
            setQueueInfo(newQueueInfo);
            
            if (statusData.status >= 1 && newQueueInfo.remainingMinutes !== undefined) {
              const backendRemainingTime = Math.max(Math.round(newQueueInfo.remainingMinutes), 0);
              setCountdownMinutes(backendRemainingTime);
              lastBackendTimeRef.current = backendRemainingTime;
              console.log(`🚚 Commande passée en prioritaire ! Temps restant: ${backendRemainingTime} min`);
            }
          }
        } catch (refreshError) {
          console.error('Erreur rafraîchissement données:', refreshError);
        }
      } else {
        toast.error(data.message || t('cantUpgradePriority'));
      }
    } catch (error) {
      console.error('Erreur upgrade priorité:', error);
      toast.error(t('networkError2'));
    } finally {
      setPriorityUpgradeLoading(false);
    }
  };

  const categories = [
    { id: 'hash',   name: 'HASH',    image: '/emoji/choco1.png'  },
    { id: 'weed',   name: 'WEED',    image: '/emoji/weed2.png'  },
    { id: 'pharma', name: 'PHARMA',  image: '/emoji/pharma1.png' },
    { id: 'others', name: 'OTHERS',  image: '/emoji/gala1.png'  },
  ];

  // ── Produits en rupture de stock ──────────────────────────────
  // Produits en stock : LA MOUSSE + Xanax + Oxcycodon + Golden Bloom
  const isProductSoldOut = (product) => {
    if (!product) return false;
    // Aperçu Access temporaire : tout disponible (pas de rupture)
    if (shopPreviewMode) return false;
    // LA MOUSSE = id 6, sans variant
    if (product.id === 6 && !product.variant) return false;
    // Xanax, Oxcycodon, Golden Bloom, Makatussin Generic, Bubble Hash, Magic Chocolate, Dry (One Piece), Double Static → en stock
    if (['xanax', 'oxycodo', 'goldenBloom', 'makatussin', 'bubbleHash', 'polkadot', 'dryOnePiece', 'plasmaStatic'].includes(product.variant)) return false;
    // Tout le reste est en rupture
    return true;
  };
  // Catégories entièrement épuisées (tous les produits de la catégorie sont sold out)
  const isCategorySoldOut = (categoryId) => {
    const prods = products[categoryId] || [];
    if (prods.length === 0) return false;
    return prods.every(p => isProductSoldOut(p));
  };

  const quantityOptions = {
    Cali: [
      { amount: 5, price: 50, label: '5G' },
      { amount: 10, price: 80, label: '10G' },
      { amount: 25, price: 160, label: '25G' },
      { amount: 50, price: 280, label: '50G' },
      { amount: 100, price: 500, label: '100G', treasure: true }
    ],
    mousse: [
      { amount: 5, price: 30, label: '5G' },
      { amount: 10, price: 50, label: '10G' },
      { amount: 25, price: 90, label: '25G' },
      { amount: 50, price: 160, label: '50G' },
      { amount: 75, price: 230, label: '75G' },
      { amount: 100, price: 280, label: '100G', treasure: true }
    ],
    mousseDry: [
      { amount: 5, price: 80, label: '5G'},
      { amount: 10, price: 140, label: '10G', treasure: true },
      { amount: 25, price: 300, label: '25G' },
      { amount: 50, price: 500, label: '50G' },
      { amount: 100, price: 800, label: '100G' }
    ],
    // DRY (nouvelle version, vidéo onepiece.mp4) — mêmes prix de revente que DRYTECH X GOLDENFARM,
    // seul le prix de touche (custom <5g) change (voir calculateBulkPrice)
    mousseDryOnePiece: [
      { amount: 5, price: 80, label: '5G'},
      { amount: 10, price: 140, label: '10G', treasure: true },
      { amount: 25, price: 300, label: '25G' },
      { amount: 50, price: 500, label: '50G' },
      { amount: 100, price: 800, label: '100G' }
    ],
    mousseStatic: [
      { amount: 1, price: 50, label: '1G', treasure: true },
      { amount: 2, price: 90, label: '2G', treasure: true },
      { amount: 5, price: 170, label: '5G', treasure: true },
      { amount: 10, price: 300, label: '10G' },
      { amount: 25, price: 600, label: '25G' },
      { amount: 50, price: 1150, label: '50G' }
    ],
    mousseFrozen: [
      { amount: 1, price: 40, label: '1G', treasure: true },
      { amount: 2, price: 70, label: '2G'},
      { amount: 5, price: 150, label: '5G', treasure: true },
      { amount: 10, price: 280, label: '10G' },
      { amount: 25, price: 580, label: '25G' },
      { amount: 50, price: 1100, label: '50G' }
    ],
    bubbleHash: [
      { amount: 2, price: 60, label: '2G' },
      { amount: 3, price: 80, label: '3G' },
      { amount: 5, price: 130, label: '5G', treasure: true },
    ],
    Baida: [
      { amount: 0.5, price: 50, label: '0.5G' },
      { amount: 1, price: 80, label: '1G' },
      { amount: 5, price: 330, label: '5G', treasure: true },
      { amount: 10, price: 600, label: '10G' },
      { amount: 50, price: 2200, label: '50G' }
    ],
    BaidaSuisse: [
      { amount: 0.5, price: 50, label: '0.5G' },
      { amount: 1, price: 80, label: '1G' },
      { amount: 2, price: 150, label: '2G' },
      { amount: 3, price: 220, label: '3G', treasure: true },
      { amount: 5, price: 330, label: '5G', treasure: true },
      { amount: 10, price: 600, label: '10G' }
    ],
    STILPANE: [
      { amount: 1, price: 100, label: '100ML'},
      { amount: 2, price: 200, label: '200ML', treasure: true },
    ],
    makatussin: [
      { amount: 1,  price: 140,  label: '1 flacon' },
      { amount: 2,  price: 260,  label: '2 flacons', treasure: true },
      { amount: 3,  price: 370,  label: '3 flacons' },
      { amount: 4,  price: 480,  label: '4 flacons' },
      { amount: 5,  price: 630,  label: '5 flacons', treasure: true },
      { amount: 10, price: 1100, label: '10 flacons' },
    ],
    oxycodo: [
      { amount: 1, price: 40, label: '1 PILL' },
      { amount: 2, price: 70, label: '2 PILLS' },
      { amount: 5, price: 140, label: '5 PILLS' },
      { amount: 10, price: 200, label: '10 PILLS' },
      { amount: 60, price: 800, label: '60 PILLS' }
    ],
    Xanax: [
      { amount: 5, price: 50, label: '5 bar', unit: 'bar' },
      { amount: 10, price: 70, label: '10 bar', unit: 'bar' },
      { amount: 20, price: 120, label: '20 bar', unit: 'bar' },
      { amount: 50, price: 200, label: '50 bar', unit: 'bar' },
      { amount: 100, price: 350, label: '100 bar', unit: 'bar', treasure: true }
    ],
    mousseWPFF: [
      { amount: 1, price: 30, label: '1G' },
      { amount: 2, price: 50, label: '2G' },
      { amount: 5, price: 120, label: '5G' },
      { amount: 10, price: 200, label: '10G', treasure: true  }
    ],
    mousseDryKinder: [
      { amount: 5, price: 40, label: '5G' },
      { amount: 10, price: 70, label: '10G' },
      { amount: 25, price: 130, label: '25G' },
      { amount: 50, price: 250, label: '50G' },
      { amount: 75, price: 380, label: '75G' },
      { amount: 100, price: 440, label: '100G', treasure: true }
    ],
    goldenBloom: [
      { amount: 1,  price: 50,  label: '1 PEN',  unit: 'pen' },
      { amount: 3,  price: 180, label: '3 PEN',  unit: 'pen', treasure: true },
      { amount: 5,  price: 280, label: '5 PEN',  unit: 'pen' },
      { amount: 10, price: 500, label: '10 PEN', unit: 'pen' },
    ],
    polkadot: [
      { amount: 1, price: 150, label: '1 bar', unit: 'bar' },
      { amount: 2, price: 220, label: '2 bar', unit: 'bar' },
      { amount: 3, price: 310, label: '3 bar', unit: 'bar', treasure: true },
    ],
    grizzlyBoys: [
      { amount: 3.5, price: 60, label: '3.5G' },
      { amount: 7, price: 110, label: '7G' },
      { amount: 10.5, price: 170, label: '10.5G' }
    ],
    wholeMelt: [
      { amount: 1,  price: 50,  label: '1G',  treasure: true },
      { amount: 2,  price: 70,  label: '2G' },
      { amount: 5,  price: 140, label: '5G' },
      { amount: 10, price: 170, label: '10G', treasure: true },
      { amount: 28, price: 350, label: '28G' },
    ],
    caliPlates: [
      { amount: 1,   price: 30,  label: '1G',  treasure: true },
      { amount: 2.5, price: 60,  label: '2.5G' },
      { amount: 5,   price: 120, label: '5G' },
      { amount: 10,  price: 180, label: '10G', treasure: true },
      { amount: 25,  price: 380, label: '25G' },
      { amount: 50,  price: 700, label: '50G' },
    ],
    quagen: [
      { amount: 1, price: 500, label: '1 flacon', treasure: true },
    ],
    plasmaStatic: [
      { amount: 1,   price: 30,  label: '1G',  treasure: true },
      { amount: 2.5, price: 60,  label: '2.5G' },
      { amount: 5,   price: 120, label: '5G' },
      { amount: 10,  price: 180, label: '10G', treasure: true },
      { amount: 25,  price: 390, label: '25G' },
      { amount: 50,  price: 700, label: '50G' },
    ],
    gorillaGlue: [
      { amount: 5,   price: 60,  label: '5G' },
      { amount: 10,  price: 90,  label: '10G', treasure: true },
      { amount: 15,  price: 130, label: '15G' },
      { amount: 25,  price: 160, label: '25G' },
      { amount: 50,  price: 290, label: '50G' },
      { amount: 100, price: 520, label: '100G', treasure: true },
    ],
  };  

  const products = {
    // ─── Nouvelles catégories UI ──────────────────────────────────
    hash: [
      { id: 28, name: 'DRY🍫',                 description: 'DRY - Premium',                      video: '/video/onepiece.mp4', badge: '🍫 Premium',    variant: 'dryOnePiece',  legacyCategory: 'mousse' },
      { id: 6,  name: 'LA MOUSSE',             description: 'Premium Mousse - Texture mystique',  video: '/video/STAN1.mov',    badge: '🧽 Défonce+',                           legacyCategory: 'mousse' },
      { id: 7,  name: 'SAHAS STATIC',          description: 'Static Sift 130 - 73u',              video: '/video/tic1.mov',     badge: '🍂 Premium',   variant: 'static',      legacyCategory: 'mousse' },
      { id: 11, name: 'SAHAS FROZEN 73U',      description: 'Frozen 130 - 73u',                   video: '/video/tic2.mov',     badge: '🧱 Frozen',    variant: 'frozen',      legacyCategory: 'mousse' },
      { id: 8,  name: 'Bubble Hash 🫧',         description: 'Bubble Hash - Premium',              video: '/video/bubble1.mp4',  badge: '🫧 Premium',   variant: 'bubbleHash',  legacyCategory: 'mousse' },
      { id: 18, name: 'The Whole Melt🍯🇺🇸',   description: 'Live Resin - Premium USA',            video: '/video/melt.mov',     badge: '🍯🇺🇸',         variant: 'wholeMelt',   legacyCategory: 'mousse' },
      { id: 23, name: 'Cali Plates 🇺🇸',       description: 'Premium Dry Sift',                   video: '/video/plate1.mov',   badge: '🇺🇸 Premium',   variant: 'caliPlates',  legacyCategory: 'mousse' },
      { id: 25, name: 'Double Static 🇲🇦', description: 'Double Static - Maroc Premium',     video: '/video/tic3.mp4',   badge: '🇲🇦 Double',     variant: 'plasmaStatic', legacyCategory: 'mousse' },
    ],
    weed: [
      { id: 26, name: 'Gorilla Glue 🦍',        description: 'Premium Weed - Qualité top shelf',   video: '/emoji/Glue.mov',     badge: '🦍',            variant: 'gorillaGlue',    legacyCategory: 'Cali'   },
      { id: 16, name: 'DOSIDOS CANADA🇨🇦',     description: 'Canada Cali - Qualité premium',      video: '/video/dosidos.mov',  badge: '🇨🇦',           variant: 'dosidos',        legacyCategory: 'Cali'   },
      { id: 5,  name: 'WHITE RUNTZ🌺',          description: 'Canada Cali - Qualité premium',      video: '/video/WWD1.mov',     badge: '🌺',            variant: 'whiteRunz',      legacyCategory: 'Cali'   },
    ],
    others: [
      { id: 21, name: 'GOLDEN BLOOM 🇺🇸',       description: 'premium vape pen 🖊️',               video: '/video/puff.mov',     badge: '🖊️ Vape Pen',   variant: 'goldenBloom',    legacyCategory: 'goldenBloom' },
      { id: 27, name: 'MAGIC CHOCOLATE 🍫',       description: 'Magic Chocolate Bar',               video: '/video/polka.mp4',    badge: '🍫 Edible',      variant: 'polkadot',       legacyCategory: 'polkadot' },
    ],
    pharma: [
      { id: 13, name: 'XANAX 🇨🇭',             description: 'XANAX',                              video: '/video/xan1.mov',     badge: '🇨🇭 Pharma',    variant: 'xanax',          legacyCategory: 'STILPANE' },
      { id: 20, name: 'Oxcycodon 💊',           description: 'Oxycodone 80MG - Pharma',            video: '/video/oxcy1.mov',    badge: '💊 Pharma',     variant: 'oxycodo',        legacyCategory: 'STILPANE' },
      { id: 10, name: 'STILPANE',               description: 'STILPANE SYRUP 100ML',               video: '/video/syrup1.mov',   badge: '🥤🇿🇦 HARD',                               legacyCategory: 'STILPANE' },
      { id: 22, name: 'Makatussin Generic 🥤',  description: 'Makatussin Generic 80ml',            video: '/video/maka1.mov',    badge: '🥤 Pharma',     variant: 'makatussin',     legacyCategory: 'STILPANE' },
      { id: 24, name: 'Quagen 1180ml🥤🇺🇸',     description: 'Quagen Syrup 1180ml',                video: '/video/quagen.mov',   badge: '🥤🇺🇸 HARD',     variant: 'quagen',         legacyCategory: 'STILPANE' },
    ],
    // ─── Catégories legacy (compatibilité prix / logique interne) ─
    mousse: [
      { id: 6,  name: 'LA MOUSSE',              description: 'Premium Mousse - Texture mystique', video: '/video/STAN1.mov',  badge: '🧽 Défonce+' },
      { id: 28, name: 'DRY🍫',                  description: 'DRY - Premium',                     video: '/video/onepiece.mp4', badge: '🍫 Premium', variant: 'dryOnePiece' },
      { id: 7,  name: 'SAHAS STATIC - KING LOUIS', description: 'Static Sift 130 - 73u',          video: '/video/tic1.mov',   badge: '🍂 Premium',  variant: 'static' },
      { id: 11, name: ' SAHAS FROZEN 73U',       description: 'Frozen 130 - 73u ',                video: '/video/tic2.mov',   badge: '🧱 Frozen',   variant: 'frozen' },
      { id: 8,  name: 'Bubble Hash 🫧',           description: 'Bubble Hash - Premium',            video: '/video/bubble1.mp4', badge: '🫧 Premium',  variant: 'bubbleHash' },
      { id: 18, name: 'The Whole Melt🍯🇺🇸',    description: 'Live Resin - Premium USA',          video: '/video/melt.mov',   badge: '🍯🇺🇸',        variant: 'wholeMelt' },
      { id: 23, name: 'Cali Plates 🇺🇸',        description: 'Premium Dry Sift',                 video: '/video/plate1.mov', badge: '🇺🇸 Premium',  variant: 'caliPlates' },
      { id: 25, name: 'Double Static 🇲🇦', description: 'Double Static - Maroc Premium',    video: '/video/tic3.mp4', badge: '🇲🇦 Double',    variant: 'plasmaStatic' },
    ],
    Cali: [
      { id: 5, name: 'WHITE RUNTZ🌺', description: 'Canada Cali - Qualité premium', video: '/video/WWD1.mov', badge: '🌺', variant: 'whiteRunz' },
      { id: 16, name: 'DOSIDOS CANADA🇨🇦', description: 'Canada Cali - Qualité premium', video: '/video/dosidos.mov', badge: '🇨🇦', variant: 'dosidos' },
      { id: 17, name: 'GRIZZLYBOYZ 🇺🇸', description: 'USA Cali - Qualité premium', video: '/video/boys.mov', badge: '🇺🇸', variant: 'grizzlyBoys' }
    ],
    STILPANE: [
      { id: 10, name: 'STILPANE', description: 'STILPANE SYRUP 100ML', video: '/video/syrup1.mov', badge: '🥤🇿🇦 HARD' },
      { id: 13, name: 'XANAX 🇨🇭', description: 'XANAX', video: '/video/xan1.mov', badge: '🇨🇭 Pharma', variant: 'xanax' },
      { id: 20, name: 'Oxcycodon 💊', description: 'Oxycodone 80MG - Pharma', video: '/video/oxcy1.mov', badge: '💊 Pharma', variant: 'oxycodo' },
      { id: 22, name: 'Makatussin Generic 🥤', description: 'Makatussin Generic 80ml', video: '/video/maka1.mov', badge: '🥤 Pharma', variant: 'makatussin' },
      { id: 24, name: 'Quagen 1180ml🥤🇺🇸', description: 'Quagen Syrup 1180ml', video: '/video/quagen.mov', badge: '🥤🇺🇸 HARD', variant: 'quagen' },
    ],
    goldenBloom: [
      { id: 21, name: 'GOLDEN BLOOM 🇺🇸', description: 'premium vape pen 🖊️', video: '/video/puff.mov', badge: '🖊️ Vape Pen', variant: 'goldenBloom' },
    ],
  };

  const handleCategoryClick = (categoryId) => {
    if (!isShopOpen) return;
    setSelectedUICategory(categoryId);
    const categoryProducts = products[categoryId] || [];
    
    if (categoryProducts.length > 0) {
      const firstProduct = categoryProducts[0];
      // Utiliser la catégorie legacy (pour les calculs de prix) si disponible
      const legacyCat = firstProduct.legacyCategory || categoryId;
      setSelectedCategory(legacyCat);
      setSelectedProduct(firstProduct);
      setSelectedQuantity(null);
      setIsBulkMode(false);
      // Tracker le clic produit
      trackEvent('product_click', { productName: firstProduct?.name || categoryId });
      // setShowBulkSection supprimé - mode bulk désactivé
      setCurrentView('product-detail');
    }
  };

  const calculateBulkPrice = (grams) => {
    // Pour Canada Cali, utiliser un prix dégressif basé sur la quantité
    let basePricePerGram;
    if (selectedCategory === 'Cali') {
      if (selectedProduct?.variant === 'grizzlyBoys') {
        // GRIZZLYBOYS: 3.5G=60, 7G=110, 10.5G=170
        if (grams >= 10.5) {
          basePricePerGram = 170 / 10.5;
        } else if (grams >= 7) {
          basePricePerGram = 110 / 7;
        } else {
          basePricePerGram = 60 / 3.5;
        }
      } else if (selectedProduct?.variant === 'gorillaGlue') {
        // GORILLA GLUE: 5G=60, 10G=90, 15G=130, 25G=160, 50G=290, 100G=520
        if (grams >= 100) {
          basePricePerGram = 520 / 100;
        } else if (grams >= 50) {
          basePricePerGram = 290 / 50;
        } else if (grams >= 25) {
          basePricePerGram = 160 / 25;
        } else if (grams >= 15) {
          basePricePerGram = 130 / 15;
        } else if (grams >= 10) {
          basePricePerGram = 90 / 10;
        } else {
          basePricePerGram = 60 / 5;
        }
      } else {
        // WHITE RUNZ / DOSIDOS - Prix dégressif (5g=50, 10g=80, 25g=160, 50g=280, 100g=500)
        if (grams >= 100) {
          basePricePerGram = 5;
        } else if (grams >= 50) {
          basePricePerGram = 5.6;
        } else if (grams >= 25) {
          basePricePerGram = 6.4;
        } else if (grams >= 10) {
          basePricePerGram = 8;
        } else {
          basePricePerGram = 10;
        }
      }
    } else if (selectedCategory === 'mousse') {
      // Prix dégressif selon la variante de mousse
      if (selectedProduct?.variant === 'dry') {
        // Variante DRY (basé sur les tarifs: 5g=90, 10g=160, 20g=230, 50g=500, 100g=900)
        if (grams >= 100) {
          basePricePerGram = 8; // 100g = 800
        } else if (grams >= 50) {
          basePricePerGram = 10; // 50g = 500
        } else if (grams >= 25) {
          basePricePerGram = 12; // 25g = 300
        } else if (grams >= 10) {
          basePricePerGram = 14; // 10g = 140
        } else {
          basePricePerGram = 16; // 5g = 80
        }
      } else if (selectedProduct?.variant === 'dryOnePiece') {
        // Variante DRY (nouvelle version) — mêmes prix de revente que DRYTECH X GOLDENFARM
        if (grams >= 100) {
          basePricePerGram = 8; // 100g = 800
        } else if (grams >= 50) {
          basePricePerGram = 10; // 50g = 500
        } else if (grams >= 25) {
          basePricePerGram = 12; // 25g = 300
        } else if (grams >= 10) {
          basePricePerGram = 14; // 10g = 140
        } else {
          basePricePerGram = 16; // 5g = 80
        }
      } else if (selectedProduct?.variant === 'static') {
        // Variante STATIC (basé sur les tarifs: 1g=50, 2g=80, 5g=180, 10g=320, 25g=750, 50g=1400)
        if (grams >= 50) {
          basePricePerGram = 28; // 50g = 1400, donc 28/g
        } else if (grams >= 25) {
          basePricePerGram = 30; // 25g = 750, donc 30/g
        } else if (grams >= 10) {
          basePricePerGram = 32; // 10g = 320, donc 32/g
        } else if (grams >= 5) {
          basePricePerGram = 36; // 5g = 180, donc 36/g
        } else if (grams >= 2) {
          basePricePerGram = 40; // 2g = 80, donc 40/g
        } else {
          basePricePerGram = 50; // 1g = 50, donc 50/g
        }
      } else if (selectedProduct?.variant === 'frozen') {
        // Variante FROZEN (basé sur les tarifs: 1g=40, 2g=70, 5g=160, 10g=300, 25g=670, 50g=1300)
        if (grams >= 50) {
          basePricePerGram = 26; // 50g = 1300, donc 26/g
        } else if (grams >= 25) {
          basePricePerGram = 26.8; // 25g = 670, donc 26.8/g
        } else if (grams >= 10) {
          basePricePerGram = 30; // 10g = 300, donc 30/g
        } else if (grams >= 5) {
          basePricePerGram = 32; // 5g = 160, donc 32/g
        } else if (grams >= 2) {
          basePricePerGram = 35; // 2g = 70, donc 35/g
        } else {
          basePricePerGram = 40; // 1g = 40, donc 40/g
        }
      } else if (selectedProduct?.variant === 'bubbleHash') {
        // Variante BUBBLE HASH (basé sur les tarifs: 2g=60, 3g=80, 5g=130)
        if (grams >= 5) {
          basePricePerGram = 130 / 5; // 26/g
        } else if (grams >= 3) {
          basePricePerGram = 80 / 3; // ≈26.67/g
        } else if (grams >= 2) {
          basePricePerGram = 60 / 2; // 30/g
        } else {
          basePricePerGram = 7.5; // Prix de touche (moins de 2g)
        }
      } else if (selectedProduct?.variant === 'wpff') {
        // WPFF SINALOA CAKE: 1G=30, 2G=50, 5G=100, 10G=170, 25G=350, 50G=500
        if (grams >= 50) {
          basePricePerGram = 10; // 50g = 500
        } else if (grams >= 25) {
          basePricePerGram = 14; // 25g = 350
        } else if (grams >= 10) {
          basePricePerGram = 17; // 10g = 170
        } else if (grams >= 5) {
          basePricePerGram = 20; // 5g = 100
        } else if (grams >= 2) {
          basePricePerGram = 25; // 2g = 50
        } else {
          basePricePerGram = 30; // 1g = 30
        }
      } else if (selectedProduct?.variant === 'wholeMelt') {
        // THE WHOLE MELT: 1G=50, 2G=70, 5G=140, 10G=170, 28G=350
        if (grams >= 28) {
          basePricePerGram = 350 / 28;
        } else if (grams >= 10) {
          basePricePerGram = 17; // 10g = 170
        } else if (grams >= 5) {
          basePricePerGram = 28; // 5g = 140
        } else if (grams >= 2) {
          basePricePerGram = 35; // 2g = 70
        } else {
          basePricePerGram = 50; // 1g = 50
        }
      } else if (selectedProduct?.variant === 'caliPlates') {
        // CALI PLATES: 1G=30, 2.5G=60, 5G=120, 10G=180, 25G=380, 50G=700
        if (grams >= 50) {
          basePricePerGram = 14; // 50g = 700
        } else if (grams >= 25) {
          basePricePerGram = 380 / 25;
        } else if (grams >= 10) {
          basePricePerGram = 18; // 10g = 180
        } else if (grams >= 5) {
          basePricePerGram = 24; // 5g = 120
        } else if (grams >= 2.5) {
          basePricePerGram = 24; // 2.5g = 60
        } else {
          basePricePerGram = 30; // 1g = 30
        }
      } else {
        // Variante par défaut jaune mousse (basé sur les tarifs: 5g=30, 10g=50, 25g=100, 50g=180, 75g=230, 100g=300)
        if (grams >= 100) {
          basePricePerGram = 3; // 100g = 300, donc 3/g    → profit = 100 CHF
        } else if (grams >= 75) {
          return Math.round(getPassPrice(230, selectedProduct?.name || '')); // 75g = 230 (prix fixe) → profit = 80 CHF
        } else if (grams >= 50) {
          basePricePerGram = 3.6; // 50g = 180, donc 3.6/g → profit = 60 CHF
        } else if (grams >= 25) {
          basePricePerGram = 4; // 25g = 100, donc 4/g    → profit = 40 CHF
        } else if (grams >= 10) {
          basePricePerGram = 5; // 10g = 50, donc 5/g     → profit = 30 CHF ✓
        } else {
          basePricePerGram = 6; // 5g = 30, donc 6/g      → profit = 20 CHF
        }
      }
    } else if (selectedCategory === 'Baida') {
      // Prix dégressif pour Baida (basé sur les tarifs: 0.5g=40, 1g=70, 5g=320, 10g=500, 50g=2200)
      if (grams >= 50) {
        basePricePerGram = 44; // 50g = 2200, donc 44/g
      } else if (grams >= 10) {
        basePricePerGram = 50; // 10g = 500, donc 50/g
      } else if (grams >= 5) {
        basePricePerGram = 64; // 5g = 320, donc 64/g
      } else if (grams >= 1) {
        basePricePerGram = 70; // 1g = 70, donc 70/g
      } else {
        basePricePerGram = 80; // 0.5g = 40, donc 80/g
      }
    } else {
      basePricePerGram = 10; // Prix par défaut
    }
    const totalBasePrice = grams * basePricePerGram;
    const productName = selectedProduct?.name || '';
    return Math.round(getPassPrice(totalBasePrice, productName));
  };

  // Sauvegarder le panier dans localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Fonction pour ajouter un article au panier
  const handleAddToCart = async () => {
    if (shopPreviewMode) {
      openAccessPub();
      return;
    }
    // Bloquer l'ajout si le produit est en rupture de stock
    if (isProductSoldOut(selectedProduct)) {
      setShowSoldOutNotif(true);
      setTimeout(() => setShowSoldOutNotif(false), 3200);
      return;
    }
    // Pour Canada Cali, Premium Mousse et STILPANE, le mode bulk n'est pas autorisé - seulement les quantités fixes
    if ((selectedCategory === 'Cali' || selectedCategory === 'mousse' || selectedCategory === 'STILPANE' || selectedCategory === 'goldenBloom' || selectedCategory === 'polkadot') && isBulkMode) {
      const productName = selectedCategory === 'Cali' ? 'Canada Cali' :
                          selectedCategory === 'mousse' ? 'Premium Mousse' :
                          selectedCategory === 'goldenBloom' ? 'Golden Bloom' :
                          selectedCategory === 'polkadot' ? 'Magic Chocolate' :
                          'Stilpane Syrup';
      const quantities = selectedCategory === 'Cali' ? '5g, 10g, 25g, 50g ou 100g' :
                        selectedCategory === 'mousse' ? '5g, 10g, 25g, 50g ou 100g' :
                        selectedCategory === 'polkadot' ? '1, 2 ou 3 bar' :
                        '100ML, 200ML, 300ML, 400ML ou 500ML';
      addFomoNotification(t('selectFixedQuantity', productName, quantities), 'error');
      setIsBulkMode(false);
      // setShowBulkSection supprimé - mode bulk désactivé
      return;
    }
    
    if (!selectedProduct || (!selectedQuantity && !isBulkMode)) {
      addFomoNotification(t('selectProductQuantity'), 'error');
      return;
    }

    if (!deliveryAddress.trim()) {
      addFomoNotification('Veuillez entrer une adresse de livraison', 'error');
      return;
    }

    // Si first add + pas pass or + pas encore dismissed → afficher popup upsell gold
    if (userPass !== 'or' && !goldUpsellDismissed && cart.length === 0) {
      setShowGoldUpsellPopup(true);
      return; // On suspend l'ajout, le popup va gérer la suite
    }

    // Afficher l'écran de chargement
    setIsAddingToCart(true);

    // Calculer les valeurs
    let finalQuantity;
    let basePrice;
    let quantityGrams;
    
    if (isBulkMode) {
      basePrice = calculateBulkPrice(bulkQuantity);
      quantityGrams = bulkQuantity;
      finalQuantity = `${bulkQuantity}g`;
    } else {
      basePrice = Math.round(getPassPrice(selectedQuantity.price, selectedProduct?.name || ''));
      quantityGrams = selectedQuantity.amount;
      finalQuantity = selectedQuantity.unit === 'bar' ? selectedQuantity.label : selectedQuantity.unit === 'pen' ? selectedQuantity.label : `${selectedQuantity.amount}g`;
    }

    // Vérifier le code de parrainage si fourni
    const hasParrainageCode = parrainageCodeInput.trim().length > 0;
    const parrainageCodeToUse = hasParrainageCode ? parrainageCodeInput.trim().toUpperCase() : null;
    
    // Valider le code de parrainage si fourni
    if (parrainageCodeToUse) {
      const isOwnCode = parrainageCodeToUse === referralCode;
      const codesUtilises = JSON.parse(localStorage.getItem('parrainageCodesUtilises') || '[]');
      const isCodeAlreadyUsed = codesUtilises.includes(parrainageCodeToUse);
      
      if (isOwnCode) {
        setIsAddingToCart(false);
        addFomoNotification('Vous ne pouvez pas utiliser votre propre code de parrainage', 'error');
        return;
      }
      
      if (isCodeAlreadyUsed) {
        setIsAddingToCart(false);
        addFomoNotification(t('referralAlreadyUsed'), 'error');
        return;
      }
      
      // Récupérer le chatId de multiples sources
      const getChatIdForValidation = () => {
        if (telegramChatId) return telegramChatId;
        const storedChatId = localStorage.getItem('chatId');
        if (storedChatId) return storedChatId;
        const urlParams = new URLSearchParams(window.location.search);
        const chatIdFromUrl = urlParams.get('chatId');
        if (chatIdFromUrl) return chatIdFromUrl;
        return '';
      };
      
      const chatIdForValidation = getChatIdForValidation();
      
      console.log('🔍 Validation parrainage:', {
        code: parrainageCodeToUse,
        chatId: chatIdForValidation || 'vide',
        url: `${BACKEND_URL}/client/validate-referral`
      });
      
      // Valider le code avec le backend
      try {
        const validationResponse = await fetchWithTimeout(
          `${BACKEND_URL}/client/validate-referral?referralCode=${encodeURIComponent(parrainageCodeToUse)}&chatId=${encodeURIComponent(chatIdForValidation)}`,
          {},
          10000 // 10 secondes au lieu de 5
        );
        
        console.log('📡 Réponse validation:', validationResponse.status);
        
        if (!validationResponse.ok) {
          console.error('❌ Erreur HTTP:', validationResponse.status);
          throw new Error(`Erreur HTTP: ${validationResponse.status}`);
        }
        
        const validationData = await validationResponse.json();
        console.log('✅ Data validation:', validationData);
        
        if (!validationData.success || !validationData.valid) {
          setIsAddingToCart(false);
          addFomoNotification(validationData.message || 'Code de parrainage invalide.', 'error');
          return;
        }
        
        console.log('✅ Code de parrainage validé avec succès');
      } catch (validationError) {
        console.error('❌ Exception validation:', validationError);
        console.error('❌ Message:', validationError?.message);
        setIsAddingToCart(false);
        addFomoNotification('Erreur lors de la validation du code de parrainage.', 'error');
        return;
      }
    }

    const hasParrainageBonusCheck = hasParrainageBonus || (parrainageCodeToUse && parrainageCodeToUse !== referralCode);
    
    const isBonusExcluded = isProductExcludedFromBonus(
      selectedProduct?.name,
      selectedProduct?.variant
    );

    // Si le panier contient déjà des articles ET qu'un code de parrainage est utilisé, demander à quel produit ajouter le bonus
    if (hasParrainageBonusCheck && !isBonusExcluded && cart.length > 0) {
      // Stocker les informations en attente
      setPendingParrainageCode(parrainageCodeToUse);
      setPendingParrainageItem({
        id: Date.now() + Math.random(),
        category: selectedCategory,
        product: selectedProduct,
        quantity: finalQuantity,
        quantityGrams: quantityGrams,
        price: basePrice,
        basePrice: basePrice,
        isBulk: isBulkMode,
        bulkQuantity: isBulkMode ? bulkQuantity : null,
        selectedQuantity: !isBulkMode ? selectedQuantity : null,
        parrainageCode: parrainageCodeToUse,
        hasBonus1g: false // Ne pas ajouter le bonus maintenant, attendre la sélection
      });
      setShowParrainageProductModal(true);
      setIsAddingToCart(false); // ⭐ AJOUT: Masquer l'écran de chargement
      return;
    }
    
    // Si pas de panier existant ou pas de code de parrainage, ajouter directement avec le bonus si applicable (SAUF produits exclus)
    const isBarProduct = !isBulkMode && (selectedQuantity?.unit === 'bar' || selectedQuantity?.unit === 'pen');
    if (hasParrainageBonusCheck && cart.length === 0 && !isBonusExcluded && !isBarProduct) {
      quantityGrams += referralBonusGrams || 2;
      finalQuantity = `${quantityGrams}g`;
    }

    const { wheelBonusGrams, bonusProduct } = pickWheelBonusForProduct(
      wheelBonuses,
      selectedProduct?.name,
      selectedProduct?.variant
    );

    // Ajouter le bonus de la roue à la quantité (pas pour les produits en bar type Xanax)
    // IMPORTANT: Le bonus roue ne s'applique QUE sur le premier produit du panier (cart vide)
    const allowWheelBonus = wheelBonusGrams > 0 && !isBarProduct && cart.length === 0
      && (!isBonusExcluded || bonusProduct === 'SAHAS');
    if (allowWheelBonus) {
      quantityGrams += wheelBonusGrams;
      finalQuantity = `${quantityGrams}g`;
    }

    // Calculer le prix final (sans livraison prioritaire pour le moment, sera ajouté au total du panier)
    const finalPrice = basePrice; // Prix sans livraison prioritaire (sera calculé au total)

    // 🎮 Bonus jeu actif uniquement sur weed/hash (hors produits exclus)
    const activeGameBonus = (selectedCategory === 'Cali' || selectedCategory === 'mousse') && gameBonus2g > 0 && !isBonusExcluded ? gameBonus2g : 0;

    // Créer l'article du panier
    const cartItem = {
      id: Date.now() + Math.random(), // ID unique
      category: selectedCategory,
      uiCategory: selectedUICategory,
      product: selectedProduct,
      quantity: finalQuantity,
      quantityGrams: quantityGrams,
      price: finalPrice,
      basePrice: basePrice,
      isBulk: isBulkMode,
      bulkQuantity: isBulkMode ? bulkQuantity : null,
      selectedQuantity: !isBulkMode ? selectedQuantity : null,
      parrainageCode: parrainageCodeToUse,
      hasBonus1g: hasParrainageBonusCheck && cart.length === 0 && !isBonusExcluded,
      hasWheelBonus: wheelBonusGrams > 0,
      wheelBonusGrams: wheelBonusGrams,
      hasGameBonus: activeGameBonus > 0, // 🎮 Signal uniquement (le montant réel vient de la DB côté backend)
      wheelBonusProduct: bonusProduct // Type de bonus (MOUSS, DRY, CALI) - nécessaire pour la consommation lors de la commande
    };

    // Attendre 2-3 secondes pour l'animation de chargement
    await new Promise(resolve => setTimeout(resolve, 2500));

    // Ajouter au panier
    setCart(prevCart => [...prevCart, cartItem]);
    
    // Tracker l'ajout au panier
    trackEvent('cart_add', { productName: selectedProduct?.name || 'Inconnu' });
    
    // Masquer l'écran de chargement
    setIsAddingToCart(false);
    
    // Afficher notification de succès
    addFomoNotification(t('productAddedToCart', selectedProduct.name), 'success');
    
    // Réinitialiser les sélections (garder la catégorie et le produit)
    setSelectedQuantity(null);
    setIsBulkMode(false);
    // setShowBulkSection supprimé - mode bulk désactivé
    setBulkQuantity(100);
    setParrainageCodeInput('');
  };

  const handleApplyParrainageBonus = (selectedProductId) => {
    const bonusGrams = referralBonusGrams || 2;

    // Reste du code existant...
    // Si on vient du récapitulatif (pas de pendingParrainageItem), appliquer directement au produit sélectionné
    if (!pendingParrainageItem && pendingParrainageCode) {
      if (selectedProductId && selectedProductId !== 'new') {
        const selectedCartItem = cart.find(item => item.id === selectedProductId);
        const isExcluded = selectedCartItem && isProductExcludedFromBonus(
          selectedCartItem.product?.name,
          selectedCartItem.product?.variant
        );
        if (!isExcluded) {
          setCart(prevCart => prevCart.map(item => {
            if (item.id === selectedProductId) {
              const newQuantityGrams = item.quantityGrams + bonusGrams;
              return {
                ...item,
                quantity: `${newQuantityGrams}g`,
                quantityGrams: newQuantityGrams,
                hasBonus1g: true,
                parrainageCode: pendingParrainageCode || item.parrainageCode
              };
            }
            return item;
          }));
          addFomoNotification(t('bonus2gAddedShort'), 'success');
        }
        setParrainageCodeInput('');
        setShowParrainageProductModal(false);
        setPendingParrainageCode(null);
        return;
      }
    }

    // Si on vient de l'ajout au panier (avec pendingParrainageItem)
    if (!pendingParrainageItem) return;

    // Si l'utilisateur a choisi un produit existant dans le panier
    if (selectedProductId !== 'new') {
      const selectedCartItem = cart.find(item => item.id === selectedProductId);
      const isExcluded = selectedCartItem && isProductExcludedFromBonus(
        selectedCartItem.product?.name,
        selectedCartItem.product?.variant
      );
      if (!isExcluded) {
        setCart(prevCart => prevCart.map(item => {
          if (item.id === selectedProductId) {
            const newQuantityGrams = item.quantityGrams + bonusGrams;
            return {
              ...item,
              quantity: `${newQuantityGrams}g`,
              quantityGrams: newQuantityGrams,
              hasBonus1g: true,
              parrainageCode: pendingParrainageCode || item.parrainageCode
            };
          }
          return item;
        }));
        addFomoNotification(t('bonus2gAddedShort'), 'success');
      }
    } else {
      // Si l'utilisateur a choisi le nouveau produit
      const isExcluded = isProductExcludedFromBonus(
        pendingParrainageItem.product?.name,
        pendingParrainageItem.product?.variant
      );
      if (!isExcluded) {
        const newItem = {
          ...pendingParrainageItem,
          quantityGrams: pendingParrainageItem.quantityGrams + bonusGrams,
          quantity: `${pendingParrainageItem.quantityGrams + bonusGrams}g`,
          hasBonus1g: true
        };
        setCart(prevCart => [...prevCart, newItem]);
        addFomoNotification(t('productAddedWithBonus', pendingParrainageItem.product.name), 'success');
      } else {
        setCart(prevCart => [...prevCart, pendingParrainageItem]);
      }
    }

    // Réinitialiser les sélections
    setSelectedQuantity(null);
    setIsBulkMode(false);
    // setShowBulkSection supprimé - mode bulk désactivé
    setBulkQuantity(100);
    setParrainageCodeInput('');
    setShowParrainageProductModal(false);
    setPendingParrainageCode(null);
    setPendingParrainageItem(null);
  };
const confirmOrderNameAndPlace = async () => {
  const nameToSave = String(orderNameDraft || '').trim();
  if (!nameToSave || savingOrderName) return;
  const effectiveChatId =
    telegramChatId ||
    localStorage.getItem('telegramChatId') ||
    localStorage.getItem('chatId') ||
    getStoredNfcShopChatId() ||
    null;
  if (!effectiveChatId) {
    addFomoNotification('Session introuvable — rouvre l’app depuis Telegram.', 'error');
    return;
  }
  setSavingOrderName(true);
  try {
    try {
      const checkRes = await fetchWithTimeout(
        `${BACKEND_URL}/client/check-name?name=${encodeURIComponent(nameToSave)}&chatId=${effectiveChatId}`,
        { headers: getApiAuthHeaders(), credentials: 'include' },
        5000
      );
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (!checkData.available) {
          addFomoNotification(checkData.message || t('nameAlreadyUsed'), 'error');
          setSavingOrderName(false);
          return;
        }
      }
    } catch { /* soft — on tente quand même */ }

    try {
      const regRes = await fetchWithTimeout(`${BACKEND_URL}/client/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          chatId: effectiveChatId,
          name: nameToSave,
          pass: userPass || 'bronze',
          country: (typeof getEffectiveCountry === 'function' ? getEffectiveCountry() : selectedCountry) || 'suisse',
          city: selectedCity || '',
        }),
      }, 10000);
      if (regRes.status === 409) {
        const errData = await regRes.json().catch(() => ({}));
        addFomoNotification(errData.message || t('nameAlreadyUsed'), 'error');
        setSavingOrderName(false);
        return;
      }
    } catch { /* soft — la commande peut quand même partir */ }

    setUserName(nameToSave);
    setOrderName(nameToSave);
    setShowOrderNameModal(false);
    setSavingOrderName(false);
    await handleOrder(pendingOrderPaidWithCoins, { skipNameGate: true });
  } catch (e) {
    console.error('confirmOrderNameAndPlace:', e);
    setSavingOrderName(false);
    addFomoNotification(GENERIC_USER_ERROR, 'error');
  }
};

const handleOrder = async (paidWithCoins = false, opts = {}) => {
  // Protection contre les clics multiples
  if (isLoading) {
    return;
  }

  if (!isShopOpen) {
    alert(t('shopClosedAlert'));
    return;
  }

  // Anciens clients / fiches sans nom → pop-up humoristique uniquement à la commande
  if (!opts.skipNameGate) {
    const effectiveName = String(orderName || userName || '').trim();
    if (!effectiveName) {
      setPendingOrderPaidWithCoins(!!paidWithCoins);
      setOrderNameDraft('');
      setShowOrderNameModal(true);
      return;
    }
  }

  // Vérifier si l'utilisateur a déjà une commande active
  if (activeOrder) {
    // Vérifier que la commande est toujours active (non terminée, non annulée)
    try {
      const statusRes = await fetchWithTimeout(`${BACKEND_URL}/status/${activeOrder.orderId}`, {}, 5000);
      const contentType = statusRes.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const statusData = await statusRes.json();
        // Commande active → bloquer
        if (statusData.success && statusData.order && statusData.status !== 3 && !statusData.cancelled) {
          alert(t('activeOrderAlert'));
          setCurrentView('order-tracking');
          return;
        }
        // Commande introuvable (supprimée) ou annulée ou livrée → nettoyer et laisser passer
        setActiveOrder(null);
        setOrderStatus(0);
        saveActiveOrderData(null);
        // Continuer la nouvelle commande
      }
    } catch (error) {
      console.error('Erreur vérification commande existante:', error);
      // En cas d'erreur réseau on laisse passer (ne pas bloquer à cause d'un timeout)
      setActiveOrder(null);
      setOrderStatus(0);
      saveActiveOrderData(null);
    }
  }

  const isCartOrder = cart.length > 0 && currentView === 'cart-summary';
  
  if (!isCartOrder) {
    // Ancien système : commande d'un seul produit
  if (!selectedProduct || (!selectedQuantity && !isBulkMode)) {
    alert(t('selectProductQuantity'));
    return;
  }

  // Vérifier si un code de parrainage a été saisi
  const hasParrainageCode = parrainageCodeInput.trim().length > 0;
  const parrainageCodeToUse = hasParrainageCode ? parrainageCodeInput.trim().toUpperCase() : null;
  
  // Vérifier que le code n'est pas le sien (si défini)
  const isOwnCode = parrainageCodeToUse && parrainageCodeToUse === referralCode;
  
  // Vérifier si le code a déjà été utilisé
  const codesUtilises = JSON.parse(localStorage.getItem('parrainageCodesUtilises') || '[]');
  const isCodeAlreadyUsed = parrainageCodeToUse && codesUtilises.includes(parrainageCodeToUse);
    
    // Si un code de parrainage est saisi, le valider avec le backend
    if (parrainageCodeToUse) {
      if (isOwnCode) {
        addFomoNotification('Vous ne pouvez pas utiliser votre propre code de parrainage', 'error');
        return;
      }
      
      if (isCodeAlreadyUsed) {
        addFomoNotification(t('referralAlreadyUsed'), 'error');
        return;
      }
      
      // Récupérer le chatId de multiples sources
      const getChatIdForValidation = () => {
        if (telegramChatId) return telegramChatId;
        const storedChatId = localStorage.getItem('chatId');
        if (storedChatId) return storedChatId;
        const urlParams = new URLSearchParams(window.location.search);
        const chatIdFromUrl = urlParams.get('chatId');
        if (chatIdFromUrl) return chatIdFromUrl;
        return '';
      };
      
      const chatIdForValidation = getChatIdForValidation();
      
      // Valider le code avec le backend
      try {
        const validationResponse = await fetchWithTimeout(
          `${BACKEND_URL}/client/validate-referral?referralCode=${encodeURIComponent(parrainageCodeToUse)}&chatId=${encodeURIComponent(chatIdForValidation)}`,
          {},
          10000
        );
        
        if (!validationResponse.ok) {
          throw new Error(`Erreur HTTP: ${validationResponse.status}`);
        }
        
        const validationData = await validationResponse.json();
        
        if (!validationData.success || !validationData.valid) {
          addFomoNotification(validationData.message || 'Code de parrainage invalide. Veuillez entrer un code valide ou laisser le champ vide.', 'error');
          return;
        }
      } catch (validationError) {
        console.error('Erreur validation code de parrainage:', validationError);
        addFomoNotification(t('referralErrorShort'), 'error');
        return;
      }
    }
  
  const hasParrainageBonusCheck = hasParrainageBonus || (parrainageCodeToUse && !isOwnCode && !isCodeAlreadyUsed);

  const isSingleProductPremium = isProductExcludedFromBonus(
    selectedProduct?.name,
    selectedProduct?.variant
  );
  // Si produit exclu → bonus ignoré, pas de blocage

  const { wheelBonusGrams, bonusProduct } = pickWheelBonusForProduct(
    wheelBonuses,
    selectedProduct?.name,
    selectedProduct?.variant
  );

  let finalQuantity;
  let basePrice;
  
  // 🎮 Le bonus 2G du jeu s'applique uniquement sur weed (Cali) et hash (mousse)
  const isWeedHashCategory = selectedCategory === 'Cali' || selectedCategory === 'mousse';
  const activeGameBonus = isWeedHashCategory && gameBonus2g > 0 && !isSingleProductPremium ? gameBonus2g : 0;

  if (isBulkMode) {
    basePrice = calculateBulkPrice(bulkQuantity);
    // Ajouter les grammes gratuits (parrainage + bonus roue + bonus jeu) — ignorés si produit exclu
    const parrainGrams = (hasParrainageBonusCheck && !isSingleProductPremium) ? (referralBonusGrams || 2) : 0;
    const wheelGrams = (bonusProduct === 'SAHAS' || !isSingleProductPremium) ? wheelBonusGrams : 0;
    const totalGrams = bulkQuantity + parrainGrams + wheelGrams + activeGameBonus;
    finalQuantity = `${totalGrams}g`;
  } else {
      basePrice = Math.round(getPassPrice(selectedQuantity.price, selectedProduct?.name || ''));
    // Pour les pens/bars, pas de bonus en grammes — garder le label direct
    if (selectedQuantity.unit === 'pen' || selectedQuantity.unit === 'bar') {
      finalQuantity = selectedQuantity.label;
    } else {
      // Ajouter les grammes gratuits (parrainage + bonus roue + bonus jeu) — ignorés si produit exclu
      const parrainGrams = (hasParrainageBonusCheck && !isSingleProductPremium) ? (referralBonusGrams || 2) : 0;
      const wheelGrams = (bonusProduct === 'SAHAS' || !isSingleProductPremium) ? wheelBonusGrams : 0;
      const totalGrams = selectedQuantity.amount + parrainGrams + wheelGrams + activeGameBonus;
      finalQuantity = `${totalGrams}g`;
    }
  }

  // ============================================
  // LIVRAISON PRIORITAIRE AUTOMATIQUE GRATUITE
  // ============================================
    // Si l'utilisateur a payé ou débloqué Pass Argent ou Or,
  // il possède la livraison prioritaire GRATUITE sur TOUTES ses commandes
    const isAutoPriority = userPass === 'argent' || userPass === 'or' || packPriorityBonus;
  
  // Calcul du prix final :
    // - Si Pass Argent/Or ou lot pack priorité : livraison prioritaire automatique GRATUITE (pas de +10 CHF)
    // - Si Pass Bronze : +10 CHF seulement si l'utilisateur choisit manuellement la livraison prioritaire
  const finalPrice = basePrice + (isPriorityDelivery && !isAutoPriority ? 10 : 0);
  
  // La commande est automatiquement prioritaire si :
    // - L'utilisateur a Pass Argent ou Or (automatique)
    // - OU l'utilisateur a choisi manuellement la livraison prioritaire (Pass Bronze)
  const finalIsPriority = isAutoPriority || isPriorityDelivery;

  if (finalPrice < MINIMUM_ORDER) {
    setShowMinimumAlert(true);
    return;
  }

    // Les vérifications de code de parrainage sont déjà faites plus haut

  const orderData = {
    product: selectedProduct.name,
    quantity: finalQuantity,
    price: finalPrice,
    orderName: orderName || 'Sans nom',
    address: deliveryAddress || t('addressNotProvided'),
    isPriority: finalIsPriority,
      country: getEffectiveCountry(),
      city: selectedCity,
    parrainageCode: parrainageCodeToUse, // Code de parrainage saisi
    hasBonus1g: !isSingleProductPremium && (hasParrainageBonusCheck || (parrainageCodeToUse !== null)), // Bonus parrainage ignoré pour produits premium
    userPass: userPass || 'bronze', // Pass de l'utilisateur pour calcul des prix côté backend
    hasWheelBonus: wheelBonusGrams > 0, // Indiquer qu'un bonus roue est appliqué
    wheelBonusGrams: wheelBonusGrams, // Quantité de grammes du bonus roue
    wheelBonusProduct: bonusProduct, // Type de bonus (MOUSS, DRY, CALI)
    paidWithCoins: paidWithCoins, // Paiement en Global Coins
  };

  // 🔹 Active l'état de chargement AVANT d'envoyer la commande
  setIsLoading(true);
  
  // NE PAS consommer le bonus ici - attendre la confirmation du backend

  try {
      const orderChatId =
        telegramChatId ||
        localStorage.getItem('telegramChatId') ||
        localStorage.getItem('chatId') ||
        getStoredNfcShopChatId() ||
        null;
      // Ajouter le chatId du client dans les données de la commande
      const orderDataWithChatId = {
        ...orderData,
        clientChatId: orderChatId,
        chatId: orderChatId
      };
      
    // Envoi résilient : 401 → réparation auto de session + nouvel essai ; panne réseau/502 → 1 retry
    const { response, data, error: sendError } = await resilientJsonRequest(() => fetchWithTimeout(`${BACKEND_URL}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify(orderDataWithChatId),
    }, 45000));

    if (!response) throw sendError || new Error('Réseau indisponible');
    if (!data) throw new Error('Réponse invalide du serveur');

    // Gérer le cas commande en doublon (409)
    if (response.status === 409) {
      addFomoNotification('⚠️ Vous avez déjà une commande en cours. Attendez la livraison avant de recommander.', 'error');
      setCurrentView('order-tracking');
      setIsLoading(false);
      return;
    }

    if (response.status === 401) {
      // Réparation auto déjà tentée dans resilientJsonRequest — dernier recours seulement
      alert('Session expirée — rouvre l’app depuis Telegram.');
      setIsLoading(false);
      return;
    }

    if (data.success) {
      // Consommer les bonus SEULEMENT après confirmation du backend
      if (hasParrainageBonusCheck) {
        localStorage.removeItem('parrainageBonus2g');
        setHasParrainageBonus(false);
      }
      
      // Consommer le bonus de la roue SEULEMENT après confirmation du backend
      // IMPORTANT: Le bonus est valable UNIQUEMENT pour la prochaine commande
      // Après cette commande, le bonus est définitivement consommé et supprimé de la base de données
      if (wheelBonusGrams > 0 && bonusProduct && telegramChatId) {
        try {
          const consumeResponse = await fetchWithTimeout(`${BACKEND_URL}/client/consume-wheel-bonus`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chatId: telegramChatId,
              product: bonusProduct,
              amount: wheelBonusGrams
            })
          }, 10000);
          
          if (consumeResponse.ok) {
            const consumeData = await consumeResponse.json();
            if (consumeData.success) {
              // Mettre à jour l'état local - le bonus est maintenant complètement consommé (0)
              setWheelBonuses(prev => ({
                ...prev,
                [bonusProduct]: 0 // Le bonus est définitivement supprimé après cette commande
              }));
              addFomoNotification(t('wheelBonusApplied', wheelBonusGrams, bonusProduct), 'success');
            }
          }
        } catch (error) {
          console.error('Erreur lors de la consommation du bonus:', error);
          // En cas d'erreur, mettre à jour localement quand même pour éviter les doublons
          setWheelBonuses(prev => ({
            ...prev,
            [bonusProduct]: 0 // Le bonus est consommé même en cas d'erreur pour éviter les doublons
          }));
          addFomoNotification(t('wheelBonusApplied', wheelBonusGrams, bonusProduct), 'success');
        }
      }
      
      // Si un code de parrainage a été utilisé, le marquer comme utilisé
      if (parrainageCodeToUse) {
        const codesUtilises = JSON.parse(localStorage.getItem('parrainageCodesUtilises') || '[]');
        if (!codesUtilises.includes(parrainageCodeToUse)) {
          codesUtilises.push(parrainageCodeToUse);
          localStorage.setItem('parrainageCodesUtilises', JSON.stringify(codesUtilises));
          // Le filleul reçoit 1G offert (géré par le backend)
          // Le parrain recevra 1G après livraison (géré par le backend)
          addFomoNotification(t('referralValidatedShort'), 'success');
        }
        // Réinitialiser le champ
        setParrainageCodeInput('');
      }
      
      const newOrder = {
        orderId: data.orderId,
        ...orderData,
        status: 0,
        cancelled: false,
        telegramLink: data.telegramLink || null,
      };
      setActiveOrder(newOrder);
      setOrderStatus(0);
      setTelegramLink(data.telegramLink || null);
      // Sauvegarder immédiatement dans le localStorage pour persistance après rafraîchissement
      saveActiveOrderData(newOrder);
      
      // Récupérer immédiatement les informations de la file d'attente pour afficher le bon message
      try {
        const statusRes = await fetchWithTimeout(`${BACKEND_URL}/status/${data.orderId}`, {}, 10000);
        const statusData = await statusRes.json();
        if (statusData.success && statusData.queue) {
          const newQueueInfo = {
            ordersAhead: statusData.queue.ordersAhead ?? 0,
            estimatedWaitMinutes: statusData.queue.estimatedWaitMinutes ?? 0,
            totalActive: statusData.queue.totalActive ?? 0,
            extraDelayMinutes: statusData.queue.extraDelayMinutes ?? 0,
            priorityReductionMinutes: statusData.queue.priorityReductionMinutes ?? 0,
            effectiveMinutes: statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
            remainingMinutes: statusData.queue.remainingMinutes ?? statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
            hasEnRouteOrdersAhead: statusData.queue.hasEnRouteOrdersAhead ?? false
          };
          setQueueInfo(newQueueInfo);
        }
      } catch (queueError) {
        console.error('Erreur récupération file d\'attente:', queueError);
      }
      
      setCurrentView('order-confirmation');
    } else {
      // Si la commande échoue, NE PAS consommer le bonus
      if (data.shopClosed) {
        alert(t('shopClosedAlert'));
      } else {
        alert(safeUserError(data.message) || GENERIC_USER_ERROR);
      }
    }
  } catch (error) {
    console.error('Erreur envoi:', error);
    // Si erreur réseau, NE PAS consommer le bonus
    alert(GENERIC_USER_ERROR);
  } finally {
    // 🔹 Désactive le chargement dans tous les cas
    setIsLoading(false);
  }
    return;
  }

  // ============================================
  // NOUVEAU SYSTÈME : COMMANDE AVEC PANIER
  // ============================================
  if (cart.length === 0) {
    addFomoNotification('Votre panier est vide', 'error');
    return;
  }

  if (!deliveryAddress.trim()) {
    addFomoNotification('Veuillez entrer une adresse de livraison', 'error');
    return;
  }

  // Vérifier le code de parrainage si fourni
  const hasParrainageCode = parrainageCodeInput.trim().length > 0;
  const parrainageCodeToUse = hasParrainageCode ? parrainageCodeInput.trim().toUpperCase() : null;
  
  // Si un code de parrainage est fourni dans le récapitulatif, afficher le modal de sélection
  if (parrainageCodeToUse && cart.length > 0) {
    const isOwnCode = parrainageCodeToUse === referralCode;
    const codesUtilises = JSON.parse(localStorage.getItem('parrainageCodesUtilises') || '[]');
    const isCodeAlreadyUsed = codesUtilises.includes(parrainageCodeToUse);
    
    if (isOwnCode) {
      addFomoNotification('Vous ne pouvez pas utiliser votre propre code de parrainage', 'error');
      return;
    }
    
    if (isCodeAlreadyUsed) {
      addFomoNotification(t('referralAlreadyUsed'), 'error');
      return;
    }
    
    // Récupérer le chatId de multiples sources
    const getChatIdForValidation = () => {
      if (telegramChatId) return telegramChatId;
      const storedChatId = localStorage.getItem('chatId');
      if (storedChatId) return storedChatId;
      const urlParams = new URLSearchParams(window.location.search);
      const chatIdFromUrl = urlParams.get('chatId');
      if (chatIdFromUrl) return chatIdFromUrl;
      return '';
    };
    
    const chatIdForValidation = getChatIdForValidation();
    
    // Valider le code avec le backend
    try {
      const validationResponse = await fetchWithTimeout(
        `${BACKEND_URL}/client/validate-referral?referralCode=${encodeURIComponent(parrainageCodeToUse)}&chatId=${encodeURIComponent(chatIdForValidation)}`,
        {},
        10000
      );
      
      if (!validationResponse.ok) {
        throw new Error(`Erreur HTTP: ${validationResponse.status}`);
      }
      
      const validationData = await validationResponse.json();
      
      if (!validationData.success || !validationData.valid) {
        addFomoNotification(validationData.message || 'Code de parrainage invalide.', 'error');
        return;
      }
      
      // Si le code est valide et qu'il y a plusieurs produits, afficher le modal de sélection
      setPendingParrainageCode(parrainageCodeToUse);
      setShowParrainageProductModal(true);
      return; // Ne pas continuer la commande, attendre la sélection du produit
    } catch (validationError) {
      console.error('Erreur validation code de parrainage:', validationError);
      addFomoNotification('Erreur lors de la validation du code de parrainage.', 'error');
      return;
    }
  }

  const cartRefItem = cart.find((item) => isSahasStaticProductName(item.product?.name)) || cart[0];
  const { wheelBonusGrams, bonusProduct } = pickWheelBonusForProduct(
    wheelBonuses,
    cartRefItem?.product?.name,
    cartRefItem?.product?.variant
  );

  // Calculer le total
  const isAutoPriority = userPass === 'argent' || userPass === 'or' || packPriorityBonus;
  const finalIsPriority = isAutoPriority || isPriorityDelivery;
  const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
  const priorityCost = isAutoPriority ? 0 : (isPriorityDelivery ? 10 : 0);
  const totalPrice = subtotal + priorityCost;

  if (totalPrice < MINIMUM_ORDER) {
    setShowMinimumAlert(true);
    return;
  }

  // Créer le nom du produit comme liste de noms séparés par des virgules
  const productNames = cart.map(item => item.product.name).join(', ');
  
  // 🎮 Vérifier si le panier contient au moins un produit weed/hash pour activer le bonus jeu
  const cartHasWeedHash = cart.some(item => item.category === 'Cali' || item.category === 'mousse');
  const activeGameBonusCart = cartHasWeedHash && gameBonus2g > 0 ? gameBonus2g : 0;

  // Créer la quantité formatée : "10g (nom produit) + 3g (nom produit)"
  // Ajouter le bonus roue et le bonus jeu à la quantité totale si présents
  let formattedQuantity = cart.map(item => `${item.quantityGrams}g (${item.product.name})`).join(' + ');
  if (wheelBonusGrams > 0 && bonusProduct) {
    const variantText = wheelBonusDetail && wheelBonusDetail.product === bonusProduct
      ? ` - ${wheelBonusDetail.variantLabel}`
      : '';
    formattedQuantity += ` + ${wheelBonusGrams}g (Bonus ${bonusProduct}${variantText})`;
  }
  if (activeGameBonusCart > 0) {
    formattedQuantity += ` + ${activeGameBonusCart}g (🎮 Bonus Global Game)`;
  }

  // 🔹 Active l'état de chargement AVANT d'envoyer la commande
  setIsLoading(true);

  try {
    // Envoyer la commande avec tous les produits du panier
    const orderData = {
      product: productNames, // Noms des produits séparés par des virgules
      products: cart.map(item => ({
        name: item.product.name,
        category: item.category,
        quantity: item.quantity,
        quantityGrams: item.quantityGrams,
        price: item.price,
        parrainageCode: item.parrainageCode,
        hasBonus1g: item.hasBonus1g
      })),
      quantity: formattedQuantity, // Format: "10g (nom produit) + 3g (nom produit) + Xg (Bonus MOUSS)"
      price: totalPrice,
      orderName: orderName || userName || 'Sans nom',
      address: deliveryAddress,
      isPriority: finalIsPriority,
      country: getEffectiveCountry(),
      city: selectedCity, // Ville de livraison
      parrainageCode: parrainageCodeToUse, // Code de parrainage global (si fourni dans le récapitulatif)
      hasBonus1g: hasParrainageBonus || (parrainageCodeToUse !== null && cart.some(item => item.hasBonus1g)),
      clientChatId:
        telegramChatId ||
        localStorage.getItem('telegramChatId') ||
        localStorage.getItem('chatId') ||
        getStoredNfcShopChatId() ||
        null,
      chatId:
        telegramChatId ||
        localStorage.getItem('telegramChatId') ||
        localStorage.getItem('chatId') ||
        getStoredNfcShopChatId() ||
        null,
      userPass: userPass || 'bronze', // Pass de l'utilisateur pour calcul des prix côté backend
      hasWheelBonus: wheelBonusGrams > 0, // Indiquer qu'un bonus roue est appliqué
      wheelBonusGrams: wheelBonusGrams, // Quantité de grammes du bonus roue
      wheelBonusProduct: bonusProduct, // Type de bonus (MOUSS, DRY, CALI)
      paidWithCoins: paidWithCoins, // Paiement en Global Coins
    };

    // Envoi résilient : 401 → réparation auto de session + nouvel essai ; panne réseau/502 → 1 retry
    const { response, data, error: sendError } = await resilientJsonRequest(() => fetchWithTimeout(`${BACKEND_URL}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify(orderData),
    }, 45000));

    if (!response) throw sendError || new Error('Réseau indisponible');
    if (!data) throw new Error('Réponse invalide du serveur');

    // Gérer le cas commande en doublon (409)
    if (response.status === 409) {
      addFomoNotification('⚠️ Vous avez déjà une commande en cours. Attendez la livraison avant de recommander.', 'error');
      setCurrentView('order-tracking');
      setIsLoading(false);
      return;
    }

    if (response.status === 401) {
      // Réparation auto déjà tentée dans resilientJsonRequest — dernier recours seulement
      alert('Session expirée — rouvre l’app depuis Telegram.');
      setIsLoading(false);
      return;
    }

    if (data.success) {
      // Consommer les bonus et codes de parrainage
      if (hasParrainageBonus) {
        localStorage.removeItem('parrainageBonus1g');
        setHasParrainageBonus(false);
      }
      
      // Consommer le bonus de la roue SEULEMENT après confirmation du backend
      // IMPORTANT: Le bonus est valable UNIQUEMENT pour la prochaine commande
      // Après cette commande, le bonus est définitivement consommé et supprimé de la base de données
      if (wheelBonusGrams > 0 && bonusProduct && telegramChatId) {
        try {
          const consumeResponse = await fetchWithTimeout(`${BACKEND_URL}/client/consume-wheel-bonus`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chatId: telegramChatId,
              product: bonusProduct,
              amount: wheelBonusGrams
            })
          }, 10000);
          
          if (consumeResponse.ok) {
            const consumeData = await consumeResponse.json();
            if (consumeData.success) {
              // Mettre à jour l'état local - le bonus est maintenant complètement consommé (0)
              setWheelBonuses(prev => ({
                ...prev,
                [bonusProduct]: 0 // Le bonus est définitivement supprimé après cette commande
              }));
              addFomoNotification(t('wheelBonusApplied', wheelBonusGrams, bonusProduct), 'success');
            }
          }
        } catch (error) {
          console.error('Erreur lors de la consommation du bonus:', error);
          // En cas d'erreur, mettre à jour localement quand même pour éviter les doublons
          setWheelBonuses(prev => ({
            ...prev,
            [bonusProduct]: 0 // Le bonus est consommé même en cas d'erreur pour éviter les doublons
          }));
          addFomoNotification(t('wheelBonusApplied', wheelBonusGrams, bonusProduct), 'success');
        }
      }
      
      // Marquer tous les codes de parrainage utilisés
      const codesUtilises = JSON.parse(localStorage.getItem('parrainageCodesUtilises') || '[]');
      cart.forEach(item => {
        if (item.parrainageCode && !codesUtilises.includes(item.parrainageCode)) {
          codesUtilises.push(item.parrainageCode);
        }
      });
      if (parrainageCodeToUse && !codesUtilises.includes(parrainageCodeToUse)) {
        codesUtilises.push(parrainageCodeToUse);
      }
      localStorage.setItem('parrainageCodesUtilises', JSON.stringify(codesUtilises));
      
      // Vider le panier
      setCart([]);
      setParrainageCodeInput('');
      
      const newOrder = {
        orderId: data.orderId,
        product: orderData.product, // Noms des produits séparés par des virgules
        quantity: formattedQuantity, // Format: "10g (nom produit) + 3g (nom produit)"
        price: totalPrice,
        orderName: orderData.orderName,
        address: orderData.address,
        isPriority: finalIsPriority,
        status: 0,
        cancelled: false,
        telegramLink: data.telegramLink || null,
        cartItems: cart, // Conserver les articles pour l'affichage
        products: orderData.products // Conserver les produits pour l'affichage détaillé
      };
      
      setActiveOrder(newOrder);
      setOrderStatus(0);
      setTelegramLink(data.telegramLink || null);
      saveActiveOrderData(newOrder);
      
      // Récupérer les informations de la file d'attente
      try {
        const statusRes = await fetchWithTimeout(`${BACKEND_URL}/status/${data.orderId}`, {}, 10000);
        const statusData = await statusRes.json();
        if (statusData.success && statusData.queue) {
          const newQueueInfo = {
            ordersAhead: statusData.queue.ordersAhead ?? 0,
            estimatedWaitMinutes: statusData.queue.estimatedWaitMinutes ?? 0,
            totalActive: statusData.queue.totalActive ?? 0,
            extraDelayMinutes: statusData.queue.extraDelayMinutes ?? 0,
            priorityReductionMinutes: statusData.queue.priorityReductionMinutes ?? 0,
            effectiveMinutes: statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
            remainingMinutes: statusData.queue.remainingMinutes ?? statusData.queue.effectiveMinutes ?? statusData.queue.estimatedWaitMinutes ?? 0,
            hasEnRouteOrdersAhead: statusData.queue.hasEnRouteOrdersAhead ?? false
          };
          setQueueInfo(newQueueInfo);
        }
      } catch (queueError) {
        console.error('Erreur récupération file d\'attente:', queueError);
      }
      
      addFomoNotification(t('orderPlacedSuccess', cart.length), 'success');
      setCurrentView('order-confirmation');
    } else {
      // Commande refusée par le backend — rembourser les coins si paiement en coins
      if (paidWithCoins && telegramChatId) {
        try {
          await fetchWithTimeout(`${BACKEND_URL}/client/refund-coins`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
            credentials: 'include',
            body: JSON.stringify({ chatId: telegramChatId, amount: totalPrice })
          }, 8000);
          // Rafraîchir le solde
          try {
            const refetchRes = await fetchWithTimeout(`${BACKEND_URL}/client/info?chatId=${telegramChatId}`, { headers: getApiAuthHeaders(), credentials: 'include' }, 5000);
            const refetchData = await refetchRes.json();
            if (refetchData.success && refetchData.client) {
              setGlobalCoins(refetchData.client.globalCoins || 0);
              setFreePacks(refetchData.client.freePacks || 0);
              setPackPriorityBonus(refetchData.client.packPriorityBonus === true);
            }
          } catch(_) {}
        } catch (refundErr) { console.error('Erreur remboursement coins:', refundErr); }
      }
      if (data.shopClosed) {
        alert(t('shopClosedAlert'));
      } else {
        alert(safeUserError(data.message) || GENERIC_USER_ERROR);
      }
    }
  } catch (error) {
    console.error('Erreur envoi commande panier:', error);
    // Erreur réseau — rembourser les coins si paiement en coins
    if (paidWithCoins && telegramChatId) {
      try {
        await fetchWithTimeout(`${BACKEND_URL}/client/refund-coins`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId: telegramChatId, amount: totalPrice })
        }, 8000);
        try {
          const refetchRes = await fetchWithTimeout(`${BACKEND_URL}/client/info?chatId=${telegramChatId}`, {}, 5000);
          const refetchData = await refetchRes.json();
          if (refetchData.success && refetchData.client) setGlobalCoins(refetchData.client.globalCoins || 0);
        } catch(_) {}
      } catch (refundErr) { console.error('Erreur remboursement coins (catch):', refundErr); }
    }
    alert(GENERIC_USER_ERROR);
  } finally {
    setIsLoading(false);
  }
};


  const handleReferral = () => {
    // Afficher le code de parrainage pour qu'il puisse le copier
    setShowReferralCode(true);
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode).then(() => {
      addFomoNotification(t('codeCopiedShort'), 'success');
    }).catch(() => {
      addFomoNotification('Erreur lors de la copie', 'error');
    });
  };

  // NOUVEAU: Message boutique fermée
  const ShopClosedOverlay = () => (
    <div className="min-h-screen relative overflow-hidden">
      <SophisticatedBackground />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center relative z-10">
          <div className="relative w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center animate-pulse" style={{ 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.2) 100%)', 
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            border: '1px solid rgba(255, 255, 255, 0.4)'
          }}>
            <Lock className="w-16 h-16 text-white" />
        </div>
        
          <h1 className="text-4xl font-bold mb-4 tracking-wider gradient-text-snow" style={{ 
            textShadow: '0 2px 20px rgba(255, 255, 255, 0.3)'
          }}>
          La Boutique est Fermée
        </h1>
        
          <div className="p-6 rounded-2xl mb-6 card-premium glass-card shadow-multilayer" style={{ 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.15) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.2)'
          }}>
            <p className="text-white text-lg mb-3">Aucun livreur n'est disponible actuellement</p>
            <div className="flex items-center justify-center gap-2 text-white/80">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm">Revenez Plus tard</span>
            <Sparkles className="w-5 h-5" />
          </div>
        </div>

          <div className="space-y-2 text-white/70 text-sm">
          <p className="flex items-center justify-center gap-2">
            <Crown className="w-4 h-4" />
              GLOBAL STRAINS
          </p>
        </div>

          <div className="mt-8 p-4 rounded-xl card-premium glass-card" style={{ 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.15) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.2)'
          }}>
            <p className="text-white/80 text-xs"> {t('openWhenAvailable')}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const Confetti = () => {
    const confettiColors = ['#fbbf24', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
    const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 3 + Math.random() * 2,
      color: confettiColors[Math.floor(Math.random() * confettiColors.length)]
    }));

    return (  
      <div className="fixed inset-0 z-50 pointer-events-none">
        {confettiPieces.map((piece) => (
          <div
            key={piece.id}
            className="absolute w-3 h-3 animate-fall"
            style={{
              left: `${piece.left}%`,
              top: '-20px',
              backgroundColor: piece.color,
              animationDelay: `${piece.delay}s`,
              animationDuration: `${piece.duration}s`,
              transform: 'rotate(45deg)'
            }}
          />
        ))}
        <style>{`
          @keyframes fall {
            to {
              transform: translateY(100vh) rotate(360deg);
              opacity: 0;
            }
          }
          .animate-fall {
            animation: fall linear forwards;
          }
        `}</style>
      </div>
    );
  };

  const MinimumOrderAlert = () => (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4" 
      style={{ background: 'rgba(0, 0, 0, 0.8)' }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="max-w-md w-full p-6 rounded-2xl relative" 
        style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #991b1b 100%)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)', border: '3px solid rgba(251, 191, 36, 0.5)' }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <button 
          onClick={() => setShowMinimumAlert(false)} 
          className="absolute top-4 right-4 p-2 rounded-full" 
                  style={{ background: 'rgba(0, 0, 0, 0.3)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}
        >
          <X className="w-5 h-5 text-amber-100" />
        </button>
        <div className="text-center">
          <motion.div 
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" 
            style={{ 
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.25) 100%)',
              border: '1.5px solid rgba(251, 191, 36, 0.5)',
              boxShadow: '0 10px 30px rgba(251, 191, 36, 0.3)'
            }}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div>
            <Coins className="w-12 h-12 text-amber-200" />
            </div>
          </motion.div>
          <motion.h3 
            className="text-2xl font-bold text-yellow-300 mb-3"
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >{t('minimumOrder')}</motion.h3>
          <motion.p 
            className="text-amber-100 text-lg mb-4"
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Pour bénéficier de la livraison, le montant minimum est de 
            <motion.span 
              className="text-3xl font-bold text-white"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.2 }}
            >{MINIMUM_ORDER}{getCurrencySymbol() === 'CHF' ? ' CHF' : '€'}</motion.span>
          </motion.p>
          <motion.p 
            className="text-amber-300 text-sm mb-6"
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >{t('addMoreProducts')}</motion.p>
          <button 
            onClick={() => setShowMinimumAlert(false)} 
            className="w-full py-3 px-6 rounded-xl font-bold text-amber-100" 
            style={{ 
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.15) 100%)',
              border: '1.5px solid rgba(251, 191, 36, 0.4)',
              boxShadow: '0 4px 16px rgba(251, 191, 36, 0.2)'
            }}
          >
            Compris !
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const TelegramConfirmAlert = () => (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4" 
      style={{ background: 'rgba(0, 0, 0, 0.8)' }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        className="max-w-md w-full p-6 rounded-2xl relative" 
        style={{ background: 'linear-gradient(135deg, #0088cc 0%, #0066aa 100%)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)', border: '3px solid rgba(251, 191, 36, 0.5)' }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <button 
          onClick={() => setShowTelegramConfirm(false)} 
          className="absolute top-4 right-4 p-2 rounded-full" 
          style={{ background: 'rgba(0, 0, 0, 0.3)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <div className="text-center">
          <motion.div 
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" 
            style={{ background: 'rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 30px rgba(0, 136, 204, 0.5)' }}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.2 }}
          >
            <img 
              src="/emoji/tlg.png" 
              alt="Telegram" 
              width={40} 
              height={40} 
              className="inline-block"
            />
          </motion.div>
          <motion.h3 
            className="text-2xl font-bold text-white mb-3"
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >{t('telegramNotifications')}</motion.h3>
          <motion.p 
            className="text-white text-lg mb-4"
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Vous allez recevoir des notifications en temps réel sur Telegram pour suivre votre commande.
          </motion.p>
          <motion.p 
            className="text-blue-200 text-sm mb-6"
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >Veuillez cliquez sur "OK"</motion.p>
          <button 
            onClick={async () => {
              // Activer les notifications Telegram pour cette commande
              if (activeOrder?.orderId) {
                try {
                  const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${activeOrder.orderId}/telegram-notifications`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ enabled: true })
                  });
                  const data = await response.json();
                  if (data.success) {
                    setTelegramNotificationsEnabled(true);
                    console.log('Notifications Telegram activées');
                  }
                } catch (error) {
                  console.error('Erreur activation notifications:', error);
                }
              }
              setShowTelegramConfirm(false);
              if (telegramLink) {
                window.open(telegramLink, '_blank');
              }
            }} 
            className="w-full py-3 px-6 rounded-xl font-bold text-white" 
            style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)', border: '2px solid rgba(255, 255, 255, 0.3)' }}
          >
            OK
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderLoadingScreen = () => (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden" style={{ 
      background: 'linear-gradient(180deg, #1a1d24 0%, #2a2f3a 50%, #1a1d24 100%)' 
    }}>
      {/* Effet de lumière ambiante */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(74,144,226,0.10) 0%, transparent 70%)'}}>
      </div>
      
      {/* Effet de lumière bleue en arrière-plan */}
      <div className="absolute inset-0 opacity-30" style={{ 
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(74, 144, 226, 0.4) 0%, transparent 70%)' 
      }}></div>
      
      {/* Carte glassmorphism principale */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 text-center card-premium shadow-multilayer rounded-3xl p-12 max-w-md mx-4"
        style={{
          background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
          backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
          WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
          border: '1.5px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 60px rgba(74, 144, 226, 0.2)'
        }}
      >
        {/* Loader avec effet bleu */}
        <div className="relative w-32 h-32 mx-auto mb-8">
          {/* Cercle externe animé */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
              border: '2px solid rgba(74, 144, 226, 0.5)'
            }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.6, 0.8, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          {/* Cercle interne avec loader */}
          <div className="absolute inset-4 rounded-full flex items-center justify-center" style={{
            background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <Loader 
              className="w-16 h-16 text-white spin-smooth" 
              style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
            />
          </div>
        </div>
        
        {/* Titre avec gradient neige */}
        <h2 className="text-3xl font-bold mb-3 gradient-text-snow" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
        }}>
          Traitement en cours...
        </h2>
        
        {/* Message */}
        <p className="text-white/90 text-lg mb-4" style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)' }}>
          
        </p>
        
        {/* Icônes avec effet scintillant */}
        <div className="flex items-center justify-center gap-2 text-white/80">
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="w-5 h-5" style={{ filter: 'drop-shadow(0 0 8px rgba(74, 144, 226, 0.8))' }} />
          </motion.div>
          <span className="text-sm">Cela ne prendra qu'un instant</span>
          <motion.div
            animate={{ rotate: [360, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="w-5 h-5" style={{ filter: 'drop-shadow(0 0 8px rgba(74, 144, 226, 0.8))' }} />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );

  const renderBannedScreen = () => (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden" style={{
      background: 'linear-gradient(180deg, #1a1d24 0%, #2a2f3a 50%, #1a1d24 100%)'
    }}>
      {/* Effet de lumière ambiante */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(74,144,226,0.10) 0%, transparent 70%)'}}>
      </div>

      {/* Effet de lumière rouge en arrière-plan */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(220, 38, 38, 0.4) 0%, transparent 70%)'
      }}></div>

      {/* Carte glassmorphism principale */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 text-center card-premium shadow-multilayer rounded-3xl p-12 max-w-md mx-4"
        style={{
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.15) 0%, rgba(185, 28, 28, 0.1) 50%, rgba(220, 38, 38, 0.15) 100%)',
          backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
          WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
          border: '1.5px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 60px rgba(220, 38, 38, 0.2)'
        }}
      >
        {/* Icône X avec effet rouge */}
        <div className="relative w-32 h-32 mx-auto mb-8">
          {/* Cercle externe animé */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.4) 0%, rgba(185, 28, 28, 0.3) 100%)',
              border: '2px solid rgba(220, 38, 38, 0.5)'
            }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.6, 0.8, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          {/* Cercle interne avec icône X */}
          <div className="absolute inset-4 rounded-full flex items-center justify-center" style={{
            background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(185, 28, 28, 0.15) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <X
              className="w-16 h-16 text-white"
              style={{ filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, 0.6))' }}
            />
          </div>
        </div>

        {/* Titre avec gradient rouge */}
        <h2 className="text-3xl font-bold mb-3" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
        }}>
          Vous avez été banni
        </h2>

        {/* Message */}
        <p className="text-white/90 text-lg mb-4" style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)' }}>
          Votre accès à la boutique a été révoqué par un administrateur.
        </p>

        {/* Icônes avec effet scintillant */}
        <div className="flex items-center justify-center gap-2 text-white/80">
          <span className="text-sm">Contactez le support pour plus d'informations</span>
        </div>
      </motion.div>
    </div>
  );

  // Fonction pour supprimer un article du panier
  const handleRemoveFromCart = (itemId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
    addFomoNotification(t('cartItemRemovedShort'), 'success');
  };

  // Fonction pour calculer le total du panier
  const calculateCartTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
    const isAutoPriority = userPass === 'argent' || userPass === 'or' || packPriorityBonus;
    const priorityCost = isAutoPriority ? 0 : (isPriorityDelivery ? 10 : 0);
    return subtotal + priorityCost;
  };

  // Fonction pour afficher le panier
  const renderCart = () => {
    if (cart.length === 0) {
      return (
        <div className="min-h-screen relative with-app-mobile-nav">
          <SophisticatedBackground />
          <div className="relative z-10 p-5 gs-page-pad">
            <div className="flex items-center mb-5">
              <button 
                onClick={() => setCurrentView('home')} 
                className="p-2.5 rounded-xl text-white transition"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                  border: '1.5px solid rgba(74, 144, 226, 0.4)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
                }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="ml-3 flex-1">
                <h2 className="text-xl font-bold text-amber-100">{t('cart')}</h2>
              </div>
            </div>
            <div className="max-w-md mx-auto mt-20 text-center">
              <ShoppingCart className="w-20 h-20 text-amber-400/50 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-amber-100 mb-2">{t('emptyCart')}</h3>
              <p className="text-amber-200/70 mb-6">Ajoutez des produits pour commencer</p>
              <button
                onClick={() => setCurrentView('home')}
                className="card-premium shadow-multilayer py-3 px-6 rounded-xl font-bold text-white"
                style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)',
                  border: '1.5px solid rgba(255, 255, 255, 0.4)'
                }}
              >
                Continuer les achats
              </button>
            </div>
          </div>
          <MobileNavBottomSpacer />
        </div>
      );
    }

    const total = calculateCartTotal();

    return (
      <div className="min-h-screen relative with-app-mobile-nav">
        <SophisticatedBackground />
        <div className="relative z-10 p-5 gs-page-pad">
          <div className="flex items-center mb-5">
            <button onClick={() => setCurrentView('home')} className="p-2.5 rounded-xl" style={{ background: 'linear-gradient(135deg, #78350f 0%, #92400e 100%)' }}>
              <ChevronLeft className="w-5 h-5 text-amber-100" />
            </button>
            <div className="ml-3 flex-1">
              <h2 className="text-xl font-bold text-amber-100">{t('cart')}</h2>
              <p className="text-amber-600 text-xs">{cart.length} {cart.length === 1 ? 'article' : 'articles'}</p>
            </div>
          </div>

          <div className="max-w-md mx-auto space-y-4 mb-6">
            {cart.map((item, index) => {
              const category = categories.find(c => c.id === (item.uiCategory || item.category));
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="card-premium shadow-multilayer rounded-2xl p-4 relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 50%, rgba(255, 255, 255, 0.15) 100%)',
                    border: '1.5px solid rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <button
                    onClick={() => handleRemoveFromCart(item.id)}
                    className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl" style={{ 
                      background: 'linear-gradient(135deg, rgba(120, 53, 15, 0.6) 0%, rgba(146, 64, 14, 0.6) 100%)',
                      boxShadow: '0 4px 16px rgba(217, 119, 6, 0.3)'
                    }}>
                      {category?.image
                        ? <img src={category.image} alt={category.name || ''} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}} />
                        : <span className="text-2xl">{category?.emoji || '📦'}</span>
                      }
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-1">{item.product.name}</h3>
                      <p className="text-amber-200/70 text-sm mb-2">{category?.name || ''}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white/80 text-sm">{item.quantity}</span>
                        <span className="text-white font-bold text-lg">{formatPrice(item.price)}</span>
                      </div>
                      {item.hasBonus1g && (
                        <div className="mt-2 bg-green-600/20 rounded-lg px-2 py-1 border border-green-500/30 inline-block">
                          <span className="text-green-300 text-xs font-semibold">+{referralBonusGrams || 2}g OFFERTS</span>
                        </div>
                      )}
                      {item.hasGameBonus && (
                        <div className="mt-1 rounded-lg px-2 py-1 border inline-block" style={{ background: 'rgba(168,85,247,0.15)', borderColor: 'rgba(168,85,247,0.4)' }}>
                          <span style={{ color: '#c084fc', fontSize: 11, fontWeight: 700 }}>🎮 +2G Global Game</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Récapitulatif */}
          <div className="max-w-md mx-auto card-premium shadow-multilayer rounded-2xl p-5 mb-6" style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 50%, rgba(255, 255, 255, 0.15) 100%)',
            border: '1.5px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm">{t('subtotal')}</span>
                <span className="text-white font-bold">{formatPrice(cart.reduce((sum, item) => sum + item.price, 0))}</span>
              </div>
              {((userPass === 'argent' || userPass === 'or') || isPriorityDelivery) && (
                <div className="flex justify-between items-center">
                  <span className="text-white/80 text-sm flex items-center gap-1"><Sparkles className="w-4 h-4" />{t('priorityDelivery')}</span>
                  <span className="text-white font-bold">
                    {(userPass === 'argent' || userPass === 'or') ? 'GRATUIT' : `+${formatPrice(10)}`}
                  </span>
                </div>
              )}
              <div className="border-t border-white/20 pt-2 flex justify-between items-center">
                <span className="text-white font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-white" />{t('total')}</span>
                <span className="text-3xl font-bold text-white">{formatPrice(total)}</span>
              </div>
            </div>
            
            <button
              onClick={() => setCurrentView('cart-summary')}
              className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition"
              style={{
                backgroundColor: '#1756E6',
                border: '1.5px solid rgba(74, 144, 226, 0.4)',
                backdropFilter: 'blur(40px) saturate(300%)',
                WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
              }}
            >
              <div className="flex items-center justify-center gap-3 relative z-10">
                <Package className="w-5 h-5" />
                <span>{t('proceedToCheckout')}</span>
              </div>
            </button>
          </div>
        </div>
        <MobileNavBottomSpacer />
      </div>
    );
  };

  // Fonction pour afficher le récapitulatif final avant commande
  const renderCartSummary = () => {
    const total = calculateCartTotal();

    return (
      <div className="min-h-screen relative with-app-mobile-nav">
        <SophisticatedBackground />
        <div className="relative z-10 p-5 gs-page-pad">
          <div className="flex items-center mb-5">
            <button 
              onClick={() => setCurrentView('cart')} 
              className="p-2.5 rounded-xl text-white transition"
              style={{ 
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                border: '1.5px solid rgba(74, 144, 226, 0.4)',
                backdropFilter: 'blur(40px) saturate(300%)',
                WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="ml-3 flex-1">
              <h2 className="text-xl font-bold text-amber-100">{t('summary')}</h2>
            </div>
          </div>

          <div className="max-w-md mx-auto space-y-4 mb-6">
            {/* Nom sur la commande */}
            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <Scroll className="w-5 h-5 text-white" />
                Nom sur la commande
              </label>
              <input
                type="text"
                value={(userName && userName.trim()) || (orderName && orderName.trim()) || t('notDefined')}
                readOnly
                style={{
                  background: 'rgba(10,20,50,0.45)',
                  color: '#e2e8f0',
                  borderRadius: 12,
                  padding: '12px 14px',
                  border: '1.5px solid rgba(125,211,252,0.22)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  width: '100%',
                  fontSize: 14,
                  fontWeight: 500,
                  boxSizing: 'border-box',
                  outline: 'none',
                  cursor: 'default',
                  boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.25)'
                }}
              />
            </motion.div>

            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <span className="text-lg">🌍</span>
                Pays de livraison
              </label>
              <div className="flex gap-3">
                {(() => {
                  const isFranceDisabled = !countryConfig.france;
                  const isSuisseDisabled = !countryConfig.suisse;
                  
                  return (
                    <>
                      <button
                        onClick={() => {
                          if (!isFranceDisabled) {
                            persistCountrySelection('france');
                          }
                        }}
                        disabled={isFranceDisabled}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                          getEffectiveCountry() === 'france'
                            ? 'text-amber-100 shadow-lg scale-105'
                            : isFranceDisabled
                            ? 'bg-gray-500/30 text-gray-400 border-2 border-gray-500/30 cursor-not-allowed opacity-50'
                            : 'bg-white/10 text-white border-2 border-white/20 hover:bg-white/20'
                        }`}
                        style={{
                          backdropFilter: 'blur(10px)',
                          background: selectedCountry === 'france'
                            ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.25) 100%)'
                            : undefined,
                          border: selectedCountry === 'france'
                            ? '1.5px solid rgba(251, 191, 36, 0.5)'
                            : undefined,
                          boxShadow: selectedCountry === 'france' 
                            ? '0 4px 16px rgba(251, 191, 36, 0.3), inset 0 0 20px rgba(251, 191, 36, 0.1)' 
                            : isFranceDisabled
                            ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                            : 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                          pointerEvents: isFranceDisabled ? 'none' : 'auto'
                        }}
                      >
                        🇫🇷 France
                      </button>
                      <button
                        onClick={() => {
                          if (!isSuisseDisabled) {
                            persistCountrySelection('suisse');
                          }
                        }}
                        disabled={isSuisseDisabled}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                          getEffectiveCountry() === 'suisse'
                            ? 'text-amber-100 shadow-lg scale-105'
                            : isSuisseDisabled
                            ? 'bg-gray-500/30 text-gray-400 border-2 border-gray-500/30 cursor-not-allowed opacity-50'
                            : 'bg-white/10 text-white border-2 border-white/20 hover:bg-white/20'
                        }`}
                        style={{
                          backdropFilter: 'blur(10px)',
                          background: selectedCountry === 'suisse'
                            ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.25) 100%)'
                            : undefined,
                          border: selectedCountry === 'suisse'
                            ? '1.5px solid rgba(251, 191, 36, 0.5)'
                            : undefined,
                          boxShadow: selectedCountry === 'suisse' 
                            ? '0 4px 16px rgba(251, 191, 36, 0.3), inset 0 0 20px rgba(251, 191, 36, 0.1)' 
                            : isSuisseDisabled
                            ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                            : 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                          pointerEvents: isSuisseDisabled ? 'none' : 'auto'
                        }}
                      >
                        🇨🇭 Suisse
                      </button>
                    </>
                  );
                })()}
              </div>
            </motion.div>

            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <MapPin className="w-5 h-5 text-white" />
                Ville de livraison
              </label>
              <div
                className="w-full bg-white/10 text-white rounded-xl p-3 border-2 border-white/20 cursor-default"
                style={{
                  background: 'rgba(10,20,50,0.45)',
                  color: '#e2e8f0',
                  borderRadius: 12,
                  padding: '12px 14px',
                  border: '1.5px solid rgba(125,211,252,0.22)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.25)'
                }}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg">{selectedCountry === 'suisse' ? '🇨🇭' : '🇫🇷'}</span>
                  <span className="font-semibold">{selectedCity || t('cityNotDefined')}</span>
                </span>
              </div>
            </motion.div>

            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <Scroll className="w-5 h-5 text-white" />
                {t('deliveryAddress')}
              </label>
              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Entrez votre adresse de livraison..."
                rows="3"
                className="w-full bg-white/10 text-white placeholder-white/50 rounded-xl p-4 border-2 border-white/20 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/30 resize-none"
                style={{
                  backdropFilter: 'blur(10px)',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                  fontSize: '16px',
                  WebkitTextSizeAdjust: '100%',
                  touchAction: 'auto'
                }}
              />
            </motion.div>

            {/* Liste des articles */}
            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <Package className="w-5 h-5 text-white" />
                Articles ({cart.length})
              </label>
              <div className="space-y-3">
                {cart.map((item, index) => {
                  const category = categories.find(c => c.id === (item.uiCategory || item.category));
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        {category?.image
                        ? <img src={category.image} alt={category.name || ''} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}} />
                        : <span className="text-2xl">{category?.emoji || '📦'}</span>
                      }
                        <div>
                          <p className="text-white font-semibold text-sm">{item.product.name}</p>
                          <p className="text-amber-200/70 text-xs">{item.quantity}</p>
                        </div>
                      </div>
                      <span className="text-white font-bold">{formatPrice(item.price)}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Code de parrainage (si pas déjà utilisé) */}
            <motion.div
              className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                <Crown className="w-5 h-5 text-white" />
                Code de parrainage (optionnel)
              </label>
              <input
                type="text"
                value={parrainageCodeInput && parrainageCodeInput.trim() ? parrainageCodeInput : 'Aucun code'}
                readOnly
                style={{
                  background: 'rgba(10,20,50,0.45)',
                  color: '#e2e8f0',
                  borderRadius: 12,
                  padding: '12px 14px',
                  border: '1.5px solid rgba(125,211,252,0.22)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  width: '100%',
                  fontSize: 14,
                  fontWeight: 600,
                  boxSizing: 'border-box',
                  outline: 'none',
                  cursor: 'default',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.25)',
                  touchAction: 'auto',
                  WebkitTextSizeAdjust: '100%'
                }}
              />
              {parrainageCodeInput && (
                <p className="text-amber-300 text-xs mt-2">
                  💡 Vous recevrez 2G offerts si le code est valide
                </p>
              )}
            </motion.div>

            {/* Livraison prioritaire */}
            {(userPass === 'argent' || userPass === 'or') ? (
              <div
                className="card-premium shadow-multilayer relative p-4 rounded-xl max-w-sm mx-auto ring-2 ring-white ring-offset-1"
                style={{
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.15) 100%)',
                  border: '1.5px solid rgba(251, 191, 36, 0.4)'
                }}
              >
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(251, 191, 36, 0.2)' }}><Gem className="w-5 h-5 text-amber-200" /></div>
                    <div className="text-left">
                      <span className="block font-bold text-base text-amber-100">{t('priorityDelivery')}</span>
                      <span className="block text-xs text-amber-200/70">{t('priorityIncluded')}</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-amber-200">GRATUIT</div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsPriorityDelivery(!isPriorityDelivery)}
                className={`card-premium shadow-multilayer relative p-4 rounded-xl max-w-sm mx-auto w-full ${isPriorityDelivery ? 'ring-2 ring-white ring-offset-1' : ''}`}
                style={{
                  background: isPriorityDelivery
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 50%, rgba(255, 255, 255, 0.9) 100%)'
                    : 'linear-gradient(135deg, rgba(108, 117, 125, 0.3) 0%, rgba(73, 80, 87, 0.3) 50%, rgba(108, 117, 125, 0.3) 100%)',
                  border: isPriorityDelivery
                    ? '1.5px solid rgba(255, 255, 255, 0.5)'
                    : '1.5px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{
                        background: isPriorityDelivery 
                          ? 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 100%)'
                          : 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 100%)',
                        border: '1px solid rgba(74, 144, 226, 0.3)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)'
                      }}
                    >
                      <Gem className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <span className="block font-bold text-base text-white">{t('priorityDelivery')}</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white">+{formatPrice(10)}</div>
                </div>
              </button>
            )}

            {/* Total */}
            <motion.div
              className="card-premium shadow-multilayer rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 50%, rgba(255, 255, 255, 0.15) 100%)',
                border: '1.5px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80 text-sm">{t('subtotal')}</span>
                  <span className="text-white font-bold">{formatPrice(cart.reduce((sum, item) => sum + item.price, 0))}</span>
                </div>
                {((userPass === 'argent' || userPass === 'or') || isPriorityDelivery) && (
                  <div className="flex justify-between items-center">
                    <span className="text-white/80 text-sm flex items-center gap-1"><Sparkles className="w-4 h-4" />{t('priorityDelivery')}</span>
                    <span className="text-white font-bold">
                      {(userPass === 'argent' || userPass === 'or') ? 'GRATUIT' : `+${formatPrice(10)}`}
                    </span>
                  </div>
                )}
                <div className="border-t border-white/20 pt-2 flex justify-between items-center">
                  <span className="text-white font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-white" />{t('total')}</span>
                  <span className="text-3xl font-bold text-white">{formatPrice(total)}</span>
                </div>
                {(() => {
                  const isWH = selectedCategory === 'Cali' || selectedCategory === 'mousse';
                  if (isWH && gameBonus2g > 0) return (
                    <div style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.35)', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>🎮</span>
                      <span style={{ color: '#c084fc', fontSize: 12, fontWeight: 700 }}>+{gameBonus2g}G Global Game offerts sur cette commande !</span>
                    </div>
                  );
                  return null;
                })()}
              </div>

              <button
                type="button"
                onClick={() => handleOrder(false)}
                disabled={isLoading || total < MINIMUM_ORDER}
                className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#22C55E',
                  border: '1.5px solid rgba(74, 144, 226, 0.4)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)',
                  cursor: (isLoading || total < MINIMUM_ORDER) ? 'not-allowed' : 'pointer'
                }}
              >
                <div className="flex items-center justify-center gap-3 relative z-10">
                  <ShoppingCart className="w-5 h-5" />
                  <span>{t('placeOrder', formatPrice(total))}</span>
                </div>
              </button>

              {/* Bouton Payer en Global Coins — masqué pour Baida/SNOW */}
              {isAuthenticated && !cart.some(item => item.category === 'Baida') && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!telegramChatId) {
                      setShowInsufficientCoinsPopup('insuffisant');
                      setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                      return;
                    }
                    if (globalCoins < 50) {
                      setShowInsufficientCoinsPopup('insuffisant');
                      setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                      return;
                    }
                    if (globalCoins < total) {
                      setShowInsufficientCoinsPopup('insuffisant');
                      setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                      return;
                    }
                    setIsLoading(true);
                    try {
                      const res = await fetchWithTimeout(`${BACKEND_URL}/client/spend-coins`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          chatId: telegramChatId,
                          amount: total,
                          products: cart.map(item => ({ name: item.product?.name || '', category: item.category || '' }))
                        })
                      }, 8000);
                      const data = await res.json();
                      if (data.success) {
                        setGlobalCoins(data.globalCoins);
                        setCoinAnimationActive(true);
                        setTimeout(() => setCoinAnimationActive(false), 800);
                        handleOrder(true);
                      } else if (data.reason === 'monthly_limit') {
                        setIsLoading(false);
                        setShowInsufficientCoinsPopup('monthly');
                        setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                      } else {
                        setIsLoading(false);
                        setShowInsufficientCoinsPopup('insuffisant');
                        setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                      }
                    } catch (e) {
                      console.error('Erreur paiement coins:', e);
                      setIsLoading(false);
                      setShowInsufficientCoinsPopup('insuffisant');
                      setTimeout(() => setShowInsufficientCoinsPopup(false), 3500);
                    }
                  }}
                  disabled={isLoading || total < MINIMUM_ORDER || globalCoins < 50 || globalCoins < total}
                 className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition disabled:cursor-not-allowed"
                  style={{
                    marginTop: 10,
                    backgroundColor: '#6823DB',
                    border: '1.5px solid rgba(74, 144, 226, 0.4)',
                    backdropFilter: 'blur(40px) saturate(300%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                    boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)',
                    cursor: (isLoading || total < MINIMUM_ORDER || globalCoins < 50 || globalCoins < total) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <CoinIcon size={20} />
                  <span>Payer en Global Coins</span>
                  <span style={{ opacity: 1, fontSize: 12 }}></span>
                </button>
              )}
            </motion.div>
          </div>
        </div>
        <MobileNavBottomSpacer />
      </div>
    );
  };

  const renderOrderConfirmation = () => (
    <div className="min-h-screen relative overflow-hidden">
      <SophisticatedBackground />
      <div className="relative z-10 p-6 flex flex-col items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <motion.div 
            className="text-center mb-8"
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div 
              className="relative w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center" 
              style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 20px 60px rgba(16, 185, 129, 0.5)' }}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <div>
              <CheckCircle className="w-20 h-20 text-white" />
              </div>
            </motion.div>
            <motion.h1 
              className="text-4xl font-bold mb-3" 
              style={{ background: 'linear-gradient(to right, #10b981, #059669, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {t('orderConfirmed')}
            </motion.h1>
            <motion.p 
              className="text-white/70 text-lg"
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >{t('orderSentToDriver')}</motion.p>
          </motion.div>

          <motion.div 
            className="rounded-2xl p-6 mb-6 relative overflow-hidden" 
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(248,249,250,0.06) 100%)',
              backdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              WebkitBackdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              border: '1.5px solid rgba(255,255,255,0.28)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.20)'
            }}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.40) 50%, transparent)', pointerEvents: 'none' }} />
            <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}>
                <ShoppingCart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">{t('orderDetails')}</h3>
                <p className="text-white/50 text-sm">#{activeOrder?.orderId?.slice(0, 8)}</p>
              </div>
            </div>
            <motion.div 
              className="space-y-3"
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {/* Affichage des produits - Liste structurée si panier */}
              {activeOrder?.products && Array.isArray(activeOrder.products) && activeOrder.products.length > 0 ? (
                <motion.div 
                  className="rounded-xl p-4 mb-3"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)' }}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-white/70" />
                    <span className="text-white/70 text-sm font-semibold">{t('orderedProducts')}</span>
                  </div>
                  <div className="space-y-2">
                    {activeOrder.products.map((product, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: 'rgba(251, 191, 36, 0.05)' }}>
                        <span className="text-white font-medium text-sm flex-1">{product.name}</span>
                        <span className="text-blue-200 font-bold text-sm ml-3">{product.quantityGrams}g</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <>
                  <motion.div 
                  className="flex justify-between items-center"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                    <span className="text-white/55 text-sm">Produit</span>
                    <span className="text-white font-bold text-sm text-right max-w-[60%]">{activeOrder?.product || '—'}</span>
                </motion.div>
                  <motion.div 
                    className="flex justify-between items-center"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                  >
                    <span className="text-white/55 text-sm">{t('quantity')}</span>
                    <span className="text-white font-bold text-sm">{activeOrder?.quantity || '—'}</span>
                  </motion.div>
                </>
              )}
              
              <motion.div 
                className="flex justify-between items-center"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.2 }}
              >
                <span className="text-white/55 text-sm">Adresse</span>
                <span className="text-white font-bold text-right text-sm max-w-[60%]">{activeOrder?.address || '—'}</span>
              </motion.div>
              <AnimatePresence>
              {activeOrder?.isPriority && (
                  <motion.div 
                    className="flex justify-between items-center"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                  <span className="text-white/55 text-sm">Livraison</span>
                  <span className="text-yellow-400 font-bold flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />Prioritaire
                  </span>
                  </motion.div>
                )}
              </AnimatePresence>
              <motion.div 
                className="flex justify-between items-center pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <span className="text-white font-bold flex items-center gap-2">
                  <Coins className="w-5 h-5 text-blue-300" />Total
                </span>
                <motion.span 
                  className="text-3xl font-bold text-white"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.2 }}
                >{activeOrder?.country === 'suisse' ? formatPriceDirect(activeOrder?.price || 0, 'CHF') : formatPriceDirect(activeOrder?.price || 0, '€')}</motion.span>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="rounded-2xl p-5 mb-6 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(248,249,250,0.05) 100%)',
              backdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              WebkitBackdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              border: '1.5px solid rgba(255,255,255,0.25)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.18)'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35) 50%, transparent)', pointerEvents: 'none' }} />
            <div className="mb-3">
              <p className="text-xs uppercase text-white/50 tracking-[0.3em]">File d'attente</p>
              <h3 className="text-lg font-bold text-white">{t('trackYourOrder')}</h3>
            </div>
            <div className={`grid gap-3 text-center ${orderStatus === 0 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <p className="text-[11px] text-white/50">Avant vous</p>
                <p className="text-2xl font-bold text-white">{queueInfo.ordersAhead ?? 0}</p>
              </div>
                {orderStatus === 0 ? (
                <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[11px] text-white/50">{t('estimatedWaitLabel')}</p>
                  <p className="text-2xl font-bold text-blue-200">
                    {`${Math.max(Math.round(queueInfo.remainingMinutes ?? queueInfo.estimatedWaitMinutes ?? 0), 0)} min`}
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[11px] text-white/50">{t('estimatedWaitLabel')}</p>
                  <p className="text-2xl font-bold text-blue-200">-</p>
                </div>
                )}
              </div>
            {/* Temps estimé premium en dessous en forme rectangulaire */}
            {/* ── Bloc temps estimé premium ── */}
            <motion.div
              className="rounded-2xl mt-4 relative overflow-hidden"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.18)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
            >
              <div style={{ padding: '16px 20px 8px' }}>
                <p className="text-[10px] uppercase text-white/45 tracking-wider font-semibold text-center">{t('estimatedTimeLabel')}</p>
              </div>
              {orderStatus === 0 ? (
                <div style={{ padding: '4px 20px 14px' }}>
                  <p className="text-sm font-bold text-white/80 text-center px-2">
                    {(queueInfo.ordersAhead ?? 0) >= 2 
                      ? t('waitingDeliveriesAhead')
                      : (queueInfo.hasEnRouteOrdersAhead ? t('driverFinishingOrderInProgress') : t('driverPreparingShort'))}
                  </p>
                </div>
              ) : (
                <div style={{ padding: '0 20px 16px', textAlign: 'center' }}>
                  <motion.span
                    className="font-extrabold text-white"
                    style={{ fontSize: 48, lineHeight: 1.1, letterSpacing: '-0.03em' }}
                    animate={{ opacity: [1, 0.8, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {Math.max(countdownMinutes, 0)}
                  </motion.span>
                  <span className="text-white/50 text-lg font-medium"> min</span>
                </div>
              )}
              {/* Barre de progression 0→100% — uniquement quand en route */}
              {(() => {
                const prog = orderStatus >= 1
                  ? Math.min(100, Math.max(0, Math.round((1 - Math.max(countdownMinutes,0) / Math.max(effectiveTotalDeliveryTime,1)) * 100)))
                  : 0; // Figé à 0 tant que la commande n'est pas en route
                return (
                  <div style={{ padding: '0 20px 16px' }}>
                    <div className="flex justify-between text-[10px] mb-1.5">
                      <span style={{ color: 'rgba(255,255,255,0.35)' }}>Progression</span>
                      <span style={{ color: 'rgba(255,255,255,0.70)', fontWeight: 700 }}>{prog}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 100, background: 'rgba(255,255,255,0.10)', overflow: 'hidden', position: 'relative' }}>
                      <motion.div
                        initial={{ width: '0%' }}
                        animate={{ width: `${prog}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        style={{
                          height: '100%',
                          borderRadius: 100,
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.90) 50%, rgba(255,255,255,0.65) 100%)',
                          boxShadow: '0 0 8px rgba(255,255,255,0.50)',
                        }}
                      />
                    </div>
                  </div>
                );
              })()}
            </motion.div>
            <p className="text-[11px] text-white/35 mt-3">
              {activeOrder?.country === 'suisse'
                ? '⚡ Suisse - Prioritaire : 25 min · Standard : 40 min. Passez prioritaire 1er'
                : '⚡ France - Prioritaire : 15 min · Standard : 25 min. Passez prioritaire 1er'}
            </p>
          </motion.div>

          <motion.div 
            className="space-y-3"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {telegramLink && (
              <motion.div 
                className="rounded-2xl p-4 mb-3 relative overflow-hidden" 
                style={{ background: 'linear-gradient(135deg, #0088cc 0%, #0066aa 100%)', boxShadow: '0 8px 24px rgba(0, 136, 204, 0.5)' }}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-white text-sm mb-3 text-center">{t('telegramTrack')}</p>
                
                <button 
                  onClick={() => setShowTelegramConfirm(true)} 
                  className="w-full py-3 px-6 rounded-xl font-bold relative overflow-hidden" 
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)', 
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 0 20px rgba(0, 136, 204, 0.4), 0 0 40px rgba(0, 136, 204, 0.3)'
                  }}
                >
                  <div className="flex items-center justify-center gap-3 text-white relative z-10">
                    <img 
                      src="/emoji/tlg.png" 
                      alt="Telegram" 
                      width={20} 
                      height={20} 
                      className="inline-block"
                    />
                    <span className="font-bold">Activer</span>
                  </div>
                </button>
              </motion.div>
            )}
            {!activeOrder?.cancelled && orderStatus < 3 && (
            <button 
              onClick={() => setCurrentView('order-tracking')} 
              className="w-full py-4 px-6 rounded-xl font-bold text-left text-white transition"
              style={{ 
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                border: '1.5px solid rgba(74, 144, 226, 0.4)',
                backdropFilter: 'blur(40px) saturate(300%)',
                WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
              }}
            >
              <div className="flex items-center gap-3 relative z-10">
                <ShoppingCart className="w-5 h-5" />
                <div>
                {t('trackOrderBtn')}
                  <p className="text-xs text-white/80">
                    {queueInfo.ordersAhead ?? 0} commande(s) avant vous · {orderStatus === 0 ? (queueInfo.hasEnRouteOrdersAhead ? t('driverFinishingOrder') : t('driverPreparingShort')) : `~${Math.max(countdownMinutes, 0)} min`}
                  </p>
                </div>
              </div>
            </button>
            )}
            <button 
              onClick={() => setCurrentView('home')} 
              className="w-full py-4 px-6 rounded-xl font-bold text-white transition"
              style={{ 
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                border: '1.5px solid rgba(74, 144, 226, 0.4)',
                backdropFilter: 'blur(40px) saturate(300%)',
                WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
              }}
            >
              <div className="flex items-center justify-center gap-3 relative z-10">
                <ChevronLeft className="w-5 h-5" />
                <span>{t('backToHome')}</span>
              </div>
            </button>
          </motion.div>
        </div>
      </div>
      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );

  const renderOrderTracking = () => {
    // Si la commande est annulée ou livrée, ne pas afficher et nettoyer
    if (activeOrder?.cancelled || orderStatus >= 3) {
      setActiveOrder(null);
      setOrderStatus(0);
      saveActiveOrderData(null);
      setCurrentView('home');
      return null;
    }
    if (!activeOrder) {
      return (
        <div className="flex flex-col items-center justify-center h-screen overflow-hidden relative" style={{ 
          background: 'linear-gradient(180deg, #1a1d24 0%, #2a2f3a 50%, #1a1d24 100%)' 
        }}>
          {/* Effet de lumière ambiante */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(74,144,226,0.10) 0%, transparent 70%)'}}>
          </div>
          
          {/* Effet de lumière bleue en arrière-plan */}
          <div className="absolute inset-0 opacity-30" style={{ 
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(74, 144, 226, 0.4) 0%, transparent 70%)' 
          }}></div>
          
          {/* Carte glassmorphism principale */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 text-center card-premium shadow-multilayer rounded-3xl p-12 max-w-md mx-4"
            style={{
              background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
              backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 60px rgba(74, 144, 226, 0.2)'
            }}
          >
            {/* Loader avec effet bleu */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* Cercle externe animé */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
                  border: '2px solid rgba(74, 144, 226, 0.5)'
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 0.8, 0.6]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              {/* Cercle interne avec loader */}
              <div className="absolute inset-4 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Loader 
                  className="w-16 h-16 text-white spin-smooth" 
                  style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
                />
              </div>
            </div>
            
            {/* Titre avec gradient neige */}
            <h2 className="text-3xl font-bold mb-3 gradient-text-snow"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
              }}
            >
              Chargement du suivi...
            </h2>
          </motion.div>
        </div>
      );
    }

    const trackingSteps = [
      { id: 0, label: t('trackStep0Label'), emoji: "📦", color: "#fbbf24" },
      { id: 1, label: t('trackStep1Label'), emoji: "🚚", color: "#f59e0b" },
      { id: 2, label: t('trackStep2Label'), emoji: "⏰", color: "#d97706" },
      { id: 3, label: t('trackStep3Label'), emoji: "✅", color: "#10b981" },
    ];

    return (
      <div className="min-h-screen relative overflow-hidden" style={{ background: 'transparent' }}>
        <SophisticatedBackground />
        {showConfetti && <Confetti />}
        <div className="relative z-10 p-5 pb-24 max-w-md mx-auto w-full gs-page-pad">
          
          {/* ── En-tête ── */}
          <div className="flex items-center gap-3 mb-6 pt-2">
            <button 
              onClick={() => setCurrentView('home')} 
              className="p-2.5 rounded-xl text-white transition"
              style={{
                background: 'linear-gradient(135deg, rgba(74,144,226,0.2) 0%, rgba(107,163,232,0.15) 50%, rgba(74,144,226,0.2) 100%)',
                border: '1.5px solid rgba(74,144,226,0.4)',
                backdropFilter: 'blur(40px) saturate(300%)',
                WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                boxShadow: '0 4px 16px rgba(74,144,226,0.3), 0 0 0 1px rgba(74,144,226,0.2) inset'
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-white">Suivi de Commande</h2>
              <p className="text-white/50 text-xs flex items-center gap-1 mt-0.5">
                <Truck className="w-3 h-3" /> Livraison en temps réel
              </p>
            </div>
          </div>

          {/* ── Carte commande ── */}
          <motion.div
            className="rounded-2xl p-5 mb-4 relative overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)',
              border: '1.5px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              WebkitBackdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.22)'
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.38) 50%, transparent)', pointerEvents: 'none' }} />
            <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <div className="p-2.5 rounded-xl" style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)'
              }}>
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">
                  Commande de {activeOrder?.orderName || userName || 'Client'}
                </h3>
                <p className="text-white/50 text-xs">#{activeOrder?.orderId?.slice(0,8)}</p>
              </div>
            </div>
            <div className="space-y-2.5">
              {/* Produits */}
              {activeOrder?.products && activeOrder.products.length > 0 ? (
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {activeOrder.products.map((p, i) => (
                    <div key={i} className="flex justify-between items-center py-1">
                      <span className="text-white/80 text-sm font-medium">{p.name}</span>
                      <span className="text-blue-200 text-sm font-bold">{p.quantityGrams}g</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80 text-sm font-medium">{activeOrder?.product || '—'}</span>
                    <span className="text-white text-sm font-bold">{activeOrder?.quantity || '—'}</span>
                  </div>
                </div>
              )}
              {/* Adresse + Prix */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Adresse</p>
                  <p className="text-white text-xs font-medium leading-snug">{activeOrder?.address || '—'}</p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Total</p>
                  <p className="text-white text-sm font-bold">{activeOrder?.country === 'suisse' ? formatPriceDirect(activeOrder?.price || 0, 'CHF') : formatPriceDirect(activeOrder?.price || 0, '€')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── File d'attente ── */}
          <motion.div
            className="rounded-2xl mb-4 relative overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1.5px solid rgba(255,255,255,0.25)',
              backdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              WebkitBackdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.20)'
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35) 50%, transparent)', pointerEvents: 'none' }} />
            {/* Stats avant/temps */}
            <div style={{ padding: '16px 16px 12px' }}>
              <p className="text-[10px] uppercase text-white/40 tracking-wider mb-3">File d'attente</p>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">Avant vous</p>
                  <p className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>{queueInfo.ordersAhead ?? 0}</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">{t('estimatedTimeLabel')}</p>
                  <p className="text-3xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>
                    {orderStatus >= 1
                      ? Math.max(countdownMinutes, 0)
                      : Math.max(Math.round(queueInfo.remainingMinutes ?? queueInfo.estimatedWaitMinutes ?? 0), 0)}
                    <span className="text-sm font-normal text-white/45"> min</span>
                  </p>
                </div>
              </div>
            </div>
            {/* Barre de progression — uniquement quand la commande est en route (status >= 1) */}
            {orderStatus >= 1 && (() => {
              const prog = Math.min(100, Math.max(0, Math.round((1 - Math.max(countdownMinutes,0) / Math.max(effectiveTotalDeliveryTime,1)) * 100)));
              return (
                <div style={{ padding: '0 16px 16px' }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] text-white/35 uppercase tracking-wider">Progression livraison</span>
                    <span className="text-xs font-bold text-white/70">{prog}%</span>
                  </div>
                  {/* Track de fond */}
                  <div style={{ height: 8, borderRadius: 100, background: 'rgba(255,255,255,0.08)', position: 'relative', overflow: 'hidden' }}>
                    {/* Barre de progression animée */}
                    <motion.div
                      initial={{ width: '0%' }}
                      animate={{ width: `${prog}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      style={{
                        height: '100%',
                        borderRadius: 100,
                        background: 'linear-gradient(90deg, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0.85) 60%, rgba(255,255,255,0.65) 100%)',
                        boxShadow: '0 0 10px rgba(255,255,255,0.45), 0 0 20px rgba(255,255,255,0.20)',
                        position: 'relative',
                      }}
                    >
                      {/* Reflet brillant sur la barre */}
                      <div style={{ position: 'absolute', top: 1, left: 4, right: 4, height: '40%', borderRadius: 100, background: 'rgba(255,255,255,0.50)' }} />
                      {/* Indicateur de tête */}
                      {prog > 3 && (
                        <motion.div
                          animate={{ opacity: [0.8, 1, 0.8], boxShadow: ['0 0 6px rgba(255,255,255,0.8)', '0 0 14px rgba(255,255,255,1)', '0 0 6px rgba(255,255,255,0.8)'] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          style={{ position: 'absolute', right: -1, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: 'white', boxShadow: '0 0 10px white' }}
                        />
                      )}
                    </motion.div>
                  </div>
                </div>
              );
            })()}
            <div style={{ padding: '0 16px 14px' }}>
              <p className="text-[10px] text-white/25 text-center">
                {activeOrder?.country === 'suisse' ? '⚡ Suisse — Prioritaire : 25 min · Standard : 40 min' : '⚡ France — Prioritaire : 15 min · Standard : 25 min'}
              </p>
            </div>
          </motion.div>

          {/* ── Infos si commande assignée ── */}
          <AnimatePresence>
            {queueInfo.ordersAhead !== null && orderStatus === 0 && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="w-full rounded-2xl p-4 mb-4 text-left"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',
                  border: '1.5px solid rgba(255,255,255,0.20)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  cursor: 'default'
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.20)' }}>
                    <Clock className="w-4 h-4 text-white/80" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">
                      {queueInfo.ordersAhead === 0 ? t('driverArriving') : `${queueInfo.ordersAhead} commande(s) avant vous`}
                    </p>
                    <p className="text-white/50 text-xs">
                      {orderStatus === 0 ? (queueInfo.hasEnRouteOrdersAhead ? t('driverFinishingOrder') : t('driverPreparingShort')) : `~${Math.max(countdownMinutes,0)} min`}
                    </p>
                  </div>
                </div>
              </motion.button>
            )}
          </AnimatePresence>

          {/* ── Étapes de suivi ── */}
          <motion.div
            className="rounded-2xl p-5 mb-4 relative overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1.5px solid rgba(255,255,255,0.24)',
              backdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              WebkitBackdropFilter: 'blur(45px) saturate(320%) brightness(1.12)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.18)'
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.32) 50%, transparent)', pointerEvents: 'none' }} />
            <div className="relative">
              <div className="absolute left-[27px] top-4 bottom-4 w-0.5 rounded-full" style={{ background: 'rgba(107,163,232,0.15)' }}>
                <div className="absolute top-0 left-0 right-0 rounded-full transition-all duration-1000" style={{ height: `${(orderStatus/3)*100}%`, background: 'linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%)' }} />
              </div>
              <div className="space-y-4">
                {trackingSteps.map((step) => {
                  const isCompleted = orderStatus >= step.id;
                  const isCurrent = orderStatus === step.id;
                  return (
                    <motion.div key={step.id} className="flex items-start gap-4" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
                      <motion.div
                        className="relative z-10 flex items-center justify-center w-14 h-14 rounded-xl flex-shrink-0"
                        animate={isCurrent ? { scale: [1, 1.05, 1] } : {}}
                        transition={{ duration: 2, repeat: Infinity }}
                        style={{
                          background: isCompleted ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)',
                          border: isCurrent ? '1.5px solid rgba(255,255,255,0.60)' : isCompleted ? '1.5px solid rgba(255,255,255,0.25)' : '1.5px solid rgba(255,255,255,0.08)',
                          boxShadow: isCurrent ? '0 0 20px rgba(255,255,255,0.20), inset 0 1px 0 rgba(255,255,255,0.30)' : 'none'
                        }}
                      >
                        <span className="text-2xl">{step.emoji}</span>
                      </motion.div>
                      <div className={`flex-1 p-3 rounded-xl transition-all duration-500 ${isCurrent ? 'scale-[1.02]' : ''}`} style={{
                        background: isCompleted ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.03)',
                        border: isCurrent ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.07)'
                      }}>
                        <h3 className={`font-bold text-sm ${isCompleted ? 'text-white' : 'text-white/30'}`}>{step.label}</h3>
                        <p className={`text-xs mt-0.5 ${isCompleted ? 'text-blue-200/70' : 'text-white/20'}`}>
                          {step.id === 0 && t('trackStep0Desc')}
                          {step.id === 1 && t('trackStep1Desc')}
                            {step.id === 2 && t('trackStep2Desc')}
                          {step.id === 3 && t('trackStep3Desc')}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* ── Boutons d'actions ── */}
          <motion.div
            className="space-y-3 mb-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            {/* Jouer à qui paye */}
            <motion.button
              onClick={() => { setShowPlayerSelection(true); setGamePhase('waiting'); setFingerPositions([]); setGameWinner(null); }}
              whileTap={{ scale: 0.97 }}
              className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition"
              animate={{
                boxShadow: [
                  '0 4px 16px rgba(125,211,252,0.2), 0 0 0 1px rgba(125,211,252,0.25) inset',
                  '0 4px 24px rgba(125,211,252,0.40), 0 0 0 1px rgba(125,211,252,0.40) inset',
                  '0 4px 16px rgba(125,211,252,0.2), 0 0 0 1px rgba(125,211,252,0.25) inset'
                ]
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background: 'linear-gradient(145deg, #1c1c1c 0%, #141414 100%)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.09)',
              }}
            >
              <div className="flex items-center justify-center gap-3">
                <span>{t('playWhoPayGame')}</span>
              </div>
            </motion.button>

            {/* Telegram notifications */}
            {telegramLink && (
              <button
                onClick={async () => {
                  if (telegramNotificationsEnabled && activeOrder?.orderId) {
                    try {
                      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${activeOrder.orderId}/telegram-notifications`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ enabled: false })
                      });
                      const data = await response.json();
                      if (data.success) setTelegramNotificationsEnabled(false);
                    } catch (error) { console.error('Erreur désactivation notifications:', error); }
                  } else {
                    setShowTelegramConfirm(true);
                  }
                }}
                className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition"
                style={{
                  background: telegramNotificationsEnabled
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(5,150,105,0.14) 100%)'
                    : 'linear-gradient(135deg, rgba(0,136,204,0.18) 0%, rgba(0,102,170,0.14) 100%)',
                  border: telegramNotificationsEnabled ? '1.5px solid rgba(16,185,129,0.35)' : '1.5px solid rgba(0,136,204,0.40)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  boxShadow: telegramNotificationsEnabled ? '0 4px 16px rgba(16,185,129,0.2)' : '0 4px 16px rgba(0,136,204,0.2)'
                }}
              >
                <div className="flex items-center justify-center gap-3">
                  {telegramNotificationsEnabled ? (
                    <><span className="text-green-300 font-bold">{t('notificationsEnabled')}</span><span className="text-white/50 text-sm">{t('deactivateShort')}</span></>
                  ) : (
                    <><img src="/emoji/tlg.png" alt="Telegram" width={20} height={20} /><span>{t('telegramTrack')}</span></>
                  )}
                </div>
              </button>
            )}

            {/* Annuler la commande */}
            {orderStatus === 0 && !activeOrder?.cancelled && (
              <button
                onClick={async () => {
                  if (window.confirm(t('cancelOrderConfirmMsg'))) {
                    const success = await cancelOrder(activeOrder?.orderId);
                    if (success) {
                      setOrderStatus(3);
                      setTimeout(() => { setCurrentView('home'); setActiveOrder(null); saveActiveOrderData(null); }, 2000);
                    }
                  }
                }}
                className="w-full py-4 px-6 rounded-xl font-bold text-white transition"
                style={{
                  background: 'linear-gradient(135deg, rgba(220,38,38,0.18) 0%, rgba(185,28,28,0.14) 100%)',
                  border: '1.5px solid rgba(220,38,38,0.35)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  boxShadow: '0 4px 16px rgba(220,38,38,0.18)'
                }}
              >
                <div className="flex items-center justify-center gap-3">
                  <X className="w-5 h-5 text-red-300" />
                  <span className="text-red-200">Annuler ma commande</span>
                </div>
              </button>
            )}

            {/* Commande annulée */}
            {activeOrder?.cancelled && (
              <div className="w-full py-4 px-6 rounded-xl font-bold text-white/60 text-center"
                style={{ background: 'rgba(107,114,128,0.15)', border: '1.5px solid rgba(107,114,128,0.25)' }}>
                <span>{t('orderCancelledLabel')}</span>
              </div>
            )}
          </motion.div>

          {/* ── Retour accueil ── */}
          <button
            onClick={() => setCurrentView('home')}
            className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition"
            style={{
              background: 'linear-gradient(135deg, rgba(74,144,226,0.2) 0%, rgba(107,163,232,0.15) 50%, rgba(74,144,226,0.2) 100%)',
              border: '1.5px solid rgba(74,144,226,0.4)',
              backdropFilter: 'blur(40px) saturate(300%)',
              WebkitBackdropFilter: 'blur(40px) saturate(300%)',
              boxShadow: '0 4px 16px rgba(74,144,226,0.3), 0 0 0 1px rgba(74,144,226,0.2) inset, 0 0 30px rgba(74,144,226,0.2)'
            }}
          >
            <div className="flex items-center justify-center gap-3">
              <ChevronLeft className="w-5 h-5" />
              <span>{t('backToHome')}</span>
            </div>
            </button>
          </div>
        </div>
    );
  };

  const renderDriverPanel = () => {
    // Si une commande est en vue détaillée, afficher cette vue
    if (driverOrderDetailView) {
      return renderDriverOrderDetail();
    }

    return (
    <div className="min-h-screen relative overflow-hidden">
      <SophisticatedBackground />
      <div className="relative z-10 p-4 sm:p-6 max-w-6xl mx-auto w-full">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10 w-full">
          <div>
            <p className="text-sm uppercase text-white/70 tracking-[0.35em] mb-2 flex items-center gap-2">
              Interface Livreur
              {hasDriverNotifications && (
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
              )}
            </p>
            <h1 className="text-3xl font-bold text-white">Gestion Livraison</h1>
            <p className="text-white/80 text-sm mt-1 flex items-center gap-2">
              Gérez et surveillez vos commande en temps réel.
              {driverChatUnread > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-600/20 text-red-200 border border-red-600/30">
                  {driverChatUnread} nouveau{driverChatUnread > 1 ? 'x' : ''}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <button
              onClick={() => fetchDriverOrders({ showSpinner: true })}
              className="px-4 py-3 rounded-2xl font-semibold text-white w-full sm:w-auto text-center"
              style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              Actualiser
            </button>
            <button
              onClick={openDriverChatFromPanel}
              disabled={selectedDriverOrder?.status === 3}
              className={`px-4 py-3 rounded-2xl font-semibold w-full sm:w-auto text-center relative ${
                selectedDriverOrder?.status === 3 
                  ? 'text-white/60 cursor-not-allowed opacity-60' 
                  : 'text-gray-900'
              }`}
              style={{ 
                background: selectedDriverOrder?.status === 3 
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' 
              }}
            >
              {selectedDriverOrder?.status === 3 ? 'delivered' : 'Contacter le client'}
              {selectedDriverOrder?.status !== 3 && driverChatUnread > 0 && (
                <span className="absolute -top-2 -right-2 h-6 min-w-[1.5rem] px-2 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow-lg">
                  {driverChatUnread}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="rounded-3xl p-4 border border-white/20 bg-white/5 backdrop-blur-md">
            <div className="flex items-center justify-between text-white/70 text-xs uppercase tracking-wide">
              <span>Prioritaires</span>
              <Sparkles className="w-4 h-4 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white mt-2">{driverOrderStats.priority}</p>
            <p className="text-xs text-white/60 mt-1">TOTAL</p>
          </div>
          <div className="rounded-3xl p-4 border border-white/20 bg-white/5 backdrop-blur-md">
            <div className="flex items-center justify-between text-white/70 text-xs uppercase tracking-wide">
              <span>Standard</span>
              <Package className="w-4 h-4 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white mt-2">{driverOrderStats.standard}</p>
            <p className="text-xs text-white/60 mt-1">TOTAL</p>
          </div>
          <div className="rounded-3xl p-4 border border-white/20 bg-white/5 backdrop-blur-md">
            <div className="flex items-center justify-between text-white/70 text-xs uppercase tracking-wide">
              <span>En route</span>
              <Truck className="w-4 h-4 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white mt-2">{driverOrderStats.enRoute + driverOrderStats.arriving}</p>
            <p className="text-xs text-white/60 mt-1">TOTAL</p>
          </div>
          <div className="rounded-3xl p-4 border border-white/20 bg-white/5 backdrop-blur-md">
            <div className="flex items-center justify-between text-white/70 text-xs uppercase tracking-wide">
              <span>TOTAL</span>
              <CheckCircle className="w-4 h-4 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white mt-2">{driverDeliveredCount}</p>
            <p className="text-xs text-white/60 mt-1">TOTAL</p>
          </div>
        </div>
       
        {/* Compteur d'argent du livreur */}
        <motion.div 
          className="rounded-3xl p-5 border border-amber-500/50 bg-gradient-to-br from-amber-500/20 to-yellow-500/10 backdrop-blur-md mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('revenueGenerated')}</p>
              <p className="text-4xl font-bold text-white">
                {driverCountry === 'suisse' 
                  ? formatPriceDirect(driverEarnings / EUR_TO_CHF_RATE, 'CHF')
                  : formatPriceDirect(driverEarnings, '€')
                }
              </p>
              <p className="text-xs text-white/60 mt-1"></p>
            </div>
            <div className="text-5xl"></div>
          </div>
        </motion.div>

        {/* Bouton de déconnexion */}
        <motion.button
          onClick={handleDriverDisconnect}
          className="w-full py-4 px-6 rounded-2xl font-bold text-white flex items-center justify-center gap-3 mb-6"
          style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)'
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <LogOut className="w-5 h-5" />
          <span> {t('logout')}</span>
        </motion.button>

        <p className="text-white/60 text-xs mb-6">
          Déconnectez-vous uniquement si vous avez terminé ou si vous êtes en pause .
        </p>

        {driverOrders.length === 0 && driverLoadingOrders ? (
          <div className="flex flex-col items-center justify-center py-20">
            {/* Loader avec effet bleu */}
            <div className="relative w-24 h-24 mb-6">
              {/* Cercle externe animé */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
                  border: '2px solid rgba(74, 144, 226, 0.5)'
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 0.8, 0.6]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              {/* Cercle interne avec loader */}
              <div className="absolute inset-3 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Loader 
                  className="w-12 h-12 text-white spin-smooth" 
                  style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
                />
              </div>
            </div>
            <p className="text-white/90 text-lg font-semibold gradient-text-snow"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
            Chargement des commandes...
            </p>
          </div>
        ) : driverOrders.length === 0 && driverOrdersError ? (
          <div className="rounded-3xl p-10 text-center" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(248,113,113,0.35)' }}>
            <p className="text-lg text-white/90">
              {driverOrdersErrorKind === 'auth'
                ? 'Ta session a expiré.'
                : driverOrdersErrorKind === 'server'
                  ? 'Service momentanément indisponible.'
                  : 'Impossible de charger tes commandes.'}
            </p>
            <p className="text-sm text-white/60 mt-2">
              {driverOrdersErrorKind === 'auth'
                ? 'Rouvre l’application depuis le bouton dans Telegram, puis réessaie.'
                : driverOrdersErrorKind === 'server'
                  ? 'Réessaie dans quelques instants.'
                  : 'Vérifie ta connexion puis relance la synchronisation.'}
            </p>
            <button
              onClick={() => (driverOrdersErrorKind === 'auth' ? window.location.reload() : fetchDriverOrders({ showSpinner: true }))}
              className="mt-5 px-6 py-3 rounded-2xl font-semibold text-white"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              {driverOrdersErrorKind === 'auth' ? 'Rouvrir l’application' : 'Réessayer'}
            </button>
          </div>
        ) : driverOrders.length === 0 ? (
          <div className="rounded-3xl p-10 text-center text-white/80 border border-white/20" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
            <p className="text-lg">Aucune commande pour le moment.</p>
            <p className="text-sm text-white/60 mt-2"></p>
          </div>
        ) : ( 
          <div className="relative">
            {driverOrdersError && (
              <div className="mb-4 rounded-2xl px-4 py-3 text-xs text-amber-200 flex items-center justify-between gap-3"
                style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <span>Synchronisation impossible — dernière liste connue affichée.</span>
                <button onClick={() => fetchDriverOrders()} className="underline shrink-0">Réessayer</button>
              </div>
            )}
            {driverLoadingOrders && driverOrders.length > 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl backdrop-blur-sm" style={{
                background: 'rgba(26, 29, 36, 0.7)'
              }}>
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-16 h-16">
                    {/* Cercle externe animé */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
                        border: '2px solid rgba(74, 144, 226, 0.5)'
                      }}
                      animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.6, 0.8, 0.6]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    {/* Cercle interne avec loader */}
                    <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{
                      background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <Loader 
                        className="w-8 h-8 text-white spin-smooth" 
                        style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
                      />
                    </div>
                  </div>
                  <p className="text-white/90 text-sm font-semibold">Actualisation…</p>
                </div>
              </div>
            )}
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[...driverOrders]
            .filter((order) => Boolean(order && order.orderId))
            .sort((a, b) => {
              // Trier les commandes : prioritaires en premier, puis par date
              if (a.isPriority && !b.isPriority) return -1;
              if (!a.isPriority && b.isPriority) return 1;
              return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            }).map((order) => {
              const statusStyle = orderStatusStyles[order.status] || orderStatusStyles[0];
              // Vérifier si cette commande est assignée à un autre livreur
              const isAssignedToOther = order.assignedDriverChatId && order.assignedDriverChatId !== telegramChatId;
              const isAssignedToMe = order.assignedDriverChatId === telegramChatId;
              return (
                <div
                  key={order.orderId}
                  onClick={() => setDriverOrderDetailView(order)}
                    className={`rounded-3xl p-5 border flex flex-col gap-4 w-full cursor-pointer transition-all hover:scale-[1.01] ${
                      isAssignedToOther 
                        ? 'border-yellow-500/50 bg-yellow-500/5' 
                        : isAssignedToMe
                        ? order.isPriority
                          ? 'border-green-500/50 bg-gradient-to-br from-green-500/20 via-amber-500/15 to-yellow-500/10'
                          : 'border-green-500/50 bg-green-500/5'
                        : order.isPriority
                        ? 'border-amber-400/70 bg-gradient-to-br from-amber-500/30 via-yellow-500/20 to-amber-400/15'
                        : 'border-white/20 bg-white/5'
                    }`}
                    style={{ 
                      position: 'relative',
                      backdropFilter: 'blur(12px)',
                      boxShadow: order.isPriority 
                        ? '0 6px 24px rgba(245, 158, 11, 0.4), 0 0 0 1px rgba(251, 191, 36, 0.3) inset, 0 0 30px rgba(251, 191, 36, 0.15)' 
                        : undefined,
                      background: order.isPriority && !isAssignedToMe && !isAssignedToOther
                        ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(251, 191, 36, 0.18) 50%, rgba(217, 119, 6, 0.22) 100%)'
                        : undefined
                    }}
                >
                  {/* Badge temps estimé restant — uniquement si commande en route */}
                  {order.status >= 1 && order.status < 3 && (() => {
                    void driverCardTick; // force re-render chaque seconde
                    let remainingSecs = null;
                    if (order.enRouteEstimatedMinutes !== undefined && order.enRouteAt) {
                      const elapsedMs = Date.now() - new Date(order.enRouteAt).getTime();
                      const elapsedMin = elapsedMs / 60000;
                      remainingSecs = Math.max(Math.round((order.enRouteEstimatedMinutes - elapsedMin) * 60), 0);
                    }
                    const displayMin = remainingSecs !== null ? Math.floor(remainingSecs / 60) : null;
                    const displaySec = remainingSecs !== null ? remainingSecs % 60 : null;
                    return (
                      <div style={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: order.status === 1
                          ? 'linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(5,150,105,0.35) 100%)'
                          : 'linear-gradient(135deg, rgba(245,158,11,0.25) 0%, rgba(217,119,6,0.35) 100%)',
                        border: order.status === 1
                          ? '1px solid rgba(16,185,129,0.5)'
                          : '1px solid rgba(245,158,11,0.5)',
                        borderRadius: 10,
                        padding: '4px 10px',
                        backdropFilter: 'blur(10px)',
                      }}>
                        <Clock style={{ width: 12, height: 12, color: order.status === 1 ? '#6ee7b7' : '#fcd34d' }} />
                        <span style={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 700,
                          fontSize: 12,
                          color: order.status === 1 ? '#6ee7b7' : '#fcd34d',
                          letterSpacing: '0.02em',
                          fontVariantNumeric: 'tabular-nums'
                        }}>
                          {displayMin !== null
                            ? `${displayMin}:${String(displaySec).padStart(2, '0')}`
                            : '—'}
                        </span>
                      </div>
                    );
                  })()}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                      <p className="text-white/70 text-xs uppercase tracking-widest">Commande</p>
                        <h3 className="text-white text-xl font-bold truncate flex items-center gap-2">
                          {order.orderName || order.customerName || 'Sans nom'}
                          {(order.clientDeliveredOrdersCount || 0) >= 3 && (
                            <img src="/emoji/certif.png" alt="certifié" title="Client certifié (3+ commandes livrées)" style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
                          )}
                        </h3>
                      <p className="text-xs text-white/60 mt-1 flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {order.customerPhone || 'Non disponible'}
                      </p>
                      {/* Afficher qui a pris la commande */}
                      {order.assignedDriverChatId && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${
                          isAssignedToMe ? 'text-green-400' : 'text-yellow-400'
                        }`}>
                          {isAssignedToMe ? (
                            <>✅ Vous avez pris cette commande</>
                          ) : (
                            <>⚠️ Prise par un autre livreur</>
                          )}
                        </p>
                      )}
                    </div>
                    <span
                        className="px-3 py-1 rounded-full text-xs font-semibold text-center"
                      style={{ 
                        background: order.cancelled ? 'rgba(107, 114, 128, 0.3)' : statusStyle.bg, 
                        color: order.cancelled ? '#9ca3af' : statusStyle.color 
                      }}
                    >
                      {order.cancelled ? 'Annulée' : statusStyle.label}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm text-white/80">
                    {order.isPriority && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-amber-400/30 to-yellow-400/30 border border-amber-400/50 text-amber-200 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Prioritaire
                        </span>
                      </div>
                    )}
                    
                    {/* Affichage des produits - Liste structurée si panier */}
                    {order.products && Array.isArray(order.products) && order.products.length > 0 ? (
                      <div className="rounded-lg p-2.5 mb-2" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Package className="w-3.5 h-3.5 text-white/60" />
                          <span className="text-white/70 text-xs font-medium">Produits</span>
                        </div>
                        <div className="space-y-1.5">
                          {order.products.map((product, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                              <span className="text-white text-xs font-medium flex-1 truncate pr-2">{product.name}</span>
                              <span className="text-white/90 font-bold text-xs whitespace-nowrap">{product.quantityGrams}g</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-white/60" />
                        <div className="flex-1 min-w-0">
                          <span className="text-white/70 text-xs">Produit: </span>
                          <span className="text-white text-xs font-medium truncate">{order.product || '—'}</span>
                          {order.quantity && (
                            <>
                              <span className="text-white/70 text-xs mx-1">•</span>
                              <span className="text-white text-xs font-medium">{order.quantity}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {order.city && (
                      <p className="flex items-center gap-2 text-white/90 font-medium">
                        <MapPin className="w-4 h-4 text-amber-300/80" />
                        <span>{order.country === 'suisse' ? '🇨🇭' : '🇫🇷'} {order.city}</span>
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-white/60" />
                        <span className="line-clamp-2">{order.address || 'Adresse indisponible'}</span>
                    </p>
                    {order.notes && <p className="text-white/60 text-xs italic">Note: {order.notes}</p>}
                <p className="flex items-center gap-2 text-white/70 text-xs">
                  <MessageCircle className="w-3 h-3 text-white/60" />
                  Notifications client : <span className="font-bold text-white">{order.clientNotificationCount ?? 0}</span>
                </p>
                  </div>
                    <div className="flex flex-wrap items-center justify-between text-white/80 text-sm gap-2">
                    <span>Total</span>
                    <strong className="text-white text-lg">{order.price ? (order.country === 'suisse' ? formatPriceDirect(order.price, 'CHF') : formatPriceDirect(order.price, '€')) : '—'}</strong>
                  </div>
                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDriverContact(order, { navigateToChat: true });
                      }}
                      disabled={order.status === 3}
                      className={`mt-auto flex items-center justify-center gap-2 rounded-2xl py-3 font-semibold w-full ${
                        order.status === 3 
                          ? 'text-white/60 cursor-not-allowed opacity-60' 
                          : 'text-gray-900'
                      }`}
                      style={{ 
                        background: order.status === 3 
                          ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' 
                      }}
                  >
                      {order.status === 3 ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Livré
                        </>
                      ) : (
                        <>
                    <MessageCircle className="w-4 h-4" />
                    Contacter le client
                        </>
                      )}
                      {order.status !== 3 && order.orderId === selectedDriverOrder?.orderId && driverChatUnread > 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">
                        {driverChatUnread}
                      </span>
                    )}
                  </button>
                  {/* Bouton reprendre la commande — visible uniquement si assignée à un autre livreur */}
                  {isAssignedToOther && order.status !== 3 && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm('Reprendre cette commande ? Vous devenez le livreur responsable et reprenez là où elle en est.')) return;
                        try {
                          const res = await fetchWithTimeout(`${BACKEND_URL}/orders/${order.orderId}/takeover`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ chatId: telegramChatId })
                          }, 8000);
                          const data = await res.json();
                          if (data.success) {
                            toast.success('Commande reprise avec succès');
                            fetchDriverOrders();
                          } else {
                            toast.error(data.message || 'Erreur lors de la reprise');
                          }
                        } catch (err) {
                          console.error('Erreur takeover:', err);
                          toast.error('Erreur réseau lors de la reprise');
                        }
                      }}
                      className="mt-2 flex items-center justify-center gap-2 rounded-2xl py-3 font-semibold w-full text-white"
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        border: '1.5px solid rgba(251,191,36,0.5)',
                        boxShadow: '0 4px 16px rgba(245,158,11,0.3)',
                      }}
                    >
                      <Truck className="w-4 h-4" />
                      Reprendre la commande
                    </button>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };

  // Fonction pour mettre à jour le statut d'une commande
  const handleDriverStatusUpdate = async (orderId, newStatus) => {
    if (!telegramChatId) {
      toast.error(t('chatIdMissing'));
      return;
    }

    try {
      const statusMap = {
        'en_route': 'en_route',
        'arrivee_imminente': 'arrivee_imminente',
        'livre': 'livre'
      };
      
      const statusValue = statusMap[newStatus] || newStatus;
      
      const response = await fetchWithTimeout(`${BACKEND_URL}/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Id': telegramChatId
        },
        body: JSON.stringify({
          orderId,
          status: statusValue,
          chatId: telegramChatId
        })
      }, 10000);

      const data = await response.json();
      
      if (!data.success && response.status === 409 && data.message && data.message.includes('Une commande est déjà en route')) {
        toast.error('⚠️ Une commande est déjà en route. Terminez-la avant d\'en mettre une autre en route.', { duration: 5000 });
        return;
      }

      if (data.success) {
        toast.success(`${t('statusUpdated')} : ${newStatus === 'en_route' ? t('enRoute') : newStatus === 'arrivee_imminente' ? t('arrivalImminent') : t('delivered')}`);
        
        // Mettre à jour immédiatement le statut localement pour un feedback instantané
        const statusValueMap = {
          'en_route': 1,
          'arrivee_imminente': 2,
          'livre': 3
        };
        const newStatusValue = statusValueMap[newStatus];
        
        console.log(`🔄 Mise à jour statut: ${newStatus} -> ${newStatusValue} pour commande ${orderId.slice(0, 8)}`);
        
        // Mettre à jour la vue détaillée immédiatement
        if (driverOrderDetailView?.orderId === orderId && newStatusValue !== undefined) {
          setDriverOrderDetailView(prev => ({
            ...prev,
            status: newStatusValue
          }));
          console.log(`✅ Vue détaillée mise à jour avec statut ${newStatusValue}`);
        }
        
        // Mettre à jour aussi dans la liste des commandes
        if (newStatusValue !== undefined) {
          setDriverOrders(prevOrders => 
            prevOrders.map(o => 
              o.orderId === orderId 
                ? { ...o, status: newStatusValue }
                : o
            )
          );
        }
        
        // Rafraîchir les commandes depuis le backend pour synchroniser (IMPORTANT pour avoir le statut à jour)
        await fetchDriverOrders({ showSpinner: false });
        
        // Mettre à jour la vue détaillée avec les données du backend après rafraîchissement
        setTimeout(async () => {
          await fetchDriverOrders({ showSpinner: false });
          
          setDriverOrders(currentOrders => {
            const updatedOrder = currentOrders.find(o => o.orderId === orderId);
            if (updatedOrder && driverOrderDetailView?.orderId === orderId) {
              console.log(`🔄 Synchronisation vue détaillée avec backend, statut: ${updatedOrder.status}`);
              setDriverOrderDetailView(updatedOrder);
              
              if (updatedOrder.status === 3) {
                console.log(`✅ Commande ${orderId.slice(0, 8)} confirmée comme livrée côté livreur`);
              }
            }
            return currentOrders;
          });
        }, 500);
      } else {
        toast.error(data.message || t('errorUpdatingStatus'));
      }
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      toast.error(t('errorUpdatingStatus'));
    }
  };

  // Fonction pour déconnecter le livreur
  const handleDriverDisconnect = async () => {
    if (!telegramChatId) {
      toast.error(t('chatIdMissing'));
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir vous déconnecter du panel ?')) {
      return;
    }

    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/driver/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Id': telegramChatId
        },
        body: JSON.stringify({ chatId: telegramChatId })
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('logoutSuccess'));
        // Réinitialiser l'état du livreur
        setIsDriverMode(false);
        setDriverOrders([]);
        setDriverOrderDetailView(null);
        setCurrentView('home');
        // Nettoyer le localStorage (y compris le cache local des commandes)
        localStorage.removeItem('telegramChatId');
        localStorage.removeItem(DRIVER_ORDERS_CACHE_KEY);
        setTelegramChatId(null);
      } else {
        toast.error(data.message || t('errorLoggingOut'));
      }
    } catch (error) {
      console.error('Erreur déconnexion livreur:', error);
      toast.error(t('errorLoggingOut'));
    }
  };

  // Fonction pour annuler une commande
  const handleDriverCancelOrder = async (orderId) => {
    if (!telegramChatId) {
      toast.error(t('chatIdMissing'));
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) {
      return;
    }

    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Id': telegramChatId
        },
        body: JSON.stringify({ chatId: telegramChatId })
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('orderCancelledSuccessfully'));
        await fetchDriverOrders({ showSpinner: false });
        setDriverOrderDetailView(null);
      } else {
        toast.error(data.message || t('errorCancelling'));
      }
    } catch (error) {
      console.error('Erreur annulation commande:', error);
      toast.error(t('errorCancellingOrder'));
    }
  };

  // Fonction pour définir le temps exact
  const handleDriverSetExactTime = async (orderId, exactMinutes) => {
    if (!telegramChatId) {
      toast.error(t('chatIdMissing'));
      return;
    }

    if (!exactMinutes || exactMinutes < 1 || exactMinutes > 300) {
      toast.error(t('timeMustBeBetween'));
      return;
    }

    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/orders/${orderId}/exact-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Id': telegramChatId
        },
        body: JSON.stringify({
          exactMinutes: parseInt(exactMinutes, 10),
          chatId: telegramChatId
        })
      }, 10000);

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('exactTimeSet', exactMinutes));
        setShowExactTimeModal(false);
        setExactTimeInput('');
        await fetchDriverOrders({ showSpinner: false });
        // Mettre à jour la vue détaillée si elle est ouverte
        if (driverOrderDetailView?.orderId === orderId) {
          const updatedOrder = driverOrders.find(o => o.orderId === orderId);
          if (updatedOrder) {
            setDriverOrderDetailView(updatedOrder);
          }
        }
      } else {
        toast.error(data.message || t('errorSettingTime'));
      }
    } catch (error) {
      console.error('Erreur définition temps exact:', error);
      toast.error(t('errorSettingTime'));
    }
  };

  // Vue détaillée d'une commande pour le livreur
  const renderDriverOrderDetail = () => {
    if (!driverOrderDetailView) return null;

    const order = driverOrderDetailView;
    const statusStyle = orderStatusStyles[order.status] || orderStatusStyles[0];
    const isAssignedToMe = order.assignedDriverChatId === telegramChatId;
    const isAssignedToOther = order.assignedDriverChatId && order.assignedDriverChatId !== telegramChatId;

    return (
      <div className="min-h-screen relative overflow-hidden">
        <SophisticatedBackground />
        <div className="relative z-10 p-4 sm:p-6 max-w-4xl mx-auto w-full">
          {/* Header avec bouton retour */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setDriverOrderDetailView(null)}
              className="p-3 rounded-2xl font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-white">Détails de la commande</h2>
            <div className="w-12"></div> {/* Spacer pour centrer */}
          </div>

          {/* Carte principale de la commande */}
          <motion.div
            className="rounded-3xl p-6 border mb-6"
            style={{
              background: order.isPriority 
                ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(251, 191, 36, 0.15) 50%, rgba(217, 119, 6, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              borderColor: order.isPriority 
                ? 'rgba(245, 158, 11, 0.6)'
                : isAssignedToMe 
                ? 'rgba(34, 197, 94, 0.5)' 
                : isAssignedToOther 
                ? 'rgba(234, 179, 8, 0.5)' 
                : 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(12px)',
              boxShadow: order.isPriority 
                ? '0 8px 32px rgba(245, 158, 11, 0.3), inset 0 0 20px rgba(251, 191, 36, 0.1)' 
                : undefined
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* En-tête de la commande */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Package className="w-6 h-6 text-white/80" />
                  <h3 className="text-2xl font-bold text-white">{order.orderName || order.customerName || 'Sans nom'}</h3>
                </div>
                <p className="text-white/60 text-sm">Commande #{order.orderId?.slice(0, 8)}</p>
                {isAssignedToMe && (
                  <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Vous avez pris cette commande
                  </p>
                )}
                {isAssignedToOther && (
                  <p className="text-yellow-400 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Prise par un autre livreur
                  </p>
                )}
              </div>
              <span
                className="px-4 py-2 rounded-full text-sm font-semibold"
                style={{
                  background: order.cancelled ? 'rgba(107, 114, 128, 0.3)' : statusStyle.bg,
                  color: order.cancelled ? '#9ca3af' : statusStyle.color
                }}
              >
                {order.cancelled ? 'Annulée' : statusStyle.label}
              </span>
            </div>

            {/* Informations de la commande */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Produits - Liste structurée si panier, sinon simple */}
              {order.products && Array.isArray(order.products) && order.products.length > 0 ? (
                <>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-white/60 text-xs mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Produit
                    </p>
                    <div className="space-y-1.5">
                      {order.products.map((product, idx) => (
                        <div key={idx} className="text-white font-medium text-sm py-1">
                          {product.name}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-white/60 text-xs mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Quantité
                    </p>
                    <div className="space-y-1.5">
                      {order.products.map((product, idx) => (
                        <div key={idx} className="text-white font-semibold text-sm py-1">
                          {product.quantityGrams}g ({product.name})
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-white/60 text-xs mb-1">Produit</p>
                    <p className="text-white font-semibold">{order.product || '—'}</p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-white/60 text-xs mb-1">{t('quantityLabel')}</p>
                    <p className="text-white font-semibold">{order.quantity || '—'}</p>
                  </div>
                </>
              )}
              
              <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <p className="text-white/60 text-xs mb-1">Prix</p>
                <p className="text-white font-semibold">
                  {order.price ? (order.country === 'suisse' ? formatPriceDirect(order.price, 'CHF') : formatPriceDirect(order.price, '€')) : '—'}
                </p>
              </div>
              <div className="rounded-2xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <p className="text-white/60 text-xs mb-1">Type</p>
                <p className="text-white font-semibold flex items-center gap-1">
                  {order.isPriority ? (
                    <>
                      <Sparkles className="w-4 h-4 text-yellow-400" />
                      Prioritaire
                    </>
                  ) : (
                    'Standard'
                  )}
                </p>
              </div>
            </div>

            {/* Ville de livraison */}
            {order.city && (
              <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-white/60" />
                  <div className="flex-1">
                    <p className="text-white/60 text-xs mb-1">{t('deliveryCityLabel')}</p>
                    <p className="text-white font-semibold flex items-center gap-2">
                      <span>{order.country === 'suisse' ? '🇨🇭' : '🇫🇷'}</span>
                      <span>{order.city}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Adresse */}
            <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-white/60 mt-0.5" />
                <div className="flex-1">
                  <p className="text-white/60 text-xs mb-1">{t('deliveryAddress')}</p>
                  <p className="text-white font-medium">{order.address || 'Adresse indisponible'}</p>
                </div>
              </div>
            </div>

            {/* Téléphone client */}
            {order.customerPhone && (
              <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-white/60" />
                  <div className="flex-1">
                    <p className="text-white/60 text-xs mb-1">Téléphone client</p>
                    <a href={`tel:${order.customerPhone}`} className="text-white font-medium hover:text-blue-400 transition-colors">
                      {order.customerPhone}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Paiement Global Coins */}
            {order.paidWithCoins && (
              <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(125, 211, 252, 0.08)', border: '1.5px solid rgba(125, 211, 252, 0.35)' }}>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 20 }}>🪙</span>
                  <div className="flex-1">
                    <p className="text-xs mb-1" style={{ color: 'rgba(125,211,252,0.7)' }}>Mode de paiement</p>
                    <p className="font-bold" style={{ color: '#7dd3fc' }}>Payé en Global Coins</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {order.notes && (
              <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                <p className="text-white/60 text-xs mb-1">Notes</p>
                <p className="text-white italic">{order.notes}</p>
              </div>
            )}

            {/* Boutons de gestion du statut */}
            {!order.cancelled && order.status !== 3 && (
              <div className="space-y-3 mb-6">
                <p className="text-white/80 font-semibold text-sm mb-3">Mettre à jour la commande</p>
                
                {/* Bouton "En route" - affiché si status est 0 */}
                {order.status === 0 && (
                  <motion.button
                    onClick={() => handleDriverStatusUpdate(order.orderId, 'en_route')}
                    className="w-full py-4 px-6 rounded-2xl font-bold text-white flex items-center justify-center gap-3"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Truck className="w-5 h-5" />
                    <span>En route</span>
                  </motion.button>
                )}

                {/* Bouton "Arrivée imminente" - affiché si status est 1 */}
                {order.status === 1 && (
                  <motion.button
                    onClick={() => handleDriverStatusUpdate(order.orderId, 'arrivee_imminente')}
                    className="w-full py-4 px-6 rounded-2xl font-bold text-white flex items-center justify-center gap-3"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Clock className="w-5 h-5" />
                    <span>Arrivée imminente</span>
                  </motion.button>
                )}

                {/* Bouton "Livré" - affiché si status est 2 */}
                {order.status === 2 && (
                  <motion.button
                    onClick={() => handleDriverStatusUpdate(order.orderId, 'livre')}
                    className="w-full py-4 px-6 rounded-2xl font-bold text-white flex items-center justify-center gap-3"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>Livré ✅ </span>
                  </motion.button>
                )}
              </div>
            )}

            {/* Boutons d'action */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {/* Bouton Temps exact */}
              {order.status >= 1 && order.status < 3 && (
                <motion.button
                  onClick={() => setShowExactTimeModal(true)}
                  className="py-3 px-4 rounded-2xl font-semibold text-white flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Clock className="w-5 h-5" />
                  <span> Définir temps exact</span>
                </motion.button>
              )}

              {/* Bouton Contacter le client */}
              {order.status !== 3 && (
                <motion.button
                  onClick={() => {
                    setSelectedDriverOrder(order);
                    setIsDriverChatOpen(true);
                    setIsDriverChatMinimized(false);
                    setCurrentView('driver-chat');
                  }}
                  className="py-3 px-4 rounded-2xl font-semibold text-gray-900 flex items-center justify-center gap-2 relative"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Contacter le client</span>
                  {order.orderId === selectedDriverOrder?.orderId && driverChatUnread > 0 && (
                    <span className="absolute -top-2 -right-2 h-6 min-w-[1.5rem] px-2 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow-lg">
                      {driverChatUnread}
                    </span>
                  )}
                </motion.button>
              )}

              {/* Bouton Annuler */}
              {order.status < 3 && !order.cancelled && (
                <motion.button
                  onClick={() => handleDriverCancelOrder(order.orderId)}
                  className="py-3 px-4 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 col-span-1 sm:col-span-2"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)'
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <XCircle className="w-5 h-5" />
                  <span>Annuler la commande</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Modal pour définir le temps exact */}
        {showExactTimeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              className="rounded-3xl p-6 max-w-md w-full"
              style={{
                background: 'linear-gradient(135deg, rgba(68, 64, 60, 0.98) 0%, rgba(45, 31, 15, 0.98) 100%)',
                border: '2px solid rgba(251, 191, 36, 0.3)'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <h3 className="text-xl font-bold text-white mb-4">⏱️ Définir le temps exact</h3>
              <p className="text-white/70 text-sm mb-4">
                Entrez le temps estimé en minutes (entre 1 et 300)
              </p>
              <input
                type="number"
                min="1"
                max="300"
                value={exactTimeInput}
                onChange={(e) => setExactTimeInput(e.target.value)}
                placeholder="Ex: 25"
                className="w-full bg-white/10 text-white rounded-2xl p-4 border-2 border-white/20 focus:border-amber-400 focus:outline-none mb-4"
                style={{ fontSize: '16px' }}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowExactTimeModal(false);
                    setExactTimeInput('');
                  }}
                  className="flex-1 py-3 px-4 rounded-2xl font-semibold text-white"
                  style={{
                    background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.3) 0%, rgba(75, 85, 99, 0.3) 100%)'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDriverSetExactTime(order.orderId, exactTimeInput)}
                  className="flex-1 py-3 px-4 rounded-2xl font-semibold text-white"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  }}
                >
                  Confirmer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverChatView = () => {
    const order = selectedDriverOrder;
    const goBackToPanel = () => {
      setCurrentView('driver-panel');
          setIsDriverChatOpen(false);
      setDriverChatPreview(null);
      setDriverChatPreviewMeta(null);
    };

    const chatBackgroundStyle = {
      backgroundImage:
        'radial-gradient(circle at 15% 10%, rgba(96,165,250,0.18), transparent 45%), radial-gradient(circle at 85% 0%, rgba(37,99,235,0.16), transparent 50%)',
      backgroundColor: 'transparent',
    };

    const statusLabel = order?.cancelled ? 'Annulée' : order?.isPriority ? 'Prioritaire' : 'Standard';
    const statusClass = order?.cancelled
      ? 'bg-red-500/20 text-red-200 border border-red-500/30'
      : order?.isPriority
      ? 'bg-blue-400/20 text-blue-100 border border-blue-300/40'
      : 'bg-white/5 text-white/80 border border-white/10';

    return (
      <div className="min-h-screen flex flex-col text-white" style={{ background: 'linear-gradient(160deg, #050b22 0%, #0a1a4a 55%, #0c2466 100%)' }}>
        <div className="flex flex-col flex-1">
          <header className="sticky top-0 z-20 px-4 sm:px-6 py-4" style={{ background: 'linear-gradient(145deg, rgba(96,165,250,0.22), rgba(37,99,235,0.16))', borderBottom: '1px solid rgba(147,197,253,0.30)', backdropFilter: 'blur(30px) saturate(250%)', WebkitBackdropFilter: 'blur(30px) saturate(250%)', boxShadow: '0 8px 30px rgba(10,30,100,0.35), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
            <div className="flex items-center gap-3">
              <button
                onClick={goBackToPanel}
                className="flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour
              </button>
              {order && (
                <button
                  onClick={handleClearDriverChat}
                  className="flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-semibold text-red-200 bg-red-500/15 hover:bg-red-500/25 transition"
                  title="Nettoyer la conversation (inclut ✅ Temps exacte défini et ⏱️ Temps exacte)"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Nettoyer</span>
                </button>
              )}
              {order && (
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-blue-400/10 border border-blue-300/30 flex items-center justify-center text-lg font-semibold uppercase">
                    {(order.customerName || order.orderName || 'C').slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-blue-100/80">
                      Client • {order.customerPhone || 'Numéro indisponible'}
                    </p>
                    <h2 className="text-lg font-semibold truncate">
                      {order.orderName || order.customerName || 'Commande'}
                    </h2>
                    <p className="text-xs text-white/65 line-clamp-1">{order.address || 'Adresse indisponible'}</p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-1 text-xs text-white/70">
                    <span className={`px-3 py-1 rounded-full text-[11px] ${statusClass}`}>{statusLabel}</span>
                    {order.price && <span className="font-semibold">{order.country === 'suisse' ? formatPriceDirect(order.price, 'CHF') : `${order.price}€`}</span>}
                  </div>
                </div>
              )}
            </div>
          </header>

          {!order ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-white/70 px-6">
              <MessageCircle className="w-10 h-10 mb-4 text-emerald-400" />
              <p className="text-lg font-semibold mb-2">Aucune conversation sélectionnée</p>
              <p className="text-sm text-white/50">Revenez au panel administrateur pour choisir une commande.</p>
              <button
                onClick={goBackToPanel}
                className="mt-6 px-5 py-2 rounded-2xl bg-emerald-500 text-emerald-950 font-semibold"
              >
                Retour au panel
              </button>
            </div>
          ) : (
            <>
              <div className="px-4 sm:px-6 py-3 text-center text-[11px] text-blue-100/70">
                Canal sécurisé client ↔ livreur • Visible uniquement par vous deux
              </div>
              <div
                className="flex-1 overflow-y-auto px-3 sm:px-6 pb-32 space-y-3 custom-scrollbar"
                style={chatBackgroundStyle}
              >
                {driverChatMessages.length === 0 && (
                  <div className="text-center text-white/60 text-sm mt-10">{t('noMessages')}</div>
                )}
                {driverChatMessages.map((message, index) => {
                  const isClient = message.role === 'client' || message.role === 'customer';
                  const isSelf = !isClient;
                  return (
                    <div key={message.id || index} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[90%] sm:max-w-[70%] rounded-3xl px-4 py-3 shadow-lg text-sm leading-relaxed ${
                          isSelf
                            ? 'text-white rounded-tr-sm border border-blue-200/50'
                            : 'text-white rounded-tl-sm border border-white/25'
                        }`}
                        style={{ background: isSelf ? 'linear-gradient(135deg, rgba(96,165,250,0.62), rgba(37,99,235,0.55))' : 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))', backdropFilter: 'blur(14px) saturate(200%)', WebkitBackdropFilter: 'blur(14px) saturate(200%)', boxShadow: isSelf ? '0 6px 18px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.35)' : '0 4px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.22)' }}
                      >
                        {message.type === 'image' && message.url ? (
                          <div className="relative rounded-xl mb-2">
                            <img
                              src={message.url}
                              alt={t('attachment')}
                              loading="lazy"
                              data-msg-id={String(message.id || '')}
                              data-attempts="0"
                              className={`rounded-xl w-full max-h-64 object-contain bg-black/30 cursor-zoom-in border border-blue-400/20 ${driverChatUploadingId === message.id ? 'opacity-40' : ''}`}
                              onClick={(e) => {
                                if (driverChatUploadingId === message.id) return;
                                const imgEl = e?.currentTarget || null;
                                const final = (imgEl && imgEl.getAttribute && imgEl.getAttribute('src')) || message.url;
                                setDriverChatPreview(final);
                                setDriverChatPreviewMeta({
                                  author: message.authorName,
                                  date: formatChatTimestamp(message.createdAt),
                                });
                              }}
                              onError={(e) => {
                                const el = e?.currentTarget;
                                if (!el) return;
                                const raw = String(el.getAttribute('data-attempts') || '0');
                                const attempt = parseInt(raw, 10) || 0;
                                const base = message.url || '';
                                if (attempt === 0) {
                                  el.setAttribute('data-attempts', '1');
                                  const sep = String(el.src || '').includes('?') ? '&' : '?';
                                  el.src = `${el.src}${sep}r=${Date.now()}`;
                                  return;
                                }
                                if (attempt === 1) {
                                  el.setAttribute('data-attempts', '2');
                                  try {
                                    const fixed = resolveMediaUrlFallback(base);
                                    if (fixed && fixed !== el.src) {
                                      el.src = fixed;
                                      return;
                                    }
                                  } catch { /* */ }
                                }
                                if (attempt <= 2) {
                                  el.setAttribute('data-attempts', String(attempt + 1));
                                  const key = String(message.id || '');
                                  const fb = chatBlobByMsgIdRef.current.get(key);
                                  if (fb) { el.src = fb; return; }
                                }
                              }}
                            />
                            {driverChatUploadingId === message.id && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-black/30 backdrop-blur-[2px] p-3">
                                <div className="w-10 h-10 mb-2 rounded-full border-3 border-blue-300/40 border-t-sky-300 animate-spin" style={{ borderTopColor: 'rgba(125,211,252,0.95)' }} />
                                <div className="w-4/5">
                                  <LiquidProgressBar progress={driverChatUploadProgress} />
                                </div>
                                <p className="mt-1 text-[11px] font-semibold text-blue-100 drop-shadow">{Math.round(driverChatUploadProgress)} %</p>
                              </div>
                            )}
                          </div>
                        ) : message.type === 'video' && message.url ? (
                          <video
                            controls
                            className="rounded-xl mb-2 w-full max-h-64 object-contain bg-black/30 border border-blue-400/20"
                            playsInline
                            preload="metadata"
                          >
                            {message.url.endsWith('.mp4') && (
                              <source src={message.url} type="video/mp4" />
                            )}
                            {message.url.endsWith('.mov') && (
                              <source src={message.url} type="video/quicktime" />
                            )}
                            {!message.url.endsWith('.mp4') && !message.url.endsWith('.mov') && (
                              <source src={message.url} type="video/mp4" />
                            )}
                          </video>
                        ) : (
                          <p className="whitespace-pre-line break-words">{message.content}</p>
                        )}
                        <p
                          className={`text-[10px] mt-2 opacity-80 flex items-center gap-1 ${
                            isSelf ? 'justify-end text-white/70' : 'text-white/65'
                          }`}
                        >
                          {message.authorName && <span>{message.authorName}</span>}
                          <span>{formatChatTimestamp(message.createdAt)}</span>
                        </p>
                      </div>
    </div>
  );
                })}
              </div>
              <div className="fixed bottom-0 left-0 right-0 z-20 px-3 sm:px-6" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))', background: 'linear-gradient(to top, rgba(5,11,34,0.95), rgba(5,11,34,0))' }}>
                <div className="rounded-3xl" style={{ background: 'linear-gradient(145deg, rgba(96,165,250,0.22), rgba(37,99,235,0.16))', border: '1.5px solid rgba(147,197,253,0.40)', backdropFilter: 'blur(30px) saturate(250%)', WebkitBackdropFilter: 'blur(30px) saturate(250%)', boxShadow: '0 12px 40px rgba(10,30,100,0.5), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                  <div className="flex items-end gap-2 p-3">
                    <label
                      htmlFor="driver-chat-photo-full"
                      className={`p-3 rounded-2xl border border-blue-400/20 cursor-pointer transition ${
                        driverChatUploading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-400/10'
                      }`}
                      title={t('sendPhotoVideo')}
                    >
                      <ImageIcon className="w-5 h-5 text-blue-100" />
                      <input
                        id="driver-chat-photo-full"
                        type="file"
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={handleDriverPhotoUpload}
                        disabled={driverChatUploading}
                      />
                    </label>
                    <textarea
                      value={driverChatInput}
                      onChange={(e) => setDriverChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleDriverChatSend();
                        }
                      }}
                      placeholder="Écrire un message..."
                      rows={1}
                      className="flex-1 resize-none rounded-2xl bg-white/10 border border-blue-300/35 text-white placeholder-white/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300/40"
                    />
                    <button
                      onClick={handleDriverChatSend}
                      disabled={driverChatSending || !driverChatInput.trim()}
                      className="h-12 w-12 rounded-2xl font-bold disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(96,165,250,0.75), rgba(37,99,235,0.70))',
                        border: '1.5px solid rgba(191,219,254,0.6)',
                        color: '#fff',
                        boxShadow: '0 6px 18px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.4)'
                      }}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                  {(driverChatUploading || driverChatSending) && (
                    <div className="px-5 pb-3">
                      {driverChatUploading && (
                        <>
                          <LiquidProgressBar progress={driverChatUploadProgress} />
                          <p className="text-[11px] text-blue-100/80 mt-1 text-center font-medium">
                            {t('uploadingPhoto')} • {Math.round(driverChatUploadProgress)} %
                          </p>
                        </>
                      )}
                      {!driverChatUploading && driverChatSending && (
                        <p className="text-[11px] text-blue-100/70">{t('sendingMessage')}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {driverChatPreview && (
          <div
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
            onClick={() => {
              setDriverChatPreview(null);
              setDriverChatPreviewMeta(null);
            }}
          >
            <div className="relative w-full max-w-5xl max-h-[90vh] flex flex-col">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDriverChatPreview(null);
                  setDriverChatPreviewMeta(null);
                }}
                className="absolute -top-4 -right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full border border-white/20"
                aria-label={t('closePreview')}
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={driverChatPreview}
                alt={t('preview')}
                className="w-full h-full object-contain rounded-2xl border border-white/20 shadow-2xl bg-black"
                onClick={(e) => e.stopPropagation()}
              />
              {driverChatPreviewMeta && (
                <div className="mt-3 text-center text-sm text-white/80">
                  {driverChatPreviewMeta.author && <span className="font-semibold">{driverChatPreviewMeta.author}</span>}
                  {driverChatPreviewMeta.author && driverChatPreviewMeta.date && <span className="mx-2">•</span>}
                  {driverChatPreviewMeta.date && <span>{driverChatPreviewMeta.date}</span>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Composant popup "Qui Paye aujourd'hui ?"
  const renderGamePopup = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999] flex items-center justify-center p-5"
      style={{
        background: 'linear-gradient(180deg, rgba(26, 29, 36, 0.95) 0%, rgba(42, 47, 58, 0.95) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="card-premium glass-card shadow-multilayer rounded-2xl p-6 max-w-md w-full relative"
        style={{
          background: 'linear-gradient(135deg, rgba(68, 64, 60, 0.95) 0%, rgba(87, 83, 78, 0.95) 100%)',
          border: '2px solid rgba(251, 191, 36, 0.3)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Bouton X style Apple */}
        <button
          onClick={() => setShowGamePopup(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-white/20"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <X className="w-4 h-4 text-white/80" />
        </button>

        <div className="text-center pt-2">
          {/* Icône */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
            className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.3) 100%)',
              boxShadow: '0 8px 32px rgba(251, 191, 36, 0.4)',
              border: '2px solid rgba(251, 191, 36, 0.5)'
            }}
          >
            <span className="text-4xl">🎲</span>
          </motion.div>

          {/* Texte principal */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold mb-3 text-white"
            style={{
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
            }}
          >
            Avec des amis ?<br />Qui Paye aujourd'hui ?
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/70 text-sm mb-6 font-medium"
          >
            Posez vos doigts et laissez le hasard décider ! 🎮
          </motion.p>

          {/* Bouton vers le jeu */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => {
              setShowGamePopup(false);
              setShowPlayerSelection(true);
              setGamePhase('waiting');
              setFingerPositions([]);
              setGameWinner(null);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 px-6 rounded-2xl font-bold text-lg"
            style={{
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: '#1a1d24',
              border: '2px solid rgba(251, 191, 36, 0.5)',
              boxShadow: '0 8px 32px rgba(251, 191, 36, 0.4)'
            }}
          >
            🎮 Jouer Maintenant !
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Popup de sélection du nombre de joueurs - Style formulaire
  const renderPlayerSelection = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[65] flex items-center justify-center p-5"
      style={{
        background: 'rgba(8, 12, 24, 0.72)',
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)'
      }}
      onClick={() => setShowPlayerSelection(false)}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0, y: 24 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        style={{
          background: 'linear-gradient(145deg, #0a0a0a 0%, #141414 50%, #0d0d0d 100%)',
          backdropFilter: 'blur(50px) saturate(200%)',
          WebkitBackdropFilter: 'blur(50px) saturate(200%)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 28,
          padding: '36px 28px 28px',
          maxWidth: 340,
          width: '100%',
          position: 'relative',
          boxShadow: '0 25px 80px rgba(0,0,0,0.90), 0 8px 32px rgba(0,0,0,0.70), inset 0 1px 0 rgba(255,255,255,0.08)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18) 50%, transparent)', pointerEvents: 'none' }} />
        <button
          onClick={() => setShowPlayerSelection(false)}
          style={{
            position: 'absolute', top: 14, right: 14,
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(145deg, #2a2a2a 0%, #111111 100%)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.70)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.10)',
            zIndex: 10
          }}
        >
          <X className="w-4 h-4" />
        </button>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900, color: '#ffffff', margin: 0, letterSpacing: '-0.02em' }}>
                Qui paye ?
              </h2>
            </div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.40)', margin: 0, fontWeight: 400, letterSpacing: '0.02em' }}>
              Sélectionnez le nombre de joueurs
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {[2, 3, 4].map((num) => (
              <motion.button
                key={num}
                onClick={() => setNumberOfPlayers(num)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                style={{
                  aspectRatio: '1',
                  borderRadius: 18,
                  fontWeight: 900,
                  fontSize: 28,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  background: numberOfPlayers === num
                    ? 'linear-gradient(145deg, #282828 0%, #1a1a1a 100%)'
                    : 'linear-gradient(145deg, #161616 0%, #0e0e0e 100%)',
                  color: numberOfPlayers === num ? '#ffffff' : 'rgba(255,255,255,0.30)',
                  border: numberOfPlayers === num
                    ? '1px solid rgba(255,255,255,0.22)'
                    : '1px solid rgba(255,255,255,0.07)',
                  boxShadow: numberOfPlayers === num
                    ? '0 8px 28px rgba(0,0,0,0.70), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.40)'
                    : '0 4px 12px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.05)',
                  cursor: 'pointer', transition: 'all 0.15s ease'
                }}
              >
                <span>{num}</span>
                <span style={{ fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>joueurs</span>
              </motion.button>
            ))}
          </div>
          <motion.button
            onClick={() => {
              setShowPlayerSelection(false);
              setShowGame(true);
              setGamePhase('waiting');
              setFingerPositions([]);
              setGameWinner(null);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: 16,
              fontWeight: 800,
              fontSize: 15,
              fontFamily: "'Inter', sans-serif",
              background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.16)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.70), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.30)',
              cursor: 'pointer',
              letterSpacing: '0.10em',
              textTransform: 'uppercase'
            }}
          >
            JOUER
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Composant du jeu FingerChooser - VERSION ULTRA-ROBUSTE SANS BUGS
  const renderFingerChooserGame = () => {
    // Couleurs pour chaque joueur
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

    // Détection multi-touch ROBUSTE
    const handleTouch = (e) => {
      // Ne pas bloquer les événements si on est en phase result (pour permettre les clics sur les boutons)
      if (gamePhase === 'result' || gamePhase === 'exit') return;

      e.preventDefault();
      e.stopPropagation();

      const touches = Array.from(e.touches || []);

      // Créer les positions des doigts
      const newPositions = touches.slice(0, numberOfPlayers).map((touch, i) => ({
        id: touch.identifier,
        x: touch.clientX || touch.pageX,
        y: touch.clientY || touch.pageY,
        color: colors[i]
      }));

      setFingerPositions(newPositions);

      // Activer les pulsations si tous les doigts sont posés
      if (newPositions.length === numberOfPlayers && gamePhase === 'waiting') {
        setGamePhase('touching');
      }
    };

    const handleTouchEnd = (e) => {
      // Ne pas bloquer les événements si on est en phase result (pour permettre les clics sur les boutons)
      if (gamePhase === 'result' || gamePhase === 'exit') return;

      e.preventDefault();
      e.stopPropagation();

      // Si un joueur lève son doigt pendant la phase touching, réinitialiser
      if (gamePhase === 'touching' && e.touches.length < numberOfPlayers) {
        setGamePhase('waiting');
        setFingerPositions([]);
      } else if (e.touches.length > 0) {
        // Mettre à jour les positions des doigts restants
        handleTouch(e);
      }
    };

    return (
      <div
        className="fixed inset-0 z-[60] select-none"
        style={{
          touchAction: 'none',
          background: '#0a0d14'
        }}
        onTouchStart={handleTouch}
        onTouchMove={handleTouch}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image background */}
        <img src="/emoji/SWISS3.png" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', objectPosition: 'center', pointerEvents: 'none' }} />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/20" />

        {/* Logo */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-30">
          <img src="/emoji/logoapp1.png" alt="" className="w-48 h-48" style={{ maxWidth: 'min(192px, 42vw)', maxHeight: 'min(192px, 42vw)', width: '100%', height: 'auto', objectFit: 'contain' }} />
        </div>

        {/* Bouton fermer */}
        <button
          onPointerDown={(e) => {
            e.stopPropagation();
            setShowGame(false);
            setGamePhase('waiting');
            setFingerPositions([]);
            setGameWinner(null);
          }}
          className="absolute top-5 right-5 z-[999] w-10 h-10 rounded-full bg-black/40 flex items-center justify-center"
          style={{
            pointerEvents: 'auto',
            touchAction: 'auto',
            cursor: 'pointer'
          }}
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Cercles des doigts */}
        {fingerPositions.map((finger, i) => {
          const isWinner = gamePhase === 'result' && i === gameWinner;
          const isLoser = gamePhase === 'result' && i !== gameWinner;

          return (
            <div
              key={finger.id}
              className="absolute w-24 h-24 rounded-full pointer-events-none"
              style={{
                left: finger.x - 48,
                top: finger.y - 48,
                background: isWinner ? '#ef4444' : finger.color,
                boxShadow: isWinner
                  ? '0 0 0 12px rgba(239,68,68,0.5), 0 20px 60px rgba(239,68,68,0.9)'
                  : `0 4px 20px ${finger.color}88`,
                transform: isWinner
                  ? 'scale(2.5)'
                  : gamePhase === 'touching'
                    ? 'scale(1.05)'
                    : 'scale(1)',
                filter: isLoser ? 'blur(8px)' : 'none',
                zIndex: isWinner ? 100 : 50,
                animation: isWinner
                  ? 'pulse-winner 0.8s ease-in-out infinite'
                  : gamePhase === 'touching'
                    ? 'pulse-touch 1.2s ease-in-out infinite'
                    : 'none'
              }}
            />
          );
        })}

        {/* Overlay flou pour résultat */}
        {gamePhase === 'result' && (
          <div className="absolute inset-0 z-[80] bg-black/40 backdrop-blur-xl" />
        )}

        {/* Boutons */}
        {gamePhase === 'result' && (
          <div className="absolute bottom-20 left-0 right-0 z-[999] px-6" style={{ pointerEvents: 'auto', touchAction: 'auto' }}>
            <div className="max-w-xs mx-auto space-y-3">
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setGamePhase('waiting');
                  setFingerPositions([]);
                  setGameWinner(null);
                }}
                className="w-full py-4 rounded-2xl font-bold text-white text-lg relative z-[1000]"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  boxShadow: '0 8px 32px rgba(16,185,129,0.5)',
                  pointerEvents: 'auto',
                  touchAction: 'auto',
                  cursor: 'pointer'
                }}
              >
                 {t('restartBtn')}
              </button>
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowGame(false);
                  setGamePhase('waiting');
                  setFingerPositions([]);
                  setGameWinner(null);
                  setCurrentView('order-tracking');
                }}
                className="w-full py-4 rounded-2xl font-bold text-white backdrop-blur-lg relative z-[1000]"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  pointerEvents: 'auto',
                  touchAction: 'auto',
                  cursor: 'pointer'
                }}
              >
                {t('quitBtn')}
              </button>
            </div>
          </div>
        )}

        {/* Animations CSS */}
        <style>{`
          @keyframes pulse-winner {
            0%, 100% { transform: scale(2); }
            50% { transform: scale(2.5); }
          }
          @keyframes pulse-touch {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.08); }
          }
        `}</style>
      </div>
    );
  };

  const renderHome = () => (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col with-app-mobile-nav md:pb-6" 
    >
      <SophisticatedBackground />
      
      {/* Notification live — derrière tout l'UI accueil, devant le background uniquement */}
      {typeof document !== 'undefined' && document.body && !showSubscriptionModal && !showPaymentModal && !showArcadeModal && !isChatOpen && createPortal(
        <div
          className="gs-visitors-strip pointer-events-none"
          style={{
            position: 'fixed',
            top: 'var(--gs-top-row-3)',
            left: 0,
            right: 0,
            zIndex: 'var(--gs-z-visitors)',
          }}
        >
          <div className="flex items-center justify-center gap-2 py-1 px-2">
            <div className="flex items-center gap-2" style={{ maxWidth: '100%' }}>
              <div className="relative flex-shrink-0">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></div>
              </div>
              <span
                className="text-amber-100 font-semibold"
                style={{
                  fontSize: 'clamp(10px, 2.7vw, 12px)',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  textShadow: '0 1px 8px rgba(0,0,0,0.55)',
                }}
              >
                {t('visitorsToday2', liveUsersCount)}
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Messages FOMO qui défilent - Sans background */}
      {/* Messages FOMO défilants désactivés */}
      
      {/* Badge Pass — accueil uniquement, masqué en aperçu Access / sous les modals */}
      {isAuthenticated && !shopPreviewMode && !showSubscriptionModal && !showPaymentModal && !showArcadeModal && !isChatOpen && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed left-3 gs-top-bar-1"
          style={{ top: 'var(--gs-top-row)', zIndex: 'var(--gs-z-chrome)' }}
        >
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.32) 60%, rgba(10,20,80,0.42) 100%)',
              border: '1px solid rgba(107,163,232,0.35)',
              boxShadow: '0 4px 16px rgba(74,144,226,0.18), inset 0 1px 0 rgba(255,255,255,0.22)',
              backdropFilter: 'blur(30px) saturate(250%)',
              WebkitBackdropFilter: 'blur(30px) saturate(250%)',
            }}
          >
            <img
              src={userPass === 'or' ? '/emoji/or1.webp' : userPass === 'argent' ? '/emoji/silver1.webp' : '/emoji/bronze1.png'}
              alt={userPass}
              style={{ width: 18, height: 18, objectFit: 'contain', filter: userPass === 'or' ? 'drop-shadow(0 1px 4px rgba(251,191,36,0.7))' : userPass === 'argent' ? 'drop-shadow(0 1px 4px rgba(147,197,253,0.6))' : 'drop-shadow(0 1px 4px rgba(205,127,50,0.6))' }}
            />
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: 'linear-gradient(135deg,#fff 0%,#bfdbfe 70%,#93c5fd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Pass {userPass === 'or' ? 'Or' : userPass === 'argent' ? 'Argent' : 'Bronze'}
            </span>
          </div>
        </motion.div>
      )}
      <div className="relative z-10 p-6 gs-page-pad-lg">
        <AppNavbarDesktop
          currentView={currentView}
          setCurrentView={setCurrentView}
          cart={cart}
          freePacks={freePacks}
          t={t}
          setShowArcadeModal={setShowArcadeModal}
          setShowSubscriptionModal={setShowSubscriptionModal}
          trackEvent={trackEvent}
          isScrolled={isScrolled}
          userPass={userPass}
          previewLocked={shopPreviewMode}
          onLockedNav={openAccessPub}
          onAccessNav={openAccessPub}
        />

        <div className="text-center mb-10">
          <motion.div 
            className="relative mx-auto mb-6 flex items-center justify-center group"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{ width: 'clamp(92px, 26vw, 148px)', height: 'clamp(92px, 26vw, 148px)' }}
          >
            <img 
              src="/emoji/logoapp1.png"
              alt="GLOBAL STRAINS" 
              className="object-contain filter drop-shadow-2xl"
              style={{
                width: '100%',
                height: '100%',
                filter: 'drop-shadow(0 10px 30px rgba(251, 191, 36, 0.4))',
                borderRadius: '12px',
              }}
            />
          </motion.div>     
        </div>
        {/* Grille 4 catégories — 1 ligne, images uniformes avec labels */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 18, alignItems: 'center', justifyContent: 'center' }}>
            {categories.map((cat, index) => {
              const catSoldOut = isCategorySoldOut(cat.id);
              return (
                <motion.button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.06, y: -4 }}
                  whileTap={{ scale: 0.94 }}
                  style={{ background: 'none', border: 'none', boxShadow: 'none', padding: 0, cursor: 'pointer', outline: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                >
                  <div style={{
                    width: 76, height: 76,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, position: 'relative',
                  }}>
                    <img
                      src={cat.image}
                      alt={cat.name}
                      style={{
                        width: 76, height: 76,
                        objectFit: cat.id === 'pharma' ? 'cover' : 'contain',
                        display: 'block',
                        filter: catSoldOut
                          ? 'grayscale(100%) brightness(0.45) drop-shadow(0 2px 6px rgba(0,0,0,0.5))'
                          : 'drop-shadow(0 3px 8px rgba(0,0,0,0.5))',
                        transition: 'filter 0.3s ease',
                      }}
                    />
                    {catSoldOut && (
                      <div style={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(135deg, rgba(20,20,30,0.55) 0%, rgba(10,10,20,0.65) 100%)',
                        backdropFilter: 'blur(2px)',
                        WebkitBackdropFilter: 'blur(2px)',
                        borderRadius: 12,
                        border: '1px solid rgba(150,150,180,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{
                          fontSize: 8, fontWeight: 800,
                          color: 'rgba(200,200,220,0.80)',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                          lineHeight: 1.2,
                          padding: '0 4px',
                          textShadow: '0 1px 4px rgba(0,0,0,0.7)',
                        }}>Épuisé</span>
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: '0.1em',
                    color: catSoldOut ? 'rgba(150,150,170,0.55)' : 'rgba(255,255,255,0.85)',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    transition: 'color 0.3s ease',
                  }}>{cat.name}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
        <div className="space-y-4 max-w-md mx-auto">
          {shopPreviewMode && (
            <motion.button
              type="button"
              onClick={openAccessPub}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 px-5 rounded-2xl font-bold relative overflow-hidden group"
              style={{
                background: 'linear-gradient(145deg, rgba(251,191,36,0.28) 0%, rgba(245,158,11,0.32) 45%, rgba(234,88,12,0.26) 100%)',
                backdropFilter: 'blur(36px) saturate(240%) brightness(1.08)',
                WebkitBackdropFilter: 'blur(36px) saturate(240%) brightness(1.08)',
                border: '1.5px solid rgba(253,224,71,0.55)',
                boxShadow: '0 10px 36px rgba(245,158,11,0.38), 0 0 32px rgba(234,88,12,0.15), inset 0 1px 0 rgba(255,255,255,0.35)',
                cursor: 'pointer',
              }}
            >
              <div style={{
                position: 'absolute', top: 0, left: '12%', right: '12%', height: 1,
                background: 'linear-gradient(90deg, transparent, rgba(253,224,71,0.85) 50%, transparent)',
                pointerEvents: 'none',
              }} />
              <div className="flex items-center justify-center gap-3 relative z-10">
                <img
                  src="/emoji/key.png"
                  alt=""
                  style={{
                    width: 44, height: 44, objectFit: 'contain', flexShrink: 0,
                    filter: 'drop-shadow(0 0 12px rgba(251,191,36,0.75))',
                  }}
                />
                <div style={{ textAlign: 'left', minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 900, letterSpacing: '-0.01em',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 45%, #fb923c 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    Obtenir un accès
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(254,243,199,0.78)', marginTop: 2 }}>
                    Aperçu test · débloque panier & commandes
                  </div>
                </div>
              </div>
            </motion.button>
          )}
          {!shopPreviewMode && (
          <motion.button 
            onClick={() => setCurrentView('treasure')} 
            className="card-premium shadow-multilayer w-full py-5 px-6 rounded-2xl font-bold relative overflow-hidden group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            style={{ 
              background: treasureTurns > 0 
                ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 30%, #e9ecef 50%, #f8f9fa 70%, #ffffff 100%)'
                : 'linear-gradient(135deg, #374151 0%, #4b5563 50%, #6b7280 50%, #4b5563 100%)',
              backgroundSize: treasureTurns > 0 ? '200% 200%' : '100% 100%',
              border: treasureTurns > 0 
                ? '2px solid rgba(255, 255, 255, 0.4)'
                : '2px solid rgba(107, 114, 128, 0.4)',
              opacity: treasureTurns > 0 ? 1 : 0.7,
              boxShadow: treasureTurns > 0 
                ? '0 8px 32px rgba(255, 255, 255, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.2) inset, 0 0 40px rgba(255, 255, 255, 0.15)'
                : '0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(107, 114, 128, 0.2) inset',
              animation: treasureTurns > 0 ? 'gradient-shift 3s ease infinite' : 'none'
            }}
          >
            {treasureTurns > 0 && (
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
            )}
            <div className={`flex items-center justify-center gap-3 relative z-10 ${treasureTurns > 0 ? 'text-gray-900 font-extrabold' : 'text-gray-300'}`}>
              <Gem className={`w-6 h-6 ${treasureTurns > 0 ? 'animate-pulse' : ''}`} />
              <span className="tracking-wide">
                {treasureTurns > 0 
                  ? t('wheelTour', treasureTurns)
                  : t('wheelTourNone')}
              </span>
            </div>
          </motion.button>
          )}
          {!shopPreviewMode && (
          <motion.button 
            onClick={handleReferral} 
            className="card-premium shadow-multilayer w-full py-5 px-6 rounded-2xl font-bold relative overflow-hidden group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            style={{ 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 30%, #e9ecef 50%, #f8f9fa 70%, #ffffff 100%)',
              backgroundSize: '200% 200%',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              boxShadow: `
                0 8px 32px rgba(255, 255, 255, 0.3),
                0 0 0 1px rgba(255, 255, 255, 0.2) inset,
                0 0 50px rgba(255, 255, 255, 0.15)
              `,
              animation: 'gradient-shift 3s ease infinite'
            }}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
            <div className="flex items-center justify-center gap-3 text-gray-900 relative z-10 font-extrabold tracking-wide">
              <Crown className="w-6 h-6 filter drop-shadow-lg" />
              <span>{t('referAndGet2g')}</span>
            </div>
          </motion.button>
          )}
          {showReferralCode && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              className="card-premium shadow-multilayer rounded-3xl p-7 mt-4 relative overflow-hidden group"
            style={{ 
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 30%, rgba(233, 236, 239, 0.1) 50%, rgba(248, 249, 250, 0.12) 70%, rgba(255, 255, 255, 0.15) 100%)',
                backgroundSize: '200% 200%',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                boxShadow: `
                  0 12px 48px rgba(255, 255, 255, 0.2),
                  0 0 0 1px rgba(255, 255, 255, 0.3) inset,
                  0 0 60px rgba(255, 255, 255, 0.15)
                `,
                animation: 'gradient-shift 3s ease infinite'
            }}
          >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
              <div className="relative z-10">
                <div className="text-center mb-5">
                  <Crown className="w-10 h-10 text-white mx-auto mb-3 filter drop-shadow-lg" />
                  <h3 className="text-white font-extrabold text-xl mb-2 gradient-text-snow">{t('yourReferralCodeTitle')}</h3>
                  <p className="text-white/80 text-xs">{t('shareAndWinTogether')}</p>
            </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 sm:p-5 mb-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-2 border-white/30" style={{
                  boxShadow: '0 4px 16px rgba(255, 255, 255, 0.2), inset 0 0 20px rgba(255, 255, 255, 0.1)'
                }}>
                  <code className="text-white font-mono text-2xl sm:text-3xl font-extrabold tracking-[0.2em] flex-1 text-center gradient-text-snow break-all sm:break-normal">
                    {referralCode}
                  </code>
                  <motion.button
                    onClick={copyReferralCode}
                    className="px-4 sm:px-5 py-2.5 sm:py-3 text-white rounded-xl sm:rounded-2xl font-extrabold transition relative overflow-hidden group/btn whitespace-nowrap"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                      border: '1.5px solid rgba(74, 144, 226, 0.4)',
                      backdropFilter: 'blur(40px) saturate(300%)',
                      WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                      boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
                    }}
                  >
                    <span className="relative z-10 text-sm sm:text-base">{t('copyBtn')}</span>
                  </motion.button>
                </div>
                <p className="text-amber-100/90 text-sm text-center mb-4 font-medium">
                  {t('shareThisCode')}
                </p>
                <div className="space-y-2 mb-5">
                  <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.15)', border: '1px solid rgba(255, 255, 255, 0.25)' }}>
                    <span className="text-2xl">✨</span>
                    <div>
                      <p className="text-white font-bold text-sm"><strong>{t('yourFriendGets')}</strong> {t('getsBonus')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.15)', border: '1px solid rgba(255, 255, 255, 0.25)' }}>
                    <span className="text-2xl">🎁</span>
                    <div>
                      <p className="text-white font-bold text-sm"><strong>{t('youGet')}</strong> {t('youGetBonus')}</p>
                    </div>
                  </div>
                </div>
                <motion.button
                  onClick={() => setShowReferralCode(false)}
                  className="w-full py-3 px-4 text-white rounded-2xl font-extrabold transition relative overflow-hidden group/close"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                    border: '1.5px solid rgba(74, 144, 226, 0.4)',
                    backdropFilter: 'blur(40px) saturate(300%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                    boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
                  }}
                >
                  <span className="relative z-10 tracking-wide">Fermer</span>
                </motion.button>
              </div>
            </motion.div>
          )}
          <AnimatePresence>
          {activeOrder && !activeOrder.cancelled && orderStatus < 3 && (
              <button 
                onClick={() => setCurrentView('order-tracking')} 
                className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition" 
                style={{ 
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
                  border: '1.5px solid rgba(74, 144, 226, 0.4)',
                  backdropFilter: 'blur(40px) saturate(300%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                  boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
                }}
              >
                <div className="flex items-center justify-center gap-3 relative z-10">
                  <ShoppingCart className="w-5 h-5" />
                  <div className="text-center">
                  {t('trackOrderBtn')}
                    <p className="text-xs text-white/80">
                    {queueInfo.ordersAhead ?? 0} commande(s) avant vous · {orderStatus === 0 ? (queueInfo.hasEnRouteOrdersAhead ? t('driverFinishingOrder') : t('driverPreparingShort')) : `~${Math.max(countdownMinutes, 0)} min`}
                    </p>
                  </div>
                </div>
              </button>
            )}
          </AnimatePresence>
          <button 
  onClick={openSupportTelegram}
  className="card-premium shadow-multilayer w-full py-4 px-6 rounded-xl font-bold text-white transition" 
  style={{ 
    background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)',
    border: '1.5px solid rgba(74, 144, 226, 0.4)',
    backdropFilter: 'blur(40px) saturate(300%)',
    WebkitBackdropFilter: 'blur(40px) saturate(300%)',
    boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
  }}
>
  <div className="flex items-center justify-center gap-3 relative z-10">
    <Scroll className="w-5 h-5" />
    <span>{t('contactSupport')}</span>
  </div>
</button>
        </div>
      </div>
    </div>
  );

  const renderProductDetail = () => {
    const category = categories.find(c => c.id === selectedUICategory) || categories.find(c => c.id === selectedCategory);
    // Gestion spéciale pour les variantes
    let priceCategory = selectedCategory;
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'dry') {
      priceCategory = 'mousseDry';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'dryOnePiece') {
      priceCategory = 'mousseDryOnePiece';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'static') {
      priceCategory = 'mousseStatic';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'frozen') {
      priceCategory = 'mousseFrozen';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'bubbleHash') {
      priceCategory = 'bubbleHash';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'wpff') {
      priceCategory = 'mousseWPFF';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'wholeMelt') {
      priceCategory = 'wholeMelt';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'caliPlates') {
      priceCategory = 'caliPlates';
    }
    if (selectedCategory === 'mousse' && selectedProduct?.variant === 'plasmaStatic') {
      priceCategory = 'plasmaStatic';
    }
    if (selectedCategory === 'STILPANE' && selectedProduct?.variant === 'quagen') {
      priceCategory = 'quagen';
    }
    if (selectedCategory === 'Cali' && selectedProduct?.variant === 'grizzlyBoys') {
      priceCategory = 'grizzlyBoys';
    }
    if (selectedCategory === 'Cali' && selectedProduct?.variant === 'gorillaGlue') {
      priceCategory = 'gorillaGlue';
    }
    if (selectedCategory === 'STILPANE' && selectedProduct?.variant === 'xanax') {
      priceCategory = 'Xanax';
    }
    if (selectedCategory === 'STILPANE' && selectedProduct?.variant === 'oxycodo') {
      priceCategory = 'oxycodo';
    }
    if (selectedCategory === 'STILPANE' && selectedProduct?.variant === 'makatussin') {
      priceCategory = 'makatussin';
    }
    if (selectedCategory === 'goldenBloom' || selectedProduct?.variant === 'goldenBloom') {
      priceCategory = 'goldenBloom';
    }
    if (selectedCategory === 'polkadot' || selectedProduct?.variant === 'polkadot') {
      priceCategory = 'polkadot';
    }
    // Prix spécifiques pour Snow (Baida) en Suisse
    if (selectedCategory === 'Baida' && getEffectiveCountry() === 'suisse') {
      priceCategory = 'BaidaSuisse';
    }
    const options = quantityOptions[priceCategory] || [];
    
    const currency = getCurrencySymbol();
    
    return (
      <div className="min-h-screen relative product-detail-page with-app-mobile-nav">
        <SophisticatedBackground />
        <div className="relative p-5 product-detail-scroll gs-page-pad">
          <div className="flex items-center mb-5">
            <button onClick={() => setCurrentView('home')} className="p-2.5 rounded-xl" style={{ background: 'linear-gradient(135deg, #78350f 0%, #92400e 100%)' }}>
              <ChevronLeft className="w-5 h-5 text-amber-100" />
            </button>
            <div className="ml-3 flex-1">
              <h2 className="text-xl font-bold text-amber-100">{selectedProduct?.name}</h2>
              <p className="text-amber-600 text-xs" style={{display:'flex',alignItems:'center',gap:'5px'}}>
                {category?.image
                  ? <img src={category.image} alt={category.name} style={{width:16,height:16,borderRadius:'50%',objectFit:'cover',flexShrink:0}} />
                  : <span>{category?.emoji}</span>
                }
                <span>{category?.name}</span>
              </p>
            </div>
            {cart.length > 0 && !shopPreviewMode && (
              <button
                onClick={() => setCurrentView('cart')}
                className="relative p-2.5 rounded-xl flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)'
                }}
              >
                <ShoppingCart className="w-5 h-5 text-white" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cart.length}
                </span>
              </button>
            )}
          </div>
          
          {/* Bouton "Passez au niveau supérieur" — masqué en aperçu Access */}
          {userPass !== 'or' && !shopPreviewMode && (
            <motion.button
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => { setShowSubscriptionModal(true); trackEvent('pass_click'); }}
              className="mb-3 w-full max-w-md mx-auto block"
              style={{
                background: 'linear-gradient(135deg, rgba(74,144,226,0.42) 0%, rgba(30,80,180,0.55) 55%, rgba(10,40,120,0.58) 100%)',
                backdropFilter: 'blur(40px) saturate(280%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.12)',
                border: '1.5px solid rgba(147,197,253,0.55)',
                borderRadius: '14px',
                padding: '11px 18px',
                boxShadow: '0 4px 24px rgba(74,144,226,0.35), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(74,144,226,0.2)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(191,219,254,0.85) 50%, transparent)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <img src="/emoji/or1.webp" alt="Pass Or" style={{ width: 20, height: 20, objectFit: 'contain', filter: 'drop-shadow(0 1px 6px rgba(74,144,226,0.7))' }} />
                <span style={{ fontWeight: 800, fontSize: 13, color: '#f0f9ff', letterSpacing: '0.02em', textShadow: '0 1px 8px rgba(74,144,226,0.55)' }}>
                  {t('upgradeToNextLevelBtn')}
                </span>
                <img src="/emoji/or1.webp" alt="Pass Or" style={{ width: 20, height: 20, objectFit: 'contain', filter: 'drop-shadow(0 1px 6px rgba(74,144,226,0.7))' }} />
              </div>
            </motion.button>
          )}
          
          <div className="max-w-md mx-auto">
            <motion.div 
              className="rounded-3xl overflow-hidden mb-6 relative group"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              style={{ 
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.18) 0%, rgba(30, 80, 200, 0.22) 50%, rgba(74, 144, 226, 0.18) 100%)',
                backdropFilter: 'blur(30px) saturate(280%)',
                WebkitBackdropFilter: 'blur(30px) saturate(280%)',
                boxShadow: '0 8px 40px rgba(74, 144, 226, 0.25), 0 0 0 1.5px rgba(107, 163, 232, 0.35), 0 0 60px rgba(74, 144, 226, 0.15)',
                border: '1.5px solid rgba(107, 163, 232, 0.35)',
                padding: '8px',
              }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
              <div className="w-full rounded-2xl relative overflow-hidden" style={{ 
                aspectRatio: '16/9', 
                maxHeight: '400px', 
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 0 40px rgba(0, 0, 0, 0.8)'
              }}>
                <video
                  key={selectedProduct?.id}
                  className="w-full h-full rounded-2xl"
                  style={{ objectFit: 'contain' }}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  controlsList="nodownload nopictureinpicture"
                >
                {/* Support pour .mp4 et .mov */}
                {selectedProduct?.video && (
                  <>
                    {/* Pour .mov : servir d'abord en mp4 (compatible Chrome/Android/Telegram), puis quicktime en fallback */}
                    {selectedProduct.video.endsWith('.mov') && (
                      <>
                        <source src={selectedProduct.video} type="video/mp4" />
                        <source src={selectedProduct.video} type="video/quicktime" />
                      </>
                    )}
                    {selectedProduct.video.endsWith('.mp4') && (
                      <source src={selectedProduct.video} type="video/mp4" />
                    )}
                    {!selectedProduct.video.endsWith('.mp4') && !selectedProduct.video.endsWith('.mov') && (
                      <source src={selectedProduct.video} type="video/mp4" />
                    )}
                  </>
                )}
              </video>
            </div>
            </motion.div>

            {/* Bouton pour basculer entre les variantes Sahas */}
            {selectedCategory === 'Sahas' && products.Sahas && products.Sahas.length > 1 && (
              <div className="mb-6 flex gap-3">
                {products.Sahas.map((variant, idx) => {
                  const varSoldOut = isProductSoldOut(variant);
                  return (
                    <motion.button
                      key={variant.id}
                      onClick={() => {
                        setSelectedProduct(variant);
                        setSelectedQuantity(null);
                      }}
                      className={`flex-1 p-4 rounded-2xl font-bold text-sm transition-all ${
                        selectedProduct?.id === variant.id
                          ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-transparent'
                          : ''
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        position: 'relative', overflow: 'hidden',
                        background: varSoldOut
                          ? 'linear-gradient(135deg, rgba(30,30,40,0.85) 0%, rgba(20,20,30,0.90) 100%)'
                          : selectedProduct?.id === variant.id
                            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 50%, #f59e0b 70%, #fbbf24 100%)'
                            : 'linear-gradient(135deg, #57534e 0%, #78716c 50%, #57534e 100%)',
                        backgroundSize: (!varSoldOut && selectedProduct?.id === variant.id) ? '200% 200%' : '100% 100%',
                        boxShadow: varSoldOut
                          ? '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(150,150,180,0.08)'
                          : selectedProduct?.id === variant.id
                            ? '0 12px 48px rgba(251, 191, 36, 0.6), 0 0 0 1px rgba(251, 191, 36, 0.4) inset'
                            : '0 4px 16px rgba(0, 0, 0, 0.5)',
                        border: varSoldOut ? '1px solid rgba(150,150,180,0.15)' : 'none',
                        color: varSoldOut ? 'rgba(170,170,190,0.55)' : selectedProduct?.id === variant.id ? '#78350f' : '#fef3c7',
                        animation: (!varSoldOut && selectedProduct?.id === variant.id) ? 'gradient-shift 3s ease infinite' : 'none',
                        backdropFilter: varSoldOut ? 'blur(8px) saturate(60%)' : 'none',
                        WebkitBackdropFilter: varSoldOut ? 'blur(8px) saturate(60%)' : 'none',
                      }}
                    >
                      {varSoldOut && (
                        <span style={{
                          position: 'absolute', top: 4, right: 5,
                          fontSize: 7, fontWeight: 800, letterSpacing: '0.1em',
                          color: 'rgba(180,180,200,0.70)', textTransform: 'uppercase',
                          background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: '1px 5px',
                          border: '1px solid rgba(150,150,180,0.18)',
                        }}>Épuisé</span>
                      )}
                      {variant.variant === 'frozen' ? '🧱 SAHAS FROZEN' : '🍂 SAHAS STATIC'}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* ── Sélecteur de variantes unifié pour les nouvelles catégories ── */}
            {selectedUICategory && products[selectedUICategory] && products[selectedUICategory].length > 1 && (
              <div className={`mb-6 ${['hash','pharma'].includes(selectedUICategory) ? 'grid grid-cols-2 gap-3' : 'flex gap-3'}`}>
                {products[selectedUICategory].map((variant, idx) => {
                  const varSoldOut = isProductSoldOut(variant);
                  return (
                    <motion.button
                      key={variant.id}
                      onClick={() => {
                        setSelectedProduct(variant);
                        if (variant.legacyCategory) setSelectedCategory(variant.legacyCategory);
                        setSelectedQuantity(null);
                      }}
                      className={`${['hash','pharma'].includes(selectedUICategory) ? 'p-3' : 'flex-1 p-4'} rounded-2xl font-bold text-xs sm:text-sm transition-all ${
                        selectedProduct?.id === variant.id
                          ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-transparent'
                          : ''
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        position: 'relative', overflow: 'hidden',
                        background: varSoldOut
                          ? 'linear-gradient(135deg, rgba(30,30,40,0.85) 0%, rgba(20,20,30,0.90) 100%)'
                          : selectedProduct?.id === variant.id
                            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 50%, #f59e0b 70%, #fbbf24 100%)'
                            : 'linear-gradient(135deg, #57534e 0%, #78716c 50%, #57534e 100%)',
                        backgroundSize: (!varSoldOut && selectedProduct?.id === variant.id) ? '200% 200%' : '100% 100%',
                        boxShadow: varSoldOut
                          ? '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(150,150,180,0.08)'
                          : selectedProduct?.id === variant.id
                            ? '0 12px 48px rgba(251, 191, 36, 0.6), 0 0 0 1px rgba(251, 191, 36, 0.4) inset'
                            : '0 4px 16px rgba(0, 0, 0, 0.5)',
                        border: varSoldOut ? '1px solid rgba(150,150,180,0.15)' : 'none',
                        color: varSoldOut ? 'rgba(170,170,190,0.55)' : selectedProduct?.id === variant.id ? '#78350f' : '#fef3c7',
                        animation: (!varSoldOut && selectedProduct?.id === variant.id) ? 'gradient-shift 3s ease infinite' : 'none',
                        backdropFilter: varSoldOut ? 'blur(8px) saturate(60%)' : 'none',
                        WebkitBackdropFilter: varSoldOut ? 'blur(8px) saturate(60%)' : 'none',
                      }}
                    >
                      {varSoldOut && (
                        <span style={{
                          position: 'absolute', top: 4, right: 5,
                          fontSize: 7, fontWeight: 800, letterSpacing: '0.1em',
                          color: 'rgba(180,180,200,0.70)',
                          textTransform: 'uppercase',
                          background: 'rgba(255,255,255,0.06)',
                          borderRadius: 4, padding: '1px 5px',
                          border: '1px solid rgba(150,150,180,0.18)',
                        }}>Épuisé</span>
                      )}
                      {variant.variant === 'dry'          ? '💫 DRYTECH X GOLDENFARM' :
                       variant.variant === 'dryOnePiece'  ? 'DRY🍫'                   :
                       variant.variant === 'polkadot'     ? '🍫 MAGIC CHOCOLATE'     :
                       variant.variant === 'static'       ? '🍂 SAHAS STATIC'         :
                       variant.variant === 'frozen'       ? '🧱 SAHAS FROZEN'         :
                       variant.variant === 'bubbleHash'   ? '🫧 BUBBLE HASH'          :
                       variant.variant === 'wholeMelt'    ? '🍯 THE WHOLE MELT'       :
                       variant.variant === 'caliPlates'   ? '🇺🇸 CALI PLATES'         :
                       variant.variant === 'dosidos'      ? '🇨🇦 DOSIDOS CANADA'       :
                       variant.variant === 'whiteRunz'    ? '🌺 WHITE RUNTZ'          :
                       variant.variant === 'grizzlyBoys'  ? '🇺🇸 GRIZZLYBOYZ'         :
                       variant.variant === 'gorillaGlue'  ? '🦍 GORILLA GLUE'         :
                       variant.variant === 'xanax'        ? '🇨🇭 XANAX'               :
                       variant.variant === 'oxycodo'      ? '💊 OXYCODON'            :
                       variant.variant === 'makatussin'   ? '🥤 MAKATUSSIN'          :
                       variant.variant === 'quagen'       ? '🥤🇺🇸 QUAGEN'            :
                       variant.variant === 'goldenBloom'  ? '🖊️ GOLDEN BLOOM'        :
                       !variant.variant && variant.name?.toUpperCase().includes('STILPANE') ? '🥤 STILPANE' :
                       !variant.variant                   ? '🧽 LA MOUSSE'            :
                       variant.name}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Bouton pour basculer entre les variantes Jiblis            {/* Bouton pour basculer entre les variantes Jiblis (Kinder uniquement pour Suisse) */}
            {selectedCategory === 'Jiblis' && products.Jiblis && products.Jiblis.length > 1 && (
              <div className="mb-6 flex gap-3">
                {products.Jiblis
                  .filter(variant => getEffectiveCountry() === 'suisse' || variant.variant !== 'kinder')
                  .map((variant, idx) => (
                  <motion.button
                    key={variant.id}
                    onClick={() => {
                      setSelectedProduct(variant);
                      setSelectedQuantity(null);
                    }}
                    className={`flex-1 p-4 rounded-2xl font-bold text-sm transition-all ${
                      selectedProduct?.id === variant.id
                        ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-transparent'
                        : ''
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      background: selectedProduct?.id === variant.id
                        ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 50%, #f59e0b 70%, #fbbf24 100%)'
                        : 'linear-gradient(135deg, #57534e 0%, #78716c 50%, #57534e 100%)',
                      backgroundSize: selectedProduct?.id === variant.id ? '200% 200%' : '100% 100%',
                      boxShadow: selectedProduct?.id === variant.id
                        ? '0 12px 48px rgba(251, 191, 36, 0.6), 0 0 0 1px rgba(251, 191, 36, 0.4) inset'
                        : '0 4px 16px rgba(0, 0, 0, 0.5)',
                      color: selectedProduct?.id === variant.id ? '#78350f' : '#fef3c7',
                      animation: selectedProduct?.id === variant.id ? 'gradient-shift 3s ease infinite' : 'none'
                    }}
                  >
                    {variant.variant === 'kinder' ? '🍫 Kinder Bueno' : '🔥 Playing With Jiblis'}
                  </motion.button>
                ))}
              </div>
            )}

            <div className="mb-6">
              <motion.label 
                className="text-amber-100 font-extrabold mb-5 block flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="p-2 rounded-xl" style={{ 
                  background: 'linear-gradient(135deg, rgba(120, 53, 15, 0.6) 0%, rgba(146, 64, 14, 0.6) 100%)',
                  boxShadow: '0 4px 16px rgba(217, 119, 6, 0.3)'
                }}>
                  <Coins className="w-5 h-5 text-yellow-400" />
            </div>
                <span className="text-lg gradient-text-gold">{t('quantityLabel')}</span>
              </motion.label>
              <div className="grid grid-cols-2 gap-4" key={priceCategory}>
                {options.map((opt, i) => (
                  <motion.button
                    key={`${priceCategory}-${opt.amount}-${i}`} 
                    onClick={() => {
                      setSelectedQuantity(opt); setIsBulkMode(false);
                      if (shopPreviewMode) openAccessPub();
                    }} 
                    className={`relative p-5 rounded-2xl overflow-visible group/option transition-all ${
                      selectedQuantity?.amount === opt.amount && !isBulkMode 
                        ? 'ring-2 ring-sky-300/70 ring-offset-2 ring-offset-transparent' 
                        : ''
                    }`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ 
                      background: selectedQuantity?.amount === opt.amount && !isBulkMode 
                        ? 'linear-gradient(135deg, rgba(107,163,232,0.42) 0%, rgba(40,90,210,0.50) 50%, rgba(74,144,226,0.38) 100%)'
                        : 'linear-gradient(135deg, rgba(74,144,226,0.14) 0%, rgba(30,80,200,0.20) 50%, rgba(74,144,226,0.14) 100%)',
                      backdropFilter: 'blur(28px) saturate(260%) brightness(1.1)',
                      WebkitBackdropFilter: 'blur(28px) saturate(260%) brightness(1.1)',
                      boxShadow: selectedQuantity?.amount === opt.amount && !isBulkMode 
                        ? '0 10px 36px rgba(74,144,226,0.45), 0 0 0 1px rgba(147,197,253,0.45) inset, inset 0 1px 0 rgba(255,255,255,0.35), 0 0 34px rgba(74,144,226,0.30)'
                        : '0 4px 18px rgba(10,20,80,0.35), 0 0 0 1px rgba(107,163,232,0.18) inset, inset 0 1px 0 rgba(255,255,255,0.14)',
                      border: selectedQuantity?.amount === opt.amount && !isBulkMode 
                        ? '1.5px solid rgba(147,197,253,0.70)'
                        : '1.5px solid rgba(107,163,232,0.30)'
                    }}
                  >
                    {selectedQuantity?.amount === opt.amount && !isBulkMode && (
                      <div className="absolute inset-0 opacity-0 group-hover/option:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
                    )}
                    {opt.treasure && (
                      <motion.div 
                        className="absolute -top-2 -right-2 z-20"
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        style={{ 
                          fontSize: '1.5rem',
                          lineHeight: '1',
                          pointerEvents: 'none',
                          filter: 'drop-shadow(0 4px 8px rgba(74, 144, 226, 0.7))'
                        }}
                      >💎</motion.div>
                    )}
                    <div className="text-center relative z-10">
                      <div className={`text-2xl font-extrabold mb-1 ${
                        selectedQuantity?.amount === opt.amount && !isBulkMode 
                          ? 'text-white' 
                          : 'text-sky-100'
                      }`}>
                        {opt.label}
                      </div>
                      <div className="flex items-center justify-center gap-1.5">
                        <Coins className={`w-5 h-5 ${selectedQuantity?.amount === opt.amount && !isBulkMode ? 'text-sky-100' : 'text-sky-300'}`} />
                        <span className={`text-xl font-extrabold ${
                          selectedQuantity?.amount === opt.amount && !isBulkMode 
                            ? 'text-white' 
                            : 'text-sky-200'
                        }`}>
                          {Math.round(getPassPrice(opt.price, selectedProduct?.name || ''))}{currency}
                        </span>
                        {userPass !== 'bronze' && getPassPrice(opt.price, selectedProduct?.name || '') !== opt.price && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 800,
                            color: '#ff4d4d',
                            textDecoration: 'line-through',
                            textDecorationColor: '#ff4d4d',
                            textDecorationThickness: '2px',
                            opacity: 1,
                            marginLeft: '4px',
                            background: 'rgba(255,77,77,0.12)',
                            borderRadius: '6px',
                            padding: '1px 5px',
                            border: '1px solid rgba(255,77,77,0.25)',
                            letterSpacing: '-0.02em'
                          }}>{opt.price}{currency}</span>
                        )}
                    </div>
                    </div>
                  </motion.button>
                ))}
                {/* Mode bulk désactivé pour tous les produits */}
              </div>

              {/* Aperçu Access : swipe Commander → modal pub Access */}
              {shopPreviewMode && (
                <div className="mt-6 px-0.5" style={{
                  padding: '16px 14px 14px',
                  borderRadius: 20,
                  background: 'linear-gradient(135deg, rgba(74,144,226,0.18) 0%, rgba(30,60,140,0.32) 55%, rgba(10,20,80,0.38) 100%)',
                  backdropFilter: 'blur(28px) saturate(240%)',
                  WebkitBackdropFilter: 'blur(28px) saturate(240%)',
                  border: '1.5px solid rgba(147,197,253,0.35)',
                  boxShadow: '0 8px 28px rgba(37,99,235,0.22), inset 0 1px 0 rgba(255,255,255,0.18)',
                }}>
                  <p style={{
                    margin: '0 0 4px', textAlign: 'center', fontSize: 14, fontWeight: 900, color: '#fff',
                  }}>
                    Prêt à commander ?
                  </p>
                  <p style={{
                    margin: '0 0 14px', textAlign: 'center', fontSize: 12, lineHeight: 1.45,
                    color: 'rgba(191,219,254,0.72)', fontWeight: 600,
                  }}>
                    Glisse pour ouvrir l&apos;accès boutique
                  </p>
                  <SwipeCommanderButton
                    label="Commander"
                    hint=""
                    onComplete={openAccessPub}
                  />
                </div>
              )}

              {/* Bouton pour commander en plus grande quantité — masqué en aperçu */}
              {!shopPreviewMode && (
              <motion.a
                href={SUPPORT_TELEGRAM_APP_URL}
                onClick={(e) => { e.preventDefault(); openSupportTelegram(); }}
                className="block mt-4 p-4 rounded-2xl text-center font-bold text-white transition-all group/contact"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                  backgroundSize: '200% 200%',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.3) inset',
                  border: '1.5px solid rgba(147, 197, 253, 0.3)'
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <span>{t('iWantMore')}</span>
                </div>
                <p className="text-xs text-blue-100 mt-1 opacity-90">
                  {t('contactUsCustomOrder') || 'Contactez-nous pour une commande personnalisée'}
                </p>
              </motion.a>
              )}
            </div>
            {/* Section bulk désactivée */}
            <AnimatePresence>
              {false && (
                <motion.div 
                  className="mt-6 p-6 rounded-3xl relative overflow-hidden group/bulk-section" 
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(68, 64, 60, 0.98) 0%, rgba(87, 83, 78, 0.95) 100%)', 
                    boxShadow: isBulkMode 
                      ? '0 12px 48px rgba(251, 191, 36, 0.6), 0 0 0 1px rgba(251, 191, 36, 0.4) inset, 0 0 50px rgba(251, 191, 36, 0.2)'
                      : '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(251, 191, 36, 0.15) inset', 
                    border: isBulkMode 
                      ? '2px solid rgba(251, 191, 36, 0.5)' 
                      : '2px solid rgba(251, 191, 36, 0.2)'
                  }}
                  initial={{ opacity: 0, height: 0, y: -20 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {isBulkMode && (
                    <div className="absolute inset-0 opacity-0 group-hover/bulk-section:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
                  )}
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl" style={{ 
                          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.3) 100%)',
                          boxShadow: '0 4px 16px rgba(251, 191, 36, 0.3)'
                        }}>
                          <Crown className="w-6 h-6 text-yellow-400" />
                  </div>
                        <span className="text-amber-100 font-extrabold text-xl gradient-text-gold">En Gros</span>
                      </div>
                      <div className="px-4 py-2 rounded-full text-xs font-extrabold relative overflow-hidden" style={{ 
                        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', 
                        color: '#78350f',
                        boxShadow: '0 4px 16px rgba(217, 119, 6, 0.4)'
                      }}>
                        💰 Prix Dégressif
                      </div>
                    </div>
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-amber-200/90 text-sm font-semibold uppercase tracking-wide">{t('quantityLabel')}</span>
                      <motion.span 
                          className={`text-4xl font-extrabold gradient-text-gold`}
                        key={bulkQuantity}
                          initial={{ scale: 0.8, rotate: -5 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 300 }}
                      >{bulkQuantity}g</motion.span>
                    </div>
                      <div className="relative">
                        <input 
                          type="range" 
                          min="100" 
                          max="1000" 
                          step="100" 
                          value={bulkQuantity} 
                          onChange={(e) => { setBulkQuantity(parseInt(e.target.value)); setIsBulkMode(true); setSelectedQuantity(null); }} 
                          className="w-full h-4 rounded-lg cursor-pointer appearance-none" 
                          style={{ 
                            background: `linear-gradient(to right, #fbbf24 0%, #f59e0b ${((bulkQuantity - 100) / 900) * 100}%, #57534e ${((bulkQuantity - 100) / 900) * 100}%, #57534e 100%)`,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                          }}
                        />
                        <div className="flex justify-between mt-2 text-xs text-amber-400/70 font-medium">
                          <span>100g</span>
                          <span>1000g</span>
                        </div>
                      </div>
                  </div>
                  <motion.div 
                      className="card-premium shadow-multilayer flex items-center justify-between p-5 rounded-2xl relative overflow-hidden group/price"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    style={{ 
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 30%, rgba(233, 236, 239, 0.1) 50%, rgba(248, 249, 250, 0.12) 70%, rgba(255, 255, 255, 0.15) 100%)',
                        backgroundSize: '200% 200%',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: `
                          0 8px 32px rgba(255, 255, 255, 0.2),
                          0 0 0 1px rgba(255, 255, 255, 0.2) inset,
                          0 0 40px rgba(255, 255, 255, 0.1)
                        `,
                        animation: 'gradient-shift 3s ease infinite'
                    }}
                  >
                      <div className="absolute inset-0 opacity-0 group-hover/price:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
                      <div className="flex items-center gap-2 relative z-10">
                        <Coins className="w-5 h-5 text-white" />
                        <span className="text-white/90 font-semibold text-base">Prix</span>
                      </div>
                      <div className="flex flex-col items-end relative z-10">
                    <motion.span 
                          className="text-4xl font-extrabold gradient-text-snow"
                      key={calculateBulkPrice(bulkQuantity)}
                          initial={{ scale: 0.8, rotate: -5 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: "spring", stiffness: 300 }}
                    >{formatPrice(calculateBulkPrice(bulkQuantity))}</motion.span>
                        {userPass !== 'bronze' && (() => {
                          // Pour Canada Cali, utiliser un prix dégressif basé sur la quantité
                          let basePrice;
                          if (selectedCategory === 'Cali') {
                            if (bulkQuantity >= 100) {
                              basePrice = 5;
                            } else if (bulkQuantity >= 50) {
                              basePrice = 5.6;
                            } else if (bulkQuantity >= 25) {
                              basePrice = 6.4;
                            } else if (bulkQuantity >= 10) {
                              basePrice = 8;
                            } else {
                              basePrice = 10;
                            }
                          } else if (selectedCategory === 'mousse') {
                            // Prix dégressif pour Premium Mousse (basé sur les tarifs: 5g=30, 10g=50, 25g=100, 50g=180, 100g=300)
                            if (bulkQuantity >= 100) {
                              basePrice = 3; // 100g = 300, donc 3/g
                            } else if (bulkQuantity >= 50) {
                              basePrice = 3.6; // 50g = 180, donc 3.6/g
                            } else if (bulkQuantity >= 25) {
                              basePrice = 4; // 25g = 100, donc 4/g
                            } else if (bulkQuantity >= 10) {
                              basePrice = 5; // 10g = 50, donc 5/g
                            } else {
                              basePrice = 6; // 5g = 30, donc 6/g
                            }
                          } else {
                            basePrice = selectedCategory === 'Baida' ? 45 : 
                                           selectedCategory === 'Jiblis' ? 4.5 : 
                                           selectedCategory === 'Sahas' ? 5.5 : 4;
                          }
                          const originalPrice = Math.round(bulkQuantity * basePrice);
                          return (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: 800,
                              color: '#ff4d4d',
                              textDecoration: 'line-through',
                              textDecorationColor: '#ff4d4d',
                              textDecorationThickness: '2px',
                              background: 'rgba(255,77,77,0.12)',
                              borderRadius: '6px',
                              padding: '2px 6px',
                              border: '1px solid rgba(255,77,77,0.25)',
                              marginTop: '4px',
                              display: 'inline-block'
                            }}>{formatPrice(originalPrice)}</span>
                          );
                        })()}
                      </div>
                  </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
            {selectedQuantity && (
                <motion.div 
                  className="space-y-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                <motion.div 
                  className="card-premium glass-card shadow-multilayer rounded-2xl p-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="text-white font-bold mb-2 block text-sm flex items-center gap-2"><Scroll className="w-4 h-4 text-white" />{t('orderNameLabel')}</label>
                  <div 
                    className="w-full bg-white/10 text-white rounded-xl p-3 border-2 border-white/20 flex items-center justify-between" 
                    style={{ 
                      backdropFilter: 'blur(10px)',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                      fontSize: '16px',
                      WebkitTextSizeAdjust: '100%',
                      touchAction: 'auto'
                    }} 
                  >
                    <span className="font-semibold">{userName || orderName || t('notDefined')}</span>
                    <span className="text-white/70 text-xs">{t('confirm')}</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                    <span className="text-lg">🌍</span>
                    Pays de livraison
                  </label>
                  <div className="flex gap-3 mb-4">
                    {(() => {
                      // Utiliser countryConfig pour déterminer la disponibilité des pays
                      const isFranceDisabled = !countryConfig.france;
                      const isSuisseDisabled = !countryConfig.suisse;
                      
                      return (
                        <>
                          <button
                            onClick={() => {
                              if (!isFranceDisabled) {
                                persistCountrySelection('france');
                              }
                            }}
                            disabled={isFranceDisabled}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all text-white ${
                              getEffectiveCountry() === 'france'
                                ? 'shadow-lg scale-105'
                                : isFranceDisabled
                                ? 'bg-gray-500/30 text-gray-400 border-2 border-gray-500/30 cursor-not-allowed opacity-50'
                                : ''
                            }`}
                            style={{
                              background: selectedCountry === 'france'
                                ? 'linear-gradient(135deg, rgba(74, 144, 226, 0.35) 0%, rgba(107, 163, 232, 0.3) 50%, rgba(74, 144, 226, 0.35) 100%)'
                                : isFranceDisabled
                                ? 'rgba(107, 114, 128, 0.3)'
                                : 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
                              border: selectedCountry === 'france'
                                ? '1.5px solid rgba(74, 144, 226, 0.5)'
                                : isFranceDisabled
                                ? '2px solid rgba(107, 114, 128, 0.3)'
                                : '1.5px solid rgba(74, 144, 226, 0.3)',
                              backdropFilter: 'blur(40px) saturate(300%)',
                              WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                              boxShadow: selectedCountry === 'france' 
                                ? '0 4px 16px rgba(74, 144, 226, 0.4), inset 0 0 20px rgba(74, 144, 226, 0.15), 0 0 30px rgba(74, 144, 226, 0.3)' 
                                : isFranceDisabled
                                ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                                : 'inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 20px rgba(74, 144, 226, 0.1)',
                              pointerEvents: isFranceDisabled ? 'none' : 'auto'
                            }}
                            onMouseEnter={(e) => {
                              if (!isFranceDisabled && selectedCountry !== 'france') {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.25) 0%, rgba(107, 163, 232, 0.2) 50%, rgba(74, 144, 226, 0.25) 100%)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isFranceDisabled && selectedCountry !== 'france') {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)';
                              }
                            }}
                          >
                            🇫🇷 France
                          </button>
                          <button
                            onClick={() => {
                              if (!isSuisseDisabled) {
                                persistCountrySelection('suisse');
                              }
                            }}
                            disabled={isSuisseDisabled}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all text-white ${
                              getEffectiveCountry() === 'suisse'
                                ? 'shadow-lg scale-105'
                                : isSuisseDisabled
                                ? 'bg-gray-500/30 text-gray-400 border-2 border-gray-500/30 cursor-not-allowed opacity-50'
                                : ''
                            }`}
                            style={{
                              background: selectedCountry === 'suisse'
                                ? 'linear-gradient(135deg, rgba(74, 144, 226, 0.35) 0%, rgba(107, 163, 232, 0.3) 50%, rgba(74, 144, 226, 0.35) 100%)'
                                : isSuisseDisabled
                                ? 'rgba(107, 114, 128, 0.3)'
                                : 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
                              border: selectedCountry === 'suisse'
                                ? '1.5px solid rgba(74, 144, 226, 0.5)'
                                : isSuisseDisabled
                                ? '2px solid rgba(107, 114, 128, 0.3)'
                                : '1.5px solid rgba(74, 144, 226, 0.3)',
                              backdropFilter: 'blur(40px) saturate(300%)',
                              WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                              boxShadow: selectedCountry === 'suisse' 
                                ? '0 4px 16px rgba(74, 144, 226, 0.4), inset 0 0 20px rgba(74, 144, 226, 0.15), 0 0 30px rgba(74, 144, 226, 0.3)' 
                                : isSuisseDisabled
                                ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                                : 'inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 20px rgba(74, 144, 226, 0.1)',
                              pointerEvents: isSuisseDisabled ? 'none' : 'auto'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSuisseDisabled && selectedCountry !== 'suisse') {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.25) 0%, rgba(107, 163, 232, 0.2) 50%, rgba(74, 144, 226, 0.25) 100%)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSuisseDisabled && selectedCountry !== 'suisse') {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)';
                              }
                            }}
                          >
                            🇨🇭 Suisse
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </motion.div>
                <motion.div 
                  className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2"><Scroll className="w-5 h-5 text-white" />{t('deliveryAddress')}</label>
                  <textarea 
                    value={deliveryAddress} 
                    onChange={(e) => setDeliveryAddress(e.target.value)} 
                    placeholder={t('addressPlaceholder')} 
                    rows="3" 
                    className="w-full bg-white/10 text-white placeholder-white/50 rounded-2xl p-4 border-2 border-white/20 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/50" 
                    style={{ 
                      resize: 'none',
                      backdropFilter: 'blur(10px)',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                      fontSize: '16px',
                      WebkitTextSizeAdjust: '100%',
                      touchAction: 'auto'
                    }} 
                  />
                </motion.div>
                <motion.div 
                  className="card-premium glass-card shadow-multilayer rounded-2xl p-5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="text-white font-bold mb-3 block text-sm flex items-center gap-2">
                    <Crown className="w-5 h-5 text-white" />
                    {t('referralCodeOpt')}
                  </label>
                  <input 
                    type="text"
                    value={parrainageCodeInput} 
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      if (value.length <= 10) {
                        setParrainageCodeInput(value);
                      }
                    }}
                    placeholder="ABCD123..." 
                    className="w-full bg-white/10 text-white placeholder-white/50 rounded-2xl p-4 border-2 border-white/20 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/50" 
                    style={{ 
                      backdropFilter: 'blur(10px)',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                      fontSize: '16px',
                      WebkitTextSizeAdjust: '100%',
                      touchAction: 'auto',
                      textTransform: 'uppercase',
                      letterSpacing: '2px'
                    }} 
                  />
                  {parrainageCodeInput && (
                    <p className="text-amber-300 text-xs mt-2">
                      💡 Vous recevrez 2G offerts si le code est valide
                    </p>
                  )}
                </motion.div>
                {/* Bouton livraison prioritaire - masqué si Pass Argent ou Or (automatique) */}
                {(userPass === 'argent' || userPass === 'or') ? (
                  <div 
                    className="card-premium shadow-multilayer relative p-4 rounded-xl max-w-sm mx-auto ring-2 ring-white ring-offset-1" 
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 50%, rgba(255, 255, 255, 0.9) 100%)', 
                      border: '1.5px solid rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <div className="flex items-center justify-between relative z-10">
                      <div className="flex items-center gap-2">
                        <div 
                          className="p-2 rounded-lg"
                          style={{
                            background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 100%)',
                            border: '1px solid rgba(74, 144, 226, 0.3)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)'
                          }}
                        >
                          <Gem className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-left">
                          <span className="block font-bold text-base text-gray-900">{t('priorityDelivery')}</span>
                          <span className="block text-xs text-gray-700">{t('includedWithPass')}</span>
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">{t('gratuitLabel')}</div>
                    </div>
                  </div>
                ) : (
                <button 
                  onClick={() => setIsPriorityDelivery(!isPriorityDelivery)} 
                  className={`card-premium shadow-multilayer relative p-4 rounded-xl max-w-sm mx-auto ${isPriorityDelivery ? 'ring-2 ring-white ring-offset-1' : ''}`} 
                  style={{ 
                    background: isPriorityDelivery 
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 50%, rgba(255, 255, 255, 0.9) 100%)' 
                      : 'linear-gradient(135deg, rgba(108, 117, 125, 0.3) 0%, rgba(73, 80, 87, 0.3) 50%, rgba(108, 117, 125, 0.3) 100%)', 
                    border: isPriorityDelivery 
                      ? '1.5px solid rgba(255, 255, 255, 0.5)' 
                      : '1.5px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-2">
                      <div 
                      className="p-2 rounded-lg"
                      style={{
                        background: isPriorityDelivery 
                          ? 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 100%)'
                          : 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 100%)',
                        border: '1px solid rgba(74, 144, 226, 0.3)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)'
                      }}
                    >
                      <Gem className="w-5 h-5 text-white" />
                    </div>
                      <div className="text-left">
                        <span className={`block font-bold text-base ${isPriorityDelivery ? 'text-gray-900' : 'text-white'}`}>{t('priorityDelivery')}</span>
                        <span className={`block text-xs ${isPriorityDelivery ? 'text-gray-700' : 'text-white/70'}`}></span>
                      </div>
                    </div>
                    <div className={`text-2xl font-bold ${isPriorityDelivery ? 'text-gray-900' : 'text-white'}`}>+{formatPrice(10)}</div>
                  </div>
                </button>
                )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Case unique avec total et bouton commander - Taille originale */}
          {(selectedQuantity || isBulkMode) && (
            <div 
              className="card-premium shadow-multilayer rounded-2xl p-5 relative max-w-sm mx-auto mt-6 product-checkout-card"
              style={{ 
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.12) 50%, rgba(255, 255, 255, 0.15) 100%)', 
                border: '1.5px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80 text-sm">Produit</span>
                  <span className="text-white font-bold">
                    {isBulkMode ? `${bulkQuantity}g` : `${selectedQuantity?.amount || 0}g`}
                  </span>
                </div>
                  <div className="flex justify-between items-center">
                  <span className="text-white/80 text-sm">Prix</span>
                  <span className="text-white font-bold">
                    {isBulkMode ? formatPrice(calculateBulkPrice(bulkQuantity)) : formatPrice(Math.round(getPassPrice(selectedQuantity?.price || 0, selectedProduct?.name || '')))}
                  </span>
                </div>
                {/* Affichage livraison prioritaire - gratuite pour Pass Argent/Or */}
                {((userPass === 'argent' || userPass === 'or') || isPriorityDelivery) && (
                  <div className="flex justify-between items-center">
                    <span className="text-white/80 text-sm flex items-center gap-1"><Sparkles className="w-4 h-4" />Prioritaire</span>
                    <span className="text-white font-bold">
                      {(userPass === 'argent' || userPass === 'or') ? 'GRATUIT' : `+${formatPrice(10)}`}
                    </span>
                  </div>
                )}
                {hasParrainageBonus && (() => {
                  const pName = selectedProduct?.name || '';
                  const n = pName.toLowerCase();
                  const isBonusExcludedHere = n.includes('snow') || n.includes('baida') ||
                    n.includes('sahas frozen') || n.includes('sahas static') ||
                    (n.includes('sahas') && (n.includes('static') || n.includes('frozen'))) ||
                    n.includes('bubble hash') ||
                    n.includes('polkadot') || n.includes('magic chocolate') || n.includes('magicchocolate') ||
                    n.includes('stilpane') || n.includes('oxycod') || n.includes('oxcycod');
                  return isBonusExcludedHere ? (
                    <div className="flex justify-between items-center rounded-lg p-2 border" style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.25)' }}>
                      <span className="text-red-300 text-sm flex items-center gap-1" style={{ fontWeight: 600 }}>
                        Bonus parrainage
                      </span>
                      <span className="text-red-300 text-xs font-semibold">Indisponible pour cet article</span>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center bg-green-600/20 rounded-lg p-2 border border-green-500/30">
                      <span className="text-green-300 text-sm flex items-center gap-1" style={{ fontWeight: 600 }}>
                        Bonus Parrainage
                      </span>
                      <span className="text-green-300 font-bold text-sm">+{referralBonusGrams || 2}g OFFERTS</span>
                    </div>
                  );
                })()}
                <div className="border-t border-white/20 pt-2 flex justify-between items-center">
                  <span className="text-white font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-white" />{t('total')}</span>
                  <span className="text-3xl font-bold text-white">
                    {formatPrice((isBulkMode ? calculateBulkPrice(bulkQuantity) : Math.round(getPassPrice(selectedQuantity.price, selectedProduct?.name || ''))) + (isPriorityDelivery && (userPass !== 'argent' && userPass !== 'or') ? 10 : 0))}
                  </span>
                </div>
              </div>
              {isProductSoldOut(selectedProduct) ? (
                <button
                  type="button"
                  onClick={() => { setShowSoldOutNotif(true); setTimeout(() => setShowSoldOutNotif(false), 3200); }}
                  className="w-full py-3 px-4 rounded-lg font-bold text-sm relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30,30,45,0.85) 0%, rgba(20,20,35,0.90) 100%)',
                    backdropFilter: 'blur(22px) saturate(160%) brightness(0.85)',
                    WebkitBackdropFilter: 'blur(22px) saturate(160%) brightness(0.85)',
                    border: '1.5px solid rgba(150,150,190,0.20)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(200,200,255,0.07)',
                    color: 'rgba(180,180,200,0.65)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.03) 50%, transparent)', pointerEvents: 'none' }} />
                  <div className="relative flex items-center justify-center gap-2 z-10">
                    <span style={{ fontSize: 16 }}>🚫</span>
                    Rupture de stock
                  </div>
                </button>
              ) : shopPreviewMode ? (
                <button
                  type="button"
                  onClick={openAccessPub}
                  className="w-full py-3.5 px-4 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2.5"
                  style={{
                    background: 'linear-gradient(145deg, rgba(251,191,36,0.32) 0%, rgba(245,158,11,0.38) 50%, rgba(234,88,12,0.28) 100%)',
                    border: '1.5px solid rgba(253,224,71,0.5)',
                    boxShadow: '0 8px 24px rgba(245,158,11,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                  }}
                >
                  <img src="/emoji/key.png" alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.65))' }} />
                  Obtenir un accès
                </button>
              ) : (
                <button 
                  type="button" 
                  onClick={handleAddToCart} 
                  disabled={isLoading || !deliveryAddress.trim()}
                  className="card-premium shadow-multilayer w-full py-3 px-4 rounded-lg font-bold text-sm relative overflow-visible disabled:opacity-60 disabled:cursor-not-allowed button-snow-effect button-snow-hover text-white transition" 
                  style={{ 
                    backgroundColor: '#1756E6',
                    border: '1.5px solid rgba(74, 144, 226, 0.4)',
                    backdropFilter: 'blur(40px) saturate(300%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(300%)',
                    boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3), 0 0 0 1px rgba(74, 144, 226, 0.2) inset, 0 0 30px rgba(74, 144, 226, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(107, 163, 232, 0.25) 50%, rgba(74, 144, 226, 0.3) 100%)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 50%, rgba(74, 144, 226, 0.2) 100%)';
                    }
                  }}
                >
                  <div className="relative flex items-center justify-center gap-2 z-10">
                    <ShoppingCart className="w-5 h-5" />
                    {t('addToCartBtn')} {isBulkMode ? `${bulkQuantity}g` : selectedQuantity?.label || ''}
                    <Crown className="w-4 h-4" />
                  </div>
                </button>
              )}
              {cart.length > 0 && (
                <button 
                  type="button" 
                  onClick={() => setCurrentView('cart')}
                  className="card-premium shadow-multilayer w-full py-3 px-4 rounded-lg font-bold text-sm relative overflow-visible mt-3 button-snow-effect button-snow-hover" 
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 50%, rgba(139, 92, 246, 0.9) 100%)', 
                    color: '#ffffff',
                    border: '1.5px solid rgba(255, 255, 255, 0.4)'
                  }}
                >
                  <div className="relative flex items-center justify-center gap-2 z-10">
                    <Package className="w-5 h-5" />
                    {t('viewCart', cart.length)}
            </div>
                </button>
              )}
            </div>
          )}
        </div>
        <MobileNavBottomReserve />
        <MobileNavBottomSpacer />
      </div>
    );
  };

  // ============================================================
  //  JEU ARCADE — fonctions
  // ============================================================

  const fetchArcadeLeaderboard = async () => {
    try {
      const q = telegramChatId ? `?chatId=${encodeURIComponent(telegramChatId)}` : '';
      const res = await fetch(`${BACKEND_URL}/game/leaderboard${q}`);
      const data = await res.json();
      setArcadeLeaderboard(data.leaderboard || data.top7 || []);
    } catch (e) { console.error('leaderboard fetch error', e); }
  };

  // ── FLAPPY BIRD (logique + visuels pixel art — clone fidèle 288×512)
  const FB_W           = 288;
  const FB_H           = 512;
  const FB_BIRD_X      = 50;
  const FB_BIRD_W      = 51;
  const FB_BIRD_R      = 18;
  const FB_BIRD_SCALE  = 3;
  const FB_GRAVITY     = 0.25;
  const FB_FLAP_VY     = -4.6;
  const FB_MAX_FALL    = 10;
  const FB_PIPE_W      = 52;
  const FB_PIPE_GAP    = 96;
  const FB_PIPE_GAP_MIN = 76;
  const FB_PIPE_SPD    = 2;
  const FB_PIPE_SPD_MAX = 2.85;
  const FB_PIPE_INT    = 90;
  const FB_PIPE_INT_MIN = 72;
  const FB_GROUND_H    = 112;
  const FB_SKY         = '#4ec0ca';
  const FB_CITY_FAR    = '#a8dce4';
  const FB_CITY_LIGHT  = '#8ecbd4';
  const FB_CITY_MID    = '#6eb8c4';
  const FB_CITY_DARK   = '#5aa8b8';
  const FB_BUSH        = '#73bf2e';
  const FB_BUSH_DARK   = '#546e25';
  const FB_CLOUD_Y     = 318;
  const FB_PIPE_BODY   = '#73bf2e';
  const FB_PIPE_DARK   = '#546e25';
  const FB_PIPE_LIGHT  = '#a5e63a';
  const FB_GROUND_STRIPE1 = '#ded895';
  const FB_GROUND_STRIPE2 = '#d0ba88';
  const FB_GROUND_GRASS = '#73bf2e';
  const FB_GROUND_GRASS_D = '#546e25';

  const fbCanvasRef      = React.useRef(null);
  const fbRafRef         = React.useRef(null);
  const fbStateRef       = React.useRef(null);
  const fbScoreSubmitRef = React.useRef(false);
  const [, setFbScore]    = useState(0);
  const [fbBestScore,     setFbBestScore] = useState(() => {
    try { return parseInt(localStorage.getItem('flappy_best') || '0', 10) || 0; } catch (e) { return 0; }
  });
  const [fbWaiting,       setFbWaiting]  = useState(true);

  const fbInitState = () => ({
    phase: 'playing',
    started: false,
    vy: 0,
    birdY: FB_H / 2 - FB_GROUND_H / 2 - 20,
    birdRot: 0,
    wingFrame: 0,
    wingTick: 0,
    pipes: [],
    frameCount: 0,
    score: 0,
    groundX: 0,
    bgX: 0,
    flashOn: true,
    flashTick: 0,
    bestScore: fbBestScore,
    clouds: [
      { x: -20,  y: FB_CLOUD_Y,      spd: 0.12, w: 80, h: 28 },
      { x: 90,   y: FB_CLOUD_Y + 8,  spd: 0.08, w: 64, h: 22 },
      { x: 180,  y: FB_CLOUD_Y - 6, spd: 0.14, w: 72, h: 26 },
      { x: 260,  y: FB_CLOUD_Y + 4,  spd: 0.1,  w: 56, h: 20 },
    ],
  });

  const FB_SKYLINE = [
    { ox: 0,   w: 34, h: 58, c: FB_CITY_MID },
    { ox: 30,  w: 26, h: 42, c: FB_CITY_FAR },
    { ox: 52,  w: 40, h: 74, c: FB_CITY_DARK },
    { ox: 88,  w: 22, h: 38, c: FB_CITY_LIGHT },
    { ox: 106, w: 36, h: 52, c: FB_CITY_MID },
    { ox: 138, w: 28, h: 66, c: FB_CITY_DARK },
    { ox: 162, w: 42, h: 48, c: FB_CITY_FAR },
    { ox: 200, w: 30, h: 62, c: FB_CITY_MID },
    { ox: 226, w: 24, h: 44, c: FB_CITY_LIGHT },
  ];
  const FB_SKYLINE_W = 252;

  const fbSetupCtx = (ctx) => {
    ctx.imageSmoothingEnabled = false;
    if (ctx.webkitImageSmoothingEnabled !== undefined) ctx.webkitImageSmoothingEnabled = false;
    if (ctx.mozImageSmoothingEnabled !== undefined) ctx.mozImageSmoothingEnabled = false;
  };

  const fbPx = (ctx, x, y, w, h, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(Math.floor(x), Math.floor(y), Math.floor(w), Math.floor(h));
  };

  const FB_DIGITS = {
    '0': ['111','101','101','101','101','101','111'],
    '1': ['010','110','010','010','010','010','111'],
    '2': ['111','001','001','111','100','100','111'],
    '3': ['111','001','001','111','001','001','111'],
    '4': ['101','101','101','111','001','001','001'],
    '5': ['111','100','100','111','001','001','111'],
    '6': ['111','100','100','111','101','101','111'],
    '7': ['111','001','001','010','010','010','010'],
    '8': ['111','101','101','111','101','101','111'],
    '9': ['111','101','101','111','001','001','111'],
  };

  const fbDrawDigit = (ctx, digit, x, y, scale, fill, outline) => {
    const rows = FB_DIGITS[String(digit)];
    if (!rows) return;
    rows.forEach((row, ry) => {
      for (let rx = 0; rx < row.length; rx++) {
        if (row[rx] !== '1') continue;
        if (outline) {
          for (let oy = -1; oy <= 1; oy++) {
            for (let ox = -1; ox <= 1; ox++) {
              if (ox === 0 && oy === 0) continue;
              fbPx(ctx, x + rx * scale + ox, y + ry * scale + oy, scale, scale, outline);
            }
          }
        }
        fbPx(ctx, x + rx * scale, y + ry * scale, scale, scale, fill);
      }
    });
  };

  const fbDrawNumber = (ctx, num, cx, y, scale, fill = '#fff', outline = '#543847') => {
    const str = String(num);
    const digitW = 3 * scale;
    const gap = scale;
    const totalW = str.length * digitW + (str.length - 1) * gap;
    let x = cx - totalW / 2;
    for (const ch of str) {
      fbDrawDigit(ctx, ch, x, y, scale, fill, outline || null);
      x += digitW + gap;
    }
  };

  const fbDrawSky = (ctx) => {
    ctx.fillStyle = FB_SKY;
    ctx.fillRect(0, 0, FB_W, FB_H - FB_GROUND_H);
  };

  const fbDrawCloudBlob = (ctx, x, y, w, h) => {
    const px = (v) => Math.floor(v);
    fbPx(ctx, px(x), px(y), px(w * 0.52), px(h * 0.4), '#fff');
    fbPx(ctx, px(x - w * 0.22), px(y + h * 0.12), px(w * 0.34), px(h * 0.36), '#fff');
    fbPx(ctx, px(x + w * 0.18), px(y + h * 0.08), px(w * 0.3), px(h * 0.34), '#fff');
    fbPx(ctx, px(x + w * 0.08), px(y - h * 0.1), px(w * 0.26), px(h * 0.28), '#fff');
  };

  const fbDrawClouds = (ctx, clouds) => {
    clouds.forEach(c => fbDrawCloudBlob(ctx, c.x, c.y, c.w, c.h));
  };

  const fbDrawCity = (ctx, scrollX, groundY) => {
    const baseY = groundY - 18;
    const startX = -((scrollX * 0.5) % FB_SKYLINE_W);
    for (let rep = -1; rep <= 2; rep++) {
      FB_SKYLINE.forEach(b => {
        const x = Math.floor(startX + rep * FB_SKYLINE_W + b.ox);
        const y = baseY - b.h;
        fbPx(ctx, x, y, b.w, b.h, b.c);
        for (let wy = y + 6; wy < baseY - 4; wy += 10) {
          for (let wx = x + 4; wx < x + b.w - 6; wx += 9) {
            if (((wx + wy + b.ox) % 13) < 6) fbPx(ctx, wx, wy, 3, 3, '#d8f0f4');
          }
        }
      });
    }
  };

  const fbDrawBushes = (ctx, scrollX, groundY) => {
    const bushY = groundY - 14;
    for (let x = -((scrollX * 0.5) % 28) - 28; x < FB_W + 28; x += 14) {
      fbPx(ctx, x, bushY + 2, 14, 10, FB_BUSH_DARK);
      fbPx(ctx, x + 2, bushY - 2, 10, 8, FB_BUSH);
      fbPx(ctx, x + 8, bushY, 12, 9, FB_BUSH);
      fbPx(ctx, x + 4, bushY + 4, 16, 6, FB_BUSH_DARK);
    }
  };

  const fbDrawGround = (ctx, scrollX) => {
    const groundY = FB_H - FB_GROUND_H;
    const sx = Math.floor(scrollX) % 14;
    ctx.fillStyle = FB_GROUND_GRASS;
    ctx.fillRect(0, groundY, FB_W, 14);
    for (let x = -sx - 14; x < FB_W + 14; x += 7) {
      fbPx(ctx, x, groundY, 3, 5, FB_GROUND_GRASS_D);
      fbPx(ctx, x + 3, groundY + 1, 3, 4, FB_GROUND_GRASS);
      fbPx(ctx, x + 6, groundY, 3, 5, FB_GROUND_GRASS_D);
    }
    ctx.fillStyle = FB_GROUND_STRIPE1;
    ctx.fillRect(0, groundY + 14, FB_W, FB_GROUND_H - 14);
    for (let x = -((scrollX % 20) + 20); x < FB_W + 20; x += 10) {
      fbPx(ctx, x, groundY + 22, 10, 5, FB_GROUND_STRIPE2);
      fbPx(ctx, x + 5, groundY + 30, 10, 5, FB_GROUND_STRIPE1);
      fbPx(ctx, x + 2, groundY + 42, 10, 5, FB_GROUND_STRIPE2);
      fbPx(ctx, x + 7, groundY + 52, 10, 5, FB_GROUND_STRIPE1);
      fbPx(ctx, x, groundY + 64, 10, 5, FB_GROUND_STRIPE1);
      fbPx(ctx, x + 5, groundY + 76, 10, 5, FB_GROUND_STRIPE2);
      fbPx(ctx, x + 2, groundY + 88, 10, 5, FB_GROUND_STRIPE2);
    }
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(FB_W, groundY);
    ctx.stroke();
  };

  const fbDrawBackground = (ctx, S) => {
    const groundY = FB_H - FB_GROUND_H;
    const scroll = S.groundX || 0;
    fbDrawSky(ctx);
    fbDrawCity(ctx, scroll, groundY);
    fbDrawClouds(ctx, S.clouds);
    fbDrawBushes(ctx, scroll, groundY);
    fbDrawGround(ctx, scroll);
  };

  const fbDrawPipe = (ctx, pipe) => {
    const topH = pipe.topH;
    const gap = pipe.gap ?? FB_PIPE_GAP;
    const botY = topH + gap;
    const botH = FB_H - FB_GROUND_H - botY;
    const capH = 26;
    const drawOne = (x, y, w, h, capAtBottom) => {
      if (h <= 0) return;
      const capY = capAtBottom ? y + h - capH : y;
      const bodyY = capAtBottom ? y : y + capH;
      const bodyH = capAtBottom ? h - capH : h - capH;
      fbPx(ctx, x, capY, w, capH, FB_PIPE_BODY);
      fbPx(ctx, x, capY, w, 4, FB_PIPE_LIGHT);
      fbPx(ctx, x, capY + capH - 4, w, 4, FB_PIPE_DARK);
      fbPx(ctx, x, bodyY, w, bodyH, FB_PIPE_BODY);
      fbPx(ctx, x + 4, bodyY, 6, bodyH, FB_PIPE_LIGHT);
      fbPx(ctx, x + w - 8, bodyY, 6, bodyH, FB_PIPE_DARK);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(x + 0.5, capY + 0.5, w - 1, capH - 1);
      if (bodyH > 0) ctx.strokeRect(x + 0.5, bodyY + 0.5, w - 1, bodyH - 1);
    };
    drawOne(pipe.x, 0, FB_PIPE_W, topH, true);
    drawOne(pipe.x, botY, FB_PIPE_W, botH, false);
  };

  const FB_BIRD_PALETTE = {
    K: '#000000', Y: '#f8d838', L: '#fce85a', D: '#e8a800',
    R: '#e4002b', W: '#ffffff', B: '#000000', G: '#ffffff',
  };

  const FB_BIRD_SPRITES = [
    [
      '.....KKKKKKK.....','....KYYYYYYYK....','...KYYYYYYYYYK...',
      '..KYYYYYYYWYYYK..','.KYYYYYYYYYYYYK.','KYYYYYYYYYYYYYK',
      'KYYRRRRRRRRRRYK','KYYYRRRRRRRRRYK','.KYYRRRRRRRYK..',
      '..KYYYYYYYYYK...','...KWWWWWWYK....','....KKKKKKK.....',
    ],
    [
      '.....KKKKKKK.....','....KYYYYYYYK....','...KYYYYYYYYYK...',
      '..KYYYYYYYWYYYK..','.KYYYYYYYYYYYYK.','KYYYYYYYYYYYYYK',
      'KYYRRRRRRRRRRYK','KYYYRRRRRRRRRYK','.KYYRRRRRRRYK..',
      '..KYYYYYGGGGYK..','...KGGGGGGGGK....','....KKKKKKK.....',
    ],
    [
      '.....KKKKKKK.....','....KYYYYYYYK....','...KYYYYYYYYYK...',
      '..KYYYYYYYWYYYK..','.KYYYYYYYYYYYYK.','KYYYYYYYYYYYYYK',
      'KYYRRRRRRRRRRYK','KYYYRRRRRRRRRYK','.KYYRRRRRRRYK..',
      '..KYYYYYYYYYK...','...KGGGGGGGGK....','....KKKKKKK.....',
    ],
  ];

  const fbGetDifficulty = (score) => {
    const tier = Math.min(8, Math.floor(score / 2));
    return {
      gap: Math.max(FB_PIPE_GAP_MIN, FB_PIPE_GAP - tier * 2),
      speed: Math.min(FB_PIPE_SPD_MAX, FB_PIPE_SPD + tier * 0.1),
      interval: Math.max(FB_PIPE_INT_MIN, FB_PIPE_INT - tier * 2),
    };
  };

  const fbDrawBirdSprite = (ctx, frame, rot, birdY) => {
    const sprite = FB_BIRD_SPRITES[frame % 3];
    const scale = FB_BIRD_SCALE;
    const w = sprite[0].length * scale;
    const h = sprite.length * scale;
    ctx.save();
    ctx.translate(FB_BIRD_X + FB_BIRD_W / 2, birdY);
    ctx.rotate(rot);
    ctx.translate(-w / 2, -h / 2 + scale);
    const pal = FB_BIRD_PALETTE;
    sprite.forEach((row, ry) => {
      for (let rx = 0; rx < row.length; rx++) {
        const ch = row[rx];
        if (ch === '.' || !pal[ch]) continue;
        fbPx(ctx, rx * scale, ry * scale, scale, scale, pal[ch]);
      }
    });
    ctx.restore();
  };

  const fbDrawScoreBox = (ctx, num, cx, y) => {
    const str = String(num);
    const scale = 5;
    const digitW = 3 * scale;
    const gap = scale;
    const totalW = str.length * digitW + (str.length - 1) * gap + 16;
    const boxH = 7 * scale + 10;
    const bx = cx - totalW / 2;
    fbPx(ctx, bx, y, totalW, boxH, '#fff');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.strokeRect(bx + 1.5, y + 1.5, totalW - 3, boxH - 3);
    fbDrawNumber(ctx, num, cx, y + 6, scale, '#000', null);
  };

  const fbGetReplayBtn = () => ({
    x: FB_W / 2 - 60,
    y: FB_H - FB_GROUND_H - 54,
    w: 120,
    h: 40,
  });

  const fbDrawReplayBtn = (ctx, btn, pressed = false) => {
    const { x, y, w, h } = btn;
    const r = 8;
    const top = pressed ? '#5a9e28' : '#73bf2e';
    fbPx(ctx, x, y + 5, w, h - 5, '#546e25');
    fbPx(ctx, x, y, w, h - 6, top);
    fbPx(ctx, x + 3, y + 3, w - 6, 5, '#a5e63a');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(x + 1.5, y + 1.5, w - 3, h - 3, r);
      ctx.stroke();
    } else {
      ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
    }
    ctx.font = 'bold 18px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.strokeText('PLAY', x + w / 2, y + 26);
    ctx.fillText('PLAY', x + w / 2, y + 26);
  };

  const fbDrawTapHand = (ctx, x, y) => {
    const pal = { K: '#000', W: '#fff', S: '#ccc' };
    const hand = [
      '...KK...','..KWWK..','.KWWWWK.','KWWWWWK','KWWWWWK',
      'KWWWWWK','.KWWWK.','..KWK..','...K...',
    ];
    hand.forEach((row, ry) => {
      for (let rx = 0; rx < row.length; rx++) {
        const ch = row[rx];
        if (ch === '.' || !pal[ch]) continue;
        fbPx(ctx, x + rx * 3, y + ry * 3, 3, 3, pal[ch]);
      }
    });
    fbPx(ctx, x + 18, y - 6, 2, 4, '#000');
    fbPx(ctx, x + 22, y - 10, 2, 4, '#000');
    fbPx(ctx, x + 26, y - 6, 2, 4, '#000');
  };

  const fbDrawTapBubble = (ctx, x, y) => {
    fbPx(ctx, x, y + 8, 56, 28, '#ee462f');
    fbPx(ctx, x + 4, y + 12, 48, 20, '#f85a45');
    ctx.fillStyle = '#ee462f';
    ctx.beginPath();
    ctx.moveTo(x - 8, y + 20);
    ctx.lineTo(x + 4, y + 28);
    ctx.lineTo(x + 4, y + 12);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeRect(x + 1, y + 9, 54, 26);
    ctx.font = 'bold 16px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.strokeText('TAP', x + 28, y + 30);
    ctx.fillText('TAP', x + 28, y + 30);
  };

  const fbDrawGetReady = (ctx) => {
    const hx = FB_W * 0.36;
    const hy = FB_H * 0.42;
    fbDrawTapHand(ctx, hx, hy);
    fbDrawTapBubble(ctx, hx + 34, hy + 2);
  };

  const fbDrawGameOverTitle = (ctx) => {
    const y = FB_H * 0.14;
    ctx.font = 'bold 32px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.fillText('GAME OVER', FB_W / 2 + 2, y + 2);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 5;
    ctx.strokeText('GAME OVER', FB_W / 2, y);
    ctx.fillStyle = '#de9400';
    ctx.fillText('GAME OVER', FB_W / 2, y);
  };

  const fbDrawGameOver = (ctx, S) => {
    if (S.flashOn) {
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillRect(0, 0, FB_W, FB_H - FB_GROUND_H);
    }
    fbDrawGameOverTitle(ctx);
    const panelW = 240;
    const panelH = 130;
    const px = (FB_W - panelW) / 2;
    const py = FB_H * 0.22;
    fbPx(ctx, px, py, panelW, panelH, '#c5e8a0');
    fbPx(ctx, px + 4, py + 4, panelW - 8, panelH - 8, '#d8f0b8');
    ctx.strokeStyle = '#546e25';
    ctx.lineWidth = 4;
    ctx.strokeRect(px + 2, py + 2, panelW - 4, panelH - 4);
    ctx.font = 'bold 11px "Courier New", monospace';
    ctx.fillStyle = '#546e25';
    ctx.textAlign = 'center';
    ctx.fillText('MEDAL', px + 52, py + 24);
    const mx = px + 36;
    const my = py + 38;
    ctx.strokeStyle = '#8ab87a';
    ctx.lineWidth = 2;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(mx + 16, my + 16, 14, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    if (S.score >= 10) {
      fbPx(ctx, mx, my, 32, 32, '#cd7f32');
      fbPx(ctx, mx + 6, my + 6, 20, 20, '#ffd700');
    }
    const rx = px + 118;
    ctx.textAlign = 'left';
    ctx.fillText('SCORE', rx, py + 28);
    ctx.fillText('HIGH SCORE', rx, py + 78);
    const drawSmallBox = (val, bx, by) => {
      fbPx(ctx, bx, by, 52, 32, '#fff');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(bx + 1, by + 1, 50, 30);
      fbDrawNumber(ctx, val, bx + 26, by + 2, 4, '#000', null);
    };
    drawSmallBox(S.score, rx + 58, py + 14);
    drawSmallBox(Math.max(S.bestScore || 0, S.score), rx + 58, py + 64);
    fbDrawReplayBtn(ctx, fbGetReplayBtn(), false);
  };

  const fbDraw = () => {
    const canvas = fbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    fbSetupCtx(ctx);
    const S = fbStateRef.current;
    if (!S) return;

    fbDrawBackground(ctx, S);
    S.pipes.forEach(p => fbDrawPipe(ctx, p));
    fbDrawBirdSprite(ctx, S.wingFrame, S.birdRot, S.birdY);

    if (S.phase === 'dead') {
      fbDrawGameOver(ctx, S);
    } else if (!S.started) {
      fbDrawScoreBox(ctx, 0, FB_W / 2, 28);
      fbDrawGetReady(ctx);
    } else {
      fbDrawScoreBox(ctx, S.score, FB_W / 2, 28);
    }
  };

  const fbSubmitScore = (finalScore) => {
    if (fbScoreSubmitRef.current) return;
    fbScoreSubmitRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/game/submit-score`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: telegramChatId,
            clicks: finalScore,
            username: userName || `User${telegramChatId}`,
            accessPreview: !!shopPreviewMode,
          }),
        });
        const data = await res.json();
        setArcadeMyRank(data.success ? (data.rank ?? null) : null);
        setArcadeMyClicks(finalScore);
        if (data.success) await fetchArcadeLeaderboard();
      } catch (e) {
        setArcadeMyRank(null);
        setArcadeMyClicks(finalScore);
      }
      fbScoreSubmitRef.current = false;
    })();
  };

  const fbDie = (S) => {
    S.phase = 'dead';
    S.flashOn = true;
    S.flashTick = 0;
    const best = Math.max(fbBestScore, S.score);
    S.bestScore = best;
    if (S.score > fbBestScore) {
      setFbBestScore(S.score);
      try { localStorage.setItem('flappy_best', String(S.score)); } catch {}
    }
    setFbScore(S.score);
    fbSubmitScore(S.score);
  };

  const fbStep = () => {
    const S = fbStateRef.current;
    if (!S) return;

    if (S.phase === 'dead') {
      S.flashTick++;
      if (S.flashTick > 8) S.flashOn = false;
      return;
    }

    if (S.phase !== 'playing') return;
    S.frameCount++;
    const diff = fbGetDifficulty(S.score);
    const pipeSpd = diff.speed;
    S.groundX += pipeSpd;
    S.clouds.forEach(c => { c.x -= c.spd; if (c.x < -80) c.x = FB_W + 20; });

    if (!S.started) {
      S.birdY = FB_H / 2 - FB_GROUND_H / 2 - 20 + Math.sin(S.frameCount * 0.08) * 6;
      S.birdRot = 0;
      S.wingTick++;
      if (S.wingTick % 10 === 0) S.wingFrame = (S.wingFrame + 1) % 3;
      return;
    }

    S.vy = Math.min(S.vy + FB_GRAVITY, FB_MAX_FALL);
    S.birdY += S.vy;
    const targetRot = Math.max(-0.35, Math.min(1.2, S.vy * 0.08));
    S.birdRot += (targetRot - S.birdRot) * 0.15;
    S.wingTick++;
    const wInterval = S.vy < 0 ? 5 : 10;
    if (S.wingTick % wInterval === 0) S.wingFrame = (S.wingFrame + 1) % 3;

    if (S.birdY - FB_BIRD_R < 0) { S.birdY = FB_BIRD_R; S.vy = 0; }

    const groundY = FB_H - FB_GROUND_H;
    if (S.birdY + FB_BIRD_R >= groundY) {
      S.birdY = groundY - FB_BIRD_R;
      fbDie(S);
      return;
    }

    if (S.started && S.frameCount > 70 && (S.frameCount - 70) % diff.interval === 0) {
      const minTop = 72;
      const maxTop = FB_H - FB_GROUND_H - diff.gap - 72;
      const topH = Math.floor(Math.random() * (maxTop - minTop + 1)) + minTop;
      S.pipes.push({ x: FB_W + 2, topH, gap: diff.gap, scored: false });
    }

    for (let i = S.pipes.length - 1; i >= 0; i--) {
      const p = S.pipes[i];
      const gap = p.gap ?? FB_PIPE_GAP;
      p.x -= pipeSpd;
      if (!p.scored && p.x + FB_PIPE_W < FB_BIRD_X) {
        p.scored = true;
        S.score++;
        setFbScore(S.score);
      }
      if (p.x + FB_PIPE_W < -4) { S.pipes.splice(i, 1); continue; }
      const bL = FB_BIRD_X + 8;
      const bR = FB_BIRD_X + FB_BIRD_W - 6;
      const bT = S.birdY - FB_BIRD_R + 6;
      const bB = S.birdY + FB_BIRD_R - 4;
      const pL = p.x + 2;
      const pR = p.x + FB_PIPE_W - 2;
      const topBot = p.topH;
      const botTop = p.topH + gap;
      if (bR > pL && bL < pR && (bT < topBot || bB > botTop)) {
        fbDie(S);
        return;
      }
    }
  };

  const fbCanvasPoint = (e) => {
    const canvas = fbCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = FB_W / rect.width;
    const sy = FB_H / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return null;
    return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
  };

  const fbHitReplay = (pt) => {
    const b = fbGetReplayBtn();
    return pt && pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h;
  };

  const startArcade = () => {
    if (fbRafRef.current) { cancelAnimationFrame(fbRafRef.current); fbRafRef.current = null; }
    fbScoreSubmitRef.current = false;
    fbStateRef.current = fbInitState();
    setFbScore(0);
    setFbWaiting(true);
    setArcadePhase('playing');
    fetchArcadeLeaderboard();
    const loop = () => {
      if (!fbStateRef.current) return;
      fbStep();
      fbDraw();
      fbRafRef.current = requestAnimationFrame(loop);
    };
    fbRafRef.current = requestAnimationFrame(loop);
  };

  const handleFbTap = (e) => {
    const S = fbStateRef.current;
    if (!S) return;
    const pt = e ? fbCanvasPoint(e) : null;

    if (S.phase === 'dead') {
      if (fbHitReplay(pt)) startArcade();
      return;
    }
    if (S.phase !== 'playing') return;
    if (!S.started) { S.started = true; setFbWaiting(false); }
    S.vy = FB_FLAP_VY;
    S.wingFrame = 0;
    S.wingTick = 0;
  };

  const closeArcadeModal = () => {
    if (fbRafRef.current) { cancelAnimationFrame(fbRafRef.current); fbRafRef.current = null; }
    if (fbStateRef.current) fbStateRef.current.phase = 'idle';
    fbScoreSubmitRef.current = false;
    setShowArcadeModal(false);
    setArcadePhase('intro');
  };

  React.useEffect(() => {
    if (showArcadeModal) fetchArcadeLeaderboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArcadeModal]);

  const renderArcadeModal = () => {
    if (!showArcadeModal) return null;
    const medals = ['🥇','🥈','🥉'];
    // Access / aperçu bot → prix = accès ; boutique réelle → 2G
    const isAccessPrize = !!(shopPreviewMode || isAccessMode);
    const prizeLabel = isAccessPrize ? 'un accès offert' : '2G offerts';
    const prizeEmoji = isAccessPrize ? '🔑' : '🌿';

    const isPlaying = arcadePhase === 'playing';

    return (
      <div onClick={(e) => {
          if (e.target !== e.currentTarget) return;
          if (arcadePhase === 'results' || arcadePhase === 'submitting') return;
          closeArcadeModal();
        }}
        style={{ position:'fixed', inset:0, zIndex:'var(--gs-z-modal)',
          background: isPlaying ? '#4ec0ca' : 'rgba(5,10,30,0.9)',
          backdropFilter: isPlaying ? 'none' : 'blur(10px)',
          WebkitBackdropFilter: isPlaying ? 'none' : 'blur(10px)',
          display:'flex', alignItems:'center', justifyContent:'center', padding:'8px' }}>
        <div style={{
          width:'100%', maxWidth: isPlaying ? 300 : 340,
          background: isPlaying ? 'transparent' : 'linear-gradient(160deg,rgba(8,18,55,0.97),rgba(4,10,38,0.99))',
          border: isPlaying ? 'none' : '1.5px solid rgba(66,133,244,0.34)',
          borderRadius: isPlaying ? 0 : 26,
          boxShadow: isPlaying ? 'none' : '0 20px 60px rgba(0,0,0,0.85)',
          padding: isPlaying ? '4px 0 0' : '16px 14px 14px',
          maxHeight:'98vh', overflowY:'auto',
        }}>

          {!isPlaying && (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div>
              <span style={{ fontSize:18, fontWeight:900, color:'#e0f2fe' }}>🌍 Global Game</span>
              <span style={{ fontSize:9, color:'rgba(125,211,252,0.5)', display:'block', marginTop:1, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase' }}>Flappy Bird Edition</span>
            </div>
            <button onClick={closeArcadeModal} style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.13)', borderRadius:50, width:30, height:30, color:'#fff', fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
          </div>
          )}

          {isPlaying && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:4 }}>
              <button onClick={closeArcadeModal} style={{ background:'rgba(0,0,0,0.25)', border:'2px solid #000', borderRadius:6, width:28, height:28, color:'#fff', fontSize:13, cursor:'pointer', fontFamily:'"Courier New", monospace', fontWeight:700 }}>✕</button>
            </div>
          )}

          {/* INTRO */}
          {arcadePhase === 'intro' && (
            <div>
              <p style={{ color:'rgba(255,255,255,0.65)', fontSize:12.5, lineHeight:1.6, marginBottom:12, textAlign:'center' }}>
               Flappy Bird x Global Strains<br/>
                Le <strong style={{color:'#7dd3fc'}}>#1</strong> gagne un accès Global
              </p>
              <div style={{ marginBottom:12, background:'rgba(30,80,200,0.1)', border:'1px solid rgba(60,140,255,0.2)', borderRadius:14, padding:'10px 12px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:7 }}>
                  <p style={{ fontSize:9, color:'rgba(100,180,255,0.85)', textTransform:'uppercase', letterSpacing:'0.12em', fontWeight:700, margin:0 }}>🏆 Classement</p>
                  <button onClick={fetchArcadeLeaderboard} style={{ background:'none', border:'none', color:'rgba(100,180,255,0.5)', fontSize:13, cursor:'pointer', padding:0 }}>🔄</button>
                </div>
                {arcadeLeaderboard.length === 0
                  ? <p style={{ color:'rgba(255,255,255,0.28)', fontSize:12, textAlign:'center', padding:'5px 0', margin:0 }}>Aucune partie — sois le premier !</p>
                  : arcadeLeaderboard.slice(0,7).map((entry,i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'4px 0', borderBottom:i<Math.min(arcadeLeaderboard.length,7)-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                      <span style={{ fontSize:12.5, color: entry.isMe ? '#86efac' : (i===0?'#fbbf24':'rgba(255,255,255,0.58)'), fontWeight: (entry.isMe || i===0) ? 700 : 400 }}>
                        {medals[i]||<span style={{color:'rgba(255,255,255,0.3)',fontSize:11}}>#{i+1}</span>} {entry.anonymousName||entry.anonName}{entry.isMe ? ' (toi)' : ''}
                      </span>
                      <span style={{ fontSize:12.5, fontWeight:700, color:i===0?'#fbbf24':'#7dd3fc' }}>{entry.clicks} 🐦</span>
                    </div>
                  ))
                }
              </div>
              <button onClick={startArcade} style={{
                width:'100%', padding:'14px 0',
                background:'#73bf2e', border:'3px solid #000',
                borderRadius:4, color:'#fff', fontSize:16, fontWeight:900,
                cursor:'pointer', fontFamily:'"Courier New", monospace',
                boxShadow:'4px 4px 0 #546e25', letterSpacing:'0.08em',
                textShadow:'2px 2px 0 #333',
              }}>
                PLAY
              </button>
            </div>
          )}

          {/* PLAYING */}
          {arcadePhase === 'playing' && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              {fbWaiting && (
                <span style={{ fontSize:10, color:'#4ec0ca', fontFamily:'"Courier New", monospace', fontWeight:700 }}>TAP POUR DÉMARRER</span>
              )}
              <canvas
                ref={fbCanvasRef}
                width={FB_W}
                height={FB_H}
                onPointerDown={(e) => { e.preventDefault(); handleFbTap(e); }}
                style={{
                  width:'100%', maxWidth:288,
                  border:'3px solid #000',
                  boxShadow:'6px 6px 0 rgba(0,0,0,0.5)',
                  display:'block', flexShrink:0,
                  cursor:'pointer',
                  touchAction:'none',
                  userSelect:'none', WebkitUserSelect:'none',
                  imageRendering:'pixelated',
                }}
              />
            </div>
          )}

          {/* SUBMITTING */}
          {arcadePhase === 'submitting' && (
            <div style={{ textAlign:'center', padding:'32px 0' }}>
              <div style={{ fontSize:38, marginBottom:10 }}>⏳</div>
              <p style={{ color:'rgba(255,255,255,0.6)', fontSize:13 }}>Envoi du score…</p>
            </div>
          )}

          {/* RESULTS */}
          {arcadePhase === 'results' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:44, marginBottom:6 }}>🏆</div>
              <div style={{ fontSize:26, fontWeight:900, color:'#fbbf24', marginBottom:4 }}>{arcadeMyClicks ?? 0} tuyaux 🐦</div>
              {arcadeMyRank && (
                <div style={{ fontSize:13.5, color:'#7dd3fc', marginBottom:12, fontWeight:600 }}>
                  {userName ? <span style={{ color:'#fde68a' }}>{userName}</span> : 'Tu'} es{' '}
                  <strong style={{color:arcadeMyRank===1?'#fbbf24':'#e0f2fe'}}>#{arcadeMyRank}</strong>
                  {arcadeMyRank===1
                    ? (isAccessPrize ? ' 🥇 → accès offert !' : ' 🥇 → 2G offerts !')
                    : ' au classement'}
                </div>
              )}
              {arcadeLeaderboard.length > 0 && (
                <div style={{ background:'rgba(20,80,100,0.15)', border:'1px solid rgba(78,192,202,0.2)', borderRadius:12, padding:'9px 11px', marginBottom:12, textAlign:'left' }}>
                  <p style={{ fontSize:9, color:'rgba(100,210,220,0.75)', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:7, fontWeight:700 }}>🏆 Classement</p>
                  {arcadeLeaderboard.slice(0,7).map((entry,i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'4px 0', borderBottom:i<Math.min(arcadeLeaderboard.length,7)-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                      <span style={{ fontSize:12.5, color: entry.isMe ? '#86efac' : (i===0?'#fbbf24':'rgba(255,255,255,0.58)'), fontWeight: (entry.isMe || i===0) ? 700 : 400 }}>
                        {medals[i]||`#${i+1}`} {entry.anonymousName||entry.anonName}{entry.isMe ? ' (toi)' : ''}
                      </span>
                      <span style={{ fontSize:12.5, fontWeight:700, color:i===0?'#fbbf24':'#7dd3fc' }}>{entry.clicks} 🐦</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={startArcade} style={{
                  flex:1, padding:'12px 0', background:'#73bf2e', border:'3px solid #000',
                  borderRadius:4, color:'#fff', fontSize:14, fontWeight:900, cursor:'pointer',
                  fontFamily:'"Courier New", monospace', boxShadow:'4px 4px 0 #546e25',
                }}>PLAY</button>
                <button onClick={closeArcadeModal} style={{
                  flex:1, padding:'12px 0', background:'#de9400', border:'3px solid #000',
                  borderRadius:4, color:'#fff', fontSize:14, fontWeight:800, cursor:'pointer',
                  fontFamily:'"Courier New", monospace', boxShadow:'4px 4px 0 #a86800',
                }}>FERMER</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const clientNavViews = ['home', 'cart', 'cart-summary', 'pack', 'treasure', 'product-detail'];
  const showClientNav = ((isAuthenticated && userName) || (shopPreviewMode && userName)) && isShopOpen && !isDriverMode && !isDashboardMode
    && !showCountryModal && !showCityModal && !showNameModal && !showParrainageProductModal
    && !isChatOpen
    && !showArcadeModal
    && clientNavViews.includes(currentView);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const pad = 'var(--device-nav-height, calc(96px + env(safe-area-inset-bottom, 0px)))';
    if (showClientNav) {
      root.classList.add('has-mobile-nav');
      body.style.paddingBottom = pad;
      body.style.boxSizing = 'border-box';
    } else {
      root.classList.remove('has-mobile-nav');
      body.style.paddingBottom = '';
      body.style.boxSizing = '';
    }
    return () => {
      root.classList.remove('has-mobile-nav');
      body.style.paddingBottom = '';
      body.style.boxSizing = '';
    };
  }, [showClientNav]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
    <>
      <Toaster 
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: 'linear-gradient(135deg, rgba(68, 64, 60, 0.95) 0%, rgba(87, 83, 78, 0.85) 100%)',
              color: '#fef3c7',
              border: '1.5px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '0.75rem',
              padding: '1rem 1.5rem',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              fontFamily: 'Inter, sans-serif',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fef3c7',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fef3c7',
              },
            },
          }}
        />
      
      {/* Sélecteur de langue — accueil uniquement (chrome top) */}
      {!pwaInstallLocked && !isDashboardMode && !isDriverMode && !isChatOpen && (!isAccessMode || shopPreviewMode) && currentView === 'home' && !showSubscriptionModal && !showPaymentModal && !showArcadeModal && <LanguageSwitcher />}

      {/* ════════════════════════════════════════════════════════════
          SPLASH SCREEN — Plein écran Liquid Glass
          ════════════════════════════════════════════════════════════ */}
      {/* Mode maintenance — plein écran, aucun détail technique */}
      {maintenanceMode && !isDriverMode && !isDashboardMode && !isAccessMode && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 'var(--gs-z-lock)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 28, textAlign: 'center',
          backgroundColor: '#0d1117',
          backgroundImage: 'url(/emoji/SWISS3.png)',
          backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,16,36,0.45), rgba(8,16,36,0.58))' }} />
          <div style={{ position: 'relative', zIndex: 2, maxWidth: 340 }}>
            <img src="/emoji/logoapp1.png" alt="" style={{ width: 88, height: 88, objectFit: 'contain', margin: '0 auto 20px' }} />
            <h1 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 900, color: '#f0f9ff' }}>Global Strains</h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'rgba(191,219,254,0.85)', fontWeight: 600 }}>
              Une erreur est survenue.<br />Réessaie plus tard.
            </p>
          </div>
        </div>
      )}

      {/* Déjà connecté — bref, uniquement clients déjà inscrits (sous les modales onboarding) */}
      <AnimatePresence>
        {showConnectedWelcome && isAuthenticated && !!userName && !showNameModal && !showCountryModal && !showCityModal && !maintenanceMode && !isDriverMode && !isDashboardMode && (
          <motion.div
            key="connected-welcome"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 'var(--gs-z-sheet)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: 28, textAlign: 'center',
              backgroundColor: '#0d1117',
              backgroundImage: 'url(/emoji/SWISS3.png)',
              backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,16,36,0.38), rgba(12,28,64,0.48))' }} />
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              style={{ position: 'relative', zIndex: 2 }}
            >
              <img src="/emoji/logoapp1.png" alt="" style={{ width: 100, height: 100, objectFit: 'contain', margin: '0 auto 18px' }} />
              <p style={{
                margin: '0 0 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: 'rgba(125,211,252,0.85)',
              }}>
                Session active
              </p>
              <h1 style={{ margin: '0 0 10px', fontSize: 26, fontWeight: 900, color: '#f0f9ff', lineHeight: 1.2 }}>
                Tu es déjà connecté
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(191,219,254,0.7)', fontWeight: 600 }}>
                Bon retour, {userName}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!pwaInstallLocked && (isCheckingAuth || splashHold) && !isDriverMode && (
          <motion.div
            key="splash"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 'var(--gs-z-splash)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: '#0d1117',
              backgroundImage: 'url(/emoji/SWISS3.png)',
              backgroundSize: '100% 100%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* Voile léger — l'image a déjà un centre sombre pour le logo */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1,
              background: 'linear-gradient(180deg, rgba(8,16,36,0.28) 0%, rgba(12,28,64,0.18) 42%, rgba(8,16,36,0.42) 100%)',
            }} />
            <div className="orb-1" style={{
              position: 'absolute', zIndex: 2,
              top: '12%', left: '8%',
              width: 380, height: 380,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(74,144,226,0.28) 0%, transparent 70%)',
              filter: 'blur(38px)',
              pointerEvents: 'none',
            }} />
            <div className="orb-2" style={{
              position: 'absolute', zIndex: 2,
              bottom: '16%', right: '6%',
              width: 320, height: 320,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(107,163,232,0.22) 0%, transparent 70%)',
              filter: 'blur(42px)',
              pointerEvents: 'none',
            }} />
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute', zIndex: 2,
                width: 4 + (i % 3) * 2,
                height: 4 + (i % 3) * 2,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.55)',
                top: `${12 + i * 10}%`,
                left: `${7 + i * 11}%`,
                filter: 'blur(1px)',
                animation: `splashFloat ${2.8 + i * 0.35}s ease-in-out ${i * 0.25}s infinite`,
                boxShadow: '0 0 8px rgba(74,144,226,0.7)',
              }} />
            ))}

            <div style={{
              position: 'relative',
              zIndex: 10,
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 28px',
            }}>
              <img
                src="/emoji/logoapp1.png"
                alt="Global Strains"
                style={{
                  width: 132,
                  height: 132,
                  objectFit: 'contain',
                  animation: 'splashLogoBreath 2.6s ease-in-out infinite',
                  willChange: 'transform, filter',
                }}
              />
              <p style={{
                margin: '28px 0 0',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'rgba(191,219,254,0.88)',
                textShadow: '0 0 18px rgba(74,144,226,0.35)',
              }}>
                Chargement
              </p>
            </div>

            <div style={{
              position: 'relative',
              zIndex: 10,
              width: '100%',
              padding: '0 32px calc(36px + env(safe-area-inset-bottom))',
            }}>
              <div style={{
                width: '100%',
                maxWidth: 420,
                margin: '0 auto',
                height: 8,
                borderRadius: 999,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(74,144,226,0.16) 100%)',
                border: '1px solid rgba(191,219,254,0.28)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 24px rgba(0,0,0,0.28)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.max(8, Math.min(100, splashProgress))}%`,
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, rgba(59,130,246,0.85) 0%, rgba(147,197,253,0.95) 45%, rgba(255,255,255,0.92) 100%)',
                  boxShadow: '0 0 18px rgba(96,165,250,0.55)',
                  transition: 'width 0.18s cubic-bezier(0.22,1,0.36,1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                    animation: 'splashBarShimmer 1.4s linear infinite',
                  }} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════
          APP CONTENT — révélation après splash
          ════════════════════════════════════════ */}
      {!isCheckingAuth && !pwaInstallLocked && (!isAccessMode || shopPreviewMode) && !maintenanceMode && (
      <motion.div
        key="app-content"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
        style={{ minHeight: '100dvh', background: 'transparent' }}
      >
      <>{/* Écran de chargement pendant l'enregistrement du compte */}
      {isRegistering ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden" style={{ 
          background: 'linear-gradient(180deg, #1a1d24 0%, #2a2f3a 50%, #1a1d24 100%)' 
        }}>
          {/* Effet de lumière ambiante */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(74,144,226,0.10) 0%, transparent 70%)'}}>
          </div>
          
          {/* Effet de lumière bleue en arrière-plan */}
          <div className="absolute inset-0 opacity-30" style={{ 
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(74, 144, 226, 0.4) 0%, transparent 70%)' 
          }}></div>
          
          {/* Carte glassmorphism principale */}
            <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 text-center card-premium shadow-multilayer rounded-3xl p-12 max-w-md mx-4"
            style={{
              background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
              backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 60px rgba(74, 144, 226, 0.2)'
            }}
            >
            {/* Loader avec effet bleu */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* Cercle externe animé */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
                  border: '2px solid rgba(74, 144, 226, 0.5)'
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 0.8, 0.6]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              {/* Cercle interne avec loader */}
              <div className="absolute inset-4 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Loader 
                  className="w-16 h-16 text-white spin-smooth" 
                  style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
              />
              </div>
            </div>
            
            {/* Titre avec gradient neige */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              className="text-3xl font-bold mb-3 gradient-text-snow"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
              }}
              >
                Création du compte...
              </motion.h2>
            
            {/* Message */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              className="text-white/90 text-lg"
              style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)' }}
              >
                Enregistrement de vos données
              </motion.p>
            </motion.div>
        </div>
      ) : null}

      {/* Écran de bannissement */}
      {isBlocked && renderBannedScreen()}

      {/* Contenu principal - Ne s'affiche que si la vérification est terminée et que l'utilisateur n'est pas bloqué */}
      {!isCheckingAuth && !isBlocked && (
        <>
      {checkingShopStatus ? (
        <div>
          {renderLoadingScreen()}
        </div>
      ) : isLoading ? (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden" style={{ 
          background: 'linear-gradient(180deg, #1a1d24 0%, #2a2f3a 50%, #1a1d24 100%)' 
        }}>
          {/* Effet de lumière ambiante */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(74,144,226,0.10) 0%, transparent 70%)'}}>
          </div>
          
          {/* Effet de lumière bleue en arrière-plan */}
          <div className="absolute inset-0 opacity-30" style={{ 
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(74, 144, 226, 0.4) 0%, transparent 70%)' 
          }}></div>
          
          {/* Carte glassmorphism principale */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 text-center card-premium shadow-multilayer rounded-3xl p-12 max-w-md mx-4"
            style={{
              background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(107, 163, 232, 0.1) 50%, rgba(74, 144, 226, 0.15) 100%)',
              backdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.1)',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 60px rgba(74, 144, 226, 0.2)'
            }}
          >
            {/* Loader avec effet bleu */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* Cercle externe animé */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.4) 0%, rgba(107, 163, 232, 0.3) 100%)',
                  border: '2px solid rgba(74, 144, 226, 0.5)'
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 0.8, 0.6]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              {/* Cercle interne avec loader */}
              <div className="absolute inset-4 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2) 0%, rgba(107, 163, 232, 0.15) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Loader 
                  className="w-16 h-16 text-white spin-smooth" 
                  style={{ filter: 'drop-shadow(0 0 10px rgba(74, 144, 226, 0.6))' }}
                />
              </div>
            </div>
            
            {/* Titre avec gradient neige */}
            <h2 className="text-3xl font-bold mb-3 gradient-text-snow"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
              }}
            >
              Chargement...
            </h2>
          </motion.div>
        </div>
      ) : isDashboardMode ? (
        // Mode dashboard admin : rendu direct sans authentification client
        <AdminDashboard chatId={telegramChatId || new URLSearchParams(window.location.search).get('chatId')} BACKEND_URL={BACKEND_URL} />
      ) : isDriverMode && (currentView === "driver-panel" || currentView === "driver-chat") ? (
        // Mode driver : permettre l'accès direct au panel et au chat même sans authentification
        <div>
          {currentView === "driver-panel" ? renderDriverPanel() : renderDriverChatView()}
        </div>
      ) : showAccountDisabledModal ? (
        <div />
      ) : !isAuthenticated && !userName && !showNameModal && !showCountryModal && !showCityModal ? (
        // Filet anti écran noir : session cassée sans modal.
        // La restauration est automatique (useEffect qui retry checkAuth),
        // mais on garde un bouton manuel et un message clair.
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative z-10">
          <img src="/emoji/logoapp1.png" alt="" style={{ width: 88, height: 88, objectFit: 'contain', marginBottom: 18 }} />
          <div style={{ width: 32, height: 32, marginBottom: 14 }}>
            <Loader className="w-8 h-8 text-blue-300 spin-smooth" />
          </div>
          <h1 style={{ margin: '0 0 10px', fontSize: 22, fontWeight: 900, color: '#f0f9ff' }}>Restauration de la session…</h1>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'rgba(191,219,254,0.8)', maxWidth: 320, lineHeight: 1.5 }}>
           Connexion en cours. Si rien ne se passe, ouvre l’app depuis le bot boutique Telegram.
          </p>
          <button
            type="button"
            onClick={() => { try { checkAuthRef.current?.(); } catch { window.location.reload(); } }}
            style={{
              minHeight: 48, padding: '12px 22px', borderRadius: 16, fontWeight: 800, color: '#fff',
              background: 'linear-gradient(135deg, rgba(74,144,226,0.55), rgba(30,80,180,0.75))',
              border: '1.5px solid rgba(147,197,253,0.5)', cursor: 'pointer',
            }}
          >
            Réessayer maintenant
          </button>
        </div>
      ) : showCountryModal ? (
        // Si le modal de pays est affiché, ne rien afficher (le modal bloquera l'affichage)
        <div></div>
      ) : showCityModal ? (
        // Si le modal de ville est affiché, ne rien afficher (le modal bloquera l'affichage)
        <div></div>
      ) : showParrainageProductModal ? (
        // Si le modal de sélection du produit pour le bonus est affiché, ne rien afficher
        <div></div>
      ) : !isShopOpen ? (
        <div>
          <ShopClosedOverlay />
        </div>
      ) : currentView === "home" ? (
        <div>
          {renderHome()}
        </div>
      ) : currentView === "product-detail" ? (
        <div className="with-app-mobile-nav w-full">
          {renderProductDetail()}
        </div>
      ) : currentView === "order-confirmation" ? (
        <div>
          {renderOrderConfirmation()}
        </div>
      ) : currentView === "order-tracking" ? (
        <div>
          {renderOrderTracking()}
        </div>
      ) : currentView === "driver-panel" ? (
        <div>
          {renderDriverPanel()}
        </div>
      ) : currentView === "driver-chat" ? (
        <div>
          {renderDriverChatView()}
        </div>
      ) : currentView === "cart" ? (
        <div>
          {renderCart()}
        </div>
      ) : currentView === "cart-summary" ? (
        <div>
          {renderCartSummary()}
        </div>
      ) : currentView === "pack" ? (
        <div>
          <PackFeature
            onBack={() => setCurrentView('home')}
            onGoReferral={() => {
              setCurrentView('home');
              setTimeout(() => {
                setShowReferralCode(true);
                trackEvent('pack_referral_cta');
              }, 280);
            }}
            globalCoins={globalCoins}
            setGlobalCoins={setGlobalCoins}
            freePacks={freePacks}
            setFreePacks={setFreePacks}
            setGameBonus2g={setGameBonus2g}
            setPackPriorityBonus={setPackPriorityBonus}
            setWheelBonuses={setWheelBonuses}
            setUserPass={setUserPass}
            setPassExpiryDate={setPassExpiryDate}
            telegramChatId={telegramChatId}
            BACKEND_URL={BACKEND_URL}
            fetchWithTimeout={fetchWithTimeout}
          />
        </div>
      ) : currentView === "treasure" ? (
        <div>
          <TreasureWheel
            onBack={() => setCurrentView('home')} 
            turns={treasureTurns}
            telegramChatId={telegramChatId}
            BACKEND_URL={BACKEND_URL}
            t={t}
            onBonusClaimed={(product, amount) => {
              // Si le produit est COINS, mettre à jour le solde global coins
              if (product === 'COINS') {
                setGlobalCoins(prev => prev + amount);
              } else {
                // Mettre à jour les bonus dans l'état local
                setWheelBonuses(prev => ({
                  ...prev,
                  [product]: (prev[product] || 0) + amount
                }));
              }
            }}
            onWheelVariantChosen={(detail) => {
              setWheelBonusDetail(detail);
            }}
            onTurnUsed={async (newTurns, markAsUsed = false) => {
              const finalTurns = Math.max(newTurns, 0);
              setTreasureTurns(finalTurns);
              
              // Si le tour a été utilisé, mettre à jour le backend (source de vérité)
              if (markAsUsed && telegramChatId) {
                try {
                  const response = await fetchWithTimeout(`${BACKEND_URL}/client/update-turns`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      chatId: telegramChatId,
                      treasureTurns: finalTurns
                    })
                  });
                  
                  if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.client?.treasureTurns !== undefined) {
                      // Synchroniser avec la valeur du backend
                      const backendTurns = parseInt(data.client.treasureTurns, 10) || 0;
                      setTreasureTurns(backendTurns);
                if (treasureTurnsStorageKey) {
                        persistValueAcrossStorages(treasureTurnsStorageKey, backendTurns.toString());
                      }
                      console.log('✅ Tours de roue mis à jour dans le backend:', backendTurns);
                    }
                  }
                } catch (error) {
                  console.error('❌ Erreur mise à jour tours de roue dans le backend:', error);
                }
              }
            }}
          />
        </div>
      ) : (
        <div>
          {renderHome()}
        </div>
      )}

      {showClientNav && (
        <AppNavbarMobile
          currentView={currentView}
          setCurrentView={setCurrentView}
          cart={cart}
          freePacks={freePacks}
          t={t}
          setShowArcadeModal={setShowArcadeModal}
          setShowSubscriptionModal={setShowSubscriptionModal}
          trackEvent={trackEvent}
          isScrolled={isScrolled}
          previewLocked={shopPreviewMode}
          onLockedNav={openAccessPub}
          onAccessNav={openAccessPub}
        />
      )}

      <AnimatePresence>
      {showMinimumAlert && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <MinimumOrderAlert />
        </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
        {showTelegramConfirm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <TelegramConfirmAlert />
        </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
      {showConfetti && <Confetti />}
      </AnimatePresence>

      {/* Popup du jeu "Qui Paye aujourd'hui ?" */}
      <AnimatePresence>
        {showGamePopup && renderGamePopup()}
      </AnimatePresence>

      {/* Popup de sélection du nombre de joueurs */}
      <AnimatePresence>
        {showPlayerSelection && renderPlayerSelection()}
      </AnimatePresence>

      {/* Jeu FingerChooser */}
      <AnimatePresence>
        {showGame && renderFingerChooserGame()}
      </AnimatePresence>

      {/* Inscription / Enregistrer / nom bloquant — SUPPRIMÉS (révocation = modal accès ; nom = à la commande) */}

      
      {/* Modale d'abonnement — portal body, au-dessus navbar / coins / langue */}
      {typeof document !== 'undefined' && createPortal(
      <AnimatePresence>
        {showSubscriptionModal && (
          <motion.div
            key="subscription-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-end sm:items-center justify-center"
            style={{
              zIndex: 'var(--gs-z-modal)',
              background: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
              paddingLeft: 'max(10px, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(10px, env(safe-area-inset-right, 0px))',
            }}
            onPointerDown={(e) => { if (e.target === e.currentTarget) setShowSubscriptionModal(false); }}
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1, transition: { type: 'spring', damping: 26, stiffness: 300 } }}
              exit={{ y: 80, opacity: 0 }}
              className="w-full sm:max-w-lg flex flex-col relative"
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(160deg, rgba(74,144,226,0.22) 0%, rgba(30,60,120,0.35) 40%, rgba(10,20,60,0.55) 100%)',
                backdropFilter: 'blur(60px) saturate(300%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(60px) saturate(300%) brightness(1.12)',
                border: '1.5px solid rgba(107,163,232,0.35)',
                borderRadius: 28,
                boxShadow: '0 16px 64px rgba(0,0,0,0.55), 0 0 80px rgba(74,144,226,0.15), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(74,144,226,0.15)',
                maxHeight: 'min(92dvh, 920px)',
                overflow: 'clip',
                zIndex: 1,
              }}
            >
              {/* Ligne lumineuse haut */}
              <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1.5, background: 'linear-gradient(90deg, transparent, rgba(147,197,253,0.7) 50%, transparent)', pointerEvents: 'none', zIndex: 2 }} />

              {/* Header sticky + fermer (toujours au-dessus) */}
              <div style={{
                padding: '16px 18px 12px', textAlign: 'center', flexShrink: 0,
                position: 'sticky', top: 0, zIndex: 5,
                background: 'linear-gradient(180deg, rgba(12,28,64,0.92) 0%, rgba(12,28,64,0.72) 70%, transparent 100%)',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              }}>
                <button
                  type="button"
                  aria-label="Fermer"
                  onClick={() => setShowSubscriptionModal(false)}
                  style={{
                    position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(147,197,253,0.4)',
                    color: '#fff', cursor: 'pointer', zIndex: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6, paddingRight: 36 }}>
                  <Crown style={{ width: 26, height: 26, color: '#6ba3e8', filter: 'drop-shadow(0 0 10px rgba(74,144,226,0.8))' }} />
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 23, fontWeight: 900, background: 'linear-gradient(135deg, #ffffff 0%, #c8dff5 60%, #6ba3e8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.02em', margin: 0 }}>{t('choosePlan')}</h2>
                </div>
                <p style={{ fontSize: 13, color: 'rgba(180,210,255,0.75)', margin: 0, letterSpacing: '0.01em' }}>← Glissez pour voir tous les plans →</p>
              </div>

              {/* Cards — scroll horizontal tactile */}
              <div
                className="subscription-scroll-container"
                id="pass-scroll-container"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const cardWidth = el.scrollWidth / 3;
                  const idx = Math.round(el.scrollLeft / cardWidth);
                  const dots = document.querySelectorAll('.pass-dot');
                  dots.forEach((d, i) => {
                    const colors = ['linear-gradient(90deg,#cd7f32,#e5c9a0)', 'linear-gradient(90deg,#4a90e2,#6ba3e8)', 'linear-gradient(90deg,#f59e0b,#fbbf24)'];
                    d.style.width = i === idx ? '20px' : '6px';
                    d.style.background = i === idx ? colors[i] : 'rgba(255,255,255,0.22)';
                  });
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onMouseDown={(e) => {
                  const el = e.currentTarget;
                  el._isDown = true;
                  el._startX = e.pageX - el.offsetLeft;
                  el._scrollLeft = el.scrollLeft;
                  el.style.cursor = 'grabbing';
                }}
                onMouseLeave={(e) => { e.currentTarget._isDown = false; e.currentTarget.style.cursor = 'grab'; }}
                onMouseUp={(e) => { e.currentTarget._isDown = false; e.currentTarget.style.cursor = 'grab'; }}
                onMouseMove={(e) => {
                  const el = e.currentTarget;
                  if (!el._isDown) return;
                  e.preventDefault();
                  const x = e.pageX - el.offsetLeft;
                  const walk = (x - el._startX) * 1.5;
                  el.scrollLeft = el._scrollLeft - walk;
                }}
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  gap: 14,
                  padding: '8px 20px 20px',
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x pinch-zoom',
                  flexShrink: 0,
                  cursor: 'grab',
                  userSelect: 'none',
                  willChange: 'scroll-position',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                {/* ── BRONZE ── */}
                {(() => {
                  const isCurrent = userPass === 'bronze';
                  return (
                    <div style={{
                      minWidth: 'min(calc(100vw - 56px), 300px)',
                      maxWidth: 320,
                      scrollSnapAlign: 'center',
                      borderRadius: 24,
                      padding: '24px 20px',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'clip', touchAction: 'pan-x',
                      background: isCurrent
                        ? 'linear-gradient(145deg, rgba(205,127,50,0.28) 0%, rgba(139,90,43,0.20) 100%)'
                        : 'linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(74,144,226,0.06) 100%)',
                      backdropFilter: 'blur(40px) saturate(220%)',
                      WebkitBackdropFilter: 'blur(40px) saturate(220%)',
                      border: isCurrent ? '1.5px solid rgba(205,127,50,0.55)' : '1.5px solid rgba(255,255,255,0.18)',
                      boxShadow: isCurrent ? '0 0 40px rgba(205,127,50,0.18), inset 0 1px 0 rgba(255,255,255,0.25)' : 'inset 0 1px 0 rgba(255,255,255,0.18), 0 4px 24px rgba(0,0,0,0.2)',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', borderRadius: '24px 24px 0 0' }} />
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <img src="/emoji/bronze1.png" alt="Bronze" style={{ width: 38, height: 38, objectFit: "contain", flexShrink: 0, filter: "drop-shadow(0 2px 8px rgba(205,127,50,0.65))" }} />
                            <span style={{ fontSize: 17, fontWeight: 800, color: '#e5c9a0', letterSpacing: '-0.01em' }}>{t('passBronzeTitle')}</span>
                          </div>
                          {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(205,127,50,0.25)', color: '#f5d6a0', border: '1px solid rgba(205,127,50,0.4)', borderRadius: 100, padding: '3px 10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('currentLabel')}</span>}
                        </div>
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0 }}>{t('freeAccessToEssentials')}</p>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <span style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, fontFamily: "'Playfair Display', serif" }}>0€</span>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>{t('perMonthShort')}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                        {[
                          { ok: true, text: t('accessAllProducts') },
                          { ok: true, text: t('standardPrice') },
                          { ok: true, text: t('weeklyWheelTour') },
                          { ok: false, text: t('priorityDeliveryOffered') },
                          { ok: false, text: t('productDiscountLower') },
                        ].map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: f.ok ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)', border: f.ok ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.08)' }}>
                              <span style={{ fontSize: 11, color: f.ok ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)' }}>{f.ok ? '✓' : '✕'}</span>
                            </div>
                            <span style={{ fontSize: 12.5, color: f.ok ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.28)', lineHeight: 1.4 }}>{f.text}</span>
                          </div>
                        ))}
                      </div>
                      {isCurrent ? (
                        <div style={{ textAlign: 'center', padding: '11px', borderRadius: 14, background: 'rgba(205,127,50,0.12)', border: '1px solid rgba(205,127,50,0.25)', color: '#e5c9a0', fontSize: 13, fontWeight: 700 }}>{t('currentPlan')}</div>
                      ) : (
                        <button onClick={() => upgradePass('bronze')} style={{ width: '100%', padding: '12px', borderRadius: 14, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{t('select')}</button>
                      )}
                    </div>
                  );
                })()}

                {/* ── ARGENT ── */}
                {(() => {
                  const isCurrent = userPass === 'argent';
                  return (
                    <div style={{
                      minWidth: 'min(calc(100vw - 56px), 300px)',
                      maxWidth: 320,
                      scrollSnapAlign: 'center',
                      borderRadius: 24,
                      padding: '24px 20px',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'clip', touchAction: 'pan-x',
                      background: isCurrent
                        ? 'linear-gradient(145deg, rgba(74,144,226,0.30) 0%, rgba(107,163,232,0.20) 100%)'
                        : 'linear-gradient(145deg, rgba(74,144,226,0.16) 0%, rgba(107,163,232,0.10) 100%)',
                      backdropFilter: 'blur(40px) saturate(220%)',
                      WebkitBackdropFilter: 'blur(40px) saturate(220%)',
                      border: isCurrent ? '1.5px solid rgba(74,144,226,0.65)' : '1.5px solid rgba(74,144,226,0.28)',
                      boxShadow: '0 0 60px rgba(74,144,226,0.15), inset 0 1px 0 rgba(255,255,255,0.22)',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(107,163,232,0.6), transparent)', borderRadius: '24px 24px 0 0' }} />
                      {!isCurrent && (
                        <div style={{ position: 'absolute', top: 14, right: 14, background: 'linear-gradient(135deg, #4a90e2, #6ba3e8)', color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 100, boxShadow: '0 2px 12px rgba(74,144,226,0.5)' }}>⭐ POPULAIRE</div>
                      )}
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <img src="/emoji/silver1.webp" alt="Argent" style={{ width: 38, height: 38, objectFit: "contain", flexShrink: 0, filter: "drop-shadow(0 2px 8px rgba(74,144,226,0.65))" }} />
                          <span style={{ fontSize: 17, fontWeight: 800, color: '#c8dff5', letterSpacing: '-0.01em' }}>Pass Argent</span>
                          {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(74,144,226,0.25)', color: '#93c5fd', border: '1px solid rgba(74,144,226,0.4)', borderRadius: 100, padding: '3px 10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Actuel</span>}
                        </div>
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0 }}>L'essentiel des avantages premium</p>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, fontFamily: "'Playfair Display', serif" }}>50 CHF</span>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>/mois</span>
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(74,144,226,0.18)', border: '1px solid rgba(74,144,226,0.3)', borderRadius: 100, padding: '3px 10px', marginBottom: 18 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#93c5fd', letterSpacing: '0.04em' }}>{t('sub10OffBadge')}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                        {[
                          { ok: true, text: t('sub10OffAll') },
                          { ok: true, text: t('subWeeklyWheel') },
                          { ok: true, text: t('subPriorityFree') },
                          { ok: true, text: t('sub20Coins') },
                          { ok: false, text: t('subMaxDiscount') },
                        ].map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: f.ok ? 'rgba(74,144,226,0.25)' : 'rgba(255,255,255,0.04)', border: f.ok ? '1px solid rgba(74,144,226,0.45)' : '1px solid rgba(255,255,255,0.08)' }}>
                              <span style={{ fontSize: 11, color: f.ok ? '#93c5fd' : 'rgba(255,255,255,0.2)' }}>{f.ok ? '✓' : '✕'}</span>
                            </div>
                            <span style={{ fontSize: 12.5, color: f.ok ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.28)', lineHeight: 1.4 }}>{f.text}</span>
                          </div>
                        ))}
                      </div>
                      {isCurrent ? (
                        <div style={{ textAlign: 'center', padding: '11px', borderRadius: 14, background: 'rgba(74,144,226,0.15)', border: '1px solid rgba(74,144,226,0.3)', color: '#93c5fd', fontSize: 13, fontWeight: 700 }}>{t('subCurrentPlan')}</div>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!isAuthenticated) { setShowSubscriptionModal(false); openAccessPub(); }
                            else { setSelectedPassForPayment('argent'); setSelectedPaymentMethod(null); setShowSubscriptionModal(false); setShowPaymentModal(true); }
                          }}
                          style={{ width: '100%', padding: '13px', borderRadius: 14, background: 'linear-gradient(135deg, rgba(74,144,226,0.55) 0%, rgba(107,163,232,0.45) 100%)', border: '1.5px solid rgba(74,144,226,0.6)', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 20px rgba(74,144,226,0.35)', letterSpacing: '0.02em' }}
                        ><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><img src="/emoji/silver1.webp" alt="Argent" style={{ width: 20, height: 20, objectFit: 'contain', filter: 'drop-shadow(0 1px 4px rgba(74,144,226,0.7))' }} />Obtenir Argent →</span></button>
                      )}
                    </div>
                  );
                })()}

                {/* ── OR ── */}
                {(() => {
                  const isCurrent = userPass === 'or';
                  return (
                    <div style={{
                      minWidth: 'min(calc(100vw - 56px), 300px)',
                      maxWidth: 320,
                      scrollSnapAlign: 'center',
                      borderRadius: 24,
                      padding: '24px 20px',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'clip', touchAction: 'pan-x',
                      background: isCurrent
                        ? 'linear-gradient(145deg, rgba(251,191,36,0.28) 0%, rgba(245,158,11,0.20) 100%)'
                        : 'linear-gradient(145deg, rgba(251,191,36,0.18) 0%, rgba(245,158,11,0.12) 100%)',
                      backdropFilter: 'blur(40px) saturate(240%)',
                      WebkitBackdropFilter: 'blur(40px) saturate(240%)',
                      border: isCurrent ? '1.5px solid rgba(251,191,36,0.75)' : '1.5px solid rgba(251,191,36,0.38)',
                      boxShadow: '0 0 80px rgba(251,191,36,0.20), 0 0 120px rgba(245,158,11,0.10), inset 0 1px 0 rgba(255,255,255,0.30)',
                    }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.8), transparent)', borderRadius: '24px 24px 0 0' }} />
                      {!isCurrent && (
                        <div style={{ position: 'absolute', top: 14, right: 14, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', color: '#000', fontSize: 9, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 100, boxShadow: '0 2px 16px rgba(251,191,36,0.6)' }}>👑 BEST VALUE</div>
                      )}
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <img src="/emoji/or1.webp" alt="Or" style={{ width: 38, height: 38, objectFit: "contain", flexShrink: 0, filter: "drop-shadow(0 2px 10px rgba(251,191,36,0.75))" }} />
                          <span style={{ fontSize: 17, fontWeight: 800, background: 'linear-gradient(135deg, #fde68a, #fbbf24, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.01em' }}>Pass Or</span>
                          {isCurrent && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(251,191,36,0.25)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.45)', borderRadius: 100, padding: '3px 10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Actuel</span>}
                        </div>
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{t('totalPremiumExperience')}</p>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 42, fontWeight: 900, lineHeight: 1, fontFamily: "'Playfair Display', serif", background: 'linear-gradient(135deg, #fde68a, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>80 CHF</span>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>/mois</span>
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 100, padding: '3px 10px', marginBottom: 18 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#fbbf24', letterSpacing: '0.04em' }}>{t('sub15OffBadge')}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                        {[
                          { text: t('sub15OffAll') },
                          { text: t('subWeeklyWheel') },
                          { text: t('subPriorityFree') },
                          { text: t('sub30Coins') },
                          { text: t('subExclusiveDrops') },
                        ].map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(251,191,36,0.22)', border: '1px solid rgba(251,191,36,0.45)' }}>
                              <span style={{ fontSize: 11, color: '#fbbf24' }}>✓</span>
                            </div>
                            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.4 }}>{f.text}</span>
                          </div>
                        ))}
                      </div>
                      {isCurrent ? (
                        <div style={{ textAlign: 'center', padding: '11px', borderRadius: 14, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>{t('subCurrentPlan')}</div>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!isAuthenticated) { setShowSubscriptionModal(false); openAccessPub(); }
                            else { setSelectedPassForPayment('or'); setSelectedPaymentMethod(null); setShowSubscriptionModal(false); setShowPaymentModal(true); }
                          }}
                          style={{ width: '100%', padding: '13px', borderRadius: 14, background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)', border: '1.5px solid rgba(251,191,36,0.7)', color: '#000', fontSize: 14, fontWeight: 900, cursor: 'pointer', boxShadow: '0 4px 24px rgba(251,191,36,0.45)', letterSpacing: '0.02em' }}
                        ><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><img src="/emoji/or1.webp" alt="Or" style={{ width: 22, height: 22, objectFit: 'contain', filter: 'drop-shadow(0 1px 6px rgba(251,191,36,0.8))' }} />Obtenir Or</span></button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Dots indicateurs */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingBottom: 16, flexShrink: 0 }}>
                {['bronze', 'argent', 'or'].map((p, i) => (
                  <div
                    key={p}
                    className="pass-dot"
                    onClick={() => {
                      const el = document.getElementById('pass-scroll-container');
                      if (el) { const cardWidth = el.scrollWidth / 3; el.scrollTo({ left: cardWidth * i, behavior: 'smooth' }); }
                    }}
                    style={{
                      width: p === 'bronze' ? 20 : 6,
                      height: 6,
                      borderRadius: 100,
                      background: p === 'bronze' ? 'linear-gradient(90deg,#cd7f32,#e5c9a0)' : 'rgba(255,255,255,0.22)',
                      transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </div>

              {/* Fermer — footer sticky au-dessus de la nav */}
              <div style={{
                padding: '10px 20px max(20px, env(safe-area-inset-bottom, 0px))',
                flexShrink: 0,
                position: 'sticky', bottom: 0, zIndex: 5,
                background: 'linear-gradient(0deg, rgba(8,18,48,0.96) 0%, rgba(8,18,48,0.85) 70%, transparent 100%)',
                backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
              }}>
                <button
                  type="button"
                  onClick={() => setShowSubscriptionModal(false)}
                  style={{ width: '100%', padding: '13px', borderRadius: 16, background: 'rgba(74,144,226,0.10)', border: '1px solid rgba(74,144,226,0.25)', color: 'rgba(180,210,255,0.75)', fontSize: 14, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', transition: 'all 0.2s', letterSpacing: '0.02em', WebkitTapHighlightColor: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(74,144,226,0.18)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(74,144,226,0.10)'; e.currentTarget.style.color = 'rgba(180,210,255,0.75)'; }}
                >Fermer</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
      
      {/* Modal de paiement — portal body, au-dessus navbar / coins / langue */}
      {typeof document !== 'undefined' && createPortal(
      <AnimatePresence>
        {showPaymentModal && selectedPassForPayment && (
          <motion.div
            key="payment-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-end sm:items-center justify-center"
            style={{
              zIndex: 'var(--gs-z-modal)',
              background: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
              paddingLeft: 'max(10px, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(10px, env(safe-area-inset-right, 0px))',
            }}
            onPointerDown={(e) => { if (e.target === e.currentTarget) setShowPaymentModal(false); }}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1, transition: { type: 'spring', damping: 26, stiffness: 300 } }}
              exit={{ y: 60, opacity: 0 }}
              className="w-full sm:max-w-md flex flex-col relative"
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(160deg, rgba(74,144,226,0.22) 0%, rgba(30,60,120,0.38) 45%, rgba(10,20,60,0.58) 100%)',
                backdropFilter: 'blur(60px) saturate(300%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(60px) saturate(300%) brightness(1.12)',
                border: '1.5px solid rgba(107,163,232,0.35)',
                borderRadius: 28,
                boxShadow: '0 16px 64px rgba(0,0,0,0.55), 0 0 80px rgba(74,144,226,0.15), inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(74,144,226,0.12)',
                maxHeight: 'min(92dvh, 920px)',
                overflowY: 'auto',
                zIndex: 1,
              }}
            >
              {/* Ligne lumineuse */}
              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1.5, background: 'linear-gradient(90deg, transparent, rgba(107,163,232,0.65) 50%, transparent)', pointerEvents: 'none', zIndex: 2 }} />

              <div style={{ padding: '16px 22px 24px', position: 'relative' }}>
                <button
                  type="button"
                  aria-label="Fermer"
                  onClick={() => { setShowPaymentModal(false); setSelectedPassForPayment(null); setSelectedPaymentMethod(null); }}
                  style={{
                    position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(147,197,253,0.4)',
                    color: '#fff', cursor: 'pointer', zIndex: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 22, paddingRight: 36 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
                    <img
                      src={selectedPassForPayment === 'argent' ? '/emoji/silver1.webp' : '/emoji/or1.webp'}
                      alt={selectedPassForPayment}
                      style={{ width: 44, height: 44, objectFit: 'contain', filter: selectedPassForPayment === 'argent' ? 'drop-shadow(0 2px 8px rgba(74,144,226,0.7))' : 'drop-shadow(0 2px 10px rgba(251,191,36,0.75))' }}
                    />
                    <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, background: 'linear-gradient(135deg, #ffffff 0%, #c8dff5 60%, #6ba3e8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.02em', margin: 0 }}>
                      Finaliser l'abonnement
                    </h2>
                  </div>
                  <p style={{ fontSize: 13, color: 'rgba(180,210,255,0.70)', margin: 0 }}>{t('choosePaymentMethod')}</p>
                </div>

                {/* Récapitulatif */}
                <div style={{ background: selectedPassForPayment === 'argent' ? 'rgba(74,144,226,0.18)' : 'rgba(251,191,36,0.14)', border: selectedPassForPayment === 'argent' ? '1.5px solid rgba(74,144,226,0.40)' : '1.5px solid rgba(251,191,36,0.38)', borderRadius: 18, padding: '16px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <img src={selectedPassForPayment === 'argent' ? '/emoji/silver1.webp' : '/emoji/or1.webp'} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                      <span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Pass {selectedPassForPayment === 'argent' ? 'Argent' : 'Or'}</span>
                    </div>
                    <p style={{ fontSize: 11.5, color: 'rgba(180,210,255,0.70)', margin: 0 }}>
                      {selectedPassForPayment === 'argent' ? '-10% sur tous les produits · 20 Global Coins inclus 🪙' : '-15% sur tous les produits · 30 Global Coins inclus 🪙'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 900, lineHeight: 1, background: selectedPassForPayment === 'argent' ? 'linear-gradient(135deg,#fff,#93c5fd)' : 'linear-gradient(135deg,#fde68a,#fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                      {PASS_PRICES[selectedPassForPayment]} CHF
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(180,210,255,0.55)' }}>/mois</div>
                  </div>
                </div>

                {/* Label méthode */}
                <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(180,210,255,0.75)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>{t('paymentMethodLabel')}</div>

                {/* Méthodes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                  {[
                    { id: 'pcs', src: '/emoji/pcs.png', label: 'PCS', sub: t('securePayment'), accent: 'rgba(239,68,68,0.55)', border: 'rgba(239,68,68,0.6)' },
                    { id: 'crypto', src: '/emoji/btc.png', label: 'Bitcoin (BTC)', sub: t('cryptoPayment'), accent: 'rgba(249,115,22,0.55)', border: 'rgba(249,115,22,0.6)' },
                    { id: 'paysafecard', src: '/emoji/psf.png', label: 'Paysafecard', sub: t('securePreloadedCard'), accent: 'rgba(74,144,226,0.55)', border: 'rgba(74,144,226,0.6)' },
                  ].map(({ id, src, label, sub, accent, border }) => (
                    <motion.button
                      key={id}
                      onClick={() => setSelectedPaymentMethod(id)}
                      whileTap={{ scale: 0.97 }}
                      style={{
                        width: '100%', padding: '14px 16px', borderRadius: 16, cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 14, position: 'relative',
                        background: selectedPaymentMethod === id ? accent : 'rgba(255,255,255,0.06)',
                        border: `1.5px solid ${selectedPaymentMethod === id ? border : 'rgba(107,163,232,0.20)'}`,
                        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: selectedPaymentMethod === id ? `0 4px 20px ${accent}` : 'none',
                        transition: 'all 0.22s cubic-bezier(0.25,0.46,0.45,0.94)',
                      }}
                    >
                      <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                        <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{label}</div>
                        <div style={{ color: 'rgba(180,210,255,0.60)', fontSize: 12 }}>{sub}</div>
                      </div>
                      {selectedPaymentMethod === id && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}>
                          <CheckCircle style={{ width: 22, height: 22, color: '#fff', filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5))' }} />
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>

                {/* Bouton confirmer */}
                {selectedPaymentMethod && (
                  <motion.button
                    onClick={() => handlePayment(selectedPaymentMethod)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      width: '100%', padding: '15px', borderRadius: 16, cursor: 'pointer', marginBottom: 10,
                      background: 'linear-gradient(135deg, rgba(107,163,232,0.65) 0%, rgba(74,144,226,0.78) 50%, rgba(59,120,200,0.65) 100%)',
                      border: '1.5px solid rgba(147,197,253,0.50)',
                      color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: '0.02em',
                      boxShadow: '0 6px 28px rgba(74,144,226,0.45), inset 0 1px 0 rgba(255,255,255,0.30)',
                      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      Confirmer le paiement
                      <span style={{ fontSize: 18 }}>→</span>
                    </span>
                  </motion.button>
                )}

                {/* Bouton annuler */}
                <motion.button
                  type="button"
                  onClick={() => { setShowPaymentModal(false); setSelectedPassForPayment(null); setSelectedPaymentMethod(null); }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 16, cursor: 'pointer',
                    background: 'rgba(74,144,226,0.10)', border: '1px solid rgba(74,144,226,0.25)',
                    color: 'rgba(180,210,255,0.70)', fontWeight: 700, fontSize: 14,
                    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    transition: 'all 0.2s ease',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  Annuler
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
      
      {/* Modal de sélection de pays (bloquant - AVANT le modal de nom) */}
      <AnimatePresence>
        {showCountryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100050] flex items-center justify-center p-4 overflow-y-auto"
          >
            <SophisticatedBackground />
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -20 }}
              className="card-premium glass-card shadow-multilayer rounded-3xl p-8 max-w-md w-full my-8 relative overflow-hidden group z-10"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.1) 100%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                WebkitBackdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                boxShadow: `
                  0 8px 32px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1),
                  inset 0 -1px 0 rgba(255, 255, 255, 0.05),
                  0 0 60px rgba(255, 255, 255, 0.1)
                `
              }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <motion.div
                    className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.25) 100%)',
                      border: '1.5px solid rgba(251, 191, 36, 0.5)',
                      boxShadow: '0 8px 32px rgba(251, 191, 36, 0.3)'
                    }}
                  >
                    <MapPin className="w-10 h-10 text-amber-200" />
                  </motion.div>
                  <h2 className="text-3xl font-extrabold gradient-text-snow mb-2" style={{ textShadow: '0 2px 20px rgba(255, 255, 255, 0.3)' }}>{t('chooseCountry')}</h2>
                  <p className="text-white/80 text-sm">{t('selectCountrySubtitle')}</p>
                </div>
                
                <div className="space-y-4 mb-6">
                  {countryConfig.france && (
                  <motion.button
                    onClick={() => {
                      console.log('🇫🇷 Clic sur France détecté');
                      localStorage.setItem('clientSelectedCountry', 'france');
                      setClientSelectedCountry('france');
                      setSelectedCountry('france');
                      localStorage.removeItem('clientSelectedCity');
                      setSelectedCity('');
                      setShowCountryModal(false);
                      setShowCityModal(true);
                    }}
                    className="w-full py-5 px-6 rounded-2xl font-extrabold text-white relative overflow-hidden group/btn"
                    style={{
                      background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #4a90e2 100%)',
                      backgroundSize: '200% 200%',
                      boxShadow: '0 8px 32px rgba(74, 144, 226, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                      animation: 'gradient-shift 3s ease infinite',
                      cursor: 'pointer',
                      pointerEvents: 'auto'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-2xl">🇫🇷</span>
                      <span className="text-lg">{t('france')}</span>
                    </div>
                  </motion.button>
                  )}
                  
                  {countryConfig.suisse && (
                  <motion.button
                    onClick={() => {
                      console.log('🇨🇭 Clic sur Suisse détecté');
                      localStorage.setItem('clientSelectedCountry', 'suisse');
                      setClientSelectedCountry('suisse');
                      setSelectedCountry('suisse');
                      localStorage.removeItem('clientSelectedCity');
                      setSelectedCity('');
                      setShowCountryModal(false);
                      setShowCityModal(true);
                    }}
                    className="w-full py-5 px-6 rounded-2xl font-extrabold text-white relative overflow-hidden group/btn"
                    style={{
                      background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 50%, #d32f2f 100%)',
                      backgroundSize: '200% 200%',
                      boxShadow: '0 8px 32px rgba(211, 47, 47, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                      animation: 'gradient-shift 3s ease infinite',
                      cursor: 'pointer',
                      pointerEvents: 'auto'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-2xl">🇨🇭</span>
                      <span className="text-lg">{t('switzerland')}</span>
                    </div>
                  </motion.button>
                  )}
                  
                  {/* Message informatif si un seul pays est disponible */}
                  {countryConfig.france && !countryConfig.suisse && (
                    <p className="text-white/60 text-xs text-center mt-2">{t('franceOnly')}</p>
                  )}
                  {!countryConfig.france && countryConfig.suisse && (
                    <p className="text-white/60 text-xs text-center mt-2">{t('switzerlandOnly')}</p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      

      {/* Modal de sélection de la ville de livraison (bloquant) */}
      <AnimatePresence>
        {showCityModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100050] flex items-center justify-center p-4 overflow-y-auto"
          >
            <SophisticatedBackground />
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -20 }}
              className="card-premium glass-card shadow-multilayer rounded-3xl p-8 max-w-md w-full my-8 relative overflow-hidden group z-10"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.1) 100%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                WebkitBackdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                boxShadow: `
                  0 8px 32px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1),
                  inset 0 -1px 0 rgba(255, 255, 255, 0.05),
                  0 0 60px rgba(255, 255, 255, 0.1)
                `
              }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <motion.div
                    className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.25) 100%)',
                      border: '1.5px solid rgba(251, 191, 36, 0.5)',
                      boxShadow: '0 8px 32px rgba(251, 191, 36, 0.3)'
                    }}
                  >
                    <MapPin className="w-10 h-10 text-amber-200" />
                  </motion.div>
                  <h2 className="text-3xl font-extrabold gradient-text-snow mb-2" style={{ textShadow: '0 2px 20px rgba(255, 255, 255, 0.3)' }}>
                    {t('chooseYourCity')}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {selectedCountry === 'suisse' ? `🇨🇭 ${t('switzerland')}` : `🇫🇷 ${t('france')}`} — {t('chooseYourCity')}
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  {selectedCountry === 'suisse' && (
                    <motion.button
                      onClick={() => {
                        const city = 'Genève';
                        localStorage.setItem('clientSelectedCity', city);
                        setSelectedCity(city);
                        setShowCityModal(false);
                        console.log('🏙️ Ville choisie: Genève');
                        setTimeout(() => {
                          if (typeof checkAuth === 'function') {
                            checkAuth();
                          }
                        }, 100);
                      }}
                      className="w-full py-5 px-6 rounded-2xl font-extrabold text-white relative overflow-hidden group/btn"
                      style={{
                        background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 50%, #d32f2f 100%)',
                        backgroundSize: '200% 200%',
                        boxShadow: '0 8px 32px rgba(211, 47, 47, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                        animation: 'gradient-shift 3s ease infinite',
                        cursor: 'pointer',
                        pointerEvents: 'auto'
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl">🏔️</span>
                        <span className="text-lg">{t('genevaCityLabel')}</span>
                      </div>
                    </motion.button>
                  )}

                  {selectedCountry === 'france' && (
                    <motion.button
                      onClick={() => {
                        const city = 'Nantes';
                        localStorage.setItem('clientSelectedCity', city);
                        setSelectedCity(city);
                        setShowCityModal(false);
                        console.log('🏙️ Ville choisie: Nantes');
                        setTimeout(() => {
                          if (typeof checkAuth === 'function') {
                            checkAuth();
                          }
                        }, 100);
                      }}
                      className="w-full py-5 px-6 rounded-2xl font-extrabold text-white relative overflow-hidden group/btn"
                      style={{
                        background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #4a90e2 100%)',
                        backgroundSize: '200% 200%',
                        boxShadow: '0 8px 32px rgba(74, 144, 226, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                        animation: 'gradient-shift 3s ease infinite',
                        cursor: 'pointer',
                        pointerEvents: 'auto'
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl">🏙️</span>
                        <span className="text-lg">Nantes</span>
                      </div>
                    </motion.button>
                  )}
                </div>

                {/* Bouton retour pour rechoisir le pays */}
                <motion.button
                  onClick={() => {
                    setShowCityModal(false);
                    localStorage.removeItem('clientSelectedCountry');
                    localStorage.removeItem('clientSelectedCity');
                    setSelectedCity('');
                    setShowCountryModal(true);
                  }}
                  className="w-full py-3 px-5 rounded-2xl font-semibold text-white/70 hover:text-white transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.15)'
                  }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {t('changeCountryBtn')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ancien modal nom bloquant (Enregistrer) — SUPPRIMÉ : nom demandé uniquement à la commande */}

      {/* Modal nom à la commande — liquid glass, ton Global Strains */}
      <AnimatePresence>
        {showOrderNameModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)',
              background: 'rgba(5,10,28,0.78)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
              padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
            }}
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              style={{
                width: '100%', maxWidth: 400, borderRadius: 26, padding: '26px 22px 20px',
                background: 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.40) 55%, rgba(10,20,80,0.55) 100%)',
                backdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
                WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
                border: '1.5px solid rgba(107,163,232,0.45)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.22)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <div aria-hidden style={{
                position: 'absolute', top: -50, right: -30, width: 140, height: 140, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(74,144,226,0.35), transparent 70%)', pointerEvents: 'none',
              }} />
              <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(147,197,253,0.85)' }}>
                Global Strains
              </p>
              <h3 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 900, color: '#f0f9ff', lineHeight: 1.25 }}>
                Euh… on a oublié comment tu t&apos;appelles 😅
              </h3>
              <p style={{ margin: '0 0 18px', fontSize: 14, color: 'rgba(191,219,254,0.82)', lineHeight: 1.5 }}>
                Pas de panique — même les meilleurs dealers ont la mémoire courte parfois.
                Dis-nous ton nom pour la commande, et on envoie ça direct.
              </p>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.75)', marginBottom: 8 }}>
                Ton nom sur la commande
              </label>
              <input
                type="text"
                value={orderNameDraft}
                onChange={(e) => setOrderNameDraft(e.target.value)}
                placeholder="Ex: Alex"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && orderNameDraft.trim() && !savingOrderName) {
                    confirmOrderNameAndPlace();
                  }
                }}
                style={{
                  width: '100%', boxSizing: 'border-box', marginBottom: 16,
                  padding: '14px 16px', borderRadius: 16, fontSize: 16, fontWeight: 600, color: '#fff',
                  background: 'rgba(255,255,255,0.10)',
                  border: '1.5px solid rgba(147,197,253,0.35)',
                  outline: 'none',
                }}
              />
              <button
                type="button"
                disabled={!orderNameDraft.trim() || savingOrderName}
                onClick={confirmOrderNameAndPlace}
                style={{
                  width: '100%', minHeight: 52, borderRadius: 16, fontWeight: 800, fontSize: 15, color: '#fff',
                  cursor: (!orderNameDraft.trim() || savingOrderName) ? 'not-allowed' : 'pointer',
                  opacity: (!orderNameDraft.trim() || savingOrderName) ? 0.55 : 1,
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.75), rgba(22,163,74,0.9))',
                  border: '1.5px solid rgba(134,239,172,0.45)',
                  boxShadow: '0 10px 28px rgba(34,197,94,0.28)',
                }}
              >
                {savingOrderName ? 'Un instant…' : 'Commander'}
              </button>
              <button
                type="button"
                onClick={() => setShowOrderNameModal(false)}
                style={{
                  width: '100%', marginTop: 10, padding: '12px', borderRadius: 14, fontWeight: 600,
                  fontSize: 13, color: 'rgba(255,255,255,0.55)', background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal pour sélectionner le produit qui recevra le bonus de parrainage */}
      <AnimatePresence>
        {showParrainageProductModal && (pendingParrainageItem || pendingParrainageCode) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
          >
            <SophisticatedBackground />
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -20 }}
              className="card-premium glass-card shadow-multilayer rounded-3xl p-8 max-w-md w-full my-8 relative overflow-hidden group z-10"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(248, 249, 250, 0.1) 100%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                WebkitBackdropFilter: 'blur(45px) saturate(350%) brightness(1.15)',
                boxShadow: `
                  0 8px 32px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1),
                  inset 0 -1px 0 rgba(255, 255, 255, 0.05),
                  0 0 60px rgba(255, 255, 255, 0.1)
                `
              }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-effect"></div>
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <motion.div
                    className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    style={{
                      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 50%, #f59e0b 70%, #fbbf24 100%)',
                      backgroundSize: '200% 200%',
                      boxShadow: '0 8px 32px rgba(251, 191, 36, 0.5), 0 0 0 2px rgba(251, 191, 36, 0.4)',
                      animation: 'gradient-shift 3s ease infinite'
                    }}
                  >
                    <Crown className="w-10 h-10 text-amber-900" />
                  </motion.div>
                  <h2 className="text-3xl font-extrabold gradient-text-gold mb-2" style={{ textShadow: '0 2px 20px rgba(251, 191, 36, 0.5)' }}>
                    🎁 Bonus Parrainage
                  </h2>
                  <p className="text-white/80 text-sm mb-4">
                    À quel produit voulez-vous ajouter le bonus de <span className="font-bold text-yellow-400">+{referralBonusGrams || 2}g offerts</span> ?
                  </p>
                </div>
                
                <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                  {/* Option : Nouveau produit (seulement si on vient de l'ajout au panier) */}
                  {cart
                  .map((item, index) => {
                    const category = categories.find(c => c.id === (item.uiCategory || item.category));
                    const pn = (item.product.name || '').toLowerCase();
                    const isExcluded = pn.includes('snow') || pn.includes('baida') ||
                      pn.includes('sahas frozen') || pn.includes('sahas static') ||
                      (pn.includes('sahas') && (pn.includes('static') || pn.includes('frozen'))) ||
                      pn.includes('bubble hash') ||
                      pn.includes('polkadot') || pn.includes('magic chocolate') || pn.includes('magicchocolate') ||
                      pn.includes('stilpane') || pn.includes('oxycod') || pn.includes('oxcycod');
                    return (
                      <motion.button
                        key={item.id}
                        onClick={() => handleApplyParrainageBonus(item.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full p-4 rounded-2xl border-2 border-white/30 bg-white/10 hover:bg-white/20 transition-all relative overflow-hidden group/item"
                        style={{
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.05)'
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400/20 to-yellow-500/20">
                            {category?.image
                        ? <img src={category.image} alt={category.name || ''} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}} />
                        : <span className="text-2xl">{category?.emoji || '📦'}</span>
                      }
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="text-white font-bold text-lg mb-1">{item.product.name}</h3>
                            <p className="text-white/70 text-sm">{item.quantity}</p>
                            {item.hasBonus1g && (
                              <p className="text-green-400 text-xs font-semibold mt-1">{t('alreadyBonus')}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-white font-bold text-lg">{formatPrice(item.price)}</div>
                            {!item.hasBonus1g && (
                              isExcluded
                                ? <div className="text-red-400 text-xs font-semibold">Bonus indisponible</div>
                                : <div className="text-green-400 text-xs font-semibold">+{referralBonusGrams || 2}g offerts</div>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                  {/* Produits existants dans le panier */}
                  {cart.map((item, index) => {
                    const category = categories.find(c => c.id === (item.uiCategory || item.category));
                    const pn2 = (item.product.name || '').toLowerCase();
                    const isExcluded2 = pn2.includes('snow') || pn2.includes('baida') ||
                      pn2.includes('sahas frozen') || pn2.includes('sahas static') ||
                      (pn2.includes('sahas') && (pn2.includes('static') || pn2.includes('frozen'))) ||
                      pn2.includes('bubble hash') ||
                      pn2.includes('polkadot') || pn2.includes('magic chocolate') || pn2.includes('magicchocolate') ||
                      pn2.includes('stilpane') || pn2.includes('oxycod') || pn2.includes('oxcycod');
                    return (
                      <motion.button
                        key={item.id}
                        onClick={() => handleApplyParrainageBonus(item.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full p-4 rounded-2xl border-2 border-white/30 bg-white/10 hover:bg-white/20 transition-all relative overflow-hidden group/item"
                        style={{
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.05)'
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400/20 to-yellow-500/20">
                            {category?.image
                        ? <img src={category.image} alt={category.name || ''} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}} />
                        : <span className="text-2xl">{category?.emoji || '📦'}</span>
                      }
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="text-white font-bold text-lg mb-1">{item.product.name}</h3>
                            <p className="text-white/70 text-sm">{item.quantity}</p>
                            {item.hasBonus1g && (
                              <p className="text-green-400 text-xs font-semibold mt-1">{t('alreadyBonus')}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-white font-bold text-lg">{formatPrice(item.price)}</div>
                            {!item.hasBonus1g && (
                              isExcluded2
                                ? <div className="text-red-400 text-xs font-semibold">Bonus indisponible</div>
                                : <div className="text-green-400 text-xs font-semibold">+{referralBonusGrams || 2}g offerts</div>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
                
                {pendingParrainageItem ? (
                  <motion.button
                    onClick={() => {
                      // Annuler : ajouter le produit sans bonus
                      setCart(prevCart => [...prevCart, pendingParrainageItem]);
                      toast.success(t('addedToCart', pendingParrainageItem.product.name));
                      setSelectedQuantity(null);
                      setIsBulkMode(false);
                      // setShowBulkSection supprimé - mode bulk désactivé
                      setBulkQuantity(100);
                      setParrainageCodeInput('');
                      setShowParrainageProductModal(false);
                      setPendingParrainageCode(null);
                      setPendingParrainageItem(null);
                    }}
                    className="w-full py-3 px-4 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition border border-white/30"
                  >
                    Annuler (ajouter sans bonus)
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={() => {
                      // Annuler : fermer le modal sans appliquer le bonus
                      setParrainageCodeInput('');
                      setShowParrainageProductModal(false);
                      setPendingParrainageCode(null);
                    }}
                    className="w-full py-3 px-4 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition border border-white/30"
                  >
                    Annuler
                  </motion.button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ── POPUP MARKETING ADMIN ── */}
      <AnimatePresence>
        {marketingPopup && !isDashboardMode && !isDriverMode && (
          <motion.div
            key="marketing-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', padding: '0 0 0 0' }}
            onPointerDown={(e) => { if (e.target === e.currentTarget) { markMarketingSeen(marketingPopup.id); setMarketingPopup(null); } }}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1, transition: { type: 'spring', damping: 24, stiffness: 260 } }}
              exit={{ y: 100, opacity: 0 }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 460,
                background: 'linear-gradient(155deg, rgba(100,60,200,0.30) 0%, rgba(60,40,160,0.45) 35%, rgba(30,20,100,0.60) 70%, rgba(10,5,50,0.70) 100%)',
                backdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                WebkitBackdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                border: '1.5px solid rgba(147,112,219,0.38)',
                borderBottom: 'none',
                borderRadius: '28px 28px 0 0',
                padding: '0 0 32px',
                boxShadow: '0 -8px 60px rgba(100,60,200,0.22), 0 -2px 0 rgba(147,112,219,0.20) inset, 0 0 100px rgba(74,144,226,0.10)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              {/* Lueur violette haut-droite */}
              <div style={{ position: 'absolute', top: -60, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,rgba(147,112,219,0.22),transparent 70%)', pointerEvents: 'none' }} />
              {/* Lueur bleue bas-gauche */}
              <div style={{ position: 'absolute', bottom: -40, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle,rgba(74,144,226,0.18),transparent 70%)', pointerEvents: 'none' }} />
              {/* Ligne lumineuse top */}
              <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(147,112,219,0.70) 50%, transparent)', pointerEvents: 'none' }} />

              {/* Handle bar */}
              <div style={{ width: 36, height: 4, borderRadius: 100, background: 'rgba(147,112,219,0.45)', margin: '14px auto 0' }} />

              {/* Bouton fermer */}
              <button
                onClick={() => { markMarketingSeen(marketingPopup.id); setMarketingPopup(null); }}
                style={{ position: 'absolute', top: 12, right: 14, width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.60)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                ×
              </button>

              <div style={{ padding: '18px 24px 0' }}>
                {/* Logo + header */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ position: 'relative', marginBottom: 14 }}>
                    <div style={{ position: 'absolute', inset: -8, borderRadius: '50%', background: 'radial-gradient(circle, rgba(147,112,219,0.35), transparent 70%)', pointerEvents: 'none' }} />
                    {marketingPopup.type === 'pass'
                      ? <img src="/emoji/or1.webp" alt="Pass Or" style={{ width: 58, height: 58, objectFit: 'contain', position: 'relative', filter: 'drop-shadow(0 4px 14px rgba(251,191,36,0.65))' }} />
                      : <span style={{ fontSize: 52, display: 'block', textAlign: 'center' }}>📣</span>
                    }
                  </div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 21, fontWeight: 900, textAlign: 'center', margin: 0, lineHeight: 1.2, background: 'linear-gradient(135deg, #fff 0%, #d4b8f8 50%, #93c5fd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {marketingPopup.type === 'pass' ? t('accessHigherLevel') : marketingPopup.title}
                  </h2>
                </div>

                {/* Message amélioré */}
                <p style={{ fontSize: 14, color: 'rgba(220,210,255,0.82)', textAlign: 'center', lineHeight: 1.65, marginBottom: 20 }}>
                  {marketingPopup.type === 'pass'
                    ? t('passMarketingMsg')
                    : marketingPopup.message}
                </p>

                {/* Avantages rapides (uniquement pour pass) */}
                {marketingPopup.type === 'pass' && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {[
                      { ico: '⚡', txt: t('passAdvPrio') },
                      { ico: '💎', txt: t('passAdvDiscount') },
                      { ico: '🎡', txt: t('passAdvWheel') },
                    ].map(({ ico, txt }) => (
                      <div key={txt} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(147,112,219,0.18)', border: '1px solid rgba(147,112,219,0.30)', borderRadius: 100, padding: '4px 12px' }}>
                        <span style={{ fontSize: 13 }}>{ico}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(220,210,255,0.90)' }}>{txt}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA */}
                {marketingPopup.cta && (
                  <button
                    onClick={() => { setMarketingPopup(null); setShowSubscriptionModal(true); }}
                    style={{ width: '100%', padding: '15px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(120,80,220,0.70) 0%, rgba(90,60,180,0.80) 50%, rgba(74,50,160,0.70) 100%)', border: '1.5px solid rgba(147,112,219,0.50)', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 28px rgba(100,60,200,0.38), inset 0 1px 0 rgba(255,255,255,0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', marginBottom: 10 }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <img src="/emoji/or1.webp" alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      {marketingPopup.type === 'pass' ? t('viewPasses') : marketingPopup.cta}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => { markMarketingSeen(marketingPopup.id, 'cta'); setMarketingPopup(null); }}
                  style={{ width: '100%', padding: '11px', borderRadius: 14, background: 'rgba(147,112,219,0.10)', border: '1px solid rgba(147,112,219,0.22)', color: 'rgba(200,180,255,0.60)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {t('later')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── POPUP GLOBAL GAME ── */}
      <AnimatePresence>
        {globalGamePopup && !isDashboardMode && !isDriverMode && (
          <motion.div
            key="global-game-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', padding: '20px' }}
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.88, opacity: 0, y: 20 }}
              transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 340 }}
            >
              <div style={{
                background: 'linear-gradient(145deg, rgba(6,182,212,0.22) 0%, rgba(14,165,233,0.14) 35%, rgba(99,102,241,0.18) 70%, rgba(6,10,40,0.60) 100%)',
                backdropFilter: 'blur(55px) saturate(320%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(55px) saturate(320%) brightness(1.12)',
                border: '1.5px solid rgba(6,182,212,0.40)',
                borderRadius: '24px',
                padding: '32px 24px 28px',
                textAlign: 'center',
                boxShadow: '0 8px 48px rgba(6,182,212,0.28), 0 2px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.40)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Ligne lumineuse top cyan */}
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(6,182,212,0.80) 50%, transparent)', pointerEvents: 'none' }} />
                {/* Lueur ambiante haut */}
                <div style={{ position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)', width: 200, height: 130, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6,182,212,0.28), transparent 70%)', pointerEvents: 'none' }} />
                {/* Lueur bas-gauche indigo */}
                <div style={{ position: 'absolute', bottom: -30, left: -20, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.20), transparent 70%)', pointerEvents: 'none' }} />

                {/* Bouton fermer */}
                <button
                  onClick={() => { markMarketingSeen(globalGamePopup.id); setGlobalGamePopup(null); }}
                  style={{ position: 'absolute', top: 12, right: 14, width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.60)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  ×
                </button>

                {/* Logo game.png animé */}
                <motion.div
                  animate={{ scale: [1, 1.07, 1], filter: ['drop-shadow(0 0 12px rgba(6,182,212,0.5))', 'drop-shadow(0 0 24px rgba(6,182,212,0.9))', 'drop-shadow(0 0 12px rgba(6,182,212,0.5))'] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 76, height: 76, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(6,182,212,0.28) 0%, rgba(99,102,241,0.20) 100%)', border: '1.5px solid rgba(6,182,212,0.50)', marginBottom: 16, boxShadow: '0 0 28px rgba(6,182,212,0.25)' }}
                >
                  <img
                    src="/emoji/game.png"
                    alt="Game"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                    style={{ width: 44, height: 44, objectFit: 'contain', display: 'block' }}
                  />
                  <span style={{ fontSize: 32, display: 'none' }}>🎮</span>
                </motion.div>

                {/* Titre */}
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 900, margin: '0 0 8px', lineHeight: 1.2, background: 'linear-gradient(135deg, #fff 0%, #67e8f9 40%, #06b6d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {globalGamePopup.title || t('newGameAvailable')}
                </h2>

                {/* Message */}
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, color: 'rgba(200,245,255,0.82)', lineHeight: 1.65, margin: '0 0 16px' }}>
                  {globalGamePopup.message || t('globalGameDefaultMsg')}
                </p>

                {/* Badge 2G */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(6,182,212,0.16)', border: '1px solid rgba(6,182,212,0.38)', borderRadius: 100, padding: '5px 16px', marginBottom: 22 }}>
                  <span style={{ fontSize: 14, fontWeight: 900, color: '#22d3ee', fontFamily: "'Inter', sans-serif" }}>{t('globalGame2gBadge')}</span>
                </div>

                {/* Boutons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { markMarketingSeen(globalGamePopup.id, 'cta'); setGlobalGamePopup(null); setShowArcadeModal(true); setArcadePhase('intro'); }}
                    style={{
                      width: '100%', padding: '14px', borderRadius: 14,
                      background: 'linear-gradient(135deg, rgba(6,182,212,0.70) 0%, rgba(14,165,233,0.75) 50%, rgba(99,102,241,0.65) 100%)',
                      border: '1.5px solid rgba(6,182,212,0.55)',
                      color: '#fff', fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 15,
                      cursor: 'pointer', letterSpacing: '0.02em',
                      boxShadow: '0 6px 24px rgba(6,182,212,0.40), inset 0 1px 0 rgba(255,255,255,0.30)',
                      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    }}
                  >
                    {t('playNow')}
                  </motion.button>
                  <button
                    onClick={() => { markMarketingSeen(globalGamePopup.id); setGlobalGamePopup(null); }}
                    style={{
                      width: '100%', padding: '11px', borderRadius: 12,
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(6,182,212,0.18)',
                      color: 'rgba(200,245,255,0.42)', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {t('skip')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── POPUP LUFFA CANAL ── */}
      <AnimatePresence>
        {luffaPopup && (
          <motion.div
            key="luffa-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)', padding: '20px' }}
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0, transition: { type: 'spring', damping: 24, stiffness: 260 } }}
              exit={{ scale: 0.88, opacity: 0, y: 16 }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 380,
                background: 'linear-gradient(155deg, rgba(100,50,180,0.35) 0%, rgba(120,70,200,0.25) 40%, rgba(40,15,90,0.65) 100%)',
                backdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                WebkitBackdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                border: '1.5px solid rgba(167,139,250,0.42)',
                borderRadius: '28px',
                padding: '32px 28px 24px',
                boxShadow: '0 8px 60px rgba(120,80,220,0.30), inset 0 1px 0 rgba(200,170,255,0.18)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(167,139,250,0.75) 50%, transparent)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: -60, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle,rgba(147,112,219,0.22),transparent 70%)', pointerEvents: 'none' }} />

              {/* Logo luffa centré */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ position: 'relative', marginBottom: 14, width: 80, height: 80, borderRadius: '50%', background: 'radial-gradient(circle, rgba(147,112,219,0.28) 0%, rgba(100,60,200,0.15) 60%, transparent 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(167,139,250,0.30)', boxShadow: '0 4px 24px rgba(120,80,220,0.35)' }}>
                  <img
                    src="/emoji/luffa.png"
                    alt="Luffa"
                    style={{ width: 52, height: 52, objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(167,139,250,0.70)) drop-shadow(0 0 20px rgba(147,112,219,0.50))' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fb = document.createElement('span');
                      fb.textContent = '🌿';
                      fb.style.fontSize = '36px';
                      e.currentTarget.parentNode.appendChild(fb);
                    }}
                  />
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, textAlign: 'center', margin: 0, lineHeight: 1.2, background: 'linear-gradient(135deg, #ffffff 0%, #d8b4fe 50%, #a78bfa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Rejoins-nous sur LUFFA !
                </h2>
              </div>

              <p style={{ fontSize: 14, color: 'rgba(220,210,255,0.80)', textAlign: 'center', lineHeight: 1.65, marginBottom: 20 }}>
              {t('luffaMsg').split('+10 Global Coins')[0]}<strong style={{ color: '#c4b5fd' }}>+10 Global Coins</strong>{t('luffaMsg').split('+10 Global Coins')[1]}
              </p>

              {/* CTA Rejoindre */}
              <a
                href={luffaPopup.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  markMarketingSeen(luffaPopup.id, 'cta');
                  setLuffaPopup(null);
                }}
                style={{ display: 'block', width: '100%', padding: '15px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(120,80,220,0.75) 0%, rgba(100,60,200,0.80) 50%, rgba(80,50,180,0.72) 100%)', border: '1.5px solid rgba(147,112,219,0.55)', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 28px rgba(120,80,220,0.45), inset 0 1px 0 rgba(255,255,255,0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', marginBottom: 12, textDecoration: 'none', textAlign: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 8, background: 'rgba(255,255,255,0.18)', flexShrink: 0, overflow: 'hidden' }}>
                    <img src="/emoji/luffa.png" alt="" style={{ width: 20, height: 20, objectFit: 'contain', display: 'block' }} onError={(e) => { e.currentTarget.parentElement.innerHTML = '🌿'; }} />
                  </span>
                  {t('joinChannel')}
                </span>
              </a>

              {/* Bouton Plus tard */}
              <button
                onClick={() => {
                  markMarketingSeen(luffaPopup.id);
                  setLuffaPopup(null);
                }}
                style={{ width: '100%', padding: '11px', borderRadius: 14, background: 'rgba(147,112,219,0.10)', border: '1px solid rgba(147,112,219,0.25)', color: 'rgba(200,180,255,0.55)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {t('later')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* ── POPUP WHATSAPP CANAL ── */}
      <AnimatePresence>
        {whatsappPopup && (
          <motion.div
            key="whatsapp-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)', padding: '20px' }}
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0, transition: { type: 'spring', damping: 24, stiffness: 260 } }}
              exit={{ scale: 0.88, opacity: 0, y: 16 }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 380,
                background: 'linear-gradient(155deg, rgba(18,140,126,0.35) 0%, rgba(37,211,102,0.20) 40%, rgba(7,55,40,0.65) 100%)',
                backdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                WebkitBackdropFilter: 'blur(55px) saturate(280%) brightness(1.10)',
                border: '1.5px solid rgba(37,211,102,0.42)',
                borderRadius: '28px',
                padding: '32px 28px 24px',
                boxShadow: '0 8px 60px rgba(37,211,102,0.30), inset 0 1px 0 rgba(100,255,180,0.18)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(37,211,102,0.75) 50%, transparent)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: -60, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle,rgba(37,211,102,0.18),transparent 70%)', pointerEvents: 'none' }} />

              {/* Logo WhatsApp centré */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ position: 'relative', marginBottom: 14, width: 80, height: 80, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,211,102,0.28) 0%, rgba(18,140,126,0.15) 60%, transparent 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(37,211,102,0.30)', boxShadow: '0 4px 24px rgba(37,211,102,0.35)' }}>
                  <img
                    src="/emoji/wat1.png"
                    alt="WhatsApp"
                    style={{ width: 52, height: 52, objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(37,211,102,0.70)) drop-shadow(0 0 20px rgba(18,140,126,0.50))' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fb = document.createElement('span');
                      fb.textContent = '💬';
                      fb.style.fontSize = '36px';
                      e.currentTarget.parentNode.appendChild(fb);
                    }}
                  />
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, textAlign: 'center', margin: 0, lineHeight: 1.2, background: 'linear-gradient(135deg, #ffffff 0%, #86efac 50%, #25d366 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {whatsappPopup.title || t('joinWhatsApp')}
                </h2>
              </div>

              <p style={{ fontSize: 14, color: 'rgba(200,255,220,0.80)', textAlign: 'center', lineHeight: 1.65, marginBottom: 20 }}>
                {whatsappPopup.message || t('whatsappDefaultMsg')}
              </p>

              {/* CTA Rejoindre */}
              <a
                href={whatsappPopup.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  markMarketingSeen(whatsappPopup.id, 'cta');
                  setWhatsappPopup(null);
                }}
                style={{ display: 'block', width: '100%', padding: '15px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(37,211,102,0.75) 0%, rgba(18,140,126,0.80) 50%, rgba(7,94,84,0.72) 100%)', border: '1.5px solid rgba(37,211,102,0.55)', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 28px rgba(37,211,102,0.45), inset 0 1px 0 rgba(255,255,255,0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', marginBottom: 12, textDecoration: 'none', textAlign: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  {whatsappPopup.cta || t('saveContact')}
                </span>
              </a>

              {/* Bouton Plus tard */}
              <button
                onClick={() => {
                  markMarketingSeen(whatsappPopup.id);
                  setWhatsappPopup(null);
                }}
                style={{ width: '100%', padding: '11px', borderRadius: 14, background: 'rgba(37,211,102,0.10)', border: '1px solid rgba(37,211,102,0.25)', color: 'rgba(150,255,200,0.55)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {t('later')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* ── POPUP SONDAGE MARKETING (multi-choix) ── */}
      <AnimatePresence>
        {surveyPopup && !isDashboardMode && !isDriverMode && (
          <motion.div
            key="survey-marketing-popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
              background: 'rgba(8,8,18,0.72)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              padding: 'max(12px, env(safe-area-inset-top)) 14px max(14px, env(safe-area-inset-bottom))',
            }}
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) {
                markMarketingSeen(surveyPopup.id);
                setSurveyPopup(null);
                setSurveySelected([]);
              }
            }}
          >
            <motion.div
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 28, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 360, damping: 30 }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 420, borderRadius: 26, padding: '24px 20px 18px',
                position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(145deg, rgba(244,114,182,0.28) 0%, rgba(157,23,77,0.40) 55%, rgba(76,5,40,0.58) 100%)',
                backdropFilter: 'blur(40px) saturate(260%)', WebkitBackdropFilter: 'blur(40px) saturate(260%)',
                border: '1.5px solid rgba(249,168,212,0.45)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.22)',
                color: '#fff',
              }}
            >
              <div aria-hidden style={{
                position: 'absolute', top: -50, right: -30, width: 140, height: 140, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(244,114,182,0.35), transparent 70%)', pointerEvents: 'none',
              }} />
              <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(251,207,232,0.8)' }}>
                Sondage Global Strains
              </p>
              <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 900, color: '#fdf2f8', lineHeight: 1.3 }}>
                {surveyPopup.question || surveyPopup.title || surveyPopup.message || 'Ton avis'}
              </h3>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(252,231,243,0.75)', lineHeight: 1.45 }}>
                Tu peux cocher plusieurs réponses, puis envoyer.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {(surveyPopup.answers || []).filter(Boolean).map((ans, idx) => {
                  const on = surveySelected.includes(idx);
                  return (
                    <button
                      key={`surv-opt-${idx}`}
                      type="button"
                      onClick={() => {
                        setSurveySelected((prev) =>
                          prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]
                        );
                      }}
                      style={{
                        textAlign: 'left', padding: '12px 14px', borderRadius: 14, cursor: 'pointer',
                        fontWeight: 700, fontSize: 14, color: '#fff',
                        background: on ? 'rgba(244,114,182,0.42)' : 'rgba(255,255,255,0.08)',
                        border: on ? '1.5px solid rgba(251,207,232,0.65)' : '1.5px solid rgba(255,255,255,0.14)',
                        boxShadow: on ? '0 0 18px rgba(244,114,182,0.25)' : 'none',
                      }}
                    >
                      <span style={{ marginRight: 10, opacity: 0.9 }}>{on ? '☑' : '☐'}</span>
                      {ans}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                disabled={!surveySelected.length || surveySubmitting}
                onClick={async () => {
                  if (!surveySelected.length || surveySubmitting) return;
                  setSurveySubmitting(true);
                  try {
                    const cid = telegramChatId || localStorage.getItem('telegramChatId');
                    await fetch(`${BACKEND_URL}/client/marketing-survey-vote`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getApiAuthHeaders() },
                      body: JSON.stringify({
                        chatId: cid,
                        notifId: surveyPopup.id,
                        choices: surveySelected,
                      }),
                    });
                    markMarketingSeen(surveyPopup.id, 'cta');
                    setSurveyPopup(null);
                    setSurveySelected([]);
                    addFomoNotification('Merci pour ton vote 🙏', 'success');
                  } catch {
                    addFomoNotification('Vote non envoyé — réessaie', 'error');
                  }
                  setSurveySubmitting(false);
                }}
                style={{
                  width: '100%', minHeight: 50, borderRadius: 16, fontWeight: 800, fontSize: 15, color: '#fff',
                  cursor: (!surveySelected.length || surveySubmitting) ? 'not-allowed' : 'pointer',
                  opacity: (!surveySelected.length || surveySubmitting) ? 0.55 : 1,
                  background: 'linear-gradient(135deg, rgba(244,114,182,0.85), rgba(219,39,119,0.92))',
                  border: '1.5px solid rgba(251,207,232,0.5)',
                  boxShadow: '0 10px 28px rgba(219,39,119,0.3)',
                }}
              >
                {surveySubmitting ? 'Envoi…' : (surveyPopup.cta || 'Voter')}
              </button>
              <button
                type="button"
                onClick={() => {
                  markMarketingSeen(surveyPopup.id);
                  setSurveyPopup(null);
                  setSurveySelected([]);
                }}
                style={{
                  width: '100%', marginTop: 10, padding: '12px', borderRadius: 14, fontWeight: 600,
                  fontSize: 13, color: 'rgba(255,255,255,0.55)', background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                Plus tard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── POPUP BONUS PARRAIN / ADMIN ── */}
      <AnimatePresence>
        {parrainBonusPopup && !isDashboardMode && !isDriverMode && (() => {
          const bonusGrams = parrainBonusPopup.grams || 2;
          const dismissBonus = () => {
            markMarketingSeen(parrainBonusPopup.id);
            setParrainBonusPopup(null);
          };
          return (
            <motion.div
              key="parrain-bonus-popup"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{
                position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '24px 16px',
                background: 'rgba(8,12,22,0.60)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
              }}
              onPointerDown={(e) => { if (e.target === e.currentTarget) dismissBonus(); }}
            >
              <motion.div
                initial={{ scale: 0.90, opacity: 0, y: 18 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.90, opacity: 0, y: 18 }}
                transition={{ duration: 0.30, ease: [0.25, 0.46, 0.45, 0.94] }}
                onPointerDown={(e) => e.stopPropagation()}
                style={{
                  position: 'relative',
                  width: '100%', maxWidth: 340,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.14) 100%)',
                  backdropFilter: 'blur(52px) saturate(300%) brightness(1.12)',
                  WebkitBackdropFilter: 'blur(52px) saturate(300%) brightness(1.12)',
                  border: '1px solid rgba(255,255,255,0.36)',
                  borderRadius: '26px',
                  padding: '32px 26px 26px',
                  boxShadow: '0 12px 52px rgba(0,0,0,0.38), 0 2px 8px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.52), inset 0 -1px 0 rgba(255,255,255,0.18)',
                  overflow: 'hidden',
                }}
              >
                {/* Ligne brillance haut */}
                <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.68) 50%, transparent)', pointerEvents: 'none' }} />

                {/* Bouton fermer */}
                <button
                  onClick={dismissBonus}
                  style={{
                    position: 'absolute', top: 14, right: 14,
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.10)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    color: 'rgba(255,255,255,0.55)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 700, lineHeight: 1,
                  }}>×</button>

                {/* Titre */}
                <p style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 20, fontWeight: 900,
                  color: '#ffffff',
                  margin: '0 0 6px',
                  textAlign: 'center',
                  letterSpacing: '-0.02em',
                  textShadow: '0 1px 12px rgba(255,255,255,0.18)',
                }}>
                  {parrainBonusPopup.title || 'Bonus de parrainage'}
                </p>

                {/* Montant */}
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 42, fontWeight: 900,
                  color: '#ffffff',
                  textAlign: 'center',
                  margin: '12px 0 4px',
                  lineHeight: 1,
                  letterSpacing: '-0.03em',
                  textShadow: '0 2px 24px rgba(255,255,255,0.25)',
                }}>
                  +{bonusGrams}g
                </p>

                {/* Sous-titre */}
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13, fontWeight: 500,
                  color: 'rgba(255,255,255,0.62)',
                  textAlign: 'center',
                  margin: '0 0 20px',
                  lineHeight: 1.5,
                }}>
                  offerts sur votre prochaine commande
                </p>

                {/* Exclusions */}
                <div style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.13)',
                  borderRadius: 12,
                  padding: '9px 14px',
                  marginBottom: 22,
                }}>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 10.5, fontWeight: 600,
                    color: 'rgba(255,255,255,0.38)',
                    textAlign: 'center',
                    margin: 0,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}>
                    Non applicable sur Sahas Frozen · Sahas Static · Bubble Hash · Magic Chocolate · Stilpane · Oxycodon · SNOW · Baida
                  </p>
                </div>

                {/* Bouton Récupérer */}
                <button
                  onClick={async () => {
                    try {
                      await fetch(`${BACKEND_URL}/client/claim-parrain-bonus`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ chatId: telegramChatId, notifId: parrainBonusPopup.id })
                      });
                    } catch { /* */ }
                    localStorage.setItem('parrainageBonus1g', 'true');
                    setHasParrainageBonus(true);
                    dismissBonus();
                    addFomoNotification(t('bonus2gCoinsActivated'), 'success');
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '16px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 800,
                    fontSize: 15,
                    letterSpacing: '0.04em',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.16) 100%)',
                    border: '1.5px solid rgba(255,255,255,0.42)',
                    color: '#ffffff',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.35)',
                    transition: 'all 0.18s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.22) 100%)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 28px rgba(255,255,255,0.18), inset 0 1px 0 rgba(255,255,255,0.45)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.16) 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.35)';
                  }}
                >
                  Récupérer
                </button>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
      {/* Notifications FOMO */}
      <AnimatePresence>
        {fomoNotifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, y: -50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -50, x: '-50%' }}
            className="fixed top-20 left-1/2 z-50 max-w-sm w-full px-4"
          >
            {notification.type === 'premium-blocked' ? (
              <div style={{
                background: 'linear-gradient(135deg, rgba(30,15,60,0.82) 0%, rgba(60,10,80,0.78) 100%)',
                backdropFilter: 'blur(32px) saturate(200%)',
                WebkitBackdropFilter: 'blur(32px) saturate(200%)',
                border: '1.5px solid rgba(180,100,255,0.45)',
                borderRadius: '18px',
                padding: '14px 18px',
                boxShadow: '0 8px 40px rgba(140,50,255,0.35), 0 0 0 1px rgba(180,100,255,0.12) inset',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(180,100,255,0.7) 50%, transparent)', pointerEvents: 'none' }} />
                <p style={{ color: '#e9d5ff', fontSize: 13, fontWeight: 700, textAlign: 'center', margin: 0, letterSpacing: '0.01em' }}>
                  🚫 Parrainage non pris en compte
                </p>
                <p style={{ color: 'rgba(220,180,255,0.65)', fontSize: 11, fontWeight: 500, textAlign: 'center', margin: '4px 0 0', lineHeight: 1.4 }}>
                  Ce produit premium ne peut pas être commandé avec un bonus 2G.
                </p>
              </div>
            ) : (
            <div className={`card-premium shadow-multilayer rounded-xl p-4 ${
              notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
              notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
              'bg-amber-500/20 border-amber-500/50'
            }`}>
              <p className="text-amber-100 text-sm font-bold text-center">{notification.message}</p>
            </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* ═══ POPUP UPSELL PASS OR (avant 1er ajout panier) ═══ */}
      <AnimatePresence>
        {showGoldUpsellPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          >
            <div
              style={{ position: 'absolute', inset: 0, background: 'rgba(10,12,18,0.62)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)' }}
              onClick={() => {
                setShowGoldUpsellPopup(false);
                setGoldUpsellDismissed(true);
                handleAddToCart();
              }}
            />
            <motion.div
              initial={{ scale: 0.90, opacity: 0, y: 14 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.90, opacity: 0, y: 14 }}
              transition={{ duration: 0.24, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 340 }}
            >
              <div style={{
                background: 'linear-gradient(135deg, rgba(30,20,5,0.96) 0%, rgba(20,14,2,0.98) 100%)',
                backdropFilter: 'blur(50px) saturate(280%)',
                WebkitBackdropFilter: 'blur(50px) saturate(280%)',
                border: '1.5px solid rgba(251,191,36,0.45)',
                borderRadius: '22px',
                padding: '24px 20px 20px',
                boxShadow: '0 16px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(251,191,36,0.15) inset, 0 0 60px rgba(251,191,36,0.12)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.6) 50%, transparent)', pointerEvents: 'none' }} />
                {/* Header */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 14 }}>
                  <img src="/emoji/or1.webp" alt="Pass Or" style={{ width: 44, height: 44, objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(251,191,36,0.8))', marginBottom: 10 }} />
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 900, textAlign: 'center', margin: 0, lineHeight: 1.2, background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {t('withPassOr')}
                  </h2>
                  <p style={{ fontSize: 11, color: 'rgba(251,191,36,0.55)', fontWeight: 600, marginTop: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('youWouldHaveSaved')}</p>
                </div>
                {/* Savings calculation */}
                {(() => {
                  const currentPrice = selectedQuantity
                    ? Math.round(getPassPrice(selectedQuantity.price, selectedProduct?.name || ''))
                    : 0;
                  const orPrice = selectedQuantity
                    ? Math.ceil((selectedQuantity.price * (selectedProduct?.name === 'Baida' ? 0.95 : 0.85)) / 10) * 10
                    : 0;
                  const savedProduct = selectedQuantity ? Math.max(currentPrice - orPrice, 0) : 0;
                  const savedDelivery = 5; // livraison prioritaire gratuite
                  const totalSaved = savedProduct + savedDelivery;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '10px 14px' }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,220,120,0.85)', fontWeight: 600 }}>{t('productDiscountMinus15')}</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: '#fbbf24' }}>{savedProduct} CHF</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '10px 14px' }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,220,120,0.85)', fontWeight: 600 }}>{t('freePriorityDeliveryUpsell')}</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: '#fbbf24' }}>+{savedDelivery} CHF</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.12) 100%)', border: '1.5px solid rgba(251,191,36,0.4)', borderRadius: 12, padding: '12px 14px', marginTop: 2 }}>
                        <span style={{ fontSize: 13, color: '#fde68a', fontWeight: 800 }}>{t('totalSavings')}</span>
                        <span style={{ fontSize: 18, fontWeight: 900, color: '#fbbf24' }}>{totalSaved} CHF</span>
                      </div>
                    </div>
                  );
                })()}
                {/* CTA Subscribe */}
                <button
                  onClick={() => {
                    setShowGoldUpsellPopup(false);
                    setGoldUpsellDismissed(true);
                    setShowSubscriptionModal(true);
                    trackEvent('pass_click');
                  }}
                  style={{ width: '100%', padding: '14px', borderRadius: 14, background: 'linear-gradient(135deg, rgba(251,191,36,0.70) 0%, rgba(245,158,11,0.80) 50%, rgba(217,119,6,0.75) 100%)', border: '1.5px solid rgba(251,191,36,0.55)', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 28px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.22)', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <img src="/emoji/or1.webp" alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  {t('subscribeToPassOr')}
                  <img src="/emoji/or1.webp" alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                </button>
                {/* Skip button */}
                <button
                  onClick={() => {
                    setShowGoldUpsellPopup(false);
                    setGoldUpsellDismissed(true);
                    handleAddToCart();
                  }}
                  style={{ width: '100%', padding: '11px', borderRadius: 12, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)', fontWeight: 600, fontSize: 12, cursor: 'pointer', letterSpacing: '0.01em' }}>
                  {t('anotherTime')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Écran de chargement lors de l'ajout au panier — Liquid Glass */}
      <AnimatePresence>
        {isAddingToCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,12,18,0.55)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }} />
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 8 }}
              transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 320 }}
            >
              <div style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.13) 100%)',
                backdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                border: '1px solid rgba(255,255,255,0.32)',
                borderRadius: '22px',
                padding: '28px 24px',
                textAlign: 'center',
                boxShadow: '0 8px 40px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(255,255,255,0.15)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Top shimmer line */}
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.65) 50%, transparent)', pointerEvents: 'none' }} />
                {/* Spinner */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                    style={{
                      width: 40, height: 40, borderRadius: '50%',
                      border: '3px solid rgba(255,255,255,0.12)',
                      borderTopColor: 'rgba(255,255,255,0.85)',
                      borderRightColor: 'rgba(255,255,255,0.4)',
                    }}
                  />
                </div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, color: 'rgba(255,255,255,0.92)', margin: '0 0 4px', letterSpacing: '0.01em' }}>
                  {t('addingToCart')}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 11.5, color: 'rgba(255,255,255,0.45)', margin: 0, letterSpacing: '0.02em' }}>
                  {t('pleaseWait')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ WIDGET GLOBAL COINS — accueil uniquement (sous le Pass) ═══ */}
      <AnimatePresence>
        {(isAuthenticated || shopPreviewMode) && !isDriverMode && !isDashboardMode
          && currentView === 'home'
          && !showSubscriptionModal && !showPaymentModal
          && !showArcadeModal && !isChatOpen && (
          <motion.div
            key="home-global-coins"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="gs-top-bar-2"
            style={{
              position: 'fixed',
              top: 'var(--gs-top-row-2)',
              left: 12,
              zIndex: 'var(--gs-z-chrome)',
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <motion.div
              key={globalCoins}
              initial={{ scale: coinAnimationActive ? 1.25 : 1 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 10px',
                borderRadius: 12,
                background: 'linear-gradient(135deg, rgba(30,140,255,0.22) 0%, rgba(10,60,180,0.32) 60%, rgba(0,20,100,0.42) 100%)',
                border: '1px solid rgba(125,211,252,0.40)',
                boxShadow: '0 4px 16px rgba(30,140,255,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                backdropFilter: 'blur(28px) saturate(240%)',
                WebkitBackdropFilter: 'blur(28px) saturate(240%)',
              }}
            >
              <CoinIcon size={15} />
              <AnimatePresence mode="wait">
                <motion.span
                  key={globalCoins}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    color: '#7dd3fc',
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.03em',
                  }}
                >
                  {Math.max(0, globalCoins)} coins
                </motion.span>
              </AnimatePresence>
            </motion.div>
            {doubleGcOffer?.active && (() => {
              const expMs = doubleGcOffer.expiresAt ? Date.parse(doubleGcOffer.expiresAt) : NaN;
              const daysLeft = Number.isFinite(expMs)
                ? Math.max(0, Math.ceil((expMs - Date.now()) / (24 * 60 * 60 * 1000)))
                : null;
              return (
                <div
                  style={{
                    marginTop: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 9px',
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.28) 0%, rgba(217,119,6,0.35) 100%)',
                    border: '1px solid rgba(251,191,36,0.55)',
                    boxShadow: '0 4px 14px rgba(245,158,11,0.25), inset 0 1px 0 rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    pointerEvents: 'none',
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#fde68a', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                    🪙 {doubleGcOffer.amount || 20} GC prochaine cmd
                    {daysLeft != null ? ` · ${daysLeft}j` : ''}
                  </span>
                </div>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ POPUP SONDAGE SATISFACTION ═══ */}
      <AnimatePresence>
        {showSurveyPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          >
            {/* Backdrop */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,12,22,0.65)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' }} />
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.88, opacity: 0, y: 20 }}
              transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 340 }}
            >
              <div style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.16) 100%)',
                backdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                border: '1px solid rgba(255,255,255,0.38)',
                borderRadius: '24px',
                padding: '28px 22px 22px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.55)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Ligne de brillance haut */}
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.65) 50%, transparent)', pointerEvents: 'none' }} />

                {/* Emoji + titre */}
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 42, marginBottom: 10, filter: 'drop-shadow(0 2px 8px rgba(255,255,255,0.3))' }}>⭐</div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 18, color: '#ffffff', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                    {t('surveyTitle')}
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 12.5, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.5 }}>
                    {t('surveySubtitle')}
                  </p>
                </div>

                {/* Zone de texte */}
                <textarea
                  value={surveyComment}
                  onChange={(e) => setSurveyComment(e.target.value)}
                  placeholder={t('shareExperiencePlaceholder')}
                  maxLength={500}
                  rows={4}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '14px',
                    color: '#ffffff',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13.5,
                    fontWeight: 400,
                    padding: '12px 14px',
                    resize: 'none',
                    outline: 'none',
                    boxSizing: 'border-box',
                    marginBottom: 14,
                    lineHeight: 1.5,
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.18)',
                    WebkitUserSelect: 'text',
                    userSelect: 'text',
                  }}
                  onFocus={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.5)'; e.target.style.background = 'rgba(255,255,255,0.12)'; }}
                  onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.25)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
                />

                {/* Compteur caractères */}
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.32)', textAlign: 'right', margin: '-10px 0 14px', paddingRight: 2 }}>
                  {surveyComment.length}/500
                </p>

                {/* Bouton Envoyer */}
                <button
                  onClick={async () => {
                    setSurveySending(true);
                    try {
                      await fetchWithTimeout(`${BACKEND_URL}/client/survey`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          chatId: telegramChatId,
                          comment: surveyComment,
                          orderId: pendingSurveyData?.orderId
                        })
                      }, 8000);
                    } catch (e) { console.error(e); }
                    setShowSurveyPopup(false);
                    setSurveyComment('');
                    setPendingSurveyData(null);
                    setSurveySending(false);
                  }}
                  disabled={surveySending || surveyComment.trim().length === 0}
                  style={{
                    width: '100%',
                    padding: '13px',
                    borderRadius: '14px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 800,
                    fontSize: 14,
                    letterSpacing: '0.04em',
                    background: surveyComment.trim().length > 0
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)'
                      : 'rgba(255,255,255,0.08)',
                    border: surveyComment.trim().length > 0
                      ? '1.5px solid rgba(255,255,255,0.45)'
                      : '1.5px solid rgba(255,255,255,0.15)',
                    color: surveyComment.trim().length > 0 ? '#ffffff' : 'rgba(255,255,255,0.35)',
                    cursor: surveyComment.trim().length > 0 ? 'pointer' : 'not-allowed',
                    marginBottom: 10,
                    transition: 'all 0.18s ease',
                    boxShadow: surveyComment.trim().length > 0 ? '0 4px 18px rgba(255,255,255,0.15)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {surveySending ? (
                    <Loader style={{ width: 16, height: 16, animation: 'spin-smooth 1s linear infinite' }} />
                  ) : t('surveySendBtn')}
                </button>

                {/* Bouton Une autre fois */}
                <button
                  onClick={() => {
                    // On ferme juste localement — pendingSurvey reste en base,
                    // la popup réapparaîtra à la prochaine session du client.
                    setShowSurveyPopup(false);
                    setSurveyComment('');
                  }}
                  style={{
                    width: '100%',
                    padding: '11px',
                    borderRadius: '12px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: 12.5,
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: 'rgba(255,255,255,0.45)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}
                >
                  Une autre fois
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ POPUP GLOBAL COINS - Notification livraison ═══ */}
      <AnimatePresence>
        {showCoinsPopup && pendingCoinsNotification && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ position: 'fixed', inset: 0, zIndex: 'var(--gs-z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,12,18,0.6)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)' }} onClick={() => setShowCoinsPopup(false)} />
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.88, opacity: 0, y: 16 }}
              transition={{ duration: 0.26, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 320 }}
            >
              <div style={{
                background: 'linear-gradient(135deg, rgba(30,140,255,0.22) 0%, rgba(255,255,255,0.10) 50%, rgba(30,80,200,0.18) 100%)',
                backdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                WebkitBackdropFilter: 'blur(50px) saturate(300%) brightness(1.12)',
                border: '1px solid rgba(125,211,252,0.38)',
                borderRadius: '22px',
                padding: '32px 24px',
                textAlign: 'center',
                boxShadow: '0 8px 40px rgba(30,140,255,0.25), 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.5)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(125,211,252,0.6) 50%, transparent)', pointerEvents: 'none' }} />
                <motion.div
                  animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.2, 1.2, 1.1, 1] }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}
                >
                  <CoinIcon size={56} style={{ filter: 'drop-shadow(0 0 16px rgba(125,211,252,0.7))' }} />
                </motion.div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 22, color: '#7dd3fc', margin: '0 0 4px', letterSpacing: '0.01em' }}>
                  Commande livrée !
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: 'rgba(255,255,255,0.85)', margin: '0 0 6px' }}>
                  Vous avez gagné
                </p>
                <motion.p
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900, fontSize: 36, color: '#38bdf8', margin: '0 0 4px', letterSpacing: '-0.02em' }}
                >
                  +{pendingCoinsNotification.amount} coins
                </motion.p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 12, color: 'rgba(125,211,252,0.6)', margin: '0 0 22px' }}>
                  1 coin = 1 CHF
                </p>
                <button
                  onClick={async () => {
                    try {
                      await fetchWithTimeout(`${BACKEND_URL}/client/claim-coins`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ chatId: telegramChatId })
                      }, 8000);
                    } catch (e) { console.error(e); }
                    setShowCoinsPopup(false);
                    setPendingCoinsNotification(null);
                    setCoinAnimationActive(true);
                    setTimeout(() => setCoinAnimationActive(false), 800);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(125,211,252,0.3) 0%, rgba(30,140,255,0.3) 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1.5px solid rgba(125,211,252,0.5)',
                    borderRadius: '12px',
                    color: '#7dd3fc',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: 15,
                    padding: '12px 32px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(30,140,255,0.25)',
                    width: '100%',
                  }}
                >
                  Récupérer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ POPUP SOLDE INSUFFISANT ═══ */}
      <AnimatePresence>
        {showInsufficientCoinsPopup && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.22 }}
            style={{
              position: 'fixed',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 'var(--gs-z-modal)',
              maxWidth: 300,
              width: '90%',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(239,68,68,0.22) 0%, rgba(185,28,28,0.18) 100%)',
              backdropFilter: 'blur(40px) saturate(280%)',
              WebkitBackdropFilter: 'blur(40px) saturate(280%)',
              border: '1.5px solid rgba(239,68,68,0.4)',
              borderRadius: '16px',
              padding: '14px 20px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(239,68,68,0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <CoinIcon size={20} style={{ opacity: 0.6 }} />
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 14, color: '#fca5a5' }}>
                  {showInsufficientCoinsPopup === 'monthly' ? 'Limite mensuelle atteinte' : 'Solde insuffisant'}
                </span>
              </div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 11.5, color: 'rgba(252,165,165,0.7)', margin: '4px 0 0' }}>
                {showInsufficientCoinsPopup === 'monthly'
                  ? 'Maximum 100 coins utilisables par mois'
                  : globalCoins < 50
                  ? `Minimum 50 coins requis (vous avez ${Math.max(0, globalCoins)} coins)`
                  : `Vous avez ${Math.max(0, globalCoins)} coins disponibles`}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ NOTIFICATION RUPTURE DE STOCK — LIQUID GLASS ═══ */}
      <AnimatePresence>
        {showSoldOutNotif && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.94 }}
            transition={{ duration: 0.24, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              position: 'fixed',
              bottom: 36,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 'var(--gs-z-modal)',
              maxWidth: 320,
              width: '92%',
              pointerEvents: 'none',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(30,30,50,0.82) 0%, rgba(15,15,30,0.88) 100%)',
              backdropFilter: 'blur(50px) saturate(220%) brightness(1.08)',
              WebkitBackdropFilter: 'blur(50px) saturate(220%) brightness(1.08)',
              border: '1.5px solid rgba(180,180,220,0.22)',
              borderRadius: '20px',
              padding: '16px 22px',
              textAlign: 'center',
              boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.10)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Ligne lumineuse haut */}
              <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(200,200,255,0.35) 50%, transparent)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 22 }}>🚫</span>
                <span style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 800,
                  fontSize: 15,
                  color: 'rgba(220,220,240,0.92)',
                  letterSpacing: '-0.01em',
                }}>Produit en rupture de stock</span>
              </div>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 12,
                color: 'rgba(180,180,210,0.60)',
                margin: 0,
                lineHeight: 1.4,
              }}>
                Ce produit est temporairement indisponible.<br/>Tu peux consulter les prix et la vidéo.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ POP-UP « PASSEZ PRIORITAIRE » — liquid glass, 1re visite du suivi de la commande ═══ */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showPriorityPopup && currentView === 'order-tracking' && activeOrder?.orderId && !activeOrder?.isPriority && (
            <motion.div
              key="priority-popup-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => { if (!priorityUpgradeLoading) setShowPriorityPopup(false); }}
              style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 'var(--gs-z-modal)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
                background: 'radial-gradient(circle at 50% 40%, rgba(30,80,200,0.28) 0%, rgba(4,8,24,0.72) 70%)',
                backdropFilter: 'blur(10px) saturate(140%)',
                WebkitBackdropFilter: 'blur(10px) saturate(140%)',
              }}
            >
              <motion.div
                key="priority-popup-card"
                role="dialog"
                aria-modal="true"
                aria-label="Passez prioritaire"
                initial={{ opacity: 0, scale: 0.88, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 16 }}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 380,
                  borderRadius: 30,
                  padding: '28px 22px 20px',
                  overflow: 'hidden',
                  textAlign: 'center',
                  color: '#fff',
                  background: 'linear-gradient(145deg, rgba(110,170,245,0.30) 0%, rgba(40,100,220,0.22) 45%, rgba(20,60,170,0.30) 100%)',
                  backdropFilter: 'blur(40px) saturate(300%) brightness(1.12)',
                  WebkitBackdropFilter: 'blur(40px) saturate(300%) brightness(1.12)',
                  border: '1.5px solid rgba(147,197,253,0.45)',
                  boxShadow: '0 24px 70px rgba(10,30,100,0.55), 0 0 60px rgba(74,144,226,0.28), inset 0 1.5px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(74,144,226,0.35)',
                }}
              >
                {/* reflet liquid glass */}
                <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1.5, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85) 50%, transparent)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: -60, left: -40, width: 200, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 70%)', pointerEvents: 'none' }} />

                <button
                  type="button"
                  onClick={() => setShowPriorityPopup(false)}
                  disabled={priorityUpgradeLoading}
                  aria-label="Fermer"
                  style={{
                    position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    color: 'rgba(255,255,255,0.85)', cursor: 'pointer', padding: 0,
                  }}
                >
                  <X className="w-4 h-4" />
                </button>

                <div style={{
                  width: 64, height: 64, margin: '0 auto 14px', borderRadius: 22,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.32) 0%, rgba(147,197,253,0.18) 100%)',
                  border: '1.5px solid rgba(255,255,255,0.45)',
                  boxShadow: '0 8px 24px rgba(74,144,226,0.45), inset 0 1px 0 rgba(255,255,255,0.55)',
                }}>
                  <Sparkles className="w-8 h-8" style={{ color: '#fff', filter: 'drop-shadow(0 2px 8px rgba(147,197,253,0.9))' }} />
                </div>

                <h3 style={{ margin: '0 0 8px', fontSize: 19, fontWeight: 900, letterSpacing: '0.02em', lineHeight: 1.25, textShadow: '0 2px 12px rgba(10,30,100,0.6)' }}>
                  PASSEZ PRIORITAIRE
                  <br />
                  <span style={{ color: '#bfdbfe' }}>
                    (+ {activeOrder?.country === 'suisse' ? '10CHF' : '10€'})
                  </span>
                </h3>
                <p style={{ margin: '0 0 18px', fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.78)' }}>
                  Votre commande passe en tête de la file : le livreur s'occupe de vous en premier et votre temps d'attente est réduit.
                </p>

                <motion.button
                  type="button"
                  onClick={handlePriorityUpgrade}
                  disabled={priorityUpgradeLoading}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 18,
                    fontWeight: 900, fontSize: 14, letterSpacing: '0.04em', color: '#fff',
                    cursor: priorityUpgradeLoading ? 'wait' : 'pointer',
                    opacity: priorityUpgradeLoading ? 0.7 : 1,
                    background: 'linear-gradient(135deg, rgba(96,165,250,0.70) 0%, rgba(37,99,235,0.65) 55%, rgba(29,78,216,0.70) 100%)',
                    border: '1.5px solid rgba(191,219,254,0.65)',
                    backdropFilter: 'blur(20px) saturate(250%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(250%)',
                    boxShadow: '0 10px 30px rgba(37,99,235,0.50), inset 0 1.5px 0 rgba(255,255,255,0.45)',
                  }}
                >
                  {priorityUpgradeLoading
                    ? 'Activation...'
                    : `⚡ PASSEZ PRIORITAIRE (+ ${activeOrder?.country === 'suisse' ? '10CHF' : '10€'})`}
                </motion.button>

                <button
                  type="button"
                  onClick={() => setShowPriorityPopup(false)}
                  disabled={priorityUpgradeLoading}
                  style={{
                    marginTop: 10, width: '100%', padding: '10px 12px', borderRadius: 14,
                    fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.65)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                  }}
                >
                  Non merci, rester en livraison standard
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <FloatingChatPanel
        title={activeOrder ? activeOrder.product : t('contactDriver')}
        subtitle={t('deliveryLabel')}
        isOpen={isChatOpen}
        isMinimized={isChatMinimized}
        onClose={() => {
          setIsChatOpen(false);
          setIsChatMinimized(false);
        }}
        onMinimize={() => setIsChatMinimized((prev) => !prev)}
        messages={chatMessages}
        inputValue={chatInput}
        onInputChange={setChatInput}
        onSend={handleChatSend}
        onPhotoUpload={handleChatPhotoUpload}
        photoInputId="client-chat-photo"
        unreadCount={chatUnreadCount}
        accentColor="#ffffff"
        role="client"
        sending={chatSending}
        uploading={chatUploading}
        uploadProgress={chatUploadProgress}
        uploadingMessageId={chatUploadingMessageId}
        backendUrl={BACKEND_URL}
        {...{ placeholder: t('sendMessage') }}
      />
      
      {/* ═══ BULLE CHAT FLOTTANTE — masquée en aperçu Access temporaire ═══ */}
      <AnimatePresence>
        {!shopPreviewMode && !isDashboardMode && !isDriverMode && activeOrder?.orderId && !isChatOpen && (
          <motion.button
            key="active-order-chat-bubble"
            type="button"
            initial={{ opacity: 0, scale: 0.5, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 24 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            whileHover={{ scale: 1.07 }}
            whileTap={{ scale: 0.93 }}
            onClick={() => { setIsChatOpen(true); setIsChatMinimized(false); setChatUnreadCount(0); }}
            aria-label={t('contactDriver')}
            style={{
              position: 'fixed',
              bottom: 22,
              right: 16,
              zIndex: 'var(--gs-z-modal)',
              width: 58,
              height: 58,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(74,144,226,0.85) 0%, rgba(30,80,180,0.9) 60%, rgba(10,20,80,0.92) 100%)',
              backdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
              WebkitBackdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
              border: '1.5px solid rgba(107,163,232,0.55)',
              boxShadow: '0 8px 28px rgba(74,144,226,0.45), 0 2px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.25)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <MessageCircle className="w-6 h-6" style={{ color: '#fff' }} />
            {chatUnreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 20,
                height: 20,
                padding: '0 5px',
                borderRadius: 10,
                background: '#ef4444',
                color: '#fff',
                fontSize: 11,
                fontWeight: 800,
                fontFamily: "'Inter', sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid rgba(255,255,255,0.65)',
                boxShadow: '0 2px 8px rgba(239,68,68,0.5)',
              }}>{chatUnreadCount > 9 ? '9+' : chatUnreadCount}</span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Effet de lumière ambiante premium */}
      {!isDashboardMode && !isDriverMode && <AmbientLightEffect />}

      {/* Modal Jeu Arcade */}
      {renderArcadeModal()}
      </>
      )}
      </>
      </motion.div>
      )}
      <PWAInstallModal
        open={showInstallModal}
        onClose={() => { if (!pwaInstallLocked) setShowInstallModal(false); }}
        chatId={telegramChatId}
        locked={pwaInstallLocked}
        denied={pwaInstallDenied}
      />

      {isAccessMode && (
        <AccessModal
          open={showAccessModal && !splashHold}
          chatId={telegramChatId}
          backendUrl={BACKEND_URL}
          entryStep={accessEntryStep}
          onBlocked={() => { /* UI gérée dans le modal */ }}
          onActivationConfirmed={onAccessActivationConfirmed}
          onEnterPreview={(nm) => {
            const name = String(nm || '').trim();
            setUserName(name);
            setAccessEntryStep(null);
            setShopPreviewMode(true);
            setShowAccessModal(false);
            setCurrentView('home');
            setIsShopOpen(true);
            setShowPreviewGamePromo(true);
            try {
              sessionStorage.setItem('gs_access_preview', '1');
              sessionStorage.setItem('gs_access_name', name);
              localStorage.setItem('gs_access_name', name);
            } catch { /* */ }
          }}
        />
      )}

      {isAccessMode && (
        <AccessPubModal
          open={showAccessPubModal}
          onClose={() => setShowAccessPubModal(false)}
          onCommencer={startAccessFromPub}
          userName={userName}
        />
      )}

      {/* Modal compte révoqué (/resetall) — liquid glass + CTA bot activation */}
      {showAccountDisabledModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 'var(--gs-z-lock)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(5,10,28,0.82)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          padding: 'max(16px, env(safe-area-inset-top)) 18px max(16px, env(safe-area-inset-bottom))',
        }}>
          <div style={{
            width: '100%', maxWidth: 380, borderRadius: 26, padding: '28px 22px 22px',
            textAlign: 'center', position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.40) 55%, rgba(10,20,80,0.55) 100%)',
            backdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
            WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
            border: '1.5px solid rgba(107,163,232,0.45)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.55), 0 0 48px rgba(74,144,226,0.2), inset 0 1px 0 rgba(255,255,255,0.22)',
            color: '#fff',
          }}>
            <div aria-hidden style={{
              position: 'absolute', top: -60, right: -40, width: 160, height: 160, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(74,144,226,0.35), transparent 70%)', pointerEvents: 'none',
            }} />
            <img
              src="/emoji/logoapp1.png"
              alt="Global Strains"
              style={{
                width: 84, height: 84, objectFit: 'contain', margin: '0 auto 14px',
                filter: 'drop-shadow(0 10px 28px rgba(251,191,36,0.35))', position: 'relative', zIndex: 1,
              }}
            />
            <p style={{
              margin: '0 0 8px', fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'rgba(147,197,253,0.85)', position: 'relative', zIndex: 1,
            }}>
              Accès révoqué
            </p>
            <h2 style={{
              fontSize: 22, fontWeight: 900, margin: '0 0 12px', lineHeight: 1.25,
              color: '#f0f9ff', position: 'relative', zIndex: 1,
            }}>
              Tes accès ont été révoqués
            </h2>
            <p style={{
              fontSize: 14, color: 'rgba(191,219,254,0.82)', lineHeight: 1.55,
              margin: '0 0 22px', position: 'relative', zIndex: 1,
            }}>
              Les administrateurs ont révoqué ton compte.
              Pour commander à nouveau, obten un nouvel accès via le bot d&apos;activation.
            </p>
            <button
              type="button"
              onClick={openActivationBot}
              style={{
                width: '100%', minHeight: 52, borderRadius: 16, fontWeight: 800, fontSize: 15, color: '#fff',
                cursor: 'pointer', position: 'relative', zIndex: 1,
                background: 'linear-gradient(135deg, rgba(74,144,226,0.75), rgba(37,99,235,0.92))',
                border: '1.5px solid rgba(147,197,253,0.5)',
                boxShadow: '0 12px 32px rgba(37,99,235,0.35)',
              }}
            >
              Obtenir un accès
            </button>
          </div>
        </div>
      )}

      {/* Promo jeu FOMO — aperçu Access */}
      <AnimatePresence>
        {showPreviewGamePromo && shopPreviewMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 'var(--gs-z-lock)',
              background: 'rgba(5,10,28,0.78)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 'max(16px, env(safe-area-inset-top)) 18px max(16px, env(safe-area-inset-bottom))',
            }}
            onClick={() => setShowPreviewGamePromo(false)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 380, borderRadius: 26, padding: '28px 22px 22px',
                textAlign: 'center', position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.38) 55%, rgba(10,20,80,0.52) 100%)',
                backdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
                WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
                border: '1.5px solid rgba(107,163,232,0.45)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.55), 0 0 48px rgba(74,144,226,0.2), inset 0 1px 0 rgba(255,255,255,0.22)',
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute', top: -50, right: -30, width: 140, height: 140, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(74,144,226,0.35), transparent 70%)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  width: 120, height: 120, margin: '0 auto 14px',
                  borderRadius: 22, overflow: 'hidden', position: 'relative', zIndex: 1,
                  boxShadow: '0 0 28px rgba(74,144,226,0.35)',
                }}
              >
                <img
                  src="/emoji/bird.png"
                  alt="Global Game"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center center',
                    transform: 'scale(1)',
                    transformOrigin: 'center center',
                    filter: 'drop-shadow(0 0 16px rgba(74,144,226,0.45))',
                  }}
                />
              </div>
              <p style={{
                margin: '0 0 6px', fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: 'rgba(147,197,253,0.85)', position: 'relative', zIndex: 1,
              }}>
                Global Game
              </p>
              <h3 style={{
                margin: '0 0 10px', fontSize: 22, fontWeight: 900, lineHeight: 1.2,
                color: '#fff', position: 'relative', zIndex: 1,
              }}>
                Grimpe au classement,{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #fb923c 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  gagne un accès
                </span>
              </h3>
              <p style={{
                margin: '0 0 8px', fontSize: 13, lineHeight: 1.55, color: 'rgba(191,219,254,0.82)',
                position: 'relative', zIndex: 1,
              }}>
                Le #1 remporte un accès gratuit. Classement remis à zéro chaque dimanche.
              </p>
              <p style={{
                margin: '0 0 22px', fontSize: 12, fontWeight: 700, color: 'rgba(134,239,172,0.9)',
                position: 'relative', zIndex: 1,
              }}>
                Tu as toute la semaine pour grimper
              </p>
              <button
                type="button"
                onClick={() => { setShowPreviewGamePromo(false); setShowArcadeModal(true); setArcadePhase('intro'); }}
                style={{
                  width: '100%', padding: '16px 20px', borderRadius: 16, border: 'none',
                  background: 'linear-gradient(145deg, rgba(0,229,255,0.38) 0%, rgba(0,100,255,0.55) 50%, rgba(112,0,255,0.48) 100%)',
                  color: '#fff', fontWeight: 900, fontSize: 15, cursor: 'pointer', marginBottom: 10,
                  boxShadow: '0 10px 32px rgba(0,140,255,0.4), inset 0 1px 0 rgba(255,255,255,0.35)',
                  position: 'relative', zIndex: 1,
                }}
              >
                Jouer maintenant
              </button>
              <button
                type="button"
                onClick={() => setShowPreviewGamePromo(false)}
                style={{
                  width: '100%', padding: '11px 16px', borderRadius: 12,
                  border: '1px solid rgba(107,163,232,0.28)', background: 'rgba(255,255,255,0.06)',
                  color: 'rgba(191,219,254,0.75)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  position: 'relative', zIndex: 1,
                }}
              >
                Plus tard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
    </LanguageContext.Provider>
  );
};

export default TelegramShopApp;